import type { ExplorerModel } from './types'

export const models = [
  {
    "slug": "price-of-going-back",
    "title": "The Price of Going Back",
    "shortTitle": "Price of Going Back",
    "summary": "Elicit how an identified participant values historical progress by asking what recurring compensation they would require to live without concrete capabilities and protections, then expose the resulting personal rollback curve together with its hard stops and methodological limits.",
    "version": "0.1.0",
    "status": "working-draft",
    "accent": "#f08a5d",
    "accentSoft": "#4a241f",
    "motif": "curve",
    "framing": "Follow a private judgment from one bounded loss to a cumulative rollback curve—without turning it into a universal price of progress.",
    "metrics": [
      {
        "value": "7",
        "label": "kernel propositions"
      },
      {
        "value": "8",
        "label": "named failure modes"
      },
      {
        "value": "7",
        "label": "invariants"
      }
    ],
    "lanes": [
      {
        "id": "kernel",
        "title": "Kernel",
        "description": "Propositions necessary to preserve the idea's identity.",
        "items": [
          {
            "id": "K1",
            "title": "Value progress through concrete rollback losses",
            "statement": "The experienced value of progress can be investigated by asking what a declared participant would require to surrender concrete capabilities or protections associated with moving into an earlier world.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "nodes.0",
              "line": 60
            },
            "rationale": "Loss counterfactuals turn an abstract judgment about progress into choices about the life the participant would actually have to live.",
            "links": [],
            "lineage": []
          },
          {
            "id": "K2",
            "title": "Value lived capabilities and protections",
            "statement": "The elicited object is the access, agency, safety, health, time, connection, or legal protection a milestone makes ordinary—not merely the named device, statute, product, or invention.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "nodes.1",
              "line": 71
            },
            "rationale": "Named technologies are proxies for consequences in a life.",
            "links": [],
            "lineage": []
          },
          {
            "id": "K3",
            "title": "Make each loss boundary explicit",
            "statement": "Each prompt must say what disappears and what remains so the participant can distinguish the focal loss from an unbounded collapse of the modern world.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "nodes.2",
              "line": 80
            },
            "rationale": "An unspecified counterfactual cannot support an auditable valuation.",
            "links": [],
            "lineage": []
          },
          {
            "id": "K4",
            "title": "Constrain compensation to the destination world",
            "statement": "Compensation may purchase only substitutes that exist and are practically obtainable in the world being considered; money cannot recreate an absent medicine, right, infrastructure, institution, or relationship.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "nodes.3",
              "line": 88
            },
            "rationale": "Otherwise the payment silently imports the present into the past.",
            "links": [],
            "lineage": []
          },
          {
            "id": "K5",
            "title": "Declare whose welfare is being elicited",
            "statement": "Every curve belongs to a declared participant scope, such as the participant alone or the participant and named dependents. It does not automatically represent other adults, a population, or society.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "nodes.4",
              "line": 97
            },
            "rationale": "Scope is part of the meaning of every response and aggregate.",
            "links": [],
            "lineage": []
          },
          {
            "id": "K6",
            "title": "Preserve distinct response states",
            "statement": "Positive compensation, neutral value, disvalue, invaluable refusal, and unanswered or unknown must remain distinguishable. An invaluable response is a boundary, not an arbitrarily large finite number.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "nodes.5",
              "line": 106
            },
            "rationale": "Collapsing these states erases morally and analytically different judgments.",
            "links": [],
            "lineage": []
          },
          {
            "id": "K7",
            "title": "Reveal a cumulative rollback curve with its limitations",
            "statement": "A rendering may combine declared milestone valuations into compensation at successive rollback horizons, but it must expose the aggregation rule and preserve overlap, dependence, incompleteness, and post-reveal audit as limitations rather than implying independent causal addends.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "nodes.6",
              "line": 115
            },
            "rationale": "The curve is an elicited model of a participant's answers, not a discovered law.",
            "links": [],
            "lineage": []
          }
        ]
      },
      {
        "id": "derived",
        "title": "Derived",
        "description": "Consequences of the kernel for an inspectable elicitation.",
        "items": [
          {
            "id": "D1",
            "title": "Hide the aggregate until local judgments exist",
            "statement": "Eliciting milestones before revealing their total reduces direct fitting of answers to a desired headline number and makes later surprise auditable.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "nodes.7",
              "line": 125
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K1"
              },
              {
                "type": "derived_from",
                "target": "K3"
              },
              {
                "type": "derived_from",
                "target": "K7"
              }
            ],
            "lineage": []
          },
          {
            "id": "D2",
            "title": "Invaluable losses impose a hard stop",
            "statement": "When the declared participant refuses a loss at any finite recurring payment, the reachable rollback curve ends at that boundary unless the participant revises it.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "nodes.8",
              "line": 133
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K4"
              },
              {
                "type": "derived_from",
                "target": "K5"
              },
              {
                "type": "derived_from",
                "target": "K6"
              },
              {
                "type": "derived_from",
                "target": "K7"
              }
            ],
            "lineage": []
          },
          {
            "id": "D3",
            "title": "The result must remain revisable and inspectable",
            "statement": "A participant must be able to revisit prompts, add omitted personal milestones, and record whether the revealed aggregate appears too low, plausible, or too high.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "nodes.9",
              "line": 141
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K3"
              },
              {
                "type": "derived_from",
                "target": "K5"
              },
              {
                "type": "derived_from",
                "target": "K7"
              }
            ],
            "lineage": []
          },
          {
            "id": "D4",
            "title": "Sharing should disclose less than elicitation",
            "statement": "A shareable result may communicate curve shape, coarse totals, and hard-stop position without disclosing every sensitive milestone response or private note.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "nodes.10",
              "line": 149
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K5"
              },
              {
                "type": "derived_from",
                "target": "K6"
              }
            ],
            "lineage": []
          }
        ]
      },
      {
        "id": "hypothesis",
        "title": "Hypothesis",
        "description": "Contestable empirical assumptions on which usefulness depends.",
        "items": [
          {
            "id": "H1",
            "title": "Participants can reason about bounded rollback counterfactuals",
            "statement": "With explicit loss and remainder descriptions, participants can form judgments that are stable enough to be useful for reflection.",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.11",
              "line": 157
            },
            "evidenceNeeded": "Repeated elicitations, reordered prompts, interviews, and comprehension checks.",
            "falsifier": "Repeated responses vary primarily with wording or order even after the loss boundary is clarified, or participants cannot state what world they evaluated.",
            "stoppingRule": "Stop interpreting the curve as participant value when comprehension or within-person stability remains poor after clarification.",
            "links": [
              {
                "type": "supports",
                "target": "K1"
              },
              {
                "type": "supports",
                "target": "K3"
              }
            ],
            "lineage": []
          },
          {
            "id": "H2",
            "title": "Recurring compensation is an interpretable common unit",
            "statement": "Annual compensation supplies a usable comparison scale across heterogeneous losses.",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.12",
              "line": 172
            },
            "evidenceNeeded": "Currency and income sensitivity tests, nonmonetary alternatives, and debriefs.",
            "falsifier": "Income, wealth, currency framing, or inability to imagine period-available substitutes dominates the ordering and magnitude of responses.",
            "stoppingRule": "Replace or supplement money when scale effects overwhelm the participant's substantive ranking of losses.",
            "links": [
              {
                "type": "supports",
                "target": "K1"
              },
              {
                "type": "supports",
                "target": "K4"
              },
              {
                "type": "supports",
                "target": "K7"
              }
            ],
            "lineage": []
          },
          {
            "id": "H3",
            "title": "Milestone prompts can approximate historical world differences",
            "statement": "A curated set of milestones can make important changes in lived capability and protection concrete enough to support a useful rollback exercise.",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.13",
              "line": 186
            },
            "evidenceNeeded": "Coverage audits, historical review, participant-added milestones, and subgroup review.",
            "falsifier": "Important omitted changes or historically incoherent bundles routinely reverse the curve's interpretation after participants add or correct them.",
            "stoppingRule": "Do not publish a horizon-level interpretation when known omissions or date errors are material to that horizon.",
            "links": [
              {
                "type": "supports",
                "target": "K1"
              },
              {
                "type": "supports",
                "target": "K2"
              },
              {
                "type": "supports",
                "target": "K7"
              }
            ],
            "lineage": []
          },
          {
            "id": "H4",
            "title": "Additive revelation can provoke useful audit",
            "statement": "Even when milestone valuations overlap, revealing their declared arithmetic sum can expose interactions, omissions, and double counting that isolated prompts would leave unnoticed.",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.14",
              "line": 201
            },
            "evidenceNeeded": "Post-reveal audit responses, overlap revisions, and alternative aggregation comparisons.",
            "falsifier": "Participants systematically treat the displayed total as validated or precise despite clear evidence that dependencies dominate it.",
            "stoppingRule": "Suppress or replace the additive headline when it predictably creates more false precision than reflective value.",
            "links": [
              {
                "type": "supports",
                "target": "K7"
              },
              {
                "type": "supports",
                "target": "D1"
              },
              {
                "type": "supports",
                "target": "D3"
              }
            ],
            "lineage": []
          },
          {
            "id": "H5",
            "title": "Private-by-default elicitation improves candor",
            "statement": "Keeping detailed answers local unless deliberately shared reduces social performance pressure and supports more candid reflection.",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.15",
              "line": 217
            },
            "evidenceNeeded": "Consented comparative studies and qualitative debriefs.",
            "falsifier": "Privacy defaults do not affect candor, abandonment, or revision behavior.",
            "stoppingRule": "Preserve privacy as a safety property even if its behavioral effect is uncertain.",
            "links": [
              {
                "type": "supports",
                "target": "K5"
              },
              {
                "type": "supports",
                "target": "D4"
              }
            ],
            "lineage": []
          }
        ]
      },
      {
        "id": "implementation",
        "title": "Implementation",
        "description": "Replaceable product or measurement choices.",
        "items": [
          {
            "id": "I1",
            "title": "Genie rollback framing",
            "statement": "A consenting group is offered recurring compensation to move into an earlier world.",
            "badge": "Implementation",
            "kind": "implementation",
            "source": {
              "path": "nodes.16",
              "line": 228
            },
            "alternatives": [
              "direct counterfactual interview",
              "card sort",
              "deliberative workshop"
            ],
            "links": [
              {
                "type": "implements",
                "target": "K1"
              },
              {
                "type": "implements",
                "target": "K4"
              },
              {
                "type": "implements",
                "target": "K5"
              }
            ],
            "lineage": []
          },
          {
            "id": "I2",
            "title": "Starter milestone categories",
            "statement": "Information and creation, health, mobility and logistics, home and place, and rights and public protections organize a starter set.",
            "badge": "Implementation",
            "kind": "implementation",
            "source": {
              "path": "nodes.17",
              "line": 235
            },
            "alternatives": [
              "participant-authored milestones",
              "region-specific sets",
              "capability taxonomies"
            ],
            "links": [
              {
                "type": "implements",
                "target": "K2"
              },
              {
                "type": "implements",
                "target": "H3"
              }
            ],
            "lineage": []
          },
          {
            "id": "I3",
            "title": "Annual dollar tiers plus custom values",
            "statement": "Participants can choose finite annual compensation, zero, disvalue, or invaluable.",
            "badge": "Implementation",
            "kind": "implementation",
            "source": {
              "path": "nodes.18",
              "line": 244
            },
            "alternatives": [
              "continuous values",
              "ordinal tradeoffs",
              "time or resource equivalents"
            ],
            "links": [
              {
                "type": "implements",
                "target": "K6"
              },
              {
                "type": "implements",
                "target": "H2"
              }
            ],
            "lineage": []
          },
          {
            "id": "I4",
            "title": "Standard rollback horizons",
            "statement": "The starter rendering inspects roughly 10, 20, 40, 80, and 160 years backward.",
            "badge": "Implementation",
            "kind": "implementation",
            "source": {
              "path": "nodes.19",
              "line": 251
            },
            "alternatives": [
              "calendar eras",
              "participant-selected years",
              "event-defined horizons"
            ],
            "links": [
              {
                "type": "implements",
                "target": "K7"
              },
              {
                "type": "implements",
                "target": "H3"
              }
            ],
            "lineage": []
          },
          {
            "id": "I5",
            "title": "Declared arithmetic sum with overlap audit",
            "statement": "Finite milestone responses after each target year are summed by category and in total; overlap cues and a post-reveal plausibility audit qualify the result.",
            "badge": "Implementation",
            "kind": "implementation",
            "source": {
              "path": "nodes.20",
              "line": 258
            },
            "alternatives": [
              "dependency-adjusted totals",
              "ranges",
              "scenario bundles",
              "no aggregate"
            ],
            "links": [
              {
                "type": "implements",
                "target": "K7"
              },
              {
                "type": "implements",
                "target": "D1"
              },
              {
                "type": "implements",
                "target": "D3"
              },
              {
                "type": "implements",
                "target": "H4"
              }
            ],
            "lineage": []
          },
          {
            "id": "I6",
            "title": "Local browser state and privacy-limited sharing",
            "statement": "Detailed responses remain in browser storage, while an optional share payload contains only curve points, category totals, and the hard-stop year.",
            "badge": "Implementation",
            "kind": "implementation",
            "source": {
              "path": "nodes.21",
              "line": 267
            },
            "alternatives": [
              "paper worksheet",
              "encrypted local file",
              "consented account storage"
            ],
            "links": [
              {
                "type": "implements",
                "target": "D4"
              },
              {
                "type": "implements",
                "target": "H5"
              }
            ],
            "lineage": []
          }
        ]
      },
      {
        "id": "open_question",
        "title": "Open Question",
        "description": "Unresolved questions that a faithful rendering keeps visible.",
        "items": [
          {
            "id": "Q1",
            "title": "How should wealth and income affect recurring compensation judgments?",
            "statement": "How should wealth and income affect recurring compensation judgments?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "nodes.22",
              "line": 276
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q2",
            "title": "Which milestone selection rules minimize omission and platform double counting?",
            "statement": "Which milestone selection rules minimize omission and platform double counting?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "nodes.23",
              "line": 280
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q3",
            "title": "When should dependencies replace simple additive aggregation?",
            "statement": "When should dependencies replace simple additive aggregation?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "nodes.24",
              "line": 284
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q4",
            "title": "How should geographically and demographically specific rights and protections be scoped?",
            "statement": "How should geographically and demographically specific rights and protections be scoped?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "nodes.25",
              "line": 288
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q5",
            "title": "Which nonmonetary elicitation methods preserve hard stops while improving comparability?",
            "statement": "Which nonmonetary elicitation methods preserve hard stops while improving comparability?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "nodes.26",
              "line": 292
            },
            "links": [],
            "lineage": []
          }
        ]
      }
    ],
    "paths": [
      {
        "id": "response_to_curve",
        "title": "Response To Curve",
        "steps": [
          "declared participant scope",
          "explicit loss and remainder boundary",
          "destination-bounded substitution judgment",
          "distinct response state",
          "declared aggregation rule",
          "hard-stop application",
          "post-reveal audit"
        ]
      },
      {
        "id": "rendering_fidelity",
        "title": "Rendering Fidelity",
        "steps": [
          "exact idea commit",
          "immutable rendering manifest digest",
          "recursively identified public assets",
          "kernel coverage and declared deviations",
          "attributable signed judgment"
        ]
      }
    ],
    "checks": [
      {
        "id": "F1",
        "title": "Personal-to-social substitution",
        "description": "A participant's curve is described as the value of progress for everyone.",
        "catches": [
          "K5"
        ],
        "tone": "guard",
        "source": {
          "path": "failure_modes.0",
          "line": 297
        }
      },
      {
        "id": "F2",
        "title": "Market-price substitution",
        "description": "Required compensation is described as an observed price, income effect, or causal welfare estimate.",
        "catches": [
          "K1",
          "K7"
        ],
        "tone": "guard",
        "source": {
          "path": "failure_modes.1",
          "line": 301
        }
      },
      {
        "id": "F3",
        "title": "Present smuggled into the past",
        "description": "Compensation is allowed to purchase absent contemporary capabilities or protections.",
        "catches": [
          "K4"
        ],
        "tone": "guard",
        "source": {
          "path": "failure_modes.2",
          "line": 305
        }
      },
      {
        "id": "F4",
        "title": "Counterfactual collapse",
        "description": "The prompt does not distinguish the focal loss from unrelated changes.",
        "catches": [
          "K3"
        ],
        "tone": "guard",
        "source": {
          "path": "failure_modes.3",
          "line": 309
        }
      },
      {
        "id": "F5",
        "title": "State collapse",
        "description": "Unanswered, zero, disvalue, and invaluable responses are encoded as the same number.",
        "catches": [
          "K6"
        ],
        "tone": "guard",
        "source": {
          "path": "failure_modes.4",
          "line": 313
        }
      },
      {
        "id": "F6",
        "title": "Additive false precision",
        "description": "Overlapping milestones are summed and presented as independent causal contributions.",
        "catches": [
          "K7",
          "H4"
        ],
        "tone": "guard",
        "source": {
          "path": "failure_modes.5",
          "line": 317
        }
      },
      {
        "id": "F7",
        "title": "Historical incoherence",
        "description": "Dates or bundles imply a world that never plausibly existed without disclosure.",
        "catches": [
          "H3"
        ],
        "tone": "guard",
        "source": {
          "path": "failure_modes.6",
          "line": 321
        }
      },
      {
        "id": "F8",
        "title": "Sensitive-detail leakage",
        "description": "Sharing a curve unexpectedly reveals individual answers, dependent scope, or private notes.",
        "catches": [
          "D4",
          "H5"
        ],
        "tone": "guard",
        "source": {
          "path": "failure_modes.7",
          "line": 325
        }
      },
      {
        "id": "INV-1",
        "title": "Invariant 1",
        "description": "The curve belongs to a declared participant scope.",
        "tone": "boundary",
        "source": {
          "path": "invariants.0",
          "line": 331
        }
      },
      {
        "id": "INV-2",
        "title": "Invariant 2",
        "description": "What disappears and what remains are inspectable for each prompted loss.",
        "tone": "boundary",
        "source": {
          "path": "invariants.1",
          "line": 332
        }
      },
      {
        "id": "INV-3",
        "title": "Invariant 3",
        "description": "Compensation cannot buy what is absent from the destination world.",
        "tone": "boundary",
        "source": {
          "path": "invariants.2",
          "line": 333
        }
      },
      {
        "id": "INV-4",
        "title": "Invariant 4",
        "description": "Invaluable, unanswered, neutral, disvalue, and finite positive responses remain distinct.",
        "tone": "boundary",
        "source": {
          "path": "invariants.3",
          "line": 334
        }
      },
      {
        "id": "INV-5",
        "title": "Invariant 5",
        "description": "The aggregation rule and its overlap limitations are inspectable.",
        "tone": "boundary",
        "source": {
          "path": "invariants.4",
          "line": 335
        }
      },
      {
        "id": "INV-6",
        "title": "Invariant 6",
        "description": "A hard stop is not converted into a large finite price.",
        "tone": "boundary",
        "source": {
          "path": "invariants.5",
          "line": 336
        }
      },
      {
        "id": "INV-7",
        "title": "Invariant 7",
        "description": "A rendering is bound to one exact idea commit and one digest-identified artifact.",
        "tone": "boundary",
        "source": {
          "path": "invariants.6",
          "line": 337
        }
      }
    ],
    "nonGoals": [
      "Compute the objective value of progress.",
      "Estimate social welfare, GDP, historical causality, or willingness to pay.",
      "Determine which milestones are morally good for every person.",
      "Produce a universal milestone chronology.",
      "Eliminate framing effects, income effects, dependence, or uncertainty by interface design.",
      "Require a particular website, currency, horizon set, category system, or aggregation formula."
    ],
    "guide": {
      "question": "What would it take to give up parts of modern life?",
      "introduction": "Consider a specific loss. Say what compensation you would need. Then explore how your answers add up as you move further into the past.",
      "premise": "A personal reflection on progress",
      "scope": "The curve describes one participant’s judgments. It is not an objective price of progress or a measure of society’s welfare.",
      "scopeRefs": [
        "K5",
        "K7"
      ],
      "mechanismTitle": "From one loss to a personal picture",
      "mechanismDescription": "Follow the proposed exercise. Select a step to see the rules and assumptions behind it.",
      "steps": [
        {
          "id": "loss",
          "title": "Define a loss",
          "description": "Choose a capability or protection. Make clear what disappears, what remains, and whose life is affected.",
          "refs": [
            "K2",
            "K3",
            "K5"
          ],
          "connection": "A bounded question"
        },
        {
          "id": "response",
          "title": "Make a judgment",
          "description": "Name compensation, express no loss or a preference for the change, refuse at any price, or leave the answer unknown.",
          "refs": [
            "K1",
            "K4",
            "K6"
          ],
          "connection": "Distinct responses"
        },
        {
          "id": "curve",
          "title": "Reveal the curve",
          "description": "Combine responses using a declared rule. An unacceptable loss creates a hard stop, not a very large price.",
          "refs": [
            "K7",
            "D1",
            "D2"
          ],
          "connection": "A provisional picture"
        },
        {
          "id": "audit",
          "title": "Reconsider it",
          "description": "Look for missing milestones and overlapping losses. Revise answers when the combined picture changes your judgment.",
          "refs": [
            "D3",
            "H3",
            "H4"
          ],
          "connection": "Revisions can return to earlier steps"
        }
      ],
      "terms": [
        {
          "id": "participant",
          "title": "Participant scope",
          "meaning": "Whose welfare an answer describes: the participant alone, or the participant and named dependents.",
          "refs": [
            "K5"
          ]
        },
        {
          "id": "milestone",
          "title": "Milestone",
          "meaning": "A concrete change in lived capability or protection—not just the name of an invention.",
          "refs": [
            "K2",
            "H3"
          ]
        },
        {
          "id": "boundary",
          "title": "Loss boundary",
          "meaning": "The distinction between what disappears and what remains in the imagined world.",
          "refs": [
            "K3",
            "K4"
          ]
        },
        {
          "id": "hard-stop",
          "title": "Hard stop",
          "meaning": "A loss the participant refuses at any finite payment. The reachable curve ends there unless that judgment changes.",
          "refs": [
            "K6",
            "D2"
          ]
        },
        {
          "id": "rollback",
          "title": "Rollback curve",
          "meaning": "A picture of declared compensation at successive points in the past, qualified by overlaps, missing losses, and hard stops.",
          "refs": [
            "K7"
          ]
        }
      ],
      "example": {
        "title": "Imagine losing a modern capability",
        "text": "The useful question is what changes in your life, not what the device costs. Payment can buy only substitutes available in that earlier world.",
        "refs": [
          "K2",
          "K3",
          "K4"
        ]
      },
      "uncertainty": {
        "title": "Can people make stable judgments about an imagined loss?",
        "text": "The model calls for repeated questions, reordered prompts, interviews, and comprehension checks. It does not supply their results.",
        "refs": [
          "H1"
        ]
      },
      "featured": [
        "H1",
        "H2",
        "H3",
        "H4",
        "H5"
      ]
    },
    "contextSources": [],
    "evidenceSummary": "This revision states hypotheses and the tests needed to challenge them. It does not report completed empirical validation. Source lineage, where supplied, explains where the thinking came from.",
    "sourceSections": [
      {
        "key": "idea",
        "value": {
          "id": "https://ideas.proximitytoprogress.com/ideas/price-of-going-back",
          "name": "The Price of Going Back"
        },
        "source": {
          "path": "idea",
          "line": 2
        }
      },
      {
        "key": "specification",
        "value": {
          "id": "price-of-going-back",
          "title": "The Price of Going Back",
          "version": "0.1.0",
          "status": "working-draft",
          "purpose": "Elicit how an identified participant values historical progress by asking what recurring compensation they would require to live without concrete capabilities and protections, then expose the resulting personal rollback curve together with its hard stops and methodological limits.",
          "authority": {
            "system": "git",
            "canonical_branch": "main",
            "rule": "A rendering or attestation MUST resolve the version it targets to a full immutable Git commit object ID. A branch or website URL is a discovery route, not the identity of an idea state."
          },
          "responsibility": {
            "canonical_editor": "Cole Miller",
            "ai_use_disclosure": "AI systems assisted with criticism, formalization, dependency analysis, publication engineering, and editing. Inclusion in this specification means the canonical editor accepts responsibility for the proposition."
          },
          "generator_contract": {
            "authoritative_object": "this specification at an immutable Git commit",
            "allowed": [
              "Change medium, language, pacing, visual form, and interaction design.",
              "Replace the milestone set when the selection rule and omissions remain visible.",
              "Use currencies or nonmonetary elicitation when the changed unit is declared.",
              "Limit the historical horizon or participant scope when the limitation is explicit."
            ],
            "forbidden": [
              "Present a personal elicitation as an objective price of progress or social welfare.",
              "Treat a moving branch or mutable deployment as an immutable idea or rendering state.",
              "Convert invaluable, unanswered, neutral, and disvalue responses into one state.",
              "Hide destination-world constraints on what compensation can purchase.",
              "Present additive increments as independent causal contributions without qualification."
            ]
          }
        },
        "source": {
          "path": "specification",
          "line": 6
        }
      },
      {
        "key": "typed_layers",
        "value": {
          "kernel": {
            "description": "Propositions necessary to preserve the idea's identity.",
            "prefix": "K"
          },
          "derived": {
            "description": "Consequences of the kernel for an inspectable elicitation.",
            "prefix": "D"
          },
          "hypothesis": {
            "description": "Contestable empirical assumptions on which usefulness depends.",
            "prefix": "H"
          },
          "implementation": {
            "description": "Replaceable product or measurement choices.",
            "prefix": "I"
          },
          "open_question": {
            "description": "Unresolved questions that a faithful rendering keeps visible.",
            "prefix": "Q"
          }
        },
        "source": {
          "path": "typed_layers",
          "line": 43
        }
      },
      {
        "key": "nodes",
        "value": [
          {
            "id": "K1",
            "kind": "kernel",
            "title": "Value progress through concrete rollback losses",
            "statement": "The experienced value of progress can be investigated by asking what a declared participant would require to surrender concrete capabilities or protections associated with moving into an earlier world.",
            "rationale": "Loss counterfactuals turn an abstract judgment about progress into choices about the life the participant would actually have to live."
          },
          {
            "id": "K2",
            "kind": "kernel",
            "title": "Value lived capabilities and protections",
            "statement": "The elicited object is the access, agency, safety, health, time, connection, or legal protection a milestone makes ordinary—not merely the named device, statute, product, or invention.",
            "rationale": "Named technologies are proxies for consequences in a life."
          },
          {
            "id": "K3",
            "kind": "kernel",
            "title": "Make each loss boundary explicit",
            "statement": "Each prompt must say what disappears and what remains so the participant can distinguish the focal loss from an unbounded collapse of the modern world.",
            "rationale": "An unspecified counterfactual cannot support an auditable valuation."
          },
          {
            "id": "K4",
            "kind": "kernel",
            "title": "Constrain compensation to the destination world",
            "statement": "Compensation may purchase only substitutes that exist and are practically obtainable in the world being considered; money cannot recreate an absent medicine, right, infrastructure, institution, or relationship.",
            "rationale": "Otherwise the payment silently imports the present into the past."
          },
          {
            "id": "K5",
            "kind": "kernel",
            "title": "Declare whose welfare is being elicited",
            "statement": "Every curve belongs to a declared participant scope, such as the participant alone or the participant and named dependents. It does not automatically represent other adults, a population, or society.",
            "rationale": "Scope is part of the meaning of every response and aggregate."
          },
          {
            "id": "K6",
            "kind": "kernel",
            "title": "Preserve distinct response states",
            "statement": "Positive compensation, neutral value, disvalue, invaluable refusal, and unanswered or unknown must remain distinguishable. An invaluable response is a boundary, not an arbitrarily large finite number.",
            "rationale": "Collapsing these states erases morally and analytically different judgments."
          },
          {
            "id": "K7",
            "kind": "kernel",
            "title": "Reveal a cumulative rollback curve with its limitations",
            "statement": "A rendering may combine declared milestone valuations into compensation at successive rollback horizons, but it must expose the aggregation rule and preserve overlap, dependence, incompleteness, and post-reveal audit as limitations rather than implying independent causal addends.",
            "rationale": "The curve is an elicited model of a participant's answers, not a discovered law."
          },
          {
            "id": "D1",
            "kind": "derived",
            "title": "Hide the aggregate until local judgments exist",
            "statement": "Eliciting milestones before revealing their total reduces direct fitting of answers to a desired headline number and makes later surprise auditable.",
            "derived_from": [
              "K1",
              "K3",
              "K7"
            ]
          },
          {
            "id": "D2",
            "kind": "derived",
            "title": "Invaluable losses impose a hard stop",
            "statement": "When the declared participant refuses a loss at any finite recurring payment, the reachable rollback curve ends at that boundary unless the participant revises it.",
            "derived_from": [
              "K4",
              "K5",
              "K6",
              "K7"
            ]
          },
          {
            "id": "D3",
            "kind": "derived",
            "title": "The result must remain revisable and inspectable",
            "statement": "A participant must be able to revisit prompts, add omitted personal milestones, and record whether the revealed aggregate appears too low, plausible, or too high.",
            "derived_from": [
              "K3",
              "K5",
              "K7"
            ]
          },
          {
            "id": "D4",
            "kind": "derived",
            "title": "Sharing should disclose less than elicitation",
            "statement": "A shareable result may communicate curve shape, coarse totals, and hard-stop position without disclosing every sensitive milestone response or private note.",
            "derived_from": [
              "K5",
              "K6"
            ]
          },
          {
            "id": "H1",
            "kind": "hypothesis",
            "title": "Participants can reason about bounded rollback counterfactuals",
            "statement": "With explicit loss and remainder descriptions, participants can form judgments that are stable enough to be useful for reflection.",
            "supports": [
              "K1",
              "K3"
            ],
            "falsifier": "Repeated responses vary primarily with wording or order even after the loss boundary is clarified, or participants cannot state what world they evaluated.",
            "evidence_needed": "Repeated elicitations, reordered prompts, interviews, and comprehension checks.",
            "stopping_rule": "Stop interpreting the curve as participant value when comprehension or within-person stability remains poor after clarification."
          },
          {
            "id": "H2",
            "kind": "hypothesis",
            "title": "Recurring compensation is an interpretable common unit",
            "statement": "Annual compensation supplies a usable comparison scale across heterogeneous losses.",
            "supports": [
              "K1",
              "K4",
              "K7"
            ],
            "falsifier": "Income, wealth, currency framing, or inability to imagine period-available substitutes dominates the ordering and magnitude of responses.",
            "evidence_needed": "Currency and income sensitivity tests, nonmonetary alternatives, and debriefs.",
            "stopping_rule": "Replace or supplement money when scale effects overwhelm the participant's substantive ranking of losses."
          },
          {
            "id": "H3",
            "kind": "hypothesis",
            "title": "Milestone prompts can approximate historical world differences",
            "statement": "A curated set of milestones can make important changes in lived capability and protection concrete enough to support a useful rollback exercise.",
            "supports": [
              "K1",
              "K2",
              "K7"
            ],
            "falsifier": "Important omitted changes or historically incoherent bundles routinely reverse the curve's interpretation after participants add or correct them.",
            "evidence_needed": "Coverage audits, historical review, participant-added milestones, and subgroup review.",
            "stopping_rule": "Do not publish a horizon-level interpretation when known omissions or date errors are material to that horizon."
          },
          {
            "id": "H4",
            "kind": "hypothesis",
            "title": "Additive revelation can provoke useful audit",
            "statement": "Even when milestone valuations overlap, revealing their declared arithmetic sum can expose interactions, omissions, and double counting that isolated prompts would leave unnoticed.",
            "supports": [
              "K7",
              "D1",
              "D3"
            ],
            "falsifier": "Participants systematically treat the displayed total as validated or precise despite clear evidence that dependencies dominate it.",
            "evidence_needed": "Post-reveal audit responses, overlap revisions, and alternative aggregation comparisons.",
            "stopping_rule": "Suppress or replace the additive headline when it predictably creates more false precision than reflective value."
          },
          {
            "id": "H5",
            "kind": "hypothesis",
            "title": "Private-by-default elicitation improves candor",
            "statement": "Keeping detailed answers local unless deliberately shared reduces social performance pressure and supports more candid reflection.",
            "supports": [
              "K5",
              "D4"
            ],
            "falsifier": "Privacy defaults do not affect candor, abandonment, or revision behavior.",
            "evidence_needed": "Consented comparative studies and qualitative debriefs.",
            "stopping_rule": "Preserve privacy as a safety property even if its behavioral effect is uncertain."
          },
          {
            "id": "I1",
            "kind": "implementation",
            "title": "Genie rollback framing",
            "statement": "A consenting group is offered recurring compensation to move into an earlier world.",
            "implements": [
              "K1",
              "K4",
              "K5"
            ],
            "replaceable_with": [
              "direct counterfactual interview",
              "card sort",
              "deliberative workshop"
            ]
          },
          {
            "id": "I2",
            "kind": "implementation",
            "title": "Starter milestone categories",
            "statement": "Information and creation, health, mobility and logistics, home and place, and rights and public protections organize a starter set.",
            "implements": [
              "K2",
              "H3"
            ],
            "replaceable_with": [
              "participant-authored milestones",
              "region-specific sets",
              "capability taxonomies"
            ]
          },
          {
            "id": "I3",
            "kind": "implementation",
            "title": "Annual dollar tiers plus custom values",
            "statement": "Participants can choose finite annual compensation, zero, disvalue, or invaluable.",
            "implements": [
              "K6",
              "H2"
            ],
            "replaceable_with": [
              "continuous values",
              "ordinal tradeoffs",
              "time or resource equivalents"
            ]
          },
          {
            "id": "I4",
            "kind": "implementation",
            "title": "Standard rollback horizons",
            "statement": "The starter rendering inspects roughly 10, 20, 40, 80, and 160 years backward.",
            "implements": [
              "K7",
              "H3"
            ],
            "replaceable_with": [
              "calendar eras",
              "participant-selected years",
              "event-defined horizons"
            ]
          },
          {
            "id": "I5",
            "kind": "implementation",
            "title": "Declared arithmetic sum with overlap audit",
            "statement": "Finite milestone responses after each target year are summed by category and in total; overlap cues and a post-reveal plausibility audit qualify the result.",
            "implements": [
              "K7",
              "D1",
              "D3",
              "H4"
            ],
            "replaceable_with": [
              "dependency-adjusted totals",
              "ranges",
              "scenario bundles",
              "no aggregate"
            ]
          },
          {
            "id": "I6",
            "kind": "implementation",
            "title": "Local browser state and privacy-limited sharing",
            "statement": "Detailed responses remain in browser storage, while an optional share payload contains only curve points, category totals, and the hard-stop year.",
            "implements": [
              "D4",
              "H5"
            ],
            "replaceable_with": [
              "paper worksheet",
              "encrypted local file",
              "consented account storage"
            ]
          },
          {
            "id": "Q1",
            "kind": "open_question",
            "title": "How should wealth and income affect recurring compensation judgments?"
          },
          {
            "id": "Q2",
            "kind": "open_question",
            "title": "Which milestone selection rules minimize omission and platform double counting?"
          },
          {
            "id": "Q3",
            "kind": "open_question",
            "title": "When should dependencies replace simple additive aggregation?"
          },
          {
            "id": "Q4",
            "kind": "open_question",
            "title": "How should geographically and demographically specific rights and protections be scoped?"
          },
          {
            "id": "Q5",
            "kind": "open_question",
            "title": "Which nonmonetary elicitation methods preserve hard stops while improving comparability?"
          }
        ],
        "source": {
          "path": "nodes",
          "line": 60
        }
      },
      {
        "key": "failure_modes",
        "value": [
          {
            "id": "F1",
            "name": "Personal-to-social substitution",
            "signature": "A participant's curve is described as the value of progress for everyone.",
            "catches": [
              "K5"
            ]
          },
          {
            "id": "F2",
            "name": "Market-price substitution",
            "signature": "Required compensation is described as an observed price, income effect, or causal welfare estimate.",
            "catches": [
              "K1",
              "K7"
            ]
          },
          {
            "id": "F3",
            "name": "Present smuggled into the past",
            "signature": "Compensation is allowed to purchase absent contemporary capabilities or protections.",
            "catches": [
              "K4"
            ]
          },
          {
            "id": "F4",
            "name": "Counterfactual collapse",
            "signature": "The prompt does not distinguish the focal loss from unrelated changes.",
            "catches": [
              "K3"
            ]
          },
          {
            "id": "F5",
            "name": "State collapse",
            "signature": "Unanswered, zero, disvalue, and invaluable responses are encoded as the same number.",
            "catches": [
              "K6"
            ]
          },
          {
            "id": "F6",
            "name": "Additive false precision",
            "signature": "Overlapping milestones are summed and presented as independent causal contributions.",
            "catches": [
              "K7",
              "H4"
            ]
          },
          {
            "id": "F7",
            "name": "Historical incoherence",
            "signature": "Dates or bundles imply a world that never plausibly existed without disclosure.",
            "catches": [
              "H3"
            ]
          },
          {
            "id": "F8",
            "name": "Sensitive-detail leakage",
            "signature": "Sharing a curve unexpectedly reveals individual answers, dependent scope, or private notes.",
            "catches": [
              "D4",
              "H5"
            ]
          }
        ],
        "source": {
          "path": "failure_modes",
          "line": 297
        }
      },
      {
        "key": "invariants",
        "value": [
          "The curve belongs to a declared participant scope.",
          "What disappears and what remains are inspectable for each prompted loss.",
          "Compensation cannot buy what is absent from the destination world.",
          "Invaluable, unanswered, neutral, disvalue, and finite positive responses remain distinct.",
          "The aggregation rule and its overlap limitations are inspectable.",
          "A hard stop is not converted into a large finite price.",
          "A rendering is bound to one exact idea commit and one digest-identified artifact."
        ],
        "source": {
          "path": "invariants",
          "line": 331
        }
      },
      {
        "key": "non_goals",
        "value": [
          "Compute the objective value of progress.",
          "Estimate social welfare, GDP, historical causality, or willingness to pay.",
          "Determine which milestones are morally good for every person.",
          "Produce a universal milestone chronology.",
          "Eliminate framing effects, income effects, dependence, or uncertainty by interface design.",
          "Require a particular website, currency, horizon set, category system, or aggregation formula."
        ],
        "source": {
          "path": "non_goals",
          "line": 340
        }
      },
      {
        "key": "evidence_paths",
        "value": {
          "response_to_curve": {
            "path": [
              "declared participant scope",
              "explicit loss and remainder boundary",
              "destination-bounded substitution judgment",
              "distinct response state",
              "declared aggregation rule",
              "hard-stop application",
              "post-reveal audit"
            ]
          },
          "rendering_fidelity": {
            "path": [
              "exact idea commit",
              "immutable rendering manifest digest",
              "recursively identified public assets",
              "kernel coverage and declared deviations",
              "attributable signed judgment"
            ]
          }
        },
        "source": {
          "path": "evidence_paths",
          "line": 348
        }
      },
      {
        "key": "decision_criteria",
        "value": {
          "faithful_rendering": {
            "must": [
              "preserve the rollback-loss elicitation rather than substitute abstract approval of progress",
              "identify the participant scope and keep the result personal to it",
              "constrain compensation to substitutes available in the destination world",
              "preserve finite positive, neutral, disvalue, invaluable, and unanswered states",
              "expose what disappears and what remains",
              "state how milestone responses become a horizon-level curve",
              "preserve hard stops without finite-number substitution",
              "disclose overlap, omission, and historical-counterfactual limitations"
            ],
            "must_not": [
              "claim an objective, market, causal, or population-wide price of progress",
              "present registration or an author signature as independent verification"
            ]
          }
        },
        "source": {
          "path": "decision_criteria",
          "line": 366
        }
      }
    ],
    "sourceLocations": {
      "idea": {
        "path": "idea",
        "line": 2
      },
      "idea.id": {
        "path": "idea.id",
        "line": 2
      },
      "idea.name": {
        "path": "idea.name",
        "line": 3
      },
      "specification": {
        "path": "specification",
        "line": 6
      },
      "specification.id": {
        "path": "specification.id",
        "line": 6
      },
      "specification.title": {
        "path": "specification.title",
        "line": 7
      },
      "specification.version": {
        "path": "specification.version",
        "line": 8
      },
      "specification.status": {
        "path": "specification.status",
        "line": 9
      },
      "specification.purpose": {
        "path": "specification.purpose",
        "line": 10
      },
      "specification.authority": {
        "path": "specification.authority",
        "line": 16
      },
      "specification.authority.system": {
        "path": "specification.authority.system",
        "line": 16
      },
      "specification.authority.canonical_branch": {
        "path": "specification.authority.canonical_branch",
        "line": 17
      },
      "specification.authority.rule": {
        "path": "specification.authority.rule",
        "line": 18
      },
      "specification.responsibility": {
        "path": "specification.responsibility",
        "line": 23
      },
      "specification.responsibility.canonical_editor": {
        "path": "specification.responsibility.canonical_editor",
        "line": 23
      },
      "specification.responsibility.ai_use_disclosure": {
        "path": "specification.responsibility.ai_use_disclosure",
        "line": 24
      },
      "specification.generator_contract": {
        "path": "specification.generator_contract",
        "line": 29
      },
      "specification.generator_contract.authoritative_object": {
        "path": "specification.generator_contract.authoritative_object",
        "line": 29
      },
      "specification.generator_contract.allowed": {
        "path": "specification.generator_contract.allowed",
        "line": 31
      },
      "specification.generator_contract.allowed.0": {
        "path": "specification.generator_contract.allowed.0",
        "line": 31
      },
      "specification.generator_contract.allowed.1": {
        "path": "specification.generator_contract.allowed.1",
        "line": 32
      },
      "specification.generator_contract.allowed.2": {
        "path": "specification.generator_contract.allowed.2",
        "line": 33
      },
      "specification.generator_contract.allowed.3": {
        "path": "specification.generator_contract.allowed.3",
        "line": 34
      },
      "specification.generator_contract.forbidden": {
        "path": "specification.generator_contract.forbidden",
        "line": 36
      },
      "specification.generator_contract.forbidden.0": {
        "path": "specification.generator_contract.forbidden.0",
        "line": 36
      },
      "specification.generator_contract.forbidden.1": {
        "path": "specification.generator_contract.forbidden.1",
        "line": 37
      },
      "specification.generator_contract.forbidden.2": {
        "path": "specification.generator_contract.forbidden.2",
        "line": 38
      },
      "specification.generator_contract.forbidden.3": {
        "path": "specification.generator_contract.forbidden.3",
        "line": 39
      },
      "specification.generator_contract.forbidden.4": {
        "path": "specification.generator_contract.forbidden.4",
        "line": 40
      },
      "typed_layers": {
        "path": "typed_layers",
        "line": 43
      },
      "typed_layers.kernel": {
        "path": "typed_layers.kernel",
        "line": 44
      },
      "typed_layers.kernel.description": {
        "path": "typed_layers.kernel.description",
        "line": 44
      },
      "typed_layers.kernel.prefix": {
        "path": "typed_layers.kernel.prefix",
        "line": 45
      },
      "typed_layers.derived": {
        "path": "typed_layers.derived",
        "line": 47
      },
      "typed_layers.derived.description": {
        "path": "typed_layers.derived.description",
        "line": 47
      },
      "typed_layers.derived.prefix": {
        "path": "typed_layers.derived.prefix",
        "line": 48
      },
      "typed_layers.hypothesis": {
        "path": "typed_layers.hypothesis",
        "line": 50
      },
      "typed_layers.hypothesis.description": {
        "path": "typed_layers.hypothesis.description",
        "line": 50
      },
      "typed_layers.hypothesis.prefix": {
        "path": "typed_layers.hypothesis.prefix",
        "line": 51
      },
      "typed_layers.implementation": {
        "path": "typed_layers.implementation",
        "line": 53
      },
      "typed_layers.implementation.description": {
        "path": "typed_layers.implementation.description",
        "line": 53
      },
      "typed_layers.implementation.prefix": {
        "path": "typed_layers.implementation.prefix",
        "line": 54
      },
      "typed_layers.open_question": {
        "path": "typed_layers.open_question",
        "line": 56
      },
      "typed_layers.open_question.description": {
        "path": "typed_layers.open_question.description",
        "line": 56
      },
      "typed_layers.open_question.prefix": {
        "path": "typed_layers.open_question.prefix",
        "line": 57
      },
      "nodes": {
        "path": "nodes",
        "line": 60
      },
      "nodes.0": {
        "path": "nodes.0",
        "line": 60
      },
      "nodes.0.id": {
        "path": "nodes.0.id",
        "line": 60
      },
      "nodes.0.kind": {
        "path": "nodes.0.kind",
        "line": 61
      },
      "nodes.0.title": {
        "path": "nodes.0.title",
        "line": 62
      },
      "nodes.0.statement": {
        "path": "nodes.0.statement",
        "line": 63
      },
      "nodes.0.rationale": {
        "path": "nodes.0.rationale",
        "line": 67
      },
      "nodes.1": {
        "path": "nodes.1",
        "line": 71
      },
      "nodes.1.id": {
        "path": "nodes.1.id",
        "line": 71
      },
      "nodes.1.kind": {
        "path": "nodes.1.kind",
        "line": 72
      },
      "nodes.1.title": {
        "path": "nodes.1.title",
        "line": 73
      },
      "nodes.1.statement": {
        "path": "nodes.1.statement",
        "line": 74
      },
      "nodes.1.rationale": {
        "path": "nodes.1.rationale",
        "line": 78
      },
      "nodes.2": {
        "path": "nodes.2",
        "line": 80
      },
      "nodes.2.id": {
        "path": "nodes.2.id",
        "line": 80
      },
      "nodes.2.kind": {
        "path": "nodes.2.kind",
        "line": 81
      },
      "nodes.2.title": {
        "path": "nodes.2.title",
        "line": 82
      },
      "nodes.2.statement": {
        "path": "nodes.2.statement",
        "line": 83
      },
      "nodes.2.rationale": {
        "path": "nodes.2.rationale",
        "line": 86
      },
      "nodes.3": {
        "path": "nodes.3",
        "line": 88
      },
      "nodes.3.id": {
        "path": "nodes.3.id",
        "line": 88
      },
      "nodes.3.kind": {
        "path": "nodes.3.kind",
        "line": 89
      },
      "nodes.3.title": {
        "path": "nodes.3.title",
        "line": 90
      },
      "nodes.3.statement": {
        "path": "nodes.3.statement",
        "line": 91
      },
      "nodes.3.rationale": {
        "path": "nodes.3.rationale",
        "line": 95
      },
      "nodes.4": {
        "path": "nodes.4",
        "line": 97
      },
      "nodes.4.id": {
        "path": "nodes.4.id",
        "line": 97
      },
      "nodes.4.kind": {
        "path": "nodes.4.kind",
        "line": 98
      },
      "nodes.4.title": {
        "path": "nodes.4.title",
        "line": 99
      },
      "nodes.4.statement": {
        "path": "nodes.4.statement",
        "line": 100
      },
      "nodes.4.rationale": {
        "path": "nodes.4.rationale",
        "line": 104
      },
      "nodes.5": {
        "path": "nodes.5",
        "line": 106
      },
      "nodes.5.id": {
        "path": "nodes.5.id",
        "line": 106
      },
      "nodes.5.kind": {
        "path": "nodes.5.kind",
        "line": 107
      },
      "nodes.5.title": {
        "path": "nodes.5.title",
        "line": 108
      },
      "nodes.5.statement": {
        "path": "nodes.5.statement",
        "line": 109
      },
      "nodes.5.rationale": {
        "path": "nodes.5.rationale",
        "line": 113
      },
      "nodes.6": {
        "path": "nodes.6",
        "line": 115
      },
      "nodes.6.id": {
        "path": "nodes.6.id",
        "line": 115
      },
      "nodes.6.kind": {
        "path": "nodes.6.kind",
        "line": 116
      },
      "nodes.6.title": {
        "path": "nodes.6.title",
        "line": 117
      },
      "nodes.6.statement": {
        "path": "nodes.6.statement",
        "line": 118
      },
      "nodes.6.rationale": {
        "path": "nodes.6.rationale",
        "line": 123
      },
      "nodes.7": {
        "path": "nodes.7",
        "line": 125
      },
      "nodes.7.id": {
        "path": "nodes.7.id",
        "line": 125
      },
      "nodes.7.kind": {
        "path": "nodes.7.kind",
        "line": 126
      },
      "nodes.7.title": {
        "path": "nodes.7.title",
        "line": 127
      },
      "nodes.7.statement": {
        "path": "nodes.7.statement",
        "line": 128
      },
      "nodes.7.derived_from": {
        "path": "nodes.7.derived_from",
        "line": 131
      },
      "nodes.7.derived_from.0": {
        "path": "nodes.7.derived_from.0",
        "line": 131
      },
      "nodes.7.derived_from.1": {
        "path": "nodes.7.derived_from.1",
        "line": 131
      },
      "nodes.7.derived_from.2": {
        "path": "nodes.7.derived_from.2",
        "line": 131
      },
      "nodes.8": {
        "path": "nodes.8",
        "line": 133
      },
      "nodes.8.id": {
        "path": "nodes.8.id",
        "line": 133
      },
      "nodes.8.kind": {
        "path": "nodes.8.kind",
        "line": 134
      },
      "nodes.8.title": {
        "path": "nodes.8.title",
        "line": 135
      },
      "nodes.8.statement": {
        "path": "nodes.8.statement",
        "line": 136
      },
      "nodes.8.derived_from": {
        "path": "nodes.8.derived_from",
        "line": 139
      },
      "nodes.8.derived_from.0": {
        "path": "nodes.8.derived_from.0",
        "line": 139
      },
      "nodes.8.derived_from.1": {
        "path": "nodes.8.derived_from.1",
        "line": 139
      },
      "nodes.8.derived_from.2": {
        "path": "nodes.8.derived_from.2",
        "line": 139
      },
      "nodes.8.derived_from.3": {
        "path": "nodes.8.derived_from.3",
        "line": 139
      },
      "nodes.9": {
        "path": "nodes.9",
        "line": 141
      },
      "nodes.9.id": {
        "path": "nodes.9.id",
        "line": 141
      },
      "nodes.9.kind": {
        "path": "nodes.9.kind",
        "line": 142
      },
      "nodes.9.title": {
        "path": "nodes.9.title",
        "line": 143
      },
      "nodes.9.statement": {
        "path": "nodes.9.statement",
        "line": 144
      },
      "nodes.9.derived_from": {
        "path": "nodes.9.derived_from",
        "line": 147
      },
      "nodes.9.derived_from.0": {
        "path": "nodes.9.derived_from.0",
        "line": 147
      },
      "nodes.9.derived_from.1": {
        "path": "nodes.9.derived_from.1",
        "line": 147
      },
      "nodes.9.derived_from.2": {
        "path": "nodes.9.derived_from.2",
        "line": 147
      },
      "nodes.10": {
        "path": "nodes.10",
        "line": 149
      },
      "nodes.10.id": {
        "path": "nodes.10.id",
        "line": 149
      },
      "nodes.10.kind": {
        "path": "nodes.10.kind",
        "line": 150
      },
      "nodes.10.title": {
        "path": "nodes.10.title",
        "line": 151
      },
      "nodes.10.statement": {
        "path": "nodes.10.statement",
        "line": 152
      },
      "nodes.10.derived_from": {
        "path": "nodes.10.derived_from",
        "line": 155
      },
      "nodes.10.derived_from.0": {
        "path": "nodes.10.derived_from.0",
        "line": 155
      },
      "nodes.10.derived_from.1": {
        "path": "nodes.10.derived_from.1",
        "line": 155
      },
      "nodes.11": {
        "path": "nodes.11",
        "line": 157
      },
      "nodes.11.id": {
        "path": "nodes.11.id",
        "line": 157
      },
      "nodes.11.kind": {
        "path": "nodes.11.kind",
        "line": 158
      },
      "nodes.11.title": {
        "path": "nodes.11.title",
        "line": 159
      },
      "nodes.11.statement": {
        "path": "nodes.11.statement",
        "line": 160
      },
      "nodes.11.supports": {
        "path": "nodes.11.supports",
        "line": 163
      },
      "nodes.11.supports.0": {
        "path": "nodes.11.supports.0",
        "line": 163
      },
      "nodes.11.supports.1": {
        "path": "nodes.11.supports.1",
        "line": 163
      },
      "nodes.11.falsifier": {
        "path": "nodes.11.falsifier",
        "line": 164
      },
      "nodes.11.evidence_needed": {
        "path": "nodes.11.evidence_needed",
        "line": 167
      },
      "nodes.11.stopping_rule": {
        "path": "nodes.11.stopping_rule",
        "line": 168
      },
      "nodes.12": {
        "path": "nodes.12",
        "line": 172
      },
      "nodes.12.id": {
        "path": "nodes.12.id",
        "line": 172
      },
      "nodes.12.kind": {
        "path": "nodes.12.kind",
        "line": 173
      },
      "nodes.12.title": {
        "path": "nodes.12.title",
        "line": 174
      },
      "nodes.12.statement": {
        "path": "nodes.12.statement",
        "line": 175
      },
      "nodes.12.supports": {
        "path": "nodes.12.supports",
        "line": 177
      },
      "nodes.12.supports.0": {
        "path": "nodes.12.supports.0",
        "line": 177
      },
      "nodes.12.supports.1": {
        "path": "nodes.12.supports.1",
        "line": 177
      },
      "nodes.12.supports.2": {
        "path": "nodes.12.supports.2",
        "line": 177
      },
      "nodes.12.falsifier": {
        "path": "nodes.12.falsifier",
        "line": 178
      },
      "nodes.12.evidence_needed": {
        "path": "nodes.12.evidence_needed",
        "line": 181
      },
      "nodes.12.stopping_rule": {
        "path": "nodes.12.stopping_rule",
        "line": 182
      },
      "nodes.13": {
        "path": "nodes.13",
        "line": 186
      },
      "nodes.13.id": {
        "path": "nodes.13.id",
        "line": 186
      },
      "nodes.13.kind": {
        "path": "nodes.13.kind",
        "line": 187
      },
      "nodes.13.title": {
        "path": "nodes.13.title",
        "line": 188
      },
      "nodes.13.statement": {
        "path": "nodes.13.statement",
        "line": 189
      },
      "nodes.13.supports": {
        "path": "nodes.13.supports",
        "line": 192
      },
      "nodes.13.supports.0": {
        "path": "nodes.13.supports.0",
        "line": 192
      },
      "nodes.13.supports.1": {
        "path": "nodes.13.supports.1",
        "line": 192
      },
      "nodes.13.supports.2": {
        "path": "nodes.13.supports.2",
        "line": 192
      },
      "nodes.13.falsifier": {
        "path": "nodes.13.falsifier",
        "line": 193
      },
      "nodes.13.evidence_needed": {
        "path": "nodes.13.evidence_needed",
        "line": 196
      },
      "nodes.13.stopping_rule": {
        "path": "nodes.13.stopping_rule",
        "line": 197
      },
      "nodes.14": {
        "path": "nodes.14",
        "line": 201
      },
      "nodes.14.id": {
        "path": "nodes.14.id",
        "line": 201
      },
      "nodes.14.kind": {
        "path": "nodes.14.kind",
        "line": 202
      },
      "nodes.14.title": {
        "path": "nodes.14.title",
        "line": 203
      },
      "nodes.14.statement": {
        "path": "nodes.14.statement",
        "line": 204
      },
      "nodes.14.supports": {
        "path": "nodes.14.supports",
        "line": 208
      },
      "nodes.14.supports.0": {
        "path": "nodes.14.supports.0",
        "line": 208
      },
      "nodes.14.supports.1": {
        "path": "nodes.14.supports.1",
        "line": 208
      },
      "nodes.14.supports.2": {
        "path": "nodes.14.supports.2",
        "line": 208
      },
      "nodes.14.falsifier": {
        "path": "nodes.14.falsifier",
        "line": 209
      },
      "nodes.14.evidence_needed": {
        "path": "nodes.14.evidence_needed",
        "line": 212
      },
      "nodes.14.stopping_rule": {
        "path": "nodes.14.stopping_rule",
        "line": 213
      },
      "nodes.15": {
        "path": "nodes.15",
        "line": 217
      },
      "nodes.15.id": {
        "path": "nodes.15.id",
        "line": 217
      },
      "nodes.15.kind": {
        "path": "nodes.15.kind",
        "line": 218
      },
      "nodes.15.title": {
        "path": "nodes.15.title",
        "line": 219
      },
      "nodes.15.statement": {
        "path": "nodes.15.statement",
        "line": 220
      },
      "nodes.15.supports": {
        "path": "nodes.15.supports",
        "line": 223
      },
      "nodes.15.supports.0": {
        "path": "nodes.15.supports.0",
        "line": 223
      },
      "nodes.15.supports.1": {
        "path": "nodes.15.supports.1",
        "line": 223
      },
      "nodes.15.falsifier": {
        "path": "nodes.15.falsifier",
        "line": 224
      },
      "nodes.15.evidence_needed": {
        "path": "nodes.15.evidence_needed",
        "line": 225
      },
      "nodes.15.stopping_rule": {
        "path": "nodes.15.stopping_rule",
        "line": 226
      },
      "nodes.16": {
        "path": "nodes.16",
        "line": 228
      },
      "nodes.16.id": {
        "path": "nodes.16.id",
        "line": 228
      },
      "nodes.16.kind": {
        "path": "nodes.16.kind",
        "line": 229
      },
      "nodes.16.title": {
        "path": "nodes.16.title",
        "line": 230
      },
      "nodes.16.statement": {
        "path": "nodes.16.statement",
        "line": 231
      },
      "nodes.16.implements": {
        "path": "nodes.16.implements",
        "line": 232
      },
      "nodes.16.implements.0": {
        "path": "nodes.16.implements.0",
        "line": 232
      },
      "nodes.16.implements.1": {
        "path": "nodes.16.implements.1",
        "line": 232
      },
      "nodes.16.implements.2": {
        "path": "nodes.16.implements.2",
        "line": 232
      },
      "nodes.16.replaceable_with": {
        "path": "nodes.16.replaceable_with",
        "line": 233
      },
      "nodes.16.replaceable_with.0": {
        "path": "nodes.16.replaceable_with.0",
        "line": 233
      },
      "nodes.16.replaceable_with.1": {
        "path": "nodes.16.replaceable_with.1",
        "line": 233
      },
      "nodes.16.replaceable_with.2": {
        "path": "nodes.16.replaceable_with.2",
        "line": 233
      },
      "nodes.17": {
        "path": "nodes.17",
        "line": 235
      },
      "nodes.17.id": {
        "path": "nodes.17.id",
        "line": 235
      },
      "nodes.17.kind": {
        "path": "nodes.17.kind",
        "line": 236
      },
      "nodes.17.title": {
        "path": "nodes.17.title",
        "line": 237
      },
      "nodes.17.statement": {
        "path": "nodes.17.statement",
        "line": 238
      },
      "nodes.17.implements": {
        "path": "nodes.17.implements",
        "line": 241
      },
      "nodes.17.implements.0": {
        "path": "nodes.17.implements.0",
        "line": 241
      },
      "nodes.17.implements.1": {
        "path": "nodes.17.implements.1",
        "line": 241
      },
      "nodes.17.replaceable_with": {
        "path": "nodes.17.replaceable_with",
        "line": 242
      },
      "nodes.17.replaceable_with.0": {
        "path": "nodes.17.replaceable_with.0",
        "line": 242
      },
      "nodes.17.replaceable_with.1": {
        "path": "nodes.17.replaceable_with.1",
        "line": 242
      },
      "nodes.17.replaceable_with.2": {
        "path": "nodes.17.replaceable_with.2",
        "line": 242
      },
      "nodes.18": {
        "path": "nodes.18",
        "line": 244
      },
      "nodes.18.id": {
        "path": "nodes.18.id",
        "line": 244
      },
      "nodes.18.kind": {
        "path": "nodes.18.kind",
        "line": 245
      },
      "nodes.18.title": {
        "path": "nodes.18.title",
        "line": 246
      },
      "nodes.18.statement": {
        "path": "nodes.18.statement",
        "line": 247
      },
      "nodes.18.implements": {
        "path": "nodes.18.implements",
        "line": 248
      },
      "nodes.18.implements.0": {
        "path": "nodes.18.implements.0",
        "line": 248
      },
      "nodes.18.implements.1": {
        "path": "nodes.18.implements.1",
        "line": 248
      },
      "nodes.18.replaceable_with": {
        "path": "nodes.18.replaceable_with",
        "line": 249
      },
      "nodes.18.replaceable_with.0": {
        "path": "nodes.18.replaceable_with.0",
        "line": 249
      },
      "nodes.18.replaceable_with.1": {
        "path": "nodes.18.replaceable_with.1",
        "line": 249
      },
      "nodes.18.replaceable_with.2": {
        "path": "nodes.18.replaceable_with.2",
        "line": 249
      },
      "nodes.19": {
        "path": "nodes.19",
        "line": 251
      },
      "nodes.19.id": {
        "path": "nodes.19.id",
        "line": 251
      },
      "nodes.19.kind": {
        "path": "nodes.19.kind",
        "line": 252
      },
      "nodes.19.title": {
        "path": "nodes.19.title",
        "line": 253
      },
      "nodes.19.statement": {
        "path": "nodes.19.statement",
        "line": 254
      },
      "nodes.19.implements": {
        "path": "nodes.19.implements",
        "line": 255
      },
      "nodes.19.implements.0": {
        "path": "nodes.19.implements.0",
        "line": 255
      },
      "nodes.19.implements.1": {
        "path": "nodes.19.implements.1",
        "line": 255
      },
      "nodes.19.replaceable_with": {
        "path": "nodes.19.replaceable_with",
        "line": 256
      },
      "nodes.19.replaceable_with.0": {
        "path": "nodes.19.replaceable_with.0",
        "line": 256
      },
      "nodes.19.replaceable_with.1": {
        "path": "nodes.19.replaceable_with.1",
        "line": 256
      },
      "nodes.19.replaceable_with.2": {
        "path": "nodes.19.replaceable_with.2",
        "line": 256
      },
      "nodes.20": {
        "path": "nodes.20",
        "line": 258
      },
      "nodes.20.id": {
        "path": "nodes.20.id",
        "line": 258
      },
      "nodes.20.kind": {
        "path": "nodes.20.kind",
        "line": 259
      },
      "nodes.20.title": {
        "path": "nodes.20.title",
        "line": 260
      },
      "nodes.20.statement": {
        "path": "nodes.20.statement",
        "line": 261
      },
      "nodes.20.implements": {
        "path": "nodes.20.implements",
        "line": 264
      },
      "nodes.20.implements.0": {
        "path": "nodes.20.implements.0",
        "line": 264
      },
      "nodes.20.implements.1": {
        "path": "nodes.20.implements.1",
        "line": 264
      },
      "nodes.20.implements.2": {
        "path": "nodes.20.implements.2",
        "line": 264
      },
      "nodes.20.implements.3": {
        "path": "nodes.20.implements.3",
        "line": 264
      },
      "nodes.20.replaceable_with": {
        "path": "nodes.20.replaceable_with",
        "line": 265
      },
      "nodes.20.replaceable_with.0": {
        "path": "nodes.20.replaceable_with.0",
        "line": 265
      },
      "nodes.20.replaceable_with.1": {
        "path": "nodes.20.replaceable_with.1",
        "line": 265
      },
      "nodes.20.replaceable_with.2": {
        "path": "nodes.20.replaceable_with.2",
        "line": 265
      },
      "nodes.20.replaceable_with.3": {
        "path": "nodes.20.replaceable_with.3",
        "line": 265
      },
      "nodes.21": {
        "path": "nodes.21",
        "line": 267
      },
      "nodes.21.id": {
        "path": "nodes.21.id",
        "line": 267
      },
      "nodes.21.kind": {
        "path": "nodes.21.kind",
        "line": 268
      },
      "nodes.21.title": {
        "path": "nodes.21.title",
        "line": 269
      },
      "nodes.21.statement": {
        "path": "nodes.21.statement",
        "line": 270
      },
      "nodes.21.implements": {
        "path": "nodes.21.implements",
        "line": 273
      },
      "nodes.21.implements.0": {
        "path": "nodes.21.implements.0",
        "line": 273
      },
      "nodes.21.implements.1": {
        "path": "nodes.21.implements.1",
        "line": 273
      },
      "nodes.21.replaceable_with": {
        "path": "nodes.21.replaceable_with",
        "line": 274
      },
      "nodes.21.replaceable_with.0": {
        "path": "nodes.21.replaceable_with.0",
        "line": 274
      },
      "nodes.21.replaceable_with.1": {
        "path": "nodes.21.replaceable_with.1",
        "line": 274
      },
      "nodes.21.replaceable_with.2": {
        "path": "nodes.21.replaceable_with.2",
        "line": 274
      },
      "nodes.22": {
        "path": "nodes.22",
        "line": 276
      },
      "nodes.22.id": {
        "path": "nodes.22.id",
        "line": 276
      },
      "nodes.22.kind": {
        "path": "nodes.22.kind",
        "line": 277
      },
      "nodes.22.title": {
        "path": "nodes.22.title",
        "line": 278
      },
      "nodes.23": {
        "path": "nodes.23",
        "line": 280
      },
      "nodes.23.id": {
        "path": "nodes.23.id",
        "line": 280
      },
      "nodes.23.kind": {
        "path": "nodes.23.kind",
        "line": 281
      },
      "nodes.23.title": {
        "path": "nodes.23.title",
        "line": 282
      },
      "nodes.24": {
        "path": "nodes.24",
        "line": 284
      },
      "nodes.24.id": {
        "path": "nodes.24.id",
        "line": 284
      },
      "nodes.24.kind": {
        "path": "nodes.24.kind",
        "line": 285
      },
      "nodes.24.title": {
        "path": "nodes.24.title",
        "line": 286
      },
      "nodes.25": {
        "path": "nodes.25",
        "line": 288
      },
      "nodes.25.id": {
        "path": "nodes.25.id",
        "line": 288
      },
      "nodes.25.kind": {
        "path": "nodes.25.kind",
        "line": 289
      },
      "nodes.25.title": {
        "path": "nodes.25.title",
        "line": 290
      },
      "nodes.26": {
        "path": "nodes.26",
        "line": 292
      },
      "nodes.26.id": {
        "path": "nodes.26.id",
        "line": 292
      },
      "nodes.26.kind": {
        "path": "nodes.26.kind",
        "line": 293
      },
      "nodes.26.title": {
        "path": "nodes.26.title",
        "line": 294
      },
      "failure_modes": {
        "path": "failure_modes",
        "line": 297
      },
      "failure_modes.0": {
        "path": "failure_modes.0",
        "line": 297
      },
      "failure_modes.0.id": {
        "path": "failure_modes.0.id",
        "line": 297
      },
      "failure_modes.0.name": {
        "path": "failure_modes.0.name",
        "line": 298
      },
      "failure_modes.0.signature": {
        "path": "failure_modes.0.signature",
        "line": 299
      },
      "failure_modes.0.catches": {
        "path": "failure_modes.0.catches",
        "line": 300
      },
      "failure_modes.0.catches.0": {
        "path": "failure_modes.0.catches.0",
        "line": 300
      },
      "failure_modes.1": {
        "path": "failure_modes.1",
        "line": 301
      },
      "failure_modes.1.id": {
        "path": "failure_modes.1.id",
        "line": 301
      },
      "failure_modes.1.name": {
        "path": "failure_modes.1.name",
        "line": 302
      },
      "failure_modes.1.signature": {
        "path": "failure_modes.1.signature",
        "line": 303
      },
      "failure_modes.1.catches": {
        "path": "failure_modes.1.catches",
        "line": 304
      },
      "failure_modes.1.catches.0": {
        "path": "failure_modes.1.catches.0",
        "line": 304
      },
      "failure_modes.1.catches.1": {
        "path": "failure_modes.1.catches.1",
        "line": 304
      },
      "failure_modes.2": {
        "path": "failure_modes.2",
        "line": 305
      },
      "failure_modes.2.id": {
        "path": "failure_modes.2.id",
        "line": 305
      },
      "failure_modes.2.name": {
        "path": "failure_modes.2.name",
        "line": 306
      },
      "failure_modes.2.signature": {
        "path": "failure_modes.2.signature",
        "line": 307
      },
      "failure_modes.2.catches": {
        "path": "failure_modes.2.catches",
        "line": 308
      },
      "failure_modes.2.catches.0": {
        "path": "failure_modes.2.catches.0",
        "line": 308
      },
      "failure_modes.3": {
        "path": "failure_modes.3",
        "line": 309
      },
      "failure_modes.3.id": {
        "path": "failure_modes.3.id",
        "line": 309
      },
      "failure_modes.3.name": {
        "path": "failure_modes.3.name",
        "line": 310
      },
      "failure_modes.3.signature": {
        "path": "failure_modes.3.signature",
        "line": 311
      },
      "failure_modes.3.catches": {
        "path": "failure_modes.3.catches",
        "line": 312
      },
      "failure_modes.3.catches.0": {
        "path": "failure_modes.3.catches.0",
        "line": 312
      },
      "failure_modes.4": {
        "path": "failure_modes.4",
        "line": 313
      },
      "failure_modes.4.id": {
        "path": "failure_modes.4.id",
        "line": 313
      },
      "failure_modes.4.name": {
        "path": "failure_modes.4.name",
        "line": 314
      },
      "failure_modes.4.signature": {
        "path": "failure_modes.4.signature",
        "line": 315
      },
      "failure_modes.4.catches": {
        "path": "failure_modes.4.catches",
        "line": 316
      },
      "failure_modes.4.catches.0": {
        "path": "failure_modes.4.catches.0",
        "line": 316
      },
      "failure_modes.5": {
        "path": "failure_modes.5",
        "line": 317
      },
      "failure_modes.5.id": {
        "path": "failure_modes.5.id",
        "line": 317
      },
      "failure_modes.5.name": {
        "path": "failure_modes.5.name",
        "line": 318
      },
      "failure_modes.5.signature": {
        "path": "failure_modes.5.signature",
        "line": 319
      },
      "failure_modes.5.catches": {
        "path": "failure_modes.5.catches",
        "line": 320
      },
      "failure_modes.5.catches.0": {
        "path": "failure_modes.5.catches.0",
        "line": 320
      },
      "failure_modes.5.catches.1": {
        "path": "failure_modes.5.catches.1",
        "line": 320
      },
      "failure_modes.6": {
        "path": "failure_modes.6",
        "line": 321
      },
      "failure_modes.6.id": {
        "path": "failure_modes.6.id",
        "line": 321
      },
      "failure_modes.6.name": {
        "path": "failure_modes.6.name",
        "line": 322
      },
      "failure_modes.6.signature": {
        "path": "failure_modes.6.signature",
        "line": 323
      },
      "failure_modes.6.catches": {
        "path": "failure_modes.6.catches",
        "line": 324
      },
      "failure_modes.6.catches.0": {
        "path": "failure_modes.6.catches.0",
        "line": 324
      },
      "failure_modes.7": {
        "path": "failure_modes.7",
        "line": 325
      },
      "failure_modes.7.id": {
        "path": "failure_modes.7.id",
        "line": 325
      },
      "failure_modes.7.name": {
        "path": "failure_modes.7.name",
        "line": 326
      },
      "failure_modes.7.signature": {
        "path": "failure_modes.7.signature",
        "line": 327
      },
      "failure_modes.7.catches": {
        "path": "failure_modes.7.catches",
        "line": 328
      },
      "failure_modes.7.catches.0": {
        "path": "failure_modes.7.catches.0",
        "line": 328
      },
      "failure_modes.7.catches.1": {
        "path": "failure_modes.7.catches.1",
        "line": 328
      },
      "invariants": {
        "path": "invariants",
        "line": 331
      },
      "invariants.0": {
        "path": "invariants.0",
        "line": 331
      },
      "invariants.1": {
        "path": "invariants.1",
        "line": 332
      },
      "invariants.2": {
        "path": "invariants.2",
        "line": 333
      },
      "invariants.3": {
        "path": "invariants.3",
        "line": 334
      },
      "invariants.4": {
        "path": "invariants.4",
        "line": 335
      },
      "invariants.5": {
        "path": "invariants.5",
        "line": 336
      },
      "invariants.6": {
        "path": "invariants.6",
        "line": 337
      },
      "non_goals": {
        "path": "non_goals",
        "line": 340
      },
      "non_goals.0": {
        "path": "non_goals.0",
        "line": 340
      },
      "non_goals.1": {
        "path": "non_goals.1",
        "line": 341
      },
      "non_goals.2": {
        "path": "non_goals.2",
        "line": 342
      },
      "non_goals.3": {
        "path": "non_goals.3",
        "line": 343
      },
      "non_goals.4": {
        "path": "non_goals.4",
        "line": 344
      },
      "non_goals.5": {
        "path": "non_goals.5",
        "line": 345
      },
      "evidence_paths": {
        "path": "evidence_paths",
        "line": 348
      },
      "evidence_paths.response_to_curve": {
        "path": "evidence_paths.response_to_curve",
        "line": 349
      },
      "evidence_paths.response_to_curve.path": {
        "path": "evidence_paths.response_to_curve.path",
        "line": 350
      },
      "evidence_paths.response_to_curve.path.0": {
        "path": "evidence_paths.response_to_curve.path.0",
        "line": 350
      },
      "evidence_paths.response_to_curve.path.1": {
        "path": "evidence_paths.response_to_curve.path.1",
        "line": 351
      },
      "evidence_paths.response_to_curve.path.2": {
        "path": "evidence_paths.response_to_curve.path.2",
        "line": 352
      },
      "evidence_paths.response_to_curve.path.3": {
        "path": "evidence_paths.response_to_curve.path.3",
        "line": 353
      },
      "evidence_paths.response_to_curve.path.4": {
        "path": "evidence_paths.response_to_curve.path.4",
        "line": 354
      },
      "evidence_paths.response_to_curve.path.5": {
        "path": "evidence_paths.response_to_curve.path.5",
        "line": 355
      },
      "evidence_paths.response_to_curve.path.6": {
        "path": "evidence_paths.response_to_curve.path.6",
        "line": 356
      },
      "evidence_paths.rendering_fidelity": {
        "path": "evidence_paths.rendering_fidelity",
        "line": 358
      },
      "evidence_paths.rendering_fidelity.path": {
        "path": "evidence_paths.rendering_fidelity.path",
        "line": 359
      },
      "evidence_paths.rendering_fidelity.path.0": {
        "path": "evidence_paths.rendering_fidelity.path.0",
        "line": 359
      },
      "evidence_paths.rendering_fidelity.path.1": {
        "path": "evidence_paths.rendering_fidelity.path.1",
        "line": 360
      },
      "evidence_paths.rendering_fidelity.path.2": {
        "path": "evidence_paths.rendering_fidelity.path.2",
        "line": 361
      },
      "evidence_paths.rendering_fidelity.path.3": {
        "path": "evidence_paths.rendering_fidelity.path.3",
        "line": 362
      },
      "evidence_paths.rendering_fidelity.path.4": {
        "path": "evidence_paths.rendering_fidelity.path.4",
        "line": 363
      },
      "decision_criteria": {
        "path": "decision_criteria",
        "line": 366
      },
      "decision_criteria.faithful_rendering": {
        "path": "decision_criteria.faithful_rendering",
        "line": 367
      },
      "decision_criteria.faithful_rendering.must": {
        "path": "decision_criteria.faithful_rendering.must",
        "line": 368
      },
      "decision_criteria.faithful_rendering.must.0": {
        "path": "decision_criteria.faithful_rendering.must.0",
        "line": 368
      },
      "decision_criteria.faithful_rendering.must.1": {
        "path": "decision_criteria.faithful_rendering.must.1",
        "line": 369
      },
      "decision_criteria.faithful_rendering.must.2": {
        "path": "decision_criteria.faithful_rendering.must.2",
        "line": 370
      },
      "decision_criteria.faithful_rendering.must.3": {
        "path": "decision_criteria.faithful_rendering.must.3",
        "line": 371
      },
      "decision_criteria.faithful_rendering.must.4": {
        "path": "decision_criteria.faithful_rendering.must.4",
        "line": 372
      },
      "decision_criteria.faithful_rendering.must.5": {
        "path": "decision_criteria.faithful_rendering.must.5",
        "line": 373
      },
      "decision_criteria.faithful_rendering.must.6": {
        "path": "decision_criteria.faithful_rendering.must.6",
        "line": 374
      },
      "decision_criteria.faithful_rendering.must.7": {
        "path": "decision_criteria.faithful_rendering.must.7",
        "line": 375
      },
      "decision_criteria.faithful_rendering.must_not": {
        "path": "decision_criteria.faithful_rendering.must_not",
        "line": 377
      },
      "decision_criteria.faithful_rendering.must_not.0": {
        "path": "decision_criteria.faithful_rendering.must_not.0",
        "line": 377
      },
      "decision_criteria.faithful_rendering.must_not.1": {
        "path": "decision_criteria.faithful_rendering.must_not.1",
        "line": 378
      }
    },
    "experience": {
      "label": "Try the interactive graph",
      "url": "https://proximitytoprogress.com/values-graph/",
      "description": "Interactive web experience"
    },
    "repository": "https://github.com/uncomposed/price-of-going-back-idea.git",
    "commit": "12bd9c6298025759dedf82b799474cdffd9bf5ed",
    "modelPath": "spec/price-of-going-back.yaml",
    "modelSha256": "9e4ae96ee04b6af0f8e2c9dd704dfc7754304d00a2f15ffabd3a2754a147117b",
    "modelUrl": "/ideas/models/price-of-going-back-9e4ae96ee04b.yaml",
    "registryUrl": "https://ideas.proximitytoprogress.com/ideas/price-of-going-back"
  },
  {
    "slug": "cislunar-momentum-loop",
    "title": "Cislunar Momentum Loop",
    "shortTitle": "Cislunar Momentum Loop",
    "summary": "A staged, reusable lunar transport architecture combining a surface mass driver, momentum-exchange tethers, cargo shuttles and aerocapture to reduce recurring propellant and total Earth-origin mass during buildout and sustainment.",
    "version": "0.1.0",
    "status": "working-draft",
    "accent": "#79d4e8",
    "accentSoft": "#15364a",
    "motif": "orbit",
    "framing": "Minimize lifecycle Earth-origin mass delivered to a declared reference orbit while delivering a required lunar buildout and useful cargo flow. Primary screen: equivalent Earth-origin mass to reference LEO (EMLEO).",
    "metrics": [
      {
        "value": "7",
        "label": "kernel propositions"
      },
      {
        "value": "5",
        "label": "feasibility gates"
      },
      {
        "value": "7",
        "label": "tracked resources"
      }
    ],
    "lanes": [
      {
        "id": "kernel",
        "title": "Kernel",
        "description": "Propositions necessary to preserve the identity of the idea.",
        "items": [
          {
            "id": "K1",
            "title": "Optimize lifecycle Earth-origin mass",
            "statement": "Compare complete buildout and sustainment ledgers on one declared boundary; do not optimize a vehicle or propellant fraction while hiding infrastructure, replacement, counterflow or production mass elsewhere.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "kernel.0",
              "line": 77
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "K2",
            "title": "Treat lunar launch throughput as scarce",
            "statement": "At a fixed injection speed and efficiency, mass-driver energy and throughput scale with every kilogram launched, including propellant and expendable flight hardware as well as useful product.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "kernel.1",
              "line": 83
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "K3",
            "title": "Reuse the transport kit where reuse closes",
            "statement": "Separate useful cargo from guidance, propulsion, capture and thermal hardware so expensive flight systems can circulate when inspection, return and cycle life are better than replacement.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "kernel.2",
              "line": 89
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "K4",
            "title": "Account for momentum rather than claiming free propulsion",
            "statement": "A tether exchanges momentum between payloads, its orbit and its spin. Every catch and throw must expose the balancing counterflow, reboost or secular state change.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "kernel.3",
              "line": 95
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "K5",
            "title": "Remove energy with the least lifecycle burden",
            "statement": "Use propulsion, momentum exchange and atmospheric braking only where the complete vehicle, infrastructure, reserve and recovery ledgers make them preferable to alternatives.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "kernel.4",
              "line": 101
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "K6",
            "title": "Bootstrap in independently useful stages",
            "statement": "No promoted architecture may assume mature lunar production, mass-driver throughput, tether service or depots before the preceding stage can build, commission and sustain them.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "kernel.5",
              "line": 107
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "K7",
            "title": "Keep unclosed interfaces explicit",
            "statement": "Ground-track access is not rendezvous, a rigid-tip crossing is not capture, an aerocapture proxy is not a reusable thermal design, and a conditional mass advantage is not flight readiness.",
            "badge": "Kernel",
            "kind": "kernel",
            "source": {
              "path": "kernel.6",
              "line": 113
            },
            "links": [],
            "lineage": []
          }
        ]
      },
      {
        "id": "derived",
        "title": "Derived mechanisms",
        "description": "Mechanisms that follow from the kernel when their assumptions hold.",
        "items": [
          {
            "id": "D1",
            "title": "Lunar tether as a two-way bootstrap interface",
            "statement": "A lunar tether can exchange inbound complex equipment or returned hardware for outbound lunar mass, reducing terminal propulsion when the capture and reset cycle closes.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "derived.0",
              "line": 121
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K2"
              },
              {
                "type": "derived_from",
                "target": "K3"
              },
              {
                "type": "derived_from",
                "target": "K4"
              },
              {
                "type": "derived_from",
                "target": "K6"
              }
            ],
            "lineage": []
          },
          {
            "id": "D2",
            "title": "Shuttle as an interface isolator",
            "statement": "An orbital shuttle can aggregate many packets and isolate bulk cislunar carriage and Earth entry from the high-cadence surface launch system.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "derived.1",
              "line": 128
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K3"
              },
              {
                "type": "derived_from",
                "target": "K5"
              },
              {
                "type": "derived_from",
                "target": "K7"
              }
            ],
            "lineage": []
          },
          {
            "id": "D3",
            "title": "Aerocapture as a loaded-return energy sink",
            "statement": "If corridor, guidance, loads, heating and refurbishment close, aerocapture can remove Earth-arrival energy without importing the corresponding braking propellant.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "derived.2",
              "line": 134
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K3"
              },
              {
                "type": "derived_from",
                "target": "K5"
              }
            ],
            "lineage": []
          },
          {
            "id": "D4",
            "title": "Earth tether as an optional extension",
            "statement": "An Earth tether can throw an empty shuttle Moonward when a lunar recharge stream restores its state, but it is optional and must outperform a propulsive return after structure, risk and replacement are counted.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "derived.3",
              "line": 141
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K1"
              },
              {
                "type": "derived_from",
                "target": "K4"
              },
              {
                "type": "derived_from",
                "target": "K5"
              },
              {
                "type": "derived_from",
                "target": "K6"
              }
            ],
            "lineage": []
          },
          {
            "id": "D5",
            "title": "Locate work by comparative advantage",
            "statement": "Mining, beneficiation, refining, manufacturing and precision assembly may belong at different locations; transport discarded mass only when the power, thermal, maintenance and supply-chain trade justifies it.",
            "badge": "Derived",
            "kind": "derived",
            "source": {
              "path": "derived.4",
              "line": 148
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K1"
              },
              {
                "type": "derived_from",
                "target": "K2"
              },
              {
                "type": "derived_from",
                "target": "K6"
              }
            ],
            "lineage": []
          }
        ]
      },
      {
        "id": "hypothesis",
        "title": "Feasibility hypotheses",
        "description": "Empirical claims that can fail and require evidence.",
        "items": [
          {
            "id": "H1",
            "title": "Fixed-site packet delivery can meet an orbital collection interface",
            "statement": "Fixed-site packet delivery can meet an orbital collection interface",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "hypotheses.0",
              "line": 157
            },
            "evidenceNeeded": "Full-force multiple-shooting, finite burns, navigation dispersions, terrain footprints, missed-shot handling and cadence against a propagated hub state.",
            "falsifier": "No safe fixed-rail launch schedule meets the node position, velocity, dispersion and throughput envelope at tolerable correction cost.",
            "stoppingRule": "Do not credit mass-driver packet delivery to the hub until the targeting and miss-containment envelope closes.",
            "links": [
              {
                "type": "supports",
                "target": "D1"
              },
              {
                "type": "supports",
                "target": "D2"
              }
            ],
            "lineage": []
          },
          {
            "id": "H2",
            "title": "Momentum-exchange capture is practical at useful load and cadence",
            "statement": "Momentum-exchange capture is practical at useful load and cadence",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "hypotheses.1",
              "line": 169
            },
            "evidenceNeeded": "Six-degree-of-freedom deployment, flexible dynamics, tip prediction, compliant capture, taper, creep, fatigue, severance, spin recovery and disposal.",
            "falsifier": "Required capture windows, loads, lifetime or failure consequences cannot be met with credible materials, control authority and redundancy.",
            "stoppingRule": "Retain a propulsive interface and remove tether performance credit wherever the complete capture-and-reset cycle is unclosed.",
            "links": [
              {
                "type": "supports",
                "target": "D1"
              },
              {
                "type": "supports",
                "target": "D4"
              }
            ],
            "lineage": []
          },
          {
            "id": "H3",
            "title": "Reusable aerocapture reduces lifecycle Earth-origin mass",
            "statement": "Reusable aerocapture reduces lifecycle Earth-origin mass",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "hypotheses.2",
              "line": 181
            },
            "evidenceNeeded": "Coupled trajectory, corridor dispersion, TPS mass and loss, peak heat flux, integrated heat load, structural loads, guidance, skip-out, landing or orbit recovery and measured refurbishment demand.",
            "falsifier": "TPS, reserves, refurbishment, failure recovery or vehicle mass exceeds the lifecycle propellant and hardware burden avoided.",
            "stoppingRule": "Use propulsive capture or a different return mode until reusable aerocapture is demonstrated at the required mass, velocity and cadence.",
            "links": [
              {
                "type": "supports",
                "target": "D2"
              },
              {
                "type": "supports",
                "target": "D3"
              }
            ],
            "lineage": []
          },
          {
            "id": "H4",
            "title": "Lunar power and production can support driver throughput",
            "statement": "Lunar power and production can support driver throughput",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "hypotheses.3",
              "line": 194
            },
            "evidenceNeeded": "Site-specific illumination or nuclear supply, storage, thermal rejection, rail efficiency and wear, excavation, feed preparation and maintenance.",
            "falsifier": "Delivered power, production yield or component lifetime cannot sustain useful cadence without more imported mass than the transport loop saves.",
            "stoppingRule": "Cap throughput at the demonstrated coupled power-production-maintenance rate.",
            "links": [
              {
                "type": "supports",
                "target": "D1"
              },
              {
                "type": "supports",
                "target": "D5"
              }
            ],
            "lineage": []
          },
          {
            "id": "H5",
            "title": "The complete loop beats simpler architectures",
            "statement": "The complete loop beats simpler architectures",
            "badge": "Hypothesis",
            "kind": "hypothesis",
            "source": {
              "path": "hypotheses.4",
              "line": 205
            },
            "evidenceNeeded": "Common-boundary lifecycle comparison against expendable, reusable-propulsive, lunar-oxygen and partial-network alternatives with uncertainty and replacement.",
            "falsifier": "A simpler architecture has lower lifecycle Earth-origin mass or risk across the credible demand and failure envelope.",
            "stoppingRule": "Remove any component whose marginal savings do not exceed its capital, operations, recharge, replacement and contingency burden.",
            "links": [
              {
                "type": "supports",
                "target": "D1"
              },
              {
                "type": "supports",
                "target": "D2"
              },
              {
                "type": "supports",
                "target": "D3"
              },
              {
                "type": "supports",
                "target": "D4"
              },
              {
                "type": "supports",
                "target": "D5"
              }
            ],
            "lineage": []
          }
        ]
      },
      {
        "id": "implementation",
        "title": "Replaceable choices",
        "description": "Replaceable design choices used by a particular rendering.",
        "items": [
          {
            "id": "I1",
            "title": "Lunar site and orbital family",
            "statement": "Lunar site and orbital family",
            "badge": "replaceable",
            "kind": "implementation",
            "source": {
              "path": "implementation_choices.0",
              "line": 219
            },
            "alternatives": [
              "far-side site",
              "near-side site",
              "polar site",
              "repeat-ground-track orbit"
            ],
            "links": [],
            "lineage": []
          },
          {
            "id": "I2",
            "title": "Lunar tether geometry and material",
            "statement": "Lunar tether geometry and material",
            "badge": "replaceable",
            "kind": "implementation",
            "source": {
              "path": "implementation_choices.1",
              "line": 223
            },
            "alternatives": [
              "single arm",
              "asymmetric ports",
              "reel transition",
              "movable ballast"
            ],
            "links": [],
            "lineage": []
          },
          {
            "id": "I3",
            "title": "Packet and shuttle sizing",
            "statement": "Packet and shuttle sizing",
            "badge": "replaceable",
            "kind": "implementation",
            "source": {
              "path": "implementation_choices.2",
              "line": 227
            },
            "alternatives": [
              "small reusable packet kit",
              "modular cargo",
              "bulk shuttle"
            ],
            "links": [],
            "lineage": []
          },
          {
            "id": "I4",
            "title": "Earth return and outbound mode",
            "statement": "Earth return and outbound mode",
            "badge": "replaceable",
            "kind": "implementation",
            "source": {
              "path": "implementation_choices.3",
              "line": 231
            },
            "alternatives": [
              "aerocapture",
              "propulsive capture",
              "Earth tether",
              "electric tug"
            ],
            "links": [],
            "lineage": []
          },
          {
            "id": "I5",
            "title": "Propellant and process locations",
            "statement": "Propellant and process locations",
            "badge": "replaceable",
            "kind": "implementation",
            "source": {
              "path": "implementation_choices.4",
              "line": 235
            },
            "alternatives": [
              "lunar oxygen",
              "Earth hydrogen",
              "surface refining",
              "orbital refining"
            ],
            "links": [],
            "lineage": []
          }
        ]
      },
      {
        "id": "open_question",
        "title": "Open questions",
        "description": "Unresolved decisions that must remain visible.",
        "items": [
          {
            "id": "Q1",
            "title": "Which fixed lunar site and propagated orbit maximize safe joint access?",
            "statement": "Which fixed lunar site and propagated orbit maximize safe joint access?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "open_questions.0",
              "line": 241
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q2",
            "title": "What compliant capture design closes at the required load and cadence?",
            "statement": "What compliant capture design closes at the required load and cadence?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "open_questions.1",
              "line": 243
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q3",
            "title": "What aerocapture vehicle closes thermally and operationally at bulk-cargo scale?",
            "statement": "What aerocapture vehicle closes thermally and operationally at bulk-cargo scale?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "open_questions.2",
              "line": 245
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q4",
            "title": "When does an Earth tether outperform propulsive shuttle return?",
            "statement": "When does an Earth tether outperform propulsive shuttle return?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "open_questions.3",
              "line": 247
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q5",
            "title": "Which lunar processes belong on the surface, in orbit or on Earth?",
            "statement": "Which lunar processes belong on the surface, in orbit or on Earth?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "open_questions.4",
              "line": 249
            },
            "links": [],
            "lineage": []
          },
          {
            "id": "Q6",
            "title": "What demand, cost and learning curves justify each expansion stage?",
            "statement": "What demand, cost and learning curves justify each expansion stage?",
            "badge": "Open Question",
            "kind": "open_question",
            "source": {
              "path": "open_questions.5",
              "line": 251
            },
            "links": [],
            "lineage": []
          }
        ]
      }
    ],
    "paths": [
      {
        "id": "packet_to_hub",
        "title": "Packet To Hub",
        "steps": [
          "fixed site and rail state",
          "propagated packet with finite targeting and dispersions",
          "collision-safe terminal corridor",
          "demonstrated docking and reusable-kit recovery"
        ]
      },
      {
        "id": "lunar_tether_cycle",
        "title": "Lunar Tether Cycle",
        "steps": [
          "propagated hub and flexible deployed tether",
          "reachable position-and-velocity capture state",
          "compliant load transfer",
          "balanced post-event orbit and spin",
          "inspected recovery or safe disposal"
        ]
      },
      {
        "id": "shuttle_aerocapture",
        "title": "Shuttle Aerocapture",
        "steps": [
          "full-ephemeris departure and arrival state",
          "dispersed entry corridor and guidance",
          "aerothermal and structural margins",
          "recovered vehicle inspection",
          "demonstrated turnaround and cycle life"
        ]
      },
      {
        "id": "lifecycle_claim",
        "title": "Lifecycle Claim",
        "steps": [
          "common scenario and boundary",
          "typed capital and recurring mass ledgers",
          "cadence, capacity, replacement and reserve closure",
          "sensitivity and uncertainty propagation",
          "comparison with simpler alternatives"
        ]
      }
    ],
    "checks": [
      {
        "id": "F-1",
        "title": "Failure mode 1",
        "description": "packet launch or correction failure with unsafe surface footprint",
        "tone": "guard",
        "source": {
          "path": "failure_modes.0",
          "line": 255
        }
      },
      {
        "id": "F-2",
        "title": "Failure mode 2",
        "description": "missed tether capture or unintended double-payload overlap",
        "tone": "guard",
        "source": {
          "path": "failure_modes.1",
          "line": 256
        }
      },
      {
        "id": "F-3",
        "title": "Failure mode 3",
        "description": "tether severance, libration growth, loss of spin or orbital-state drift",
        "tone": "guard",
        "source": {
          "path": "failure_modes.2",
          "line": 257
        }
      },
      {
        "id": "F-4",
        "title": "Failure mode 4",
        "description": "hub collision, docking congestion or depot loss",
        "tone": "guard",
        "source": {
          "path": "failure_modes.3",
          "line": 258
        }
      },
      {
        "id": "F-5",
        "title": "Failure mode 5",
        "description": "aerocapture corridor miss, skip-out, TPS loss or excessive refurbishment",
        "tone": "guard",
        "source": {
          "path": "failure_modes.4",
          "line": 259
        }
      },
      {
        "id": "F-6",
        "title": "Failure mode 6",
        "description": "recharge shortfall causing secular Earth-tether momentum depletion",
        "tone": "guard",
        "source": {
          "path": "failure_modes.5",
          "line": 260
        }
      },
      {
        "id": "F-7",
        "title": "Failure mode 7",
        "description": "surface power, production or spare-parts bottleneck",
        "tone": "guard",
        "source": {
          "path": "failure_modes.6",
          "line": 261
        }
      },
      {
        "id": "F-8",
        "title": "Failure mode 8",
        "description": "hidden replacement and reserve mass erasing conditional savings",
        "tone": "guard",
        "source": {
          "path": "failure_modes.7",
          "line": 262
        }
      },
      {
        "id": "OM-1",
        "title": "Omission check 1",
        "description": "every arrow identifies mass, momentum, energy or reusable hardware",
        "tone": "guard",
        "source": {
          "path": "audit.omission_checks.0",
          "line": 308
        }
      },
      {
        "id": "OM-2",
        "title": "Omission check 2",
        "description": "every tether event shows its counterflow, reboost or state change",
        "tone": "guard",
        "source": {
          "path": "audit.omission_checks.1",
          "line": 309
        }
      },
      {
        "id": "OM-3",
        "title": "Omission check 3",
        "description": "every reusable asset includes inventory, inspection, replacement and loss",
        "tone": "guard",
        "source": {
          "path": "audit.omission_checks.2",
          "line": 310
        }
      },
      {
        "id": "OM-4",
        "title": "Omission check 4",
        "description": "every lunar consumable in orbit includes production and delivery",
        "tone": "guard",
        "source": {
          "path": "audit.omission_checks.3",
          "line": 311
        }
      },
      {
        "id": "OM-5",
        "title": "Omission check 5",
        "description": "every promoted result identifies unresolved trajectory and safety interfaces",
        "tone": "guard",
        "source": {
          "path": "audit.omission_checks.4",
          "line": 312
        }
      },
      {
        "id": "FT-1",
        "title": "Falsification test 1",
        "description": "perturb demand, cadence, lifetime, efficiency and failure rate across credible ranges",
        "tone": "question",
        "source": {
          "path": "audit.falsification_tests.0",
          "line": 314
        }
      },
      {
        "id": "FT-2",
        "title": "Falsification test 2",
        "description": "remove each major component and recompute marginal lifecycle benefit",
        "tone": "question",
        "source": {
          "path": "audit.falsification_tests.1",
          "line": 315
        }
      },
      {
        "id": "FT-3",
        "title": "Falsification test 3",
        "description": "reproduce reference results from a clean checkout with pinned inputs",
        "tone": "question",
        "source": {
          "path": "audit.falsification_tests.2",
          "line": 316
        }
      },
      {
        "id": "FT-4",
        "title": "Falsification test 4",
        "description": "test whether failed interfaces are accidentally credited elsewhere in the ledger",
        "tone": "question",
        "source": {
          "path": "audit.falsification_tests.3",
          "line": 317
        }
      },
      {
        "id": "PROMOTE",
        "title": "Promotion rule",
        "description": "Promote a configuration only when it closes the required physics, capacity, lifecycle and failure gates on a common boundary and remains non-dominated under declared uncertainty.",
        "tone": "boundary",
        "source": {
          "path": "audit.promotion_rule",
          "line": 318
        }
      }
    ],
    "nonGoals": [
      "claim perpetual motion or propellant-free transport",
      "prescribe one orbit, site, tether, vehicle, material or industrial process",
      "treat lunar resources as free or omit their production and delivery",
      "claim that the concept is flight ready, safe, economic or optimal",
      "make the Earth tether mandatory",
      "replace original technical literature with this specification"
    ],
    "guide": {
      "question": "Could reusing a lunar transport loop reduce what we launch from Earth?",
      "introduction": "Launch material from the Moon, exchange momentum in orbit, carry cargo toward Earth, and reuse the transport hardware where the complete cycle makes sense.",
      "premise": "A conditional transport architecture",
      "scope": "Any saving must include building, powering, maintaining, replacing, and recovering the whole system. The model does not establish flight readiness or an advantage over simpler transport.",
      "scopeRefs": [
        "K1",
        "K6",
        "K7",
        "H5"
      ],
      "mechanismTitle": "Follow the cargo. Account for the return.",
      "mechanismDescription": "A conceptual sequence, not an orbit or trajectory simulation. Select a component to inspect its conditions.",
      "steps": [
        {
          "id": "surface",
          "title": "Launch from the Moon",
          "description": "A surface mass driver sends material toward an orbital collection interface. Power, targeting, and safe missed-shot handling limit the flow.",
          "refs": [
            "K2",
            "H1",
            "H4"
          ],
          "connection": "Mass · outbound lunar material"
        },
        {
          "id": "exchange",
          "title": "Exchange momentum",
          "description": "A lunar tether exchanges outbound lunar mass for inbound equipment or returning hardware—if capture and reset work.",
          "refs": [
            "D1",
            "K4",
            "H2"
          ],
          "connection": "Mass + momentum · capture and transfer"
        },
        {
          "id": "shuttle",
          "title": "Carry the cargo",
          "description": "Reusable shuttles can gather packets and separate bulk transport from the surface launch system.",
          "refs": [
            "D2",
            "K3"
          ],
          "connection": "Mass + reusable hardware · Earthward cargo"
        },
        {
          "id": "arrival",
          "title": "Slow down near Earth",
          "description": "Aerocapture could remove arrival energy with less imported propellant, if heating, recovery, and refurbishment close.",
          "refs": [
            "D3",
            "H3"
          ],
          "connection": "Energy · atmospheric braking, conditional"
        }
      ],
      "returnFlow": {
        "title": "Reusable hardware returns Moonward",
        "text": "An Earth tether is an optional way to send an empty shuttle back. Its momentum must be restored by a lunar recharge stream, and it must beat a propulsive return after risk and replacement are counted.",
        "refs": [
          "D4",
          "K4",
          "H2",
          "H5"
        ]
      },
      "terms": [
        {
          "id": "mass-driver",
          "title": "Mass driver",
          "meaning": "The surface launch system whose energy and throughput budget includes every kilogram launched, including flight hardware and propellant.",
          "refs": [
            "K2",
            "H1",
            "H4"
          ]
        },
        {
          "id": "tether",
          "title": "Momentum-exchange tether",
          "meaning": "A system that trades momentum with a payload through changes in its orbit and spin. Each exchange needs counterflow, reboost, or an accounted state change.",
          "refs": [
            "K4",
            "D1"
          ]
        },
        {
          "id": "shuttle",
          "title": "Cargo shuttle",
          "meaning": "Reusable orbital transport that can aggregate packets and carry bulk cargo separately from the surface launch system.",
          "refs": [
            "D2"
          ]
        },
        {
          "id": "aerocapture",
          "title": "Aerocapture",
          "meaning": "Using atmospheric braking to remove arrival energy. Its proposed benefit depends on heating, guidance, recovery, and repeated reuse.",
          "refs": [
            "D3",
            "H3"
          ]
        },
        {
          "id": "lifecycle",
          "title": "Lifecycle Earth-origin mass",
          "meaning": "The complete mass burden from Earth over buildout and operation, including infrastructure, consumables, replacements, and recovery—not just one vehicle’s fuel.",
          "refs": [
            "K1",
            "H5"
          ]
        }
      ],
      "example": {
        "title": "Less propellant does not automatically mean a better system",
        "text": "A tether that saves fuel can still lose the comparison if its structure, recharge, replacements, and recovery add more Earth-origin mass than it avoids.",
        "refs": [
          "K1",
          "K4",
          "D4",
          "H5"
        ]
      },
      "uncertainty": {
        "title": "Can the complete loop beat simpler architectures?",
        "text": "The comparison must include capacity, failure, replacement, and uncertainty on the same boundary. Individual component savings do not answer that question.",
        "refs": [
          "H5",
          "K1"
        ]
      },
      "featured": [
        "H1",
        "H2",
        "H3",
        "H4",
        "H5"
      ]
    },
    "contextSources": [
      {
        "id": "SRC-TRADE-SCREEN",
        "title": "Lunar Transport Architecture Trade Screen",
        "uri": "https://github.com/uncomposed/lunar-infrastructure",
        "role": "Executable rendering, assumptions, source map and falsification record.",
        "source": {
          "path": "source_anchors.0",
          "line": 332
        }
      },
      {
        "id": "SRC-IRAP",
        "title": "Idea Rendering Attestation Protocol",
        "uri": "https://github.com/uncomposed/irap-protocol",
        "role": "Exact-state rendering and attributable verification model.",
        "source": {
          "path": "source_anchors.1",
          "line": 336
        }
      },
      {
        "id": "SRC-HOYT",
        "title": "Cislunar Tether Transport System",
        "uri": "https://www.niac.usra.edu/files/studies/final_report/7Hoyt.pdf",
        "role": "Lunar momentum-exchange architecture precedent and first-order scaling source.",
        "source": {
          "path": "source_anchors.2",
          "line": 340
        }
      },
      {
        "id": "SRC-MXER",
        "title": "Momentum eXchange Electrodynamic Reboost Tether Technology Assessment",
        "uri": "https://ntrs.nasa.gov/citations/20060004772",
        "role": "Earth momentum-exchange tether technology and risk precedent.",
        "source": {
          "path": "source_anchors.3",
          "line": 344
        }
      }
    ],
    "evidenceSummary": "This revision states hypotheses and the tests needed to challenge them. It does not report completed empirical validation. Source lineage, where supplied, explains where the thinking came from.",
    "sourceSections": [
      {
        "key": "idea",
        "value": {
          "id": "https://ideas.proximitytoprogress.com/ideas/cislunar-momentum-loop",
          "name": "Cislunar Momentum Loop",
          "version": "0.1.0",
          "status": "working-draft",
          "summary": "A staged, reusable lunar transport architecture combining a surface mass driver, momentum-exchange tethers, cargo shuttles and aerocapture to reduce recurring propellant and total Earth-origin mass during buildout and sustainment.",
          "responsibility": {
            "canonical_editor": "Cole Miller",
            "ai_use_disclosure": "AI systems assisted with architecture criticism, modeling, source organization, falsification design and editing. Inclusion in this state means the canonical editor accepts responsibility for the proposition."
          }
        },
        "source": {
          "path": "idea",
          "line": 2
        }
      },
      {
        "key": "decision_boundary",
        "value": {
          "objective": "Minimize lifecycle Earth-origin mass delivered to a declared reference orbit while delivering a required lunar buildout and useful cargo flow.",
          "primary_metric": "equivalent Earth-origin mass to reference LEO (EMLEO)",
          "must_include": [
            "initial infrastructure and commissioning mass",
            "recurring propellant and consumables",
            "circulating reusable hardware and replacement",
            "depots, tankage, power, thermal control and surface support",
            "tether counterflow, reboost and replacement",
            "lunar production and delivery needed to supply orbital consumables",
            "reserves, abort capability and credible failure recovery"
          ],
          "secondary_metrics": [
            "useful cargo throughput",
            "energy and peak-power demand",
            "event cadence and fleet utilization",
            "reliability, graceful degradation and recovery time",
            "cost, schedule and technology readiness"
          ]
        },
        "source": {
          "path": "decision_boundary",
          "line": 19
        }
      },
      {
        "key": "typed_layers",
        "value": {
          "kernel": {
            "description": "Propositions necessary to preserve the identity of the idea.",
            "prefix": "K"
          },
          "derived": {
            "description": "Mechanisms that follow from the kernel when their assumptions hold.",
            "prefix": "D"
          },
          "hypothesis": {
            "description": "Empirical claims that can fail and require evidence.",
            "prefix": "H"
          },
          "implementation": {
            "description": "Replaceable design choices used by a particular rendering.",
            "prefix": "I"
          },
          "open_question": {
            "description": "Unresolved decisions that must remain visible.",
            "prefix": "Q"
          }
        },
        "source": {
          "path": "typed_layers",
          "line": 39
        }
      },
      {
        "key": "resources",
        "value": [
          "Earth-launched dry hardware, oxidizer and fuel",
          "lunar surface power, storage, heat rejection and rail duty cycle",
          "lunar feedstock, beneficiation yield and production capacity",
          "propellant, donor mass, ballast and tether momentum",
          "tether material strength, lifetime, inspection and replacement",
          "shuttle, packet, depot and capture-interface fleet inventory",
          "orbital power, communications, navigation and maintenance access"
        ],
        "source": {
          "path": "resources",
          "line": 56
        }
      },
      {
        "key": "actors",
        "value": {
          "canonical_editor": {
            "responsibility": "Accept or reject changes to the canonical idea state."
          },
          "rendering_author": {
            "responsibility": "Declare assumptions, implementation choices and evidence boundaries."
          },
          "verifier": {
            "responsibility": "Judge a named claim against an exact idea commit and artifact digest."
          },
          "operator": {
            "responsibility": "Close operational, safety and recovery procedures for a specific design."
          },
          "independent_reviewer": {
            "responsibility": "Reproduce calculations, attack omissions and preserve disagreement."
          }
        },
        "source": {
          "path": "actors",
          "line": 65
        }
      },
      {
        "key": "kernel",
        "value": [
          {
            "id": "K1",
            "title": "Optimize lifecycle Earth-origin mass",
            "statement": "Compare complete buildout and sustainment ledgers on one declared boundary; do not optimize a vehicle or propellant fraction while hiding infrastructure, replacement, counterflow or production mass elsewhere."
          },
          {
            "id": "K2",
            "title": "Treat lunar launch throughput as scarce",
            "statement": "At a fixed injection speed and efficiency, mass-driver energy and throughput scale with every kilogram launched, including propellant and expendable flight hardware as well as useful product."
          },
          {
            "id": "K3",
            "title": "Reuse the transport kit where reuse closes",
            "statement": "Separate useful cargo from guidance, propulsion, capture and thermal hardware so expensive flight systems can circulate when inspection, return and cycle life are better than replacement."
          },
          {
            "id": "K4",
            "title": "Account for momentum rather than claiming free propulsion",
            "statement": "A tether exchanges momentum between payloads, its orbit and its spin. Every catch and throw must expose the balancing counterflow, reboost or secular state change."
          },
          {
            "id": "K5",
            "title": "Remove energy with the least lifecycle burden",
            "statement": "Use propulsion, momentum exchange and atmospheric braking only where the complete vehicle, infrastructure, reserve and recovery ledgers make them preferable to alternatives."
          },
          {
            "id": "K6",
            "title": "Bootstrap in independently useful stages",
            "statement": "No promoted architecture may assume mature lunar production, mass-driver throughput, tether service or depots before the preceding stage can build, commission and sustain them."
          },
          {
            "id": "K7",
            "title": "Keep unclosed interfaces explicit",
            "statement": "Ground-track access is not rendezvous, a rigid-tip crossing is not capture, an aerocapture proxy is not a reusable thermal design, and a conditional mass advantage is not flight readiness."
          }
        ],
        "source": {
          "path": "kernel",
          "line": 77
        }
      },
      {
        "key": "derived",
        "value": [
          {
            "id": "D1",
            "title": "Lunar tether as a two-way bootstrap interface",
            "derived_from": [
              "K2",
              "K3",
              "K4",
              "K6"
            ],
            "statement": "A lunar tether can exchange inbound complex equipment or returned hardware for outbound lunar mass, reducing terminal propulsion when the capture and reset cycle closes."
          },
          {
            "id": "D2",
            "title": "Shuttle as an interface isolator",
            "derived_from": [
              "K3",
              "K5",
              "K7"
            ],
            "statement": "An orbital shuttle can aggregate many packets and isolate bulk cislunar carriage and Earth entry from the high-cadence surface launch system."
          },
          {
            "id": "D3",
            "title": "Aerocapture as a loaded-return energy sink",
            "derived_from": [
              "K3",
              "K5"
            ],
            "statement": "If corridor, guidance, loads, heating and refurbishment close, aerocapture can remove Earth-arrival energy without importing the corresponding braking propellant."
          },
          {
            "id": "D4",
            "title": "Earth tether as an optional extension",
            "derived_from": [
              "K1",
              "K4",
              "K5",
              "K6"
            ],
            "statement": "An Earth tether can throw an empty shuttle Moonward when a lunar recharge stream restores its state, but it is optional and must outperform a propulsive return after structure, risk and replacement are counted."
          },
          {
            "id": "D5",
            "title": "Locate work by comparative advantage",
            "derived_from": [
              "K1",
              "K2",
              "K6"
            ],
            "statement": "Mining, beneficiation, refining, manufacturing and precision assembly may belong at different locations; transport discarded mass only when the power, thermal, maintenance and supply-chain trade justifies it."
          }
        ],
        "source": {
          "path": "derived",
          "line": 121
        }
      },
      {
        "key": "hypotheses",
        "value": [
          {
            "id": "H1",
            "title": "Fixed-site packet delivery can meet an orbital collection interface",
            "supports": [
              "D1",
              "D2"
            ],
            "evidence_needed": "Full-force multiple-shooting, finite burns, navigation dispersions, terrain footprints, missed-shot handling and cadence against a propagated hub state.",
            "falsifier": "No safe fixed-rail launch schedule meets the node position, velocity, dispersion and throughput envelope at tolerable correction cost.",
            "stopping_rule": "Do not credit mass-driver packet delivery to the hub until the targeting and miss-containment envelope closes."
          },
          {
            "id": "H2",
            "title": "Momentum-exchange capture is practical at useful load and cadence",
            "supports": [
              "D1",
              "D4"
            ],
            "evidence_needed": "Six-degree-of-freedom deployment, flexible dynamics, tip prediction, compliant capture, taper, creep, fatigue, severance, spin recovery and disposal.",
            "falsifier": "Required capture windows, loads, lifetime or failure consequences cannot be met with credible materials, control authority and redundancy.",
            "stopping_rule": "Retain a propulsive interface and remove tether performance credit wherever the complete capture-and-reset cycle is unclosed."
          },
          {
            "id": "H3",
            "title": "Reusable aerocapture reduces lifecycle Earth-origin mass",
            "supports": [
              "D2",
              "D3"
            ],
            "evidence_needed": "Coupled trajectory, corridor dispersion, TPS mass and loss, peak heat flux, integrated heat load, structural loads, guidance, skip-out, landing or orbit recovery and measured refurbishment demand.",
            "falsifier": "TPS, reserves, refurbishment, failure recovery or vehicle mass exceeds the lifecycle propellant and hardware burden avoided.",
            "stopping_rule": "Use propulsive capture or a different return mode until reusable aerocapture is demonstrated at the required mass, velocity and cadence."
          },
          {
            "id": "H4",
            "title": "Lunar power and production can support driver throughput",
            "supports": [
              "D1",
              "D5"
            ],
            "evidence_needed": "Site-specific illumination or nuclear supply, storage, thermal rejection, rail efficiency and wear, excavation, feed preparation and maintenance.",
            "falsifier": "Delivered power, production yield or component lifetime cannot sustain useful cadence without more imported mass than the transport loop saves.",
            "stopping_rule": "Cap throughput at the demonstrated coupled power-production-maintenance rate."
          },
          {
            "id": "H5",
            "title": "The complete loop beats simpler architectures",
            "supports": [
              "D1",
              "D2",
              "D3",
              "D4",
              "D5"
            ],
            "evidence_needed": "Common-boundary lifecycle comparison against expendable, reusable-propulsive, lunar-oxygen and partial-network alternatives with uncertainty and replacement.",
            "falsifier": "A simpler architecture has lower lifecycle Earth-origin mass or risk across the credible demand and failure envelope.",
            "stopping_rule": "Remove any component whose marginal savings do not exceed its capital, operations, recharge, replacement and contingency burden."
          }
        ],
        "source": {
          "path": "hypotheses",
          "line": 157
        }
      },
      {
        "key": "implementation_choices",
        "value": [
          {
            "id": "I1",
            "title": "Lunar site and orbital family",
            "status": "replaceable",
            "examples": [
              "far-side site",
              "near-side site",
              "polar site",
              "repeat-ground-track orbit"
            ]
          },
          {
            "id": "I2",
            "title": "Lunar tether geometry and material",
            "status": "replaceable",
            "examples": [
              "single arm",
              "asymmetric ports",
              "reel transition",
              "movable ballast"
            ]
          },
          {
            "id": "I3",
            "title": "Packet and shuttle sizing",
            "status": "replaceable",
            "examples": [
              "small reusable packet kit",
              "modular cargo",
              "bulk shuttle"
            ]
          },
          {
            "id": "I4",
            "title": "Earth return and outbound mode",
            "status": "replaceable",
            "examples": [
              "aerocapture",
              "propulsive capture",
              "Earth tether",
              "electric tug"
            ]
          },
          {
            "id": "I5",
            "title": "Propellant and process locations",
            "status": "replaceable",
            "examples": [
              "lunar oxygen",
              "Earth hydrogen",
              "surface refining",
              "orbital refining"
            ]
          }
        ],
        "source": {
          "path": "implementation_choices",
          "line": 219
        }
      },
      {
        "key": "open_questions",
        "value": [
          {
            "id": "Q1",
            "question": "Which fixed lunar site and propagated orbit maximize safe joint access?"
          },
          {
            "id": "Q2",
            "question": "What compliant capture design closes at the required load and cadence?"
          },
          {
            "id": "Q3",
            "question": "What aerocapture vehicle closes thermally and operationally at bulk-cargo scale?"
          },
          {
            "id": "Q4",
            "question": "When does an Earth tether outperform propulsive shuttle return?"
          },
          {
            "id": "Q5",
            "question": "Which lunar processes belong on the surface, in orbit or on Earth?"
          },
          {
            "id": "Q6",
            "question": "What demand, cost and learning curves justify each expansion stage?"
          }
        ],
        "source": {
          "path": "open_questions",
          "line": 241
        }
      },
      {
        "key": "failure_modes",
        "value": [
          "packet launch or correction failure with unsafe surface footprint",
          "missed tether capture or unintended double-payload overlap",
          "tether severance, libration growth, loss of spin or orbital-state drift",
          "hub collision, docking congestion or depot loss",
          "aerocapture corridor miss, skip-out, TPS loss or excessive refurbishment",
          "recharge shortfall causing secular Earth-tether momentum depletion",
          "surface power, production or spare-parts bottleneck",
          "hidden replacement and reserve mass erasing conditional savings"
        ],
        "source": {
          "path": "failure_modes",
          "line": 255
        }
      },
      {
        "key": "evidence_paths",
        "value": {
          "packet_to_hub": [
            "fixed site and rail state",
            "propagated packet with finite targeting and dispersions",
            "collision-safe terminal corridor",
            "demonstrated docking and reusable-kit recovery"
          ],
          "lunar_tether_cycle": [
            "propagated hub and flexible deployed tether",
            "reachable position-and-velocity capture state",
            "compliant load transfer",
            "balanced post-event orbit and spin",
            "inspected recovery or safe disposal"
          ],
          "shuttle_aerocapture": [
            "full-ephemeris departure and arrival state",
            "dispersed entry corridor and guidance",
            "aerothermal and structural margins",
            "recovered vehicle inspection",
            "demonstrated turnaround and cycle life"
          ],
          "lifecycle_claim": [
            "common scenario and boundary",
            "typed capital and recurring mass ledgers",
            "cadence, capacity, replacement and reserve closure",
            "sensitivity and uncertainty propagation",
            "comparison with simpler alternatives"
          ]
        },
        "source": {
          "path": "evidence_paths",
          "line": 265
        }
      },
      {
        "key": "decision_criteria",
        "value": {
          "faithful_rendering": [
            "preserves the lifecycle Earth-origin-mass objective and declared boundary",
            "distinguishes mass, energy, momentum and reusable-hardware flows",
            "treats the lunar tether as a two-way exchange with an explicit reset ledger",
            "treats the Earth tether as optional unless its marginal lifecycle case closes",
            "labels aerocapture as conditional until its aerothermal and operations gates pass",
            "separates kernel propositions, hypotheses and implementation choices",
            "exposes failed gates, competing alternatives and stopping rules",
            "does not present an animation, analytic proxy or conditional output as flight proof"
          ]
        },
        "source": {
          "path": "decision_criteria",
          "line": 290
        }
      },
      {
        "key": "audit",
        "value": {
          "comparison_alternatives": [
            "all-Earth expendable transport",
            "reusable propulsive lander and shuttle",
            "lunar oxygen without tethers",
            "lunar tether without Earth tether",
            "propulsive or electric outbound return without Earth tether"
          ],
          "omission_checks": [
            "every arrow identifies mass, momentum, energy or reusable hardware",
            "every tether event shows its counterflow, reboost or state change",
            "every reusable asset includes inventory, inspection, replacement and loss",
            "every lunar consumable in orbit includes production and delivery",
            "every promoted result identifies unresolved trajectory and safety interfaces"
          ],
          "falsification_tests": [
            "perturb demand, cadence, lifetime, efficiency and failure rate across credible ranges",
            "remove each major component and recompute marginal lifecycle benefit",
            "reproduce reference results from a clean checkout with pinned inputs",
            "test whether failed interfaces are accidentally credited elsewhere in the ledger"
          ],
          "promotion_rule": "Promote a configuration only when it closes the required physics, capacity, lifecycle and failure gates on a common boundary and remains non-dominated under declared uncertainty."
        },
        "source": {
          "path": "audit",
          "line": 301
        }
      },
      {
        "key": "non_goals",
        "value": [
          "claim perpetual motion or propellant-free transport",
          "prescribe one orbit, site, tether, vehicle, material or industrial process",
          "treat lunar resources as free or omit their production and delivery",
          "claim that the concept is flight ready, safe, economic or optimal",
          "make the Earth tether mandatory",
          "replace original technical literature with this specification"
        ],
        "source": {
          "path": "non_goals",
          "line": 324
        }
      },
      {
        "key": "source_anchors",
        "value": [
          {
            "id": "SRC-TRADE-SCREEN",
            "title": "Lunar Transport Architecture Trade Screen",
            "uri": "https://github.com/uncomposed/lunar-infrastructure",
            "role": "Executable rendering, assumptions, source map and falsification record."
          },
          {
            "id": "SRC-IRAP",
            "title": "Idea Rendering Attestation Protocol",
            "uri": "https://github.com/uncomposed/irap-protocol",
            "role": "Exact-state rendering and attributable verification model."
          },
          {
            "id": "SRC-HOYT",
            "title": "Cislunar Tether Transport System",
            "uri": "https://www.niac.usra.edu/files/studies/final_report/7Hoyt.pdf",
            "role": "Lunar momentum-exchange architecture precedent and first-order scaling source."
          },
          {
            "id": "SRC-MXER",
            "title": "Momentum eXchange Electrodynamic Reboost Tether Technology Assessment",
            "uri": "https://ntrs.nasa.gov/citations/20060004772",
            "role": "Earth momentum-exchange tether technology and risk precedent."
          }
        ],
        "source": {
          "path": "source_anchors",
          "line": 332
        }
      }
    ],
    "sourceLocations": {
      "idea": {
        "path": "idea",
        "line": 2
      },
      "idea.id": {
        "path": "idea.id",
        "line": 2
      },
      "idea.name": {
        "path": "idea.name",
        "line": 3
      },
      "idea.version": {
        "path": "idea.version",
        "line": 4
      },
      "idea.status": {
        "path": "idea.status",
        "line": 5
      },
      "idea.summary": {
        "path": "idea.summary",
        "line": 6
      },
      "idea.responsibility": {
        "path": "idea.responsibility",
        "line": 12
      },
      "idea.responsibility.canonical_editor": {
        "path": "idea.responsibility.canonical_editor",
        "line": 12
      },
      "idea.responsibility.ai_use_disclosure": {
        "path": "idea.responsibility.ai_use_disclosure",
        "line": 13
      },
      "decision_boundary": {
        "path": "decision_boundary",
        "line": 19
      },
      "decision_boundary.objective": {
        "path": "decision_boundary.objective",
        "line": 19
      },
      "decision_boundary.primary_metric": {
        "path": "decision_boundary.primary_metric",
        "line": 22
      },
      "decision_boundary.must_include": {
        "path": "decision_boundary.must_include",
        "line": 24
      },
      "decision_boundary.must_include.0": {
        "path": "decision_boundary.must_include.0",
        "line": 24
      },
      "decision_boundary.must_include.1": {
        "path": "decision_boundary.must_include.1",
        "line": 25
      },
      "decision_boundary.must_include.2": {
        "path": "decision_boundary.must_include.2",
        "line": 26
      },
      "decision_boundary.must_include.3": {
        "path": "decision_boundary.must_include.3",
        "line": 27
      },
      "decision_boundary.must_include.4": {
        "path": "decision_boundary.must_include.4",
        "line": 28
      },
      "decision_boundary.must_include.5": {
        "path": "decision_boundary.must_include.5",
        "line": 29
      },
      "decision_boundary.must_include.6": {
        "path": "decision_boundary.must_include.6",
        "line": 30
      },
      "decision_boundary.secondary_metrics": {
        "path": "decision_boundary.secondary_metrics",
        "line": 32
      },
      "decision_boundary.secondary_metrics.0": {
        "path": "decision_boundary.secondary_metrics.0",
        "line": 32
      },
      "decision_boundary.secondary_metrics.1": {
        "path": "decision_boundary.secondary_metrics.1",
        "line": 33
      },
      "decision_boundary.secondary_metrics.2": {
        "path": "decision_boundary.secondary_metrics.2",
        "line": 34
      },
      "decision_boundary.secondary_metrics.3": {
        "path": "decision_boundary.secondary_metrics.3",
        "line": 35
      },
      "decision_boundary.secondary_metrics.4": {
        "path": "decision_boundary.secondary_metrics.4",
        "line": 36
      },
      "typed_layers": {
        "path": "typed_layers",
        "line": 39
      },
      "typed_layers.kernel": {
        "path": "typed_layers.kernel",
        "line": 40
      },
      "typed_layers.kernel.description": {
        "path": "typed_layers.kernel.description",
        "line": 40
      },
      "typed_layers.kernel.prefix": {
        "path": "typed_layers.kernel.prefix",
        "line": 41
      },
      "typed_layers.derived": {
        "path": "typed_layers.derived",
        "line": 43
      },
      "typed_layers.derived.description": {
        "path": "typed_layers.derived.description",
        "line": 43
      },
      "typed_layers.derived.prefix": {
        "path": "typed_layers.derived.prefix",
        "line": 44
      },
      "typed_layers.hypothesis": {
        "path": "typed_layers.hypothesis",
        "line": 46
      },
      "typed_layers.hypothesis.description": {
        "path": "typed_layers.hypothesis.description",
        "line": 46
      },
      "typed_layers.hypothesis.prefix": {
        "path": "typed_layers.hypothesis.prefix",
        "line": 47
      },
      "typed_layers.implementation": {
        "path": "typed_layers.implementation",
        "line": 49
      },
      "typed_layers.implementation.description": {
        "path": "typed_layers.implementation.description",
        "line": 49
      },
      "typed_layers.implementation.prefix": {
        "path": "typed_layers.implementation.prefix",
        "line": 50
      },
      "typed_layers.open_question": {
        "path": "typed_layers.open_question",
        "line": 52
      },
      "typed_layers.open_question.description": {
        "path": "typed_layers.open_question.description",
        "line": 52
      },
      "typed_layers.open_question.prefix": {
        "path": "typed_layers.open_question.prefix",
        "line": 53
      },
      "resources": {
        "path": "resources",
        "line": 56
      },
      "resources.0": {
        "path": "resources.0",
        "line": 56
      },
      "resources.1": {
        "path": "resources.1",
        "line": 57
      },
      "resources.2": {
        "path": "resources.2",
        "line": 58
      },
      "resources.3": {
        "path": "resources.3",
        "line": 59
      },
      "resources.4": {
        "path": "resources.4",
        "line": 60
      },
      "resources.5": {
        "path": "resources.5",
        "line": 61
      },
      "resources.6": {
        "path": "resources.6",
        "line": 62
      },
      "actors": {
        "path": "actors",
        "line": 65
      },
      "actors.canonical_editor": {
        "path": "actors.canonical_editor",
        "line": 66
      },
      "actors.canonical_editor.responsibility": {
        "path": "actors.canonical_editor.responsibility",
        "line": 66
      },
      "actors.rendering_author": {
        "path": "actors.rendering_author",
        "line": 68
      },
      "actors.rendering_author.responsibility": {
        "path": "actors.rendering_author.responsibility",
        "line": 68
      },
      "actors.verifier": {
        "path": "actors.verifier",
        "line": 70
      },
      "actors.verifier.responsibility": {
        "path": "actors.verifier.responsibility",
        "line": 70
      },
      "actors.operator": {
        "path": "actors.operator",
        "line": 72
      },
      "actors.operator.responsibility": {
        "path": "actors.operator.responsibility",
        "line": 72
      },
      "actors.independent_reviewer": {
        "path": "actors.independent_reviewer",
        "line": 74
      },
      "actors.independent_reviewer.responsibility": {
        "path": "actors.independent_reviewer.responsibility",
        "line": 74
      },
      "kernel": {
        "path": "kernel",
        "line": 77
      },
      "kernel.0": {
        "path": "kernel.0",
        "line": 77
      },
      "kernel.0.id": {
        "path": "kernel.0.id",
        "line": 77
      },
      "kernel.0.title": {
        "path": "kernel.0.title",
        "line": 78
      },
      "kernel.0.statement": {
        "path": "kernel.0.statement",
        "line": 79
      },
      "kernel.1": {
        "path": "kernel.1",
        "line": 83
      },
      "kernel.1.id": {
        "path": "kernel.1.id",
        "line": 83
      },
      "kernel.1.title": {
        "path": "kernel.1.title",
        "line": 84
      },
      "kernel.1.statement": {
        "path": "kernel.1.statement",
        "line": 85
      },
      "kernel.2": {
        "path": "kernel.2",
        "line": 89
      },
      "kernel.2.id": {
        "path": "kernel.2.id",
        "line": 89
      },
      "kernel.2.title": {
        "path": "kernel.2.title",
        "line": 90
      },
      "kernel.2.statement": {
        "path": "kernel.2.statement",
        "line": 91
      },
      "kernel.3": {
        "path": "kernel.3",
        "line": 95
      },
      "kernel.3.id": {
        "path": "kernel.3.id",
        "line": 95
      },
      "kernel.3.title": {
        "path": "kernel.3.title",
        "line": 96
      },
      "kernel.3.statement": {
        "path": "kernel.3.statement",
        "line": 97
      },
      "kernel.4": {
        "path": "kernel.4",
        "line": 101
      },
      "kernel.4.id": {
        "path": "kernel.4.id",
        "line": 101
      },
      "kernel.4.title": {
        "path": "kernel.4.title",
        "line": 102
      },
      "kernel.4.statement": {
        "path": "kernel.4.statement",
        "line": 103
      },
      "kernel.5": {
        "path": "kernel.5",
        "line": 107
      },
      "kernel.5.id": {
        "path": "kernel.5.id",
        "line": 107
      },
      "kernel.5.title": {
        "path": "kernel.5.title",
        "line": 108
      },
      "kernel.5.statement": {
        "path": "kernel.5.statement",
        "line": 109
      },
      "kernel.6": {
        "path": "kernel.6",
        "line": 113
      },
      "kernel.6.id": {
        "path": "kernel.6.id",
        "line": 113
      },
      "kernel.6.title": {
        "path": "kernel.6.title",
        "line": 114
      },
      "kernel.6.statement": {
        "path": "kernel.6.statement",
        "line": 115
      },
      "derived": {
        "path": "derived",
        "line": 121
      },
      "derived.0": {
        "path": "derived.0",
        "line": 121
      },
      "derived.0.id": {
        "path": "derived.0.id",
        "line": 121
      },
      "derived.0.title": {
        "path": "derived.0.title",
        "line": 122
      },
      "derived.0.derived_from": {
        "path": "derived.0.derived_from",
        "line": 123
      },
      "derived.0.derived_from.0": {
        "path": "derived.0.derived_from.0",
        "line": 123
      },
      "derived.0.derived_from.1": {
        "path": "derived.0.derived_from.1",
        "line": 123
      },
      "derived.0.derived_from.2": {
        "path": "derived.0.derived_from.2",
        "line": 123
      },
      "derived.0.derived_from.3": {
        "path": "derived.0.derived_from.3",
        "line": 123
      },
      "derived.0.statement": {
        "path": "derived.0.statement",
        "line": 124
      },
      "derived.1": {
        "path": "derived.1",
        "line": 128
      },
      "derived.1.id": {
        "path": "derived.1.id",
        "line": 128
      },
      "derived.1.title": {
        "path": "derived.1.title",
        "line": 129
      },
      "derived.1.derived_from": {
        "path": "derived.1.derived_from",
        "line": 130
      },
      "derived.1.derived_from.0": {
        "path": "derived.1.derived_from.0",
        "line": 130
      },
      "derived.1.derived_from.1": {
        "path": "derived.1.derived_from.1",
        "line": 130
      },
      "derived.1.derived_from.2": {
        "path": "derived.1.derived_from.2",
        "line": 130
      },
      "derived.1.statement": {
        "path": "derived.1.statement",
        "line": 131
      },
      "derived.2": {
        "path": "derived.2",
        "line": 134
      },
      "derived.2.id": {
        "path": "derived.2.id",
        "line": 134
      },
      "derived.2.title": {
        "path": "derived.2.title",
        "line": 135
      },
      "derived.2.derived_from": {
        "path": "derived.2.derived_from",
        "line": 136
      },
      "derived.2.derived_from.0": {
        "path": "derived.2.derived_from.0",
        "line": 136
      },
      "derived.2.derived_from.1": {
        "path": "derived.2.derived_from.1",
        "line": 136
      },
      "derived.2.statement": {
        "path": "derived.2.statement",
        "line": 137
      },
      "derived.3": {
        "path": "derived.3",
        "line": 141
      },
      "derived.3.id": {
        "path": "derived.3.id",
        "line": 141
      },
      "derived.3.title": {
        "path": "derived.3.title",
        "line": 142
      },
      "derived.3.derived_from": {
        "path": "derived.3.derived_from",
        "line": 143
      },
      "derived.3.derived_from.0": {
        "path": "derived.3.derived_from.0",
        "line": 143
      },
      "derived.3.derived_from.1": {
        "path": "derived.3.derived_from.1",
        "line": 143
      },
      "derived.3.derived_from.2": {
        "path": "derived.3.derived_from.2",
        "line": 143
      },
      "derived.3.derived_from.3": {
        "path": "derived.3.derived_from.3",
        "line": 143
      },
      "derived.3.statement": {
        "path": "derived.3.statement",
        "line": 144
      },
      "derived.4": {
        "path": "derived.4",
        "line": 148
      },
      "derived.4.id": {
        "path": "derived.4.id",
        "line": 148
      },
      "derived.4.title": {
        "path": "derived.4.title",
        "line": 149
      },
      "derived.4.derived_from": {
        "path": "derived.4.derived_from",
        "line": 150
      },
      "derived.4.derived_from.0": {
        "path": "derived.4.derived_from.0",
        "line": 150
      },
      "derived.4.derived_from.1": {
        "path": "derived.4.derived_from.1",
        "line": 150
      },
      "derived.4.derived_from.2": {
        "path": "derived.4.derived_from.2",
        "line": 150
      },
      "derived.4.statement": {
        "path": "derived.4.statement",
        "line": 151
      },
      "hypotheses": {
        "path": "hypotheses",
        "line": 157
      },
      "hypotheses.0": {
        "path": "hypotheses.0",
        "line": 157
      },
      "hypotheses.0.id": {
        "path": "hypotheses.0.id",
        "line": 157
      },
      "hypotheses.0.title": {
        "path": "hypotheses.0.title",
        "line": 158
      },
      "hypotheses.0.supports": {
        "path": "hypotheses.0.supports",
        "line": 159
      },
      "hypotheses.0.supports.0": {
        "path": "hypotheses.0.supports.0",
        "line": 159
      },
      "hypotheses.0.supports.1": {
        "path": "hypotheses.0.supports.1",
        "line": 159
      },
      "hypotheses.0.evidence_needed": {
        "path": "hypotheses.0.evidence_needed",
        "line": 160
      },
      "hypotheses.0.falsifier": {
        "path": "hypotheses.0.falsifier",
        "line": 163
      },
      "hypotheses.0.stopping_rule": {
        "path": "hypotheses.0.stopping_rule",
        "line": 166
      },
      "hypotheses.1": {
        "path": "hypotheses.1",
        "line": 169
      },
      "hypotheses.1.id": {
        "path": "hypotheses.1.id",
        "line": 169
      },
      "hypotheses.1.title": {
        "path": "hypotheses.1.title",
        "line": 170
      },
      "hypotheses.1.supports": {
        "path": "hypotheses.1.supports",
        "line": 171
      },
      "hypotheses.1.supports.0": {
        "path": "hypotheses.1.supports.0",
        "line": 171
      },
      "hypotheses.1.supports.1": {
        "path": "hypotheses.1.supports.1",
        "line": 171
      },
      "hypotheses.1.evidence_needed": {
        "path": "hypotheses.1.evidence_needed",
        "line": 172
      },
      "hypotheses.1.falsifier": {
        "path": "hypotheses.1.falsifier",
        "line": 175
      },
      "hypotheses.1.stopping_rule": {
        "path": "hypotheses.1.stopping_rule",
        "line": 178
      },
      "hypotheses.2": {
        "path": "hypotheses.2",
        "line": 181
      },
      "hypotheses.2.id": {
        "path": "hypotheses.2.id",
        "line": 181
      },
      "hypotheses.2.title": {
        "path": "hypotheses.2.title",
        "line": 182
      },
      "hypotheses.2.supports": {
        "path": "hypotheses.2.supports",
        "line": 183
      },
      "hypotheses.2.supports.0": {
        "path": "hypotheses.2.supports.0",
        "line": 183
      },
      "hypotheses.2.supports.1": {
        "path": "hypotheses.2.supports.1",
        "line": 183
      },
      "hypotheses.2.evidence_needed": {
        "path": "hypotheses.2.evidence_needed",
        "line": 184
      },
      "hypotheses.2.falsifier": {
        "path": "hypotheses.2.falsifier",
        "line": 188
      },
      "hypotheses.2.stopping_rule": {
        "path": "hypotheses.2.stopping_rule",
        "line": 191
      },
      "hypotheses.3": {
        "path": "hypotheses.3",
        "line": 194
      },
      "hypotheses.3.id": {
        "path": "hypotheses.3.id",
        "line": 194
      },
      "hypotheses.3.title": {
        "path": "hypotheses.3.title",
        "line": 195
      },
      "hypotheses.3.supports": {
        "path": "hypotheses.3.supports",
        "line": 196
      },
      "hypotheses.3.supports.0": {
        "path": "hypotheses.3.supports.0",
        "line": 196
      },
      "hypotheses.3.supports.1": {
        "path": "hypotheses.3.supports.1",
        "line": 196
      },
      "hypotheses.3.evidence_needed": {
        "path": "hypotheses.3.evidence_needed",
        "line": 197
      },
      "hypotheses.3.falsifier": {
        "path": "hypotheses.3.falsifier",
        "line": 200
      },
      "hypotheses.3.stopping_rule": {
        "path": "hypotheses.3.stopping_rule",
        "line": 203
      },
      "hypotheses.4": {
        "path": "hypotheses.4",
        "line": 205
      },
      "hypotheses.4.id": {
        "path": "hypotheses.4.id",
        "line": 205
      },
      "hypotheses.4.title": {
        "path": "hypotheses.4.title",
        "line": 206
      },
      "hypotheses.4.supports": {
        "path": "hypotheses.4.supports",
        "line": 207
      },
      "hypotheses.4.supports.0": {
        "path": "hypotheses.4.supports.0",
        "line": 207
      },
      "hypotheses.4.supports.1": {
        "path": "hypotheses.4.supports.1",
        "line": 207
      },
      "hypotheses.4.supports.2": {
        "path": "hypotheses.4.supports.2",
        "line": 207
      },
      "hypotheses.4.supports.3": {
        "path": "hypotheses.4.supports.3",
        "line": 207
      },
      "hypotheses.4.supports.4": {
        "path": "hypotheses.4.supports.4",
        "line": 207
      },
      "hypotheses.4.evidence_needed": {
        "path": "hypotheses.4.evidence_needed",
        "line": 208
      },
      "hypotheses.4.falsifier": {
        "path": "hypotheses.4.falsifier",
        "line": 211
      },
      "hypotheses.4.stopping_rule": {
        "path": "hypotheses.4.stopping_rule",
        "line": 214
      },
      "implementation_choices": {
        "path": "implementation_choices",
        "line": 219
      },
      "implementation_choices.0": {
        "path": "implementation_choices.0",
        "line": 219
      },
      "implementation_choices.0.id": {
        "path": "implementation_choices.0.id",
        "line": 219
      },
      "implementation_choices.0.title": {
        "path": "implementation_choices.0.title",
        "line": 220
      },
      "implementation_choices.0.status": {
        "path": "implementation_choices.0.status",
        "line": 221
      },
      "implementation_choices.0.examples": {
        "path": "implementation_choices.0.examples",
        "line": 222
      },
      "implementation_choices.0.examples.0": {
        "path": "implementation_choices.0.examples.0",
        "line": 222
      },
      "implementation_choices.0.examples.1": {
        "path": "implementation_choices.0.examples.1",
        "line": 222
      },
      "implementation_choices.0.examples.2": {
        "path": "implementation_choices.0.examples.2",
        "line": 222
      },
      "implementation_choices.0.examples.3": {
        "path": "implementation_choices.0.examples.3",
        "line": 222
      },
      "implementation_choices.1": {
        "path": "implementation_choices.1",
        "line": 223
      },
      "implementation_choices.1.id": {
        "path": "implementation_choices.1.id",
        "line": 223
      },
      "implementation_choices.1.title": {
        "path": "implementation_choices.1.title",
        "line": 224
      },
      "implementation_choices.1.status": {
        "path": "implementation_choices.1.status",
        "line": 225
      },
      "implementation_choices.1.examples": {
        "path": "implementation_choices.1.examples",
        "line": 226
      },
      "implementation_choices.1.examples.0": {
        "path": "implementation_choices.1.examples.0",
        "line": 226
      },
      "implementation_choices.1.examples.1": {
        "path": "implementation_choices.1.examples.1",
        "line": 226
      },
      "implementation_choices.1.examples.2": {
        "path": "implementation_choices.1.examples.2",
        "line": 226
      },
      "implementation_choices.1.examples.3": {
        "path": "implementation_choices.1.examples.3",
        "line": 226
      },
      "implementation_choices.2": {
        "path": "implementation_choices.2",
        "line": 227
      },
      "implementation_choices.2.id": {
        "path": "implementation_choices.2.id",
        "line": 227
      },
      "implementation_choices.2.title": {
        "path": "implementation_choices.2.title",
        "line": 228
      },
      "implementation_choices.2.status": {
        "path": "implementation_choices.2.status",
        "line": 229
      },
      "implementation_choices.2.examples": {
        "path": "implementation_choices.2.examples",
        "line": 230
      },
      "implementation_choices.2.examples.0": {
        "path": "implementation_choices.2.examples.0",
        "line": 230
      },
      "implementation_choices.2.examples.1": {
        "path": "implementation_choices.2.examples.1",
        "line": 230
      },
      "implementation_choices.2.examples.2": {
        "path": "implementation_choices.2.examples.2",
        "line": 230
      },
      "implementation_choices.3": {
        "path": "implementation_choices.3",
        "line": 231
      },
      "implementation_choices.3.id": {
        "path": "implementation_choices.3.id",
        "line": 231
      },
      "implementation_choices.3.title": {
        "path": "implementation_choices.3.title",
        "line": 232
      },
      "implementation_choices.3.status": {
        "path": "implementation_choices.3.status",
        "line": 233
      },
      "implementation_choices.3.examples": {
        "path": "implementation_choices.3.examples",
        "line": 234
      },
      "implementation_choices.3.examples.0": {
        "path": "implementation_choices.3.examples.0",
        "line": 234
      },
      "implementation_choices.3.examples.1": {
        "path": "implementation_choices.3.examples.1",
        "line": 234
      },
      "implementation_choices.3.examples.2": {
        "path": "implementation_choices.3.examples.2",
        "line": 234
      },
      "implementation_choices.3.examples.3": {
        "path": "implementation_choices.3.examples.3",
        "line": 234
      },
      "implementation_choices.4": {
        "path": "implementation_choices.4",
        "line": 235
      },
      "implementation_choices.4.id": {
        "path": "implementation_choices.4.id",
        "line": 235
      },
      "implementation_choices.4.title": {
        "path": "implementation_choices.4.title",
        "line": 236
      },
      "implementation_choices.4.status": {
        "path": "implementation_choices.4.status",
        "line": 237
      },
      "implementation_choices.4.examples": {
        "path": "implementation_choices.4.examples",
        "line": 238
      },
      "implementation_choices.4.examples.0": {
        "path": "implementation_choices.4.examples.0",
        "line": 238
      },
      "implementation_choices.4.examples.1": {
        "path": "implementation_choices.4.examples.1",
        "line": 238
      },
      "implementation_choices.4.examples.2": {
        "path": "implementation_choices.4.examples.2",
        "line": 238
      },
      "implementation_choices.4.examples.3": {
        "path": "implementation_choices.4.examples.3",
        "line": 238
      },
      "open_questions": {
        "path": "open_questions",
        "line": 241
      },
      "open_questions.0": {
        "path": "open_questions.0",
        "line": 241
      },
      "open_questions.0.id": {
        "path": "open_questions.0.id",
        "line": 241
      },
      "open_questions.0.question": {
        "path": "open_questions.0.question",
        "line": 242
      },
      "open_questions.1": {
        "path": "open_questions.1",
        "line": 243
      },
      "open_questions.1.id": {
        "path": "open_questions.1.id",
        "line": 243
      },
      "open_questions.1.question": {
        "path": "open_questions.1.question",
        "line": 244
      },
      "open_questions.2": {
        "path": "open_questions.2",
        "line": 245
      },
      "open_questions.2.id": {
        "path": "open_questions.2.id",
        "line": 245
      },
      "open_questions.2.question": {
        "path": "open_questions.2.question",
        "line": 246
      },
      "open_questions.3": {
        "path": "open_questions.3",
        "line": 247
      },
      "open_questions.3.id": {
        "path": "open_questions.3.id",
        "line": 247
      },
      "open_questions.3.question": {
        "path": "open_questions.3.question",
        "line": 248
      },
      "open_questions.4": {
        "path": "open_questions.4",
        "line": 249
      },
      "open_questions.4.id": {
        "path": "open_questions.4.id",
        "line": 249
      },
      "open_questions.4.question": {
        "path": "open_questions.4.question",
        "line": 250
      },
      "open_questions.5": {
        "path": "open_questions.5",
        "line": 251
      },
      "open_questions.5.id": {
        "path": "open_questions.5.id",
        "line": 251
      },
      "open_questions.5.question": {
        "path": "open_questions.5.question",
        "line": 252
      },
      "failure_modes": {
        "path": "failure_modes",
        "line": 255
      },
      "failure_modes.0": {
        "path": "failure_modes.0",
        "line": 255
      },
      "failure_modes.1": {
        "path": "failure_modes.1",
        "line": 256
      },
      "failure_modes.2": {
        "path": "failure_modes.2",
        "line": 257
      },
      "failure_modes.3": {
        "path": "failure_modes.3",
        "line": 258
      },
      "failure_modes.4": {
        "path": "failure_modes.4",
        "line": 259
      },
      "failure_modes.5": {
        "path": "failure_modes.5",
        "line": 260
      },
      "failure_modes.6": {
        "path": "failure_modes.6",
        "line": 261
      },
      "failure_modes.7": {
        "path": "failure_modes.7",
        "line": 262
      },
      "evidence_paths": {
        "path": "evidence_paths",
        "line": 265
      },
      "evidence_paths.packet_to_hub": {
        "path": "evidence_paths.packet_to_hub",
        "line": 266
      },
      "evidence_paths.packet_to_hub.0": {
        "path": "evidence_paths.packet_to_hub.0",
        "line": 266
      },
      "evidence_paths.packet_to_hub.1": {
        "path": "evidence_paths.packet_to_hub.1",
        "line": 267
      },
      "evidence_paths.packet_to_hub.2": {
        "path": "evidence_paths.packet_to_hub.2",
        "line": 268
      },
      "evidence_paths.packet_to_hub.3": {
        "path": "evidence_paths.packet_to_hub.3",
        "line": 269
      },
      "evidence_paths.lunar_tether_cycle": {
        "path": "evidence_paths.lunar_tether_cycle",
        "line": 271
      },
      "evidence_paths.lunar_tether_cycle.0": {
        "path": "evidence_paths.lunar_tether_cycle.0",
        "line": 271
      },
      "evidence_paths.lunar_tether_cycle.1": {
        "path": "evidence_paths.lunar_tether_cycle.1",
        "line": 272
      },
      "evidence_paths.lunar_tether_cycle.2": {
        "path": "evidence_paths.lunar_tether_cycle.2",
        "line": 273
      },
      "evidence_paths.lunar_tether_cycle.3": {
        "path": "evidence_paths.lunar_tether_cycle.3",
        "line": 274
      },
      "evidence_paths.lunar_tether_cycle.4": {
        "path": "evidence_paths.lunar_tether_cycle.4",
        "line": 275
      },
      "evidence_paths.shuttle_aerocapture": {
        "path": "evidence_paths.shuttle_aerocapture",
        "line": 277
      },
      "evidence_paths.shuttle_aerocapture.0": {
        "path": "evidence_paths.shuttle_aerocapture.0",
        "line": 277
      },
      "evidence_paths.shuttle_aerocapture.1": {
        "path": "evidence_paths.shuttle_aerocapture.1",
        "line": 278
      },
      "evidence_paths.shuttle_aerocapture.2": {
        "path": "evidence_paths.shuttle_aerocapture.2",
        "line": 279
      },
      "evidence_paths.shuttle_aerocapture.3": {
        "path": "evidence_paths.shuttle_aerocapture.3",
        "line": 280
      },
      "evidence_paths.shuttle_aerocapture.4": {
        "path": "evidence_paths.shuttle_aerocapture.4",
        "line": 281
      },
      "evidence_paths.lifecycle_claim": {
        "path": "evidence_paths.lifecycle_claim",
        "line": 283
      },
      "evidence_paths.lifecycle_claim.0": {
        "path": "evidence_paths.lifecycle_claim.0",
        "line": 283
      },
      "evidence_paths.lifecycle_claim.1": {
        "path": "evidence_paths.lifecycle_claim.1",
        "line": 284
      },
      "evidence_paths.lifecycle_claim.2": {
        "path": "evidence_paths.lifecycle_claim.2",
        "line": 285
      },
      "evidence_paths.lifecycle_claim.3": {
        "path": "evidence_paths.lifecycle_claim.3",
        "line": 286
      },
      "evidence_paths.lifecycle_claim.4": {
        "path": "evidence_paths.lifecycle_claim.4",
        "line": 287
      },
      "decision_criteria": {
        "path": "decision_criteria",
        "line": 290
      },
      "decision_criteria.faithful_rendering": {
        "path": "decision_criteria.faithful_rendering",
        "line": 291
      },
      "decision_criteria.faithful_rendering.0": {
        "path": "decision_criteria.faithful_rendering.0",
        "line": 291
      },
      "decision_criteria.faithful_rendering.1": {
        "path": "decision_criteria.faithful_rendering.1",
        "line": 292
      },
      "decision_criteria.faithful_rendering.2": {
        "path": "decision_criteria.faithful_rendering.2",
        "line": 293
      },
      "decision_criteria.faithful_rendering.3": {
        "path": "decision_criteria.faithful_rendering.3",
        "line": 294
      },
      "decision_criteria.faithful_rendering.4": {
        "path": "decision_criteria.faithful_rendering.4",
        "line": 295
      },
      "decision_criteria.faithful_rendering.5": {
        "path": "decision_criteria.faithful_rendering.5",
        "line": 296
      },
      "decision_criteria.faithful_rendering.6": {
        "path": "decision_criteria.faithful_rendering.6",
        "line": 297
      },
      "decision_criteria.faithful_rendering.7": {
        "path": "decision_criteria.faithful_rendering.7",
        "line": 298
      },
      "audit": {
        "path": "audit",
        "line": 301
      },
      "audit.comparison_alternatives": {
        "path": "audit.comparison_alternatives",
        "line": 302
      },
      "audit.comparison_alternatives.0": {
        "path": "audit.comparison_alternatives.0",
        "line": 302
      },
      "audit.comparison_alternatives.1": {
        "path": "audit.comparison_alternatives.1",
        "line": 303
      },
      "audit.comparison_alternatives.2": {
        "path": "audit.comparison_alternatives.2",
        "line": 304
      },
      "audit.comparison_alternatives.3": {
        "path": "audit.comparison_alternatives.3",
        "line": 305
      },
      "audit.comparison_alternatives.4": {
        "path": "audit.comparison_alternatives.4",
        "line": 306
      },
      "audit.omission_checks": {
        "path": "audit.omission_checks",
        "line": 308
      },
      "audit.omission_checks.0": {
        "path": "audit.omission_checks.0",
        "line": 308
      },
      "audit.omission_checks.1": {
        "path": "audit.omission_checks.1",
        "line": 309
      },
      "audit.omission_checks.2": {
        "path": "audit.omission_checks.2",
        "line": 310
      },
      "audit.omission_checks.3": {
        "path": "audit.omission_checks.3",
        "line": 311
      },
      "audit.omission_checks.4": {
        "path": "audit.omission_checks.4",
        "line": 312
      },
      "audit.falsification_tests": {
        "path": "audit.falsification_tests",
        "line": 314
      },
      "audit.falsification_tests.0": {
        "path": "audit.falsification_tests.0",
        "line": 314
      },
      "audit.falsification_tests.1": {
        "path": "audit.falsification_tests.1",
        "line": 315
      },
      "audit.falsification_tests.2": {
        "path": "audit.falsification_tests.2",
        "line": 316
      },
      "audit.falsification_tests.3": {
        "path": "audit.falsification_tests.3",
        "line": 317
      },
      "audit.promotion_rule": {
        "path": "audit.promotion_rule",
        "line": 318
      },
      "non_goals": {
        "path": "non_goals",
        "line": 324
      },
      "non_goals.0": {
        "path": "non_goals.0",
        "line": 324
      },
      "non_goals.1": {
        "path": "non_goals.1",
        "line": 325
      },
      "non_goals.2": {
        "path": "non_goals.2",
        "line": 326
      },
      "non_goals.3": {
        "path": "non_goals.3",
        "line": 327
      },
      "non_goals.4": {
        "path": "non_goals.4",
        "line": 328
      },
      "non_goals.5": {
        "path": "non_goals.5",
        "line": 329
      },
      "source_anchors": {
        "path": "source_anchors",
        "line": 332
      },
      "source_anchors.0": {
        "path": "source_anchors.0",
        "line": 332
      },
      "source_anchors.0.id": {
        "path": "source_anchors.0.id",
        "line": 332
      },
      "source_anchors.0.title": {
        "path": "source_anchors.0.title",
        "line": 333
      },
      "source_anchors.0.uri": {
        "path": "source_anchors.0.uri",
        "line": 334
      },
      "source_anchors.0.role": {
        "path": "source_anchors.0.role",
        "line": 335
      },
      "source_anchors.1": {
        "path": "source_anchors.1",
        "line": 336
      },
      "source_anchors.1.id": {
        "path": "source_anchors.1.id",
        "line": 336
      },
      "source_anchors.1.title": {
        "path": "source_anchors.1.title",
        "line": 337
      },
      "source_anchors.1.uri": {
        "path": "source_anchors.1.uri",
        "line": 338
      },
      "source_anchors.1.role": {
        "path": "source_anchors.1.role",
        "line": 339
      },
      "source_anchors.2": {
        "path": "source_anchors.2",
        "line": 340
      },
      "source_anchors.2.id": {
        "path": "source_anchors.2.id",
        "line": 340
      },
      "source_anchors.2.title": {
        "path": "source_anchors.2.title",
        "line": 341
      },
      "source_anchors.2.uri": {
        "path": "source_anchors.2.uri",
        "line": 342
      },
      "source_anchors.2.role": {
        "path": "source_anchors.2.role",
        "line": 343
      },
      "source_anchors.3": {
        "path": "source_anchors.3",
        "line": 344
      },
      "source_anchors.3.id": {
        "path": "source_anchors.3.id",
        "line": 344
      },
      "source_anchors.3.title": {
        "path": "source_anchors.3.title",
        "line": 345
      },
      "source_anchors.3.uri": {
        "path": "source_anchors.3.uri",
        "line": 346
      },
      "source_anchors.3.role": {
        "path": "source_anchors.3.role",
        "line": 347
      }
    },
    "experience": {
      "label": "Explore the transport trade screen",
      "url": "https://github.com/uncomposed/lunar-infrastructure",
      "description": "Downloadable project · requires local setup"
    },
    "repository": "https://github.com/uncomposed/cislunar-momentum-loop.git",
    "commit": "4e5ca49f51655c1d52152b44bad42b6079d82338",
    "modelPath": "idea.yaml",
    "modelSha256": "179fee01415183089048fb5ec004e726e7d6b00b016b6981b6c529e42420931c",
    "modelUrl": "/ideas/models/cislunar-momentum-loop-179fee014151.yaml",
    "registryUrl": "https://ideas.proximitytoprogress.com/ideas/cislunar-momentum-loop"
  },
  {
    "slug": "voting-topics",
    "title": "Voting Topics: Forkable Civic Decision Guides",
    "shortTitle": "Voting Topics: Forkable Civic Decision Guides",
    "summary": "A trustworthy civic guide is a reproducible, inspectable, human-owned view over explicit priorities, decision rules, evidence, uncertainty, and judgment. It is not merely a polished recommendation.",
    "version": "0.1-draft",
    "status": "draft",
    "accent": "#386649",
    "accentSoft": "#e9eee3",
    "motif": "network",
    "framing": "A trustworthy civic guide is a reproducible, inspectable, human-owned view over explicit priorities, decision rules, evidence, uncertainty, and judgment. It is not merely a polished recommendation.",
    "metrics": [],
    "lanes": [
      {
        "id": "layers",
        "title": "Parts of a guide",
        "description": "Parts of a guide",
        "items": [
          {
            "id": "priority_model",
            "title": "Priority model",
            "statement": "concrete desired outcomes · personal importance",
            "kind": "definition",
            "source": {
              "path": "idea_model.information_layers.priority_model",
              "line": 36
            }
          },
          {
            "id": "decision_context",
            "title": "Decision context",
            "statement": "contests or questions · available choices · applicable decision methods · authoritative sources",
            "kind": "definition",
            "source": {
              "path": "idea_model.information_layers.decision_context",
              "line": 40
            }
          },
          {
            "id": "evidence_map",
            "title": "Evidence map",
            "statement": "material priority-choice relationships · reasons and sources · uncertainty · provenance and review state",
            "kind": "definition",
            "source": {
              "path": "idea_model.information_layers.evidence_map",
              "line": 46
            }
          },
          {
            "id": "derived_guide",
            "title": "Derived guide",
            "statement": "reproducible draft · explanation of influential inputs · translation into the applicable decision method",
            "kind": "definition",
            "source": {
              "path": "idea_model.information_layers.derived_guide",
              "line": 52
            }
          },
          {
            "id": "human_judgment_and_lineage",
            "title": "Human judgment and lineage",
            "statement": "confirmation or explicit override · authorship · immutable publication identity · parent and descendant relationships",
            "kind": "definition",
            "source": {
              "path": "idea_model.information_layers.human_judgment_and_lineage",
              "line": 57
            }
          }
        ]
      },
      {
        "id": "mechanism",
        "title": "Guide lifecycle",
        "description": "Guide lifecycle",
        "items": [
          {
            "id": "stage-0",
            "title": "Declare priorities",
            "statement": "declare priorities",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "stage-1"
              }
            ],
            "source": {
              "path": "idea_model.mechanism.0",
              "line": 64
            }
          },
          {
            "id": "stage-1",
            "title": "Adopt and inspect a decision context",
            "statement": "adopt and inspect a decision context",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "stage-2"
              }
            ],
            "source": {
              "path": "idea_model.mechanism.1",
              "line": 65
            }
          },
          {
            "id": "stage-2",
            "title": "Select materially relevant relationships",
            "statement": "select materially relevant relationships",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "stage-3"
              }
            ],
            "source": {
              "path": "idea_model.mechanism.2",
              "line": 66
            }
          },
          {
            "id": "stage-3",
            "title": "Assess choices with evidence or explicit uncertainty",
            "statement": "assess choices with evidence or explicit uncertainty",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "stage-4"
              }
            ],
            "source": {
              "path": "idea_model.mechanism.3",
              "line": 67
            }
          },
          {
            "id": "stage-4",
            "title": "Derive and inspect a draft",
            "statement": "derive and inspect a draft",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "stage-5"
              }
            ],
            "source": {
              "path": "idea_model.mechanism.4",
              "line": 68
            }
          },
          {
            "id": "stage-5",
            "title": "Confirm or explicitly override",
            "statement": "confirm or explicitly override",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "stage-6"
              }
            ],
            "source": {
              "path": "idea_model.mechanism.5",
              "line": 69
            }
          },
          {
            "id": "stage-6",
            "title": "Publish an immutable guide",
            "statement": "publish an immutable guide",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "stage-7"
              }
            ],
            "source": {
              "path": "idea_model.mechanism.6",
              "line": 70
            }
          },
          {
            "id": "stage-7",
            "title": "Let a peer inspect, fork, change, regenerate, and reshare",
            "statement": "let a peer inspect, fork, change, regenerate, and reshare",
            "kind": "mechanism",
            "links": [],
            "source": {
              "path": "idea_model.mechanism.7",
              "line": 71
            }
          }
        ]
      },
      {
        "id": "invariants",
        "title": "Principles",
        "description": "Principles",
        "items": [
          {
            "id": "principle-0",
            "title": "Desired outcomes are distinct from solutions and choices.",
            "statement": "Desired outcomes are distinct from solutions and choices.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.0",
              "line": 74
            }
          },
          {
            "id": "principle-1",
            "title": "Unknown, missing, neutral, and evidenced conflict are distinct states.",
            "statement": "Unknown, missing, neutral, and evidenced conflict are distinct states.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.1",
              "line": 75
            }
          },
          {
            "id": "principle-2",
            "title": "Substantive claims expose reason, evidence, provenance, and review state.",
            "statement": "Substantive claims expose reason, evidence, provenance, and review state.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.2",
              "line": 76
            }
          },
          {
            "id": "principle-3",
            "title": "Identical accepted inputs yield identical derived results.",
            "statement": "Identical accepted inputs yield identical derived results.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.3",
              "line": 77
            }
          },
          {
            "id": "principle-4",
            "title": "Applicable decision rules constrain the usable output.",
            "statement": "Applicable decision rules constrain the usable output.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.4",
              "line": 78
            }
          },
          {
            "id": "principle-5",
            "title": "Assistant-proposed work never silently becomes accepted human work.",
            "statement": "Assistant-proposed work never silently becomes accepted human work.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.5",
              "line": 79
            }
          },
          {
            "id": "principle-6",
            "title": "Final judgment remains inspectable and human-owned.",
            "statement": "Final judgment remains inspectable and human-owned.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.6",
              "line": 80
            }
          },
          {
            "id": "principle-7",
            "title": "Shared guides are independently forkable rather than centrally mutable.",
            "statement": "Shared guides are independently forkable rather than centrally mutable.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.7",
              "line": 81
            }
          },
          {
            "id": "principle-8",
            "title": "Changed inputs expose downstream invalidation or changed conclusions.",
            "statement": "Changed inputs expose downstream invalidation or changed conclusions.",
            "kind": "kernel",
            "source": {
              "path": "idea_model.invariants.8",
              "line": 82
            }
          }
        ]
      },
      {
        "id": "questions",
        "title": "Open questions",
        "description": "Open questions",
        "items": [
          {
            "id": "question-0",
            "title": "Expressiveness versus authoring effort",
            "statement": "expressiveness versus authoring effort",
            "kind": "open_question",
            "source": {
              "path": "idea_model.open_challenges.0",
              "line": 85
            }
          },
          {
            "id": "question-1",
            "title": "Useful incompleteness versus false certainty",
            "statement": "useful incompleteness versus false certainty",
            "kind": "open_question",
            "source": {
              "path": "idea_model.open_challenges.1",
              "line": 86
            }
          },
          {
            "id": "question-2",
            "title": "Reusable priorities versus context-specific evidence",
            "statement": "reusable priorities versus context-specific evidence",
            "kind": "open_question",
            "source": {
              "path": "idea_model.open_challenges.2",
              "line": 87
            }
          },
          {
            "id": "question-3",
            "title": "Deterministic derivation versus legitimate discretion",
            "statement": "deterministic derivation versus legitimate discretion",
            "kind": "open_question",
            "source": {
              "path": "idea_model.open_challenges.3",
              "line": 88
            }
          },
          {
            "id": "question-4",
            "title": "Social trust versus group consensus",
            "statement": "social trust versus group consensus",
            "kind": "open_question",
            "source": {
              "path": "idea_model.open_challenges.4",
              "line": 89
            }
          },
          {
            "id": "question-5",
            "title": "AI assistance versus hidden authority",
            "statement": "AI assistance versus hidden authority",
            "kind": "open_question",
            "source": {
              "path": "idea_model.open_challenges.5",
              "line": 90
            }
          },
          {
            "id": "question-6",
            "title": "Portability versus sensitive-preference privacy",
            "statement": "portability versus sensitive-preference privacy",
            "kind": "open_question",
            "source": {
              "path": "idea_model.open_challenges.6",
              "line": 91
            }
          },
          {
            "id": "question-7",
            "title": "Historical rendering identity versus expiring access",
            "statement": "historical rendering identity versus expiring access",
            "kind": "open_question",
            "source": {
              "path": "idea_model.open_challenges.7",
              "line": 92
            }
          }
        ]
      }
    ],
    "paths": [
      {
        "id": "priority_to_guide",
        "title": "Priority to guide",
        "steps": [
          "declared priority",
          "material relationship",
          "reviewed evidence or explicit uncertainty",
          "reproducible derivation",
          "human decision"
        ],
        "source": {
          "path": "idea_model.evidence_paths.priority_to_guide",
          "line": 104
        }
      },
      {
        "id": "peer_change",
        "title": "Peer change",
        "steps": [
          "parent publication",
          "declared changed input",
          "exposed downstream difference",
          "independent descendant publication"
        ],
        "source": {
          "path": "idea_model.evidence_paths.peer_change",
          "line": 111
        }
      },
      {
        "id": "assistant_contribution",
        "title": "Assistant contribution",
        "steps": [
          "bounded request",
          "attributed proposal",
          "human review",
          "explicit acceptance or rejection"
        ],
        "source": {
          "path": "idea_model.evidence_paths.assistant_contribution",
          "line": 117
        }
      }
    ],
    "checks": [
      {
        "id": "check-0",
        "title": "Desired outcomes are distinct from solutions and choices.",
        "description": "Desired outcomes are distinct from solutions and choices.",
        "source": {
          "path": "idea_model.invariants.0",
          "line": 74
        }
      },
      {
        "id": "check-1",
        "title": "Unknown, missing, neutral, and evidenced conflict are distinct states.",
        "description": "Unknown, missing, neutral, and evidenced conflict are distinct states.",
        "source": {
          "path": "idea_model.invariants.1",
          "line": 75
        }
      },
      {
        "id": "check-2",
        "title": "Substantive claims expose reason, evidence, provenance, and review state.",
        "description": "Substantive claims expose reason, evidence, provenance, and review state.",
        "source": {
          "path": "idea_model.invariants.2",
          "line": 76
        }
      },
      {
        "id": "check-3",
        "title": "Identical accepted inputs yield identical derived results.",
        "description": "Identical accepted inputs yield identical derived results.",
        "source": {
          "path": "idea_model.invariants.3",
          "line": 77
        }
      },
      {
        "id": "check-4",
        "title": "Applicable decision rules constrain the usable output.",
        "description": "Applicable decision rules constrain the usable output.",
        "source": {
          "path": "idea_model.invariants.4",
          "line": 78
        }
      },
      {
        "id": "check-5",
        "title": "Assistant-proposed work never silently becomes accepted human work.",
        "description": "Assistant-proposed work never silently becomes accepted human work.",
        "source": {
          "path": "idea_model.invariants.5",
          "line": 79
        }
      },
      {
        "id": "check-6",
        "title": "Final judgment remains inspectable and human-owned.",
        "description": "Final judgment remains inspectable and human-owned.",
        "source": {
          "path": "idea_model.invariants.6",
          "line": 80
        }
      },
      {
        "id": "check-7",
        "title": "Shared guides are independently forkable rather than centrally mutable.",
        "description": "Shared guides are independently forkable rather than centrally mutable.",
        "source": {
          "path": "idea_model.invariants.7",
          "line": 81
        }
      },
      {
        "id": "check-8",
        "title": "Changed inputs expose downstream invalidation or changed conclusions.",
        "description": "Changed inputs expose downstream invalidation or changed conclusions.",
        "source": {
          "path": "idea_model.invariants.8",
          "line": 82
        }
      }
    ],
    "nonGoals": [
      "endorse political choices",
      "determine political truth",
      "infer preferences from surveillance",
      "define a canonical election database or group profile",
      "mandate a scoring formula, interface, architecture, or AI provider",
      "require publication of implementation source or private methods"
    ],
    "guide": {
      "premise": "A personal guide you can explain",
      "question": "Can a voting guide show how your priorities became your choices?",
      "introduction": "Describe the outcomes you care about, examine the available choices, and review how evidence connects them. Keep the final judgment yours.",
      "scope": "The guide expresses its author’s judgment. It does not determine political truth or turn another person’s priorities into your own.",
      "scopeRefs": [
        "principle-6",
        "principle-7"
      ],
      "mechanismTitle": "From priorities to a guide others can inspect",
      "mechanismDescription": "Follow the proposed process from personal outcomes to an independently editable publication.",
      "steps": [
        {
          "id": "priorities",
          "title": "Name what matters",
          "description": "Start with concrete desired outcomes and their importance, separately from parties, candidates, or proposed solutions.",
          "refs": [
            "priority_model",
            "principle-0"
          ],
          "connection": "Your declared priorities"
        },
        {
          "id": "evidence",
          "title": "Review the choices",
          "description": "Inspect the decision context and connect relevant choices to priorities through evidence, reasons, or explicit uncertainty.",
          "refs": [
            "decision_context",
            "evidence_map",
            "principle-1"
          ],
          "connection": "Inspectable relationships"
        },
        {
          "id": "judgment",
          "title": "Make your judgment",
          "description": "Examine a reproducible draft, then confirm it or record an explicit override. Assistance does not silently become your decision.",
          "refs": [
            "derived_guide",
            "principle-5",
            "principle-6"
          ],
          "connection": "An accepted personal guide"
        },
        {
          "id": "share",
          "title": "Publish and let others adapt",
          "description": "A peer can inspect your guide, make an independent copy, change its inputs, and publish a descendant without rewriting your original.",
          "refs": [
            "human_judgment_and_lineage",
            "principle-7",
            "principle-8"
          ],
          "connection": "A visible history of changes"
        }
      ],
      "terms": [
        {
          "id": "priority",
          "title": "Priority",
          "meaning": "A concrete desired outcome and the personal importance assigned to it.",
          "refs": [
            "priority_model"
          ]
        },
        {
          "id": "context",
          "title": "Decision context",
          "meaning": "The contests, choices, decision methods, and authoritative sources relevant to this guide.",
          "refs": [
            "decision_context"
          ]
        },
        {
          "id": "evidence",
          "title": "Evidence map",
          "meaning": "Reasons, sources, uncertainty, and review state connecting priorities with choices.",
          "refs": [
            "evidence_map"
          ]
        },
        {
          "id": "draft",
          "title": "Derived guide",
          "meaning": "A reproducible draft showing which inputs influenced it and how it fits the applicable decision method.",
          "refs": [
            "derived_guide"
          ]
        },
        {
          "id": "fork",
          "title": "Independent copy",
          "meaning": "A descendant guide that can change inputs and conclusions while retaining its relationship to the original.",
          "refs": [
            "human_judgment_and_lineage",
            "principle-7"
          ]
        }
      ],
      "example": {
        "title": "Disagreement can produce a new guide",
        "text": "A peer can value an outcome differently, change that input in their own copy, and inspect which conclusions change. The original remains intact.",
        "refs": [
          "principle-7",
          "principle-8"
        ]
      },
      "uncertainty": {
        "title": "How much work is enough to be useful?",
        "text": "A useful guide must make incomplete evidence visible without demanding more authoring effort than a person can sustain.",
        "refs": [
          "question-0",
          "question-1"
        ]
      },
      "featured": [
        "question-0",
        "question-1",
        "question-3",
        "question-5",
        "question-6"
      ]
    },
    "contextSources": [],
    "evidenceSummary": "This revision defines behavior, principles, and questions to inspect. Its checks and background references do not establish that an implementation has passed them.",
    "sourceSections": [
      {
        "key": "idea_model",
        "value": {
          "version": "0.1-draft",
          "name": "Voting Topics: Forkable Civic Decision Guides",
          "thesis": "A trustworthy civic guide is a reproducible, inspectable, human-owned view over explicit priorities, decision rules, evidence, uncertainty, and judgment. It is not merely a polished recommendation.\n",
          "pressures": [
            "AI lowers the cost of producing persuasive recommendations.",
            "Political labels and slogans compress multidimensional priorities.",
            "Election rules and available choices vary by decision context.",
            "Evidence is incomplete, disputed, time-sensitive, and costly to review.",
            "Social trust can make a guide useful without making it universally authoritative."
          ],
          "actors": {
            "guide_author": {
              "responsibilities": [
                "declare personal priorities",
                "review evidence and uncertainty",
                "accept responsibility for the final guide"
              ]
            },
            "peer": {
              "responsibilities": [
                "inspect provenance and reasoning",
                "fork rather than mutate another person's guide"
              ]
            },
            "source_publisher": {
              "responsibilities": [
                "publish decision rules or supporting evidence"
              ]
            },
            "optional_assistant": {
              "responsibilities": [
                "propose bounded work",
                "remain distinguishable from human acceptance"
              ]
            }
          },
          "information_layers": {
            "priority_model": {
              "contains": [
                "concrete desired outcomes",
                "personal importance"
              ]
            },
            "decision_context": {
              "contains": [
                "contests or questions",
                "available choices",
                "applicable decision methods",
                "authoritative sources"
              ]
            },
            "evidence_map": {
              "contains": [
                "material priority-choice relationships",
                "reasons and sources",
                "uncertainty",
                "provenance and review state"
              ]
            },
            "derived_guide": {
              "contains": [
                "reproducible draft",
                "explanation of influential inputs",
                "translation into the applicable decision method"
              ]
            },
            "human_judgment_and_lineage": {
              "contains": [
                "confirmation or explicit override",
                "authorship",
                "immutable publication identity",
                "parent and descendant relationships"
              ]
            }
          },
          "mechanism": [
            "declare priorities",
            "adopt and inspect a decision context",
            "select materially relevant relationships",
            "assess choices with evidence or explicit uncertainty",
            "derive and inspect a draft",
            "confirm or explicitly override",
            "publish an immutable guide",
            "let a peer inspect, fork, change, regenerate, and reshare"
          ],
          "invariants": [
            "Desired outcomes are distinct from solutions and choices.",
            "Unknown, missing, neutral, and evidenced conflict are distinct states.",
            "Substantive claims expose reason, evidence, provenance, and review state.",
            "Identical accepted inputs yield identical derived results.",
            "Applicable decision rules constrain the usable output.",
            "Assistant-proposed work never silently becomes accepted human work.",
            "Final judgment remains inspectable and human-owned.",
            "Shared guides are independently forkable rather than centrally mutable.",
            "Changed inputs expose downstream invalidation or changed conclusions."
          ],
          "open_challenges": [
            "expressiveness versus authoring effort",
            "useful incompleteness versus false certainty",
            "reusable priorities versus context-specific evidence",
            "deterministic derivation versus legitimate discretion",
            "social trust versus group consensus",
            "AI assistance versus hidden authority",
            "portability versus sensitive-preference privacy",
            "historical rendering identity versus expiring access"
          ],
          "non_goals": [
            "endorse political choices",
            "determine political truth",
            "infer preferences from surveillance",
            "define a canonical election database or group profile",
            "mandate a scoring formula, interface, architecture, or AI provider",
            "require publication of implementation source or private methods"
          ],
          "evidence_paths": {
            "priority_to_guide": {
              "path": [
                "declared priority",
                "material relationship",
                "reviewed evidence or explicit uncertainty",
                "reproducible derivation",
                "human decision"
              ]
            },
            "peer_change": {
              "path": [
                "parent publication",
                "declared changed input",
                "exposed downstream difference",
                "independent descendant publication"
              ]
            },
            "assistant_contribution": {
              "path": [
                "bounded request",
                "attributed proposal",
                "human review",
                "explicit acceptance or rejection"
              ]
            }
          },
          "decision_criteria": {
            "faithful_rendering": [
              "preserves typed information layers",
              "preserves uncertainty and provenance",
              "makes derivation and human judgment distinguishable",
              "supports inspection and independent forks",
              "does not present automation or polish as authority"
            ]
          }
        },
        "source": {
          "path": "idea_model",
          "line": 2
        }
      }
    ],
    "sourceLocations": {
      "idea_model": {
        "path": "idea_model",
        "line": 2
      },
      "idea_model.version": {
        "path": "idea_model.version",
        "line": 2
      },
      "idea_model.name": {
        "path": "idea_model.name",
        "line": 3
      },
      "idea_model.thesis": {
        "path": "idea_model.thesis",
        "line": 4
      },
      "idea_model.pressures": {
        "path": "idea_model.pressures",
        "line": 10
      },
      "idea_model.pressures.0": {
        "path": "idea_model.pressures.0",
        "line": 10
      },
      "idea_model.pressures.1": {
        "path": "idea_model.pressures.1",
        "line": 11
      },
      "idea_model.pressures.2": {
        "path": "idea_model.pressures.2",
        "line": 12
      },
      "idea_model.pressures.3": {
        "path": "idea_model.pressures.3",
        "line": 13
      },
      "idea_model.pressures.4": {
        "path": "idea_model.pressures.4",
        "line": 14
      },
      "idea_model.actors": {
        "path": "idea_model.actors",
        "line": 17
      },
      "idea_model.actors.guide_author": {
        "path": "idea_model.actors.guide_author",
        "line": 18
      },
      "idea_model.actors.guide_author.responsibilities": {
        "path": "idea_model.actors.guide_author.responsibilities",
        "line": 19
      },
      "idea_model.actors.guide_author.responsibilities.0": {
        "path": "idea_model.actors.guide_author.responsibilities.0",
        "line": 19
      },
      "idea_model.actors.guide_author.responsibilities.1": {
        "path": "idea_model.actors.guide_author.responsibilities.1",
        "line": 20
      },
      "idea_model.actors.guide_author.responsibilities.2": {
        "path": "idea_model.actors.guide_author.responsibilities.2",
        "line": 21
      },
      "idea_model.actors.peer": {
        "path": "idea_model.actors.peer",
        "line": 23
      },
      "idea_model.actors.peer.responsibilities": {
        "path": "idea_model.actors.peer.responsibilities",
        "line": 24
      },
      "idea_model.actors.peer.responsibilities.0": {
        "path": "idea_model.actors.peer.responsibilities.0",
        "line": 24
      },
      "idea_model.actors.peer.responsibilities.1": {
        "path": "idea_model.actors.peer.responsibilities.1",
        "line": 25
      },
      "idea_model.actors.source_publisher": {
        "path": "idea_model.actors.source_publisher",
        "line": 27
      },
      "idea_model.actors.source_publisher.responsibilities": {
        "path": "idea_model.actors.source_publisher.responsibilities",
        "line": 28
      },
      "idea_model.actors.source_publisher.responsibilities.0": {
        "path": "idea_model.actors.source_publisher.responsibilities.0",
        "line": 28
      },
      "idea_model.actors.optional_assistant": {
        "path": "idea_model.actors.optional_assistant",
        "line": 30
      },
      "idea_model.actors.optional_assistant.responsibilities": {
        "path": "idea_model.actors.optional_assistant.responsibilities",
        "line": 31
      },
      "idea_model.actors.optional_assistant.responsibilities.0": {
        "path": "idea_model.actors.optional_assistant.responsibilities.0",
        "line": 31
      },
      "idea_model.actors.optional_assistant.responsibilities.1": {
        "path": "idea_model.actors.optional_assistant.responsibilities.1",
        "line": 32
      },
      "idea_model.information_layers": {
        "path": "idea_model.information_layers",
        "line": 35
      },
      "idea_model.information_layers.priority_model": {
        "path": "idea_model.information_layers.priority_model",
        "line": 36
      },
      "idea_model.information_layers.priority_model.contains": {
        "path": "idea_model.information_layers.priority_model.contains",
        "line": 37
      },
      "idea_model.information_layers.priority_model.contains.0": {
        "path": "idea_model.information_layers.priority_model.contains.0",
        "line": 37
      },
      "idea_model.information_layers.priority_model.contains.1": {
        "path": "idea_model.information_layers.priority_model.contains.1",
        "line": 38
      },
      "idea_model.information_layers.decision_context": {
        "path": "idea_model.information_layers.decision_context",
        "line": 40
      },
      "idea_model.information_layers.decision_context.contains": {
        "path": "idea_model.information_layers.decision_context.contains",
        "line": 41
      },
      "idea_model.information_layers.decision_context.contains.0": {
        "path": "idea_model.information_layers.decision_context.contains.0",
        "line": 41
      },
      "idea_model.information_layers.decision_context.contains.1": {
        "path": "idea_model.information_layers.decision_context.contains.1",
        "line": 42
      },
      "idea_model.information_layers.decision_context.contains.2": {
        "path": "idea_model.information_layers.decision_context.contains.2",
        "line": 43
      },
      "idea_model.information_layers.decision_context.contains.3": {
        "path": "idea_model.information_layers.decision_context.contains.3",
        "line": 44
      },
      "idea_model.information_layers.evidence_map": {
        "path": "idea_model.information_layers.evidence_map",
        "line": 46
      },
      "idea_model.information_layers.evidence_map.contains": {
        "path": "idea_model.information_layers.evidence_map.contains",
        "line": 47
      },
      "idea_model.information_layers.evidence_map.contains.0": {
        "path": "idea_model.information_layers.evidence_map.contains.0",
        "line": 47
      },
      "idea_model.information_layers.evidence_map.contains.1": {
        "path": "idea_model.information_layers.evidence_map.contains.1",
        "line": 48
      },
      "idea_model.information_layers.evidence_map.contains.2": {
        "path": "idea_model.information_layers.evidence_map.contains.2",
        "line": 49
      },
      "idea_model.information_layers.evidence_map.contains.3": {
        "path": "idea_model.information_layers.evidence_map.contains.3",
        "line": 50
      },
      "idea_model.information_layers.derived_guide": {
        "path": "idea_model.information_layers.derived_guide",
        "line": 52
      },
      "idea_model.information_layers.derived_guide.contains": {
        "path": "idea_model.information_layers.derived_guide.contains",
        "line": 53
      },
      "idea_model.information_layers.derived_guide.contains.0": {
        "path": "idea_model.information_layers.derived_guide.contains.0",
        "line": 53
      },
      "idea_model.information_layers.derived_guide.contains.1": {
        "path": "idea_model.information_layers.derived_guide.contains.1",
        "line": 54
      },
      "idea_model.information_layers.derived_guide.contains.2": {
        "path": "idea_model.information_layers.derived_guide.contains.2",
        "line": 55
      },
      "idea_model.information_layers.human_judgment_and_lineage": {
        "path": "idea_model.information_layers.human_judgment_and_lineage",
        "line": 57
      },
      "idea_model.information_layers.human_judgment_and_lineage.contains": {
        "path": "idea_model.information_layers.human_judgment_and_lineage.contains",
        "line": 58
      },
      "idea_model.information_layers.human_judgment_and_lineage.contains.0": {
        "path": "idea_model.information_layers.human_judgment_and_lineage.contains.0",
        "line": 58
      },
      "idea_model.information_layers.human_judgment_and_lineage.contains.1": {
        "path": "idea_model.information_layers.human_judgment_and_lineage.contains.1",
        "line": 59
      },
      "idea_model.information_layers.human_judgment_and_lineage.contains.2": {
        "path": "idea_model.information_layers.human_judgment_and_lineage.contains.2",
        "line": 60
      },
      "idea_model.information_layers.human_judgment_and_lineage.contains.3": {
        "path": "idea_model.information_layers.human_judgment_and_lineage.contains.3",
        "line": 61
      },
      "idea_model.mechanism": {
        "path": "idea_model.mechanism",
        "line": 64
      },
      "idea_model.mechanism.0": {
        "path": "idea_model.mechanism.0",
        "line": 64
      },
      "idea_model.mechanism.1": {
        "path": "idea_model.mechanism.1",
        "line": 65
      },
      "idea_model.mechanism.2": {
        "path": "idea_model.mechanism.2",
        "line": 66
      },
      "idea_model.mechanism.3": {
        "path": "idea_model.mechanism.3",
        "line": 67
      },
      "idea_model.mechanism.4": {
        "path": "idea_model.mechanism.4",
        "line": 68
      },
      "idea_model.mechanism.5": {
        "path": "idea_model.mechanism.5",
        "line": 69
      },
      "idea_model.mechanism.6": {
        "path": "idea_model.mechanism.6",
        "line": 70
      },
      "idea_model.mechanism.7": {
        "path": "idea_model.mechanism.7",
        "line": 71
      },
      "idea_model.invariants": {
        "path": "idea_model.invariants",
        "line": 74
      },
      "idea_model.invariants.0": {
        "path": "idea_model.invariants.0",
        "line": 74
      },
      "idea_model.invariants.1": {
        "path": "idea_model.invariants.1",
        "line": 75
      },
      "idea_model.invariants.2": {
        "path": "idea_model.invariants.2",
        "line": 76
      },
      "idea_model.invariants.3": {
        "path": "idea_model.invariants.3",
        "line": 77
      },
      "idea_model.invariants.4": {
        "path": "idea_model.invariants.4",
        "line": 78
      },
      "idea_model.invariants.5": {
        "path": "idea_model.invariants.5",
        "line": 79
      },
      "idea_model.invariants.6": {
        "path": "idea_model.invariants.6",
        "line": 80
      },
      "idea_model.invariants.7": {
        "path": "idea_model.invariants.7",
        "line": 81
      },
      "idea_model.invariants.8": {
        "path": "idea_model.invariants.8",
        "line": 82
      },
      "idea_model.open_challenges": {
        "path": "idea_model.open_challenges",
        "line": 85
      },
      "idea_model.open_challenges.0": {
        "path": "idea_model.open_challenges.0",
        "line": 85
      },
      "idea_model.open_challenges.1": {
        "path": "idea_model.open_challenges.1",
        "line": 86
      },
      "idea_model.open_challenges.2": {
        "path": "idea_model.open_challenges.2",
        "line": 87
      },
      "idea_model.open_challenges.3": {
        "path": "idea_model.open_challenges.3",
        "line": 88
      },
      "idea_model.open_challenges.4": {
        "path": "idea_model.open_challenges.4",
        "line": 89
      },
      "idea_model.open_challenges.5": {
        "path": "idea_model.open_challenges.5",
        "line": 90
      },
      "idea_model.open_challenges.6": {
        "path": "idea_model.open_challenges.6",
        "line": 91
      },
      "idea_model.open_challenges.7": {
        "path": "idea_model.open_challenges.7",
        "line": 92
      },
      "idea_model.non_goals": {
        "path": "idea_model.non_goals",
        "line": 95
      },
      "idea_model.non_goals.0": {
        "path": "idea_model.non_goals.0",
        "line": 95
      },
      "idea_model.non_goals.1": {
        "path": "idea_model.non_goals.1",
        "line": 96
      },
      "idea_model.non_goals.2": {
        "path": "idea_model.non_goals.2",
        "line": 97
      },
      "idea_model.non_goals.3": {
        "path": "idea_model.non_goals.3",
        "line": 98
      },
      "idea_model.non_goals.4": {
        "path": "idea_model.non_goals.4",
        "line": 99
      },
      "idea_model.non_goals.5": {
        "path": "idea_model.non_goals.5",
        "line": 100
      },
      "idea_model.evidence_paths": {
        "path": "idea_model.evidence_paths",
        "line": 103
      },
      "idea_model.evidence_paths.priority_to_guide": {
        "path": "idea_model.evidence_paths.priority_to_guide",
        "line": 104
      },
      "idea_model.evidence_paths.priority_to_guide.path": {
        "path": "idea_model.evidence_paths.priority_to_guide.path",
        "line": 105
      },
      "idea_model.evidence_paths.priority_to_guide.path.0": {
        "path": "idea_model.evidence_paths.priority_to_guide.path.0",
        "line": 105
      },
      "idea_model.evidence_paths.priority_to_guide.path.1": {
        "path": "idea_model.evidence_paths.priority_to_guide.path.1",
        "line": 106
      },
      "idea_model.evidence_paths.priority_to_guide.path.2": {
        "path": "idea_model.evidence_paths.priority_to_guide.path.2",
        "line": 107
      },
      "idea_model.evidence_paths.priority_to_guide.path.3": {
        "path": "idea_model.evidence_paths.priority_to_guide.path.3",
        "line": 108
      },
      "idea_model.evidence_paths.priority_to_guide.path.4": {
        "path": "idea_model.evidence_paths.priority_to_guide.path.4",
        "line": 109
      },
      "idea_model.evidence_paths.peer_change": {
        "path": "idea_model.evidence_paths.peer_change",
        "line": 111
      },
      "idea_model.evidence_paths.peer_change.path": {
        "path": "idea_model.evidence_paths.peer_change.path",
        "line": 112
      },
      "idea_model.evidence_paths.peer_change.path.0": {
        "path": "idea_model.evidence_paths.peer_change.path.0",
        "line": 112
      },
      "idea_model.evidence_paths.peer_change.path.1": {
        "path": "idea_model.evidence_paths.peer_change.path.1",
        "line": 113
      },
      "idea_model.evidence_paths.peer_change.path.2": {
        "path": "idea_model.evidence_paths.peer_change.path.2",
        "line": 114
      },
      "idea_model.evidence_paths.peer_change.path.3": {
        "path": "idea_model.evidence_paths.peer_change.path.3",
        "line": 115
      },
      "idea_model.evidence_paths.assistant_contribution": {
        "path": "idea_model.evidence_paths.assistant_contribution",
        "line": 117
      },
      "idea_model.evidence_paths.assistant_contribution.path": {
        "path": "idea_model.evidence_paths.assistant_contribution.path",
        "line": 118
      },
      "idea_model.evidence_paths.assistant_contribution.path.0": {
        "path": "idea_model.evidence_paths.assistant_contribution.path.0",
        "line": 118
      },
      "idea_model.evidence_paths.assistant_contribution.path.1": {
        "path": "idea_model.evidence_paths.assistant_contribution.path.1",
        "line": 119
      },
      "idea_model.evidence_paths.assistant_contribution.path.2": {
        "path": "idea_model.evidence_paths.assistant_contribution.path.2",
        "line": 120
      },
      "idea_model.evidence_paths.assistant_contribution.path.3": {
        "path": "idea_model.evidence_paths.assistant_contribution.path.3",
        "line": 121
      },
      "idea_model.decision_criteria": {
        "path": "idea_model.decision_criteria",
        "line": 124
      },
      "idea_model.decision_criteria.faithful_rendering": {
        "path": "idea_model.decision_criteria.faithful_rendering",
        "line": 125
      },
      "idea_model.decision_criteria.faithful_rendering.0": {
        "path": "idea_model.decision_criteria.faithful_rendering.0",
        "line": 125
      },
      "idea_model.decision_criteria.faithful_rendering.1": {
        "path": "idea_model.decision_criteria.faithful_rendering.1",
        "line": 126
      },
      "idea_model.decision_criteria.faithful_rendering.2": {
        "path": "idea_model.decision_criteria.faithful_rendering.2",
        "line": 127
      },
      "idea_model.decision_criteria.faithful_rendering.3": {
        "path": "idea_model.decision_criteria.faithful_rendering.3",
        "line": 128
      },
      "idea_model.decision_criteria.faithful_rendering.4": {
        "path": "idea_model.decision_criteria.faithful_rendering.4",
        "line": 129
      }
    },
    "experience": {
      "label": "Build a voting guide",
      "url": "https://votingtopics.proximitytoprogress.com/",
      "description": "Interactive web application"
    },
    "repository": "https://github.com/uncomposed/voting-topics-idea.git",
    "commit": "ce2a1923a515ee36e2c2e7852b169495bb015f00",
    "modelPath": "MODEL.yaml",
    "modelSha256": "b380f9d5b713c4a14f2dfcff5efba37839c79a88d2526d5935705528674cf136",
    "modelUrl": "/ideas/models/voting-topics-b380f9d5b713.yaml",
    "registryUrl": "https://ideas.proximitytoprogress.com/ideas/voting-topics"
  },
  {
    "slug": "irap",
    "title": "Idea Rendering Attestation Protocol",
    "shortTitle": "Idea Rendering Attestation Protocol",
    "summary": "A Git-native, forge-agnostic protocol for identifying an exact state of an evolving idea, linking independently published renderings to that state, and recording attributable signed judgments about those renderings.",
    "version": "0.1",
    "status": "draft",
    "accent": "#386649",
    "accentSoft": "#e9eee3",
    "motif": "network",
    "framing": "A Git-native, forge-agnostic protocol for identifying an exact state of an evolving idea, linking independently published renderings to that state, and recording attributable signed judgments about those renderings.",
    "metrics": [],
    "lanes": [
      {
        "id": "objects",
        "title": "Key definitions",
        "description": "Key definitions",
        "items": [
          {
            "id": "idea-state",
            "title": "Idea version",
            "statement": "A state is an exact Git commit, identified by repository identity, object-format algorithm, and full commit object ID.",
            "kind": "definition",
            "source": {
              "path": "core_principles.P2",
              "line": 33
            }
          },
          {
            "id": "rendering",
            "title": "Rendering",
            "statement": "Any independently published artifact claiming a relationship to an exact idea state. IRAP intentionally does not require a media or artifact type.",
            "kind": "definition",
            "source": {
              "path": "rendering.definition",
              "line": 157
            }
          },
          {
            "id": "attestation",
            "title": "Attestation",
            "statement": "A verifier's signed judgment about one rendering against one exact idea state.",
            "kind": "definition",
            "source": {
              "path": "attestation.definition",
              "line": 188
            }
          },
          {
            "id": "recognition",
            "title": "Recognized verification",
            "statement": "A signed attestation counts as recognized only when it satisfies the verification policy contained in the exact idea state referenced by that attestation.",
            "kind": "definition",
            "source": {
              "path": "trust_model.recognized_verification",
              "line": 51
            }
          }
        ]
      },
      {
        "id": "principles",
        "title": "Core principles",
        "description": "Core principles",
        "items": [
          {
            "id": "P1",
            "title": "An idea has a durable identity",
            "statement": "An idea is identified by a durable idea identifier and evolves through Git.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P1",
              "line": 32
            }
          },
          {
            "id": "P2",
            "title": "A version is an exact Git commit",
            "statement": "A state is an exact Git commit, identified by repository identity, object-format algorithm, and full commit object ID.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P2",
              "line": 33
            }
          },
          {
            "id": "P3",
            "title": "A rendering is an independent interpretation",
            "statement": "A rendering is any independently published artifact that claims a relationship to a specified idea state.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P3",
              "line": 34
            }
          },
          {
            "id": "P4",
            "title": "Verification belongs to a named reviewer",
            "statement": "Verification is an attributable act, not an intrinsic property of a rendering.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P4",
              "line": 35
            }
          },
          {
            "id": "P5",
            "title": "An attestation is a signed judgment",
            "statement": "An attestation is a signed judgment by a verifier about a rendering relative to a specific idea state.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P5",
              "line": 36
            }
          },
          {
            "id": "P6",
            "title": "Recognition follows the idea’s rules",
            "statement": "Any verifier may publish an attestation; an idea state defines which verifiers and rules produce recognized verification.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P6",
              "line": 37
            }
          },
          {
            "id": "P7",
            "title": "Evidence can take many forms",
            "statement": "Verification evidence may be arbitrary external material and is referenced by URI and content digest.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P7",
              "line": 38
            }
          },
          {
            "id": "P8",
            "title": "Identity is independent of its host",
            "statement": "The protocol is Git-native and forge-agnostic; forge URLs are mirrors or transports, not canonical identity.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P8",
              "line": 39
            }
          },
          {
            "id": "P9",
            "title": "Distribution does not establish truth",
            "statement": "Federation transports announcements and attestations but does not establish canonical truth.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P9",
              "line": 40
            }
          },
          {
            "id": "P10",
            "title": "Older versions retain their meaning",
            "statement": "Old idea states and their attestations remain meaningful after the canonical branch advances.",
            "kind": "kernel",
            "source": {
              "path": "core_principles.P10",
              "line": 41
            }
          }
        ]
      },
      {
        "id": "trust",
        "title": "Trust and disagreement",
        "description": "Trust and disagreement",
        "items": [
          {
            "id": "canonical_authority",
            "title": "Canonical authority",
            "statement": "Repository contents at the referenced Git commit define the idea state, including its verifier registry and verification policy. Moving branch names MUST be resolved to a full commit object ID before an attestation is issued.",
            "kind": "kernel",
            "source": {
              "path": "trust_model.canonical_authority",
              "line": 44
            }
          },
          {
            "id": "open_attestation",
            "title": "Open attestation",
            "statement": "Anyone MAY publish an attestation. Software MUST distinguish a validly signed attestation from a recognized verification.",
            "kind": "kernel",
            "source": {
              "path": "trust_model.open_attestation",
              "line": 48
            }
          },
          {
            "id": "recognized_verification",
            "title": "Recognized verification",
            "statement": "A signed attestation counts as recognized only when it satisfies the verification policy contained in the exact idea state referenced by that attestation.",
            "kind": "kernel",
            "source": {
              "path": "trust_model.recognized_verification",
              "line": 51
            }
          },
          {
            "id": "verifier_mind_requirement",
            "title": "Verifier mind requirement",
            "statement": "The social meaning of verification requires an intentional judging agent (a mind) to take responsibility for the judgment. The protocol cannot technically prove mentality. Idea maintainers operationalize this by deciding which verifier identities are eligible in the verifier registry.",
            "kind": "kernel",
            "source": {
              "path": "trust_model.verifier_mind_requirement",
              "line": 54
            }
          },
          {
            "id": "ai_verifiers",
            "title": "Ai verifiers",
            "statement": "AI systems MAY be eligible verifiers. Their registry entry SHOULD disclose model, configuration or version identifier where feasible, operator or custodian, and the verification procedure sufficient to understand what system made the judgment.",
            "kind": "kernel",
            "source": {
              "path": "trust_model.ai_verifiers",
              "line": 59
            }
          },
          {
            "id": "disagreement",
            "title": "Disagreement",
            "statement": "Multiple valid attestations MAY disagree. Implementations MUST NOT collapse disagreement into a single global truth value.",
            "kind": "kernel",
            "source": {
              "path": "trust_model.disagreement",
              "line": 63
            }
          }
        ]
      }
    ],
    "paths": [
      {
        "id": "recognition",
        "title": "Checking a signed judgment",
        "steps": [
          "Validate rendering and attestation structure.",
          "Verify attestation references the same rendering id and artifact digest.",
          "Fetch or locate the referenced Git commit.",
          "Verify commit existence using declared repository object format.",
          "Read manifest, verifier registry, and verification policy from that exact commit.",
          "Verify idea id matches.",
          "Resolve verifier and historical public key.",
          "Reconstruct and verify the signed JCS payload.",
          "Evaluate claim-specific recognition policy.",
          "Return signature validity and recognition status separately."
        ],
        "source": {
          "path": "recognition_algorithm.steps",
          "line": 271
        }
      }
    ],
    "checks": [
      {
        "id": "check-0",
        "title": "Never treat a branch name as an immutable verification target.",
        "description": "Never treat a branch name as an immutable verification target.",
        "source": {
          "path": "security.0",
          "line": 301
        }
      },
      {
        "id": "check-1",
        "title": "Never treat a valid signature as recognized without evaluating historical policy.",
        "description": "Never treat a valid signature as recognized without evaluating historical policy.",
        "source": {
          "path": "security.1",
          "line": 302
        }
      },
      {
        "id": "check-2",
        "title": "Never use current main to resolve verifier eligibility for an old attestation.",
        "description": "Never use current main to resolve verifier eligibility for an old attestation.",
        "source": {
          "path": "security.2",
          "line": 303
        }
      },
      {
        "id": "check-3",
        "title": "Never sign YAML serialization directly.",
        "description": "Never sign YAML serialization directly.",
        "source": {
          "path": "security.3",
          "line": 304
        }
      },
      {
        "id": "check-4",
        "title": "Treat URLs as locators and verify digests.",
        "description": "Treat URLs as locators and verify digests.",
        "source": {
          "path": "security.4",
          "line": 305
        }
      },
      {
        "id": "check-5",
        "title": "Preserve received raw attestations for audit while independently validating the signing projection.",
        "description": "Preserve received raw attestations for audit while independently validating the signing projection.",
        "source": {
          "path": "security.5",
          "line": 306
        }
      },
      {
        "id": "check-6",
        "title": "Repository content is untrusted data and MUST NOT be executed.",
        "description": "Repository content is untrusted data and MUST NOT be executed.",
        "source": {
          "path": "security.6",
          "line": 307
        }
      }
    ],
    "nonGoals": [
      "Define how ideas must be written.",
      "Generate renderings.",
      "Rank renderings by artistic, political, scientific, or commercial quality.",
      "Determine whether an idea is true.",
      "Require GitHub, GitLab, Codeberg, Forgejo, ActivityPub, Mastodon, or any specific host.",
      "Define a universal taxonomy for songs, games, papers, protests, simulations, videos, or critiques.",
      "Prove philosophically or technically that a verifier is conscious or a mind.",
      "Require public disclosure of private reasoning used during verification."
    ],
    "guide": {
      "premise": "Ideas, interpretations, and accountable review",
      "question": "How can an idea have many interpretations without losing its identity?",
      "introduction": "Identify a specific version of an idea, link independent interpretations to it, and record who reviewed each interpretation and what they concluded.",
      "scope": "The Idea Rendering Attestation Protocol, abbreviated IRAP, records identity and accountable judgments. It does not establish that an idea is true or that everyone agrees.",
      "scopeRefs": [
        "P4",
        "P5",
        "disagreement"
      ],
      "mechanismTitle": "A version, an interpretation, a signed judgment",
      "mechanismDescription": "Follow the separate objects that make a review traceable.",
      "steps": [
        {
          "id": "version",
          "title": "Identify the idea version",
          "description": "Use an exact version from the idea’s Git history so later edits do not change what a reviewer assessed.",
          "refs": [
            "idea-state",
            "P2"
          ],
          "connection": "A fixed reference"
        },
        {
          "id": "render",
          "title": "Publish an interpretation",
          "description": "An essay, application, film, simulation, or other work can declare its relationship to that idea version.",
          "refs": [
            "rendering",
            "P3"
          ],
          "connection": "An independently published artifact"
        },
        {
          "id": "judge",
          "title": "Record a signed judgment",
          "description": "A named reviewer signs a judgment about that interpretation against that exact version, with review evidence where supplied.",
          "refs": [
            "attestation",
            "P5",
            "P7"
          ],
          "connection": "An attributable review"
        },
        {
          "id": "recognize",
          "title": "Apply that version’s rules",
          "description": "Check whether the reviewer and judgment satisfy the policy belonging to the referenced idea version. Preserve disagreement.",
          "refs": [
            "recognition",
            "P6",
            "disagreement"
          ],
          "connection": "Recognition under a particular policy"
        }
      ],
      "terms": [
        {
          "id": "version",
          "title": "Idea version",
          "meaning": "The model at an exact Git commit: a fixed point in its revision history.",
          "refs": [
            "idea-state"
          ]
        },
        {
          "id": "rendering",
          "title": "Rendering",
          "meaning": "An independently published interpretation or expression that claims a relationship to an idea version.",
          "refs": [
            "rendering"
          ]
        },
        {
          "id": "attestation",
          "title": "Attestation",
          "meaning": "A reviewer’s signed judgment about one rendering against one idea version.",
          "refs": [
            "attestation"
          ]
        },
        {
          "id": "recognition",
          "title": "Recognized verification",
          "meaning": "A signed judgment that meets the rules recorded in the referenced idea version. A valid signature alone is not enough.",
          "refs": [
            "recognition",
            "open_attestation"
          ]
        },
        {
          "id": "evidence",
          "title": "Review evidence",
          "meaning": "Notes, calculations, tests, or other external material associated with a judgment.",
          "refs": [
            "P7"
          ]
        }
      ],
      "example": {
        "title": "Two reviewers may disagree",
        "text": "Both judgments can be validly signed. Recognition depends on the applicable policy, and disagreement should remain visible rather than becoming a single truth score.",
        "refs": [
          "disagreement",
          "open_attestation"
        ]
      },
      "uncertainty": {
        "title": "A signature identifies a judgment, not its correctness",
        "text": "The protocol makes responsibility and policy inspectable. It cannot prove that the idea is true or that the judging agent has a mind.",
        "refs": [
          "P4",
          "verifier_mind_requirement"
        ]
      },
      "featured": [
        "open_attestation",
        "recognized_verification",
        "disagreement",
        "verifier_mind_requirement"
      ]
    },
    "contextSources": [],
    "evidenceSummary": "This revision defines behavior, principles, and questions to inspect. Its checks and background references do not establish that an implementation has passed them.",
    "sourceSections": [
      {
        "key": "protocol",
        "value": {
          "id": "https://ideas.proximitytoprogress.com/ideas/irap",
          "name": "Idea Rendering Attestation Protocol",
          "short_name": "IRAP",
          "version": "0.1",
          "status": "draft",
          "description": "A Git-native, forge-agnostic protocol for identifying an exact state of an evolving idea, linking independently published renderings to that state, and recording attributable signed judgments about those renderings.\n"
        },
        "source": {
          "path": "protocol",
          "line": 2
        }
      },
      {
        "key": "goals",
        "value": [
          "Keep the canonical idea independent of any forge, website, social network, or rendering format.",
          "Reuse Git for version history, branching, immutable state identification, and collaboration.",
          "Treat every external expression, critique, experiment, performance, simulation, or other artifact as a rendering.",
          {
            "Make verification attributable": "a rendering is never intrinsically verified."
          },
          "Allow any mind to attest while allowing each idea state to define which attestations count as recognized verification.",
          "Permit supporting verification work to be attached without forcing it into a fixed ontology.",
          "Permit optional federation, including ActivityPub, without making federation part of the core trust model."
        ],
        "source": {
          "path": "goals",
          "line": 13
        }
      },
      {
        "key": "non_goals",
        "value": [
          "Define how ideas must be written.",
          "Generate renderings.",
          "Rank renderings by artistic, political, scientific, or commercial quality.",
          "Determine whether an idea is true.",
          "Require GitHub, GitLab, Codeberg, Forgejo, ActivityPub, Mastodon, or any specific host.",
          "Define a universal taxonomy for songs, games, papers, protests, simulations, videos, or critiques.",
          "Prove philosophically or technically that a verifier is conscious or a mind.",
          "Require public disclosure of private reasoning used during verification."
        ],
        "source": {
          "path": "non_goals",
          "line": 22
        }
      },
      {
        "key": "core_principles",
        "value": {
          "P1": "An idea is identified by a durable idea identifier and evolves through Git.",
          "P2": "A state is an exact Git commit, identified by repository identity, object-format algorithm, and full commit object ID.",
          "P3": "A rendering is any independently published artifact that claims a relationship to a specified idea state.",
          "P4": "Verification is an attributable act, not an intrinsic property of a rendering.",
          "P5": "An attestation is a signed judgment by a verifier about a rendering relative to a specific idea state.",
          "P6": "Any verifier may publish an attestation; an idea state defines which verifiers and rules produce recognized verification.",
          "P7": "Verification evidence may be arbitrary external material and is referenced by URI and content digest.",
          "P8": "The protocol is Git-native and forge-agnostic; forge URLs are mirrors or transports, not canonical identity.",
          "P9": "Federation transports announcements and attestations but does not establish canonical truth.",
          "P10": "Old idea states and their attestations remain meaningful after the canonical branch advances."
        },
        "source": {
          "path": "core_principles",
          "line": 32
        }
      },
      {
        "key": "trust_model",
        "value": {
          "canonical_authority": "Repository contents at the referenced Git commit define the idea state, including its verifier registry and verification policy. Moving branch names MUST be resolved to a full commit object ID before an attestation is issued.\n",
          "open_attestation": "Anyone MAY publish an attestation. Software MUST distinguish a validly signed attestation from a recognized verification.\n",
          "recognized_verification": "A signed attestation counts as recognized only when it satisfies the verification policy contained in the exact idea state referenced by that attestation.\n",
          "verifier_mind_requirement": "The social meaning of verification requires an intentional judging agent (a mind) to take responsibility for the judgment. The protocol cannot technically prove mentality. Idea maintainers operationalize this by deciding which verifier identities are eligible in the verifier registry.\n",
          "ai_verifiers": "AI systems MAY be eligible verifiers. Their registry entry SHOULD disclose model, configuration or version identifier where feasible, operator or custodian, and the verification procedure sufficient to understand what system made the judgment.\n",
          "disagreement": "Multiple valid attestations MAY disagree. Implementations MUST NOT collapse disagreement into a single global truth value.\n"
        },
        "source": {
          "path": "trust_model",
          "line": 44
        }
      },
      {
        "key": "repository_layout",
        "value": {
          "manifest": ".idea/manifest.yaml",
          "verifiers": ".idea/verifiers.yaml",
          "verification_policy": ".idea/verification-policy.yaml"
        },
        "source": {
          "path": "repository_layout",
          "line": 68
        }
      },
      {
        "key": "idea_manifest",
        "value": {
          "required": [
            "spec_version",
            "idea.id",
            "idea.name",
            "repository.object_format",
            "repository.canonical_ref",
            "verification.verifiers_path",
            "verification.policy_path"
          ],
          "example": {
            "spec_version": "0.1",
            "idea": {
              "id": "https://ideas.example.org/ai-pacing",
              "name": "AI Pacing"
            },
            "repository": {
              "object_format": "sha1",
              "canonical_ref": "refs/heads/main",
              "mirrors": [
                "https://codeberg.org/example/ai-pacing.git",
                "https://github.com/example/ai-pacing.git"
              ]
            },
            "verification": {
              "verifiers_path": ".idea/verifiers.yaml",
              "policy_path": ".idea/verification-policy.yaml"
            }
          }
        },
        "source": {
          "path": "idea_manifest",
          "line": 73
        }
      },
      {
        "key": "verifier_registry",
        "value": {
          "requirements": [
            "Every verifier MUST have a stable id.",
            "Every verifier MUST have at least one public verification key or immutable key reference.",
            "A verifier MAY be human, ai, organization, panel, or unspecified.",
            "Type metadata is descriptive and MUST NOT itself determine recognition.",
            "AI verifier records SHOULD identify operator or custodian and system provenance."
          ],
          "example": {
            "spec_version": "0.1",
            "verifiers": [
              {
                "id": "https://alice.example/#me",
                "display_name": "Alice",
                "kind": "human",
                "keys": [
                  {
                    "id": "alice-ed25519-2026",
                    "algorithm": "Ed25519",
                    "public_key_multibase": "zReplaceWithRealKey"
                  }
                ]
              },
              {
                "id": "https://review.example/agents/fidelity-1",
                "display_name": "Fidelity Review Agent 1",
                "kind": "ai",
                "operator": "https://review.example/",
                "system": {
                  "model": "example-model-version",
                  "procedure": "https://review.example/procedures/fidelity-v3"
                },
                "keys": [
                  {
                    "id": "fidelity-1-ed25519",
                    "algorithm": "Ed25519",
                    "public_key_multibase": "zReplaceWithRealKey"
                  }
                ]
              }
            ]
          }
        },
        "source": {
          "path": "verifier_registry",
          "line": 97
        }
      },
      {
        "key": "verification_policy",
        "value": {
          "requirements": [
            "MUST define at least one claim type.",
            "MUST define eligible verifier selection for each recognized claim type.",
            "MUST define a recognition rule.",
            "MUST be evaluated from the exact referenced idea state, never current main."
          ],
          "recommended_claim": {
            "faithful_rendering": {
              "definition": "The rendering materially represents the referenced idea state and does not silently substitute a substantively different idea while presenting itself as faithful.\n",
              "does_not_mean": [
                "The verifier endorses the idea.",
                "The verifier believes the idea is true.",
                "The verifier believes the rendering is high quality.",
                "The rendering is complete."
              ]
            }
          },
          "example": {
            "spec_version": "0.1",
            "claims": {
              "faithful_rendering": {
                "definition": "The rendering materially represents this exact idea state without silently replacing it with a substantively different idea.\n",
                "recognition": {
                  "eligible_verifiers": {
                    "ids": [
                      "https://alice.example/#me",
                      "https://review.example/agents/fidelity-1"
                    ]
                  },
                  "rule": {
                    "type": "any_one_pass"
                  }
                }
              }
            }
          }
        },
        "source": {
          "path": "verification_policy",
          "line": 126
        }
      },
      {
        "key": "rendering",
        "value": {
          "definition": "Any independently published artifact claiming a relationship to an exact idea state. IRAP intentionally does not require a media or artifact type.\n",
          "required": [
            "irap_version",
            "rendering.id",
            "rendering.artifact.uri",
            "rendering.artifact.digest",
            "rendering.renders.idea_id",
            "rendering.renders.git.repository",
            "rendering.renders.git.object_format",
            "rendering.renders.git.commit",
            "rendering.creator.id"
          ],
          "example": {
            "irap_version": "0.1",
            "rendering": {
              "id": "https://creator.example/renderings/pacing-song",
              "title": "Pacing Song",
              "artifact": {
                "uri": "https://creator.example/media/pacing-song.flac",
                "digest": "sha256:1111111111111111111111111111111111111111111111111111111111111111"
              },
              "renders": {
                "idea_id": "https://ideas.example.org/ai-pacing",
                "git": {
                  "repository": "https://codeberg.org/example/ai-pacing.git",
                  "object_format": "sha1",
                  "commit": "2222222222222222222222222222222222222222"
                }
              },
              "creator": {
                "id": "https://creator.example/#me"
              }
            }
          }
        },
        "source": {
          "path": "rendering",
          "line": 157
        }
      },
      {
        "key": "attestation",
        "value": {
          "definition": "A verifier's signed judgment about one rendering against one exact idea state.",
          "results": [
            "pass",
            "fail",
            "abstain",
            "indeterminate"
          ],
          "required": [
            "irap_version",
            "attestation.id",
            "attestation.rendering.id",
            "attestation.rendering.artifact_digest",
            "attestation.against.idea_id",
            "attestation.against.git.repository",
            "attestation.against.git.object_format",
            "attestation.against.git.commit",
            "attestation.verifier.id",
            "attestation.verifier.key_id",
            "attestation.judgment.claim",
            "attestation.judgment.result",
            "attestation.issued_at",
            "attestation.signature"
          ],
          "evidence": "OPTIONAL. Evidence may be notes, calculations, citations, code, transcripts, model outputs, tests, or any other review work. Published evidence SHOULD be bound by URI and digest.\n",
          "example": {
            "irap_version": "0.1",
            "attestation": {
              "id": "https://review.example/attestations/a-123",
              "rendering": {
                "id": "https://creator.example/renderings/pacing-song",
                "artifact_digest": "sha256:1111111111111111111111111111111111111111111111111111111111111111"
              },
              "against": {
                "idea_id": "https://ideas.example.org/ai-pacing",
                "git": {
                  "repository": "https://codeberg.org/example/ai-pacing.git",
                  "object_format": "sha1",
                  "commit": "2222222222222222222222222222222222222222"
                }
              },
              "verifier": {
                "id": "https://alice.example/#me",
                "key_id": "alice-ed25519-2026"
              },
              "judgment": {
                "claim": "faithful_rendering",
                "result": "pass",
                "note": "Materially faithful."
              },
              "evidence": [
                {
                  "uri": "https://alice.example/reviews/a-123.md",
                  "media_type": "text/markdown",
                  "digest": "sha256:3333333333333333333333333333333333333333333333333333333333333333"
                }
              ],
              "issued_at": "2026-08-29T20:00:00Z",
              "signature": {
                "algorithm": "Ed25519",
                "canonicalization": "RFC8785-JCS",
                "value": "EXAMPLE-NOT-A-REAL-SIGNATURE"
              }
            }
          }
        },
        "source": {
          "path": "attestation",
          "line": 188
        }
      },
      {
        "key": "signing",
        "value": {
          "yaml_role": "Human-editable interchange and normative protocol representation.",
          "rule": "Never sign raw YAML serialization.",
          "default_algorithm": "Ed25519",
          "signature_encoding": "base64url-no-padding",
          "signing_projection": [
            "Parse the attestation.",
            "Remove the signature member.",
            "Convert the remaining data to JSON without semantic transformation.",
            "Reject duplicate keys and require I-JSON-compatible values.",
            "Canonicalize JSON using RFC 8785 JCS.",
            "Sign the resulting UTF-8 bytes using Ed25519.",
            "Add signature metadata to the original object."
          ]
        },
        "source": {
          "path": "signing",
          "line": 239
        }
      },
      {
        "key": "content_identity",
        "value": {
          "artifact_digest_default": "sha256",
          "digest_syntax": "sha256:<64-lowercase-hex>",
          "rules": [
            "Rendering artifacts MUST have a digest of exact bytes or a stable manifest that recursively identifies them.",
            "Evidence SHOULD have digests.",
            "URI content mismatching its recorded digest MUST be surfaced as a severe warning."
          ]
        },
        "source": {
          "path": "content_identity",
          "line": 253
        }
      },
      {
        "key": "git_identity",
        "value": {
          "object_formats": [
            "sha1",
            "sha256"
          ],
          "rules": [
            "Full commit object IDs MUST be used in signed objects.",
            "Abbreviated hashes MUST NOT be used in signed objects.",
            "Branches MAY be editing targets but MUST be frozen to a commit before submission or verification.",
            "Forge URLs are locators, not semantic identity.",
            "Historical verification is evaluated from historical policy files."
          ]
        },
        "source": {
          "path": "git_identity",
          "line": 261
        }
      },
      {
        "key": "recognition_algorithm",
        "value": {
          "steps": [
            "Validate rendering and attestation structure.",
            "Verify attestation references the same rendering id and artifact digest.",
            "Fetch or locate the referenced Git commit.",
            "Verify commit existence using declared repository object format.",
            "Read manifest, verifier registry, and verification policy from that exact commit.",
            "Verify idea id matches.",
            "Resolve verifier and historical public key.",
            "Reconstruct and verify the signed JCS payload.",
            "Evaluate claim-specific recognition policy.",
            "Return signature validity and recognition status separately."
          ],
          "output_minimum": [
            "signature_valid",
            "recognized",
            "policy_commit",
            "claim",
            "result",
            "verifier_id"
          ]
        },
        "source": {
          "path": "recognition_algorithm",
          "line": 270
        }
      },
      {
        "key": "recognition_rules_v0_1",
        "value": {
          "any_one_pass": {
            "recognized_when": "At least one eligible distinct verifier has a valid pass attestation."
          },
          "threshold": {
            "parameters": [
              "count",
              "result"
            ],
            "recognized_when": "At least count eligible distinct verifier ids have valid attestations with result."
          },
          "all_groups": {
            "parameters": [
              "groups",
              "result"
            ],
            "recognized_when": "At least one eligible valid specified-result attestation comes from every listed group."
          },
          "restriction": "Implementations MUST NOT add an arbitrary expression language in v0.1."
        },
        "source": {
          "path": "recognition_rules_v0_1",
          "line": 290
        }
      },
      {
        "key": "security",
        "value": [
          "Never treat a branch name as an immutable verification target.",
          "Never treat a valid signature as recognized without evaluating historical policy.",
          "Never use current main to resolve verifier eligibility for an old attestation.",
          "Never sign YAML serialization directly.",
          "Treat URLs as locators and verify digests.",
          "Preserve received raw attestations for audit while independently validating the signing projection.",
          "Repository content is untrusted data and MUST NOT be executed."
        ],
        "source": {
          "path": "security",
          "line": 301
        }
      },
      {
        "key": "extensions",
        "value": {
          "activitypub": {
            "status": "optional",
            "purpose": "Federate notices of canonical updates, renderings, and attestations.",
            "constraints": [
              "ActivityPub identity MUST NOT replace Git state identity, signatures, or artifact digests.",
              "Delivery failure MUST NOT invalidate an attestation.",
              "Likes, boosts, replies, or follower counts MUST NOT be interpreted as verification."
            ]
          },
          "forge_api": {
            "status": "optional",
            "purpose": "UX convenience for browsing refs, commits, and diffs.",
            "constraint": "Core operation MUST remain possible over ordinary Git transport."
          },
          "transparency_log": {
            "status": "optional",
            "purpose": "Append-only publication of attestations to help detect deletion or equivocation."
          }
        },
        "source": {
          "path": "extensions",
          "line": 310
        }
      },
      {
        "key": "conformance",
        "value": {
          "core_reader": {
            "must": [
              "Parse idea manifests, verifier registries, policies, renderings, and attestations.",
              "Resolve exact Git commits.",
              "Verify content digests.",
              "Verify Ed25519 attestations with RFC8785 JCS signing projection.",
              "Distinguish cryptographic validity from recognized verification."
            ]
          },
          "verifier_tool": {
            "must": [
              "Bind judgment to one artifact digest and one exact Git state.",
              "Display verifier identity before signing.",
              "Permit optional evidence.",
              "Display or export the exact signing payload."
            ]
          },
          "product_render": {
            "rule": "A software product implementing IRAP is itself a rendering of this specification and SHOULD state the exact IRAP Git commit it implements.\n"
          }
        },
        "source": {
          "path": "conformance",
          "line": 326
        }
      },
      {
        "key": "references",
        "value": [
          "https://git-scm.com/docs/git",
          "https://git-scm.com/docs/index-format",
          "https://www.rfc-editor.org/rfc/rfc8785",
          "https://www.rfc-editor.org/rfc/rfc8032",
          "https://www.w3.org/TR/activitypub/"
        ],
        "source": {
          "path": "references",
          "line": 345
        }
      }
    ],
    "sourceLocations": {
      "protocol": {
        "path": "protocol",
        "line": 2
      },
      "protocol.id": {
        "path": "protocol.id",
        "line": 2
      },
      "protocol.name": {
        "path": "protocol.name",
        "line": 3
      },
      "protocol.short_name": {
        "path": "protocol.short_name",
        "line": 4
      },
      "protocol.version": {
        "path": "protocol.version",
        "line": 5
      },
      "protocol.status": {
        "path": "protocol.status",
        "line": 6
      },
      "protocol.description": {
        "path": "protocol.description",
        "line": 7
      },
      "goals": {
        "path": "goals",
        "line": 13
      },
      "goals.0": {
        "path": "goals.0",
        "line": 13
      },
      "goals.1": {
        "path": "goals.1",
        "line": 14
      },
      "goals.2": {
        "path": "goals.2",
        "line": 15
      },
      "goals.3": {
        "path": "goals.3",
        "line": 16
      },
      "goals.3.Make verification attributable": {
        "path": "goals.3.Make verification attributable",
        "line": 16
      },
      "goals.4": {
        "path": "goals.4",
        "line": 17
      },
      "goals.5": {
        "path": "goals.5",
        "line": 18
      },
      "goals.6": {
        "path": "goals.6",
        "line": 19
      },
      "non_goals": {
        "path": "non_goals",
        "line": 22
      },
      "non_goals.0": {
        "path": "non_goals.0",
        "line": 22
      },
      "non_goals.1": {
        "path": "non_goals.1",
        "line": 23
      },
      "non_goals.2": {
        "path": "non_goals.2",
        "line": 24
      },
      "non_goals.3": {
        "path": "non_goals.3",
        "line": 25
      },
      "non_goals.4": {
        "path": "non_goals.4",
        "line": 26
      },
      "non_goals.5": {
        "path": "non_goals.5",
        "line": 27
      },
      "non_goals.6": {
        "path": "non_goals.6",
        "line": 28
      },
      "non_goals.7": {
        "path": "non_goals.7",
        "line": 29
      },
      "core_principles": {
        "path": "core_principles",
        "line": 32
      },
      "core_principles.P1": {
        "path": "core_principles.P1",
        "line": 32
      },
      "core_principles.P2": {
        "path": "core_principles.P2",
        "line": 33
      },
      "core_principles.P3": {
        "path": "core_principles.P3",
        "line": 34
      },
      "core_principles.P4": {
        "path": "core_principles.P4",
        "line": 35
      },
      "core_principles.P5": {
        "path": "core_principles.P5",
        "line": 36
      },
      "core_principles.P6": {
        "path": "core_principles.P6",
        "line": 37
      },
      "core_principles.P7": {
        "path": "core_principles.P7",
        "line": 38
      },
      "core_principles.P8": {
        "path": "core_principles.P8",
        "line": 39
      },
      "core_principles.P9": {
        "path": "core_principles.P9",
        "line": 40
      },
      "core_principles.P10": {
        "path": "core_principles.P10",
        "line": 41
      },
      "trust_model": {
        "path": "trust_model",
        "line": 44
      },
      "trust_model.canonical_authority": {
        "path": "trust_model.canonical_authority",
        "line": 44
      },
      "trust_model.open_attestation": {
        "path": "trust_model.open_attestation",
        "line": 48
      },
      "trust_model.recognized_verification": {
        "path": "trust_model.recognized_verification",
        "line": 51
      },
      "trust_model.verifier_mind_requirement": {
        "path": "trust_model.verifier_mind_requirement",
        "line": 54
      },
      "trust_model.ai_verifiers": {
        "path": "trust_model.ai_verifiers",
        "line": 59
      },
      "trust_model.disagreement": {
        "path": "trust_model.disagreement",
        "line": 63
      },
      "repository_layout": {
        "path": "repository_layout",
        "line": 68
      },
      "repository_layout.manifest": {
        "path": "repository_layout.manifest",
        "line": 68
      },
      "repository_layout.verifiers": {
        "path": "repository_layout.verifiers",
        "line": 69
      },
      "repository_layout.verification_policy": {
        "path": "repository_layout.verification_policy",
        "line": 70
      },
      "idea_manifest": {
        "path": "idea_manifest",
        "line": 73
      },
      "idea_manifest.required": {
        "path": "idea_manifest.required",
        "line": 74
      },
      "idea_manifest.required.0": {
        "path": "idea_manifest.required.0",
        "line": 74
      },
      "idea_manifest.required.1": {
        "path": "idea_manifest.required.1",
        "line": 75
      },
      "idea_manifest.required.2": {
        "path": "idea_manifest.required.2",
        "line": 76
      },
      "idea_manifest.required.3": {
        "path": "idea_manifest.required.3",
        "line": 77
      },
      "idea_manifest.required.4": {
        "path": "idea_manifest.required.4",
        "line": 78
      },
      "idea_manifest.required.5": {
        "path": "idea_manifest.required.5",
        "line": 79
      },
      "idea_manifest.required.6": {
        "path": "idea_manifest.required.6",
        "line": 80
      },
      "idea_manifest.example": {
        "path": "idea_manifest.example",
        "line": 82
      },
      "idea_manifest.example.spec_version": {
        "path": "idea_manifest.example.spec_version",
        "line": 82
      },
      "idea_manifest.example.idea": {
        "path": "idea_manifest.example.idea",
        "line": 84
      },
      "idea_manifest.example.idea.id": {
        "path": "idea_manifest.example.idea.id",
        "line": 84
      },
      "idea_manifest.example.idea.name": {
        "path": "idea_manifest.example.idea.name",
        "line": 85
      },
      "idea_manifest.example.repository": {
        "path": "idea_manifest.example.repository",
        "line": 87
      },
      "idea_manifest.example.repository.object_format": {
        "path": "idea_manifest.example.repository.object_format",
        "line": 87
      },
      "idea_manifest.example.repository.canonical_ref": {
        "path": "idea_manifest.example.repository.canonical_ref",
        "line": 88
      },
      "idea_manifest.example.repository.mirrors": {
        "path": "idea_manifest.example.repository.mirrors",
        "line": 90
      },
      "idea_manifest.example.repository.mirrors.0": {
        "path": "idea_manifest.example.repository.mirrors.0",
        "line": 90
      },
      "idea_manifest.example.repository.mirrors.1": {
        "path": "idea_manifest.example.repository.mirrors.1",
        "line": 91
      },
      "idea_manifest.example.verification": {
        "path": "idea_manifest.example.verification",
        "line": 93
      },
      "idea_manifest.example.verification.verifiers_path": {
        "path": "idea_manifest.example.verification.verifiers_path",
        "line": 93
      },
      "idea_manifest.example.verification.policy_path": {
        "path": "idea_manifest.example.verification.policy_path",
        "line": 94
      },
      "verifier_registry": {
        "path": "verifier_registry",
        "line": 97
      },
      "verifier_registry.requirements": {
        "path": "verifier_registry.requirements",
        "line": 98
      },
      "verifier_registry.requirements.0": {
        "path": "verifier_registry.requirements.0",
        "line": 98
      },
      "verifier_registry.requirements.1": {
        "path": "verifier_registry.requirements.1",
        "line": 99
      },
      "verifier_registry.requirements.2": {
        "path": "verifier_registry.requirements.2",
        "line": 100
      },
      "verifier_registry.requirements.3": {
        "path": "verifier_registry.requirements.3",
        "line": 101
      },
      "verifier_registry.requirements.4": {
        "path": "verifier_registry.requirements.4",
        "line": 102
      },
      "verifier_registry.example": {
        "path": "verifier_registry.example",
        "line": 104
      },
      "verifier_registry.example.spec_version": {
        "path": "verifier_registry.example.spec_version",
        "line": 104
      },
      "verifier_registry.example.verifiers": {
        "path": "verifier_registry.example.verifiers",
        "line": 106
      },
      "verifier_registry.example.verifiers.0": {
        "path": "verifier_registry.example.verifiers.0",
        "line": 106
      },
      "verifier_registry.example.verifiers.0.id": {
        "path": "verifier_registry.example.verifiers.0.id",
        "line": 106
      },
      "verifier_registry.example.verifiers.0.display_name": {
        "path": "verifier_registry.example.verifiers.0.display_name",
        "line": 107
      },
      "verifier_registry.example.verifiers.0.kind": {
        "path": "verifier_registry.example.verifiers.0.kind",
        "line": 108
      },
      "verifier_registry.example.verifiers.0.keys": {
        "path": "verifier_registry.example.verifiers.0.keys",
        "line": 110
      },
      "verifier_registry.example.verifiers.0.keys.0": {
        "path": "verifier_registry.example.verifiers.0.keys.0",
        "line": 110
      },
      "verifier_registry.example.verifiers.0.keys.0.id": {
        "path": "verifier_registry.example.verifiers.0.keys.0.id",
        "line": 110
      },
      "verifier_registry.example.verifiers.0.keys.0.algorithm": {
        "path": "verifier_registry.example.verifiers.0.keys.0.algorithm",
        "line": 111
      },
      "verifier_registry.example.verifiers.0.keys.0.public_key_multibase": {
        "path": "verifier_registry.example.verifiers.0.keys.0.public_key_multibase",
        "line": 112
      },
      "verifier_registry.example.verifiers.1": {
        "path": "verifier_registry.example.verifiers.1",
        "line": 113
      },
      "verifier_registry.example.verifiers.1.id": {
        "path": "verifier_registry.example.verifiers.1.id",
        "line": 113
      },
      "verifier_registry.example.verifiers.1.display_name": {
        "path": "verifier_registry.example.verifiers.1.display_name",
        "line": 114
      },
      "verifier_registry.example.verifiers.1.kind": {
        "path": "verifier_registry.example.verifiers.1.kind",
        "line": 115
      },
      "verifier_registry.example.verifiers.1.operator": {
        "path": "verifier_registry.example.verifiers.1.operator",
        "line": 116
      },
      "verifier_registry.example.verifiers.1.system": {
        "path": "verifier_registry.example.verifiers.1.system",
        "line": 118
      },
      "verifier_registry.example.verifiers.1.system.model": {
        "path": "verifier_registry.example.verifiers.1.system.model",
        "line": 118
      },
      "verifier_registry.example.verifiers.1.system.procedure": {
        "path": "verifier_registry.example.verifiers.1.system.procedure",
        "line": 119
      },
      "verifier_registry.example.verifiers.1.keys": {
        "path": "verifier_registry.example.verifiers.1.keys",
        "line": 121
      },
      "verifier_registry.example.verifiers.1.keys.0": {
        "path": "verifier_registry.example.verifiers.1.keys.0",
        "line": 121
      },
      "verifier_registry.example.verifiers.1.keys.0.id": {
        "path": "verifier_registry.example.verifiers.1.keys.0.id",
        "line": 121
      },
      "verifier_registry.example.verifiers.1.keys.0.algorithm": {
        "path": "verifier_registry.example.verifiers.1.keys.0.algorithm",
        "line": 122
      },
      "verifier_registry.example.verifiers.1.keys.0.public_key_multibase": {
        "path": "verifier_registry.example.verifiers.1.keys.0.public_key_multibase",
        "line": 123
      },
      "verification_policy": {
        "path": "verification_policy",
        "line": 126
      },
      "verification_policy.requirements": {
        "path": "verification_policy.requirements",
        "line": 127
      },
      "verification_policy.requirements.0": {
        "path": "verification_policy.requirements.0",
        "line": 127
      },
      "verification_policy.requirements.1": {
        "path": "verification_policy.requirements.1",
        "line": 128
      },
      "verification_policy.requirements.2": {
        "path": "verification_policy.requirements.2",
        "line": 129
      },
      "verification_policy.requirements.3": {
        "path": "verification_policy.requirements.3",
        "line": 130
      },
      "verification_policy.recommended_claim": {
        "path": "verification_policy.recommended_claim",
        "line": 132
      },
      "verification_policy.recommended_claim.faithful_rendering": {
        "path": "verification_policy.recommended_claim.faithful_rendering",
        "line": 133
      },
      "verification_policy.recommended_claim.faithful_rendering.definition": {
        "path": "verification_policy.recommended_claim.faithful_rendering.definition",
        "line": 133
      },
      "verification_policy.recommended_claim.faithful_rendering.does_not_mean": {
        "path": "verification_policy.recommended_claim.faithful_rendering.does_not_mean",
        "line": 137
      },
      "verification_policy.recommended_claim.faithful_rendering.does_not_mean.0": {
        "path": "verification_policy.recommended_claim.faithful_rendering.does_not_mean.0",
        "line": 137
      },
      "verification_policy.recommended_claim.faithful_rendering.does_not_mean.1": {
        "path": "verification_policy.recommended_claim.faithful_rendering.does_not_mean.1",
        "line": 138
      },
      "verification_policy.recommended_claim.faithful_rendering.does_not_mean.2": {
        "path": "verification_policy.recommended_claim.faithful_rendering.does_not_mean.2",
        "line": 139
      },
      "verification_policy.recommended_claim.faithful_rendering.does_not_mean.3": {
        "path": "verification_policy.recommended_claim.faithful_rendering.does_not_mean.3",
        "line": 140
      },
      "verification_policy.example": {
        "path": "verification_policy.example",
        "line": 142
      },
      "verification_policy.example.spec_version": {
        "path": "verification_policy.example.spec_version",
        "line": 142
      },
      "verification_policy.example.claims": {
        "path": "verification_policy.example.claims",
        "line": 144
      },
      "verification_policy.example.claims.faithful_rendering": {
        "path": "verification_policy.example.claims.faithful_rendering",
        "line": 145
      },
      "verification_policy.example.claims.faithful_rendering.definition": {
        "path": "verification_policy.example.claims.faithful_rendering.definition",
        "line": 145
      },
      "verification_policy.example.claims.faithful_rendering.recognition": {
        "path": "verification_policy.example.claims.faithful_rendering.recognition",
        "line": 149
      },
      "verification_policy.example.claims.faithful_rendering.recognition.eligible_verifiers": {
        "path": "verification_policy.example.claims.faithful_rendering.recognition.eligible_verifiers",
        "line": 150
      },
      "verification_policy.example.claims.faithful_rendering.recognition.eligible_verifiers.ids": {
        "path": "verification_policy.example.claims.faithful_rendering.recognition.eligible_verifiers.ids",
        "line": 151
      },
      "verification_policy.example.claims.faithful_rendering.recognition.eligible_verifiers.ids.0": {
        "path": "verification_policy.example.claims.faithful_rendering.recognition.eligible_verifiers.ids.0",
        "line": 151
      },
      "verification_policy.example.claims.faithful_rendering.recognition.eligible_verifiers.ids.1": {
        "path": "verification_policy.example.claims.faithful_rendering.recognition.eligible_verifiers.ids.1",
        "line": 152
      },
      "verification_policy.example.claims.faithful_rendering.recognition.rule": {
        "path": "verification_policy.example.claims.faithful_rendering.recognition.rule",
        "line": 154
      },
      "verification_policy.example.claims.faithful_rendering.recognition.rule.type": {
        "path": "verification_policy.example.claims.faithful_rendering.recognition.rule.type",
        "line": 154
      },
      "rendering": {
        "path": "rendering",
        "line": 157
      },
      "rendering.definition": {
        "path": "rendering.definition",
        "line": 157
      },
      "rendering.required": {
        "path": "rendering.required",
        "line": 161
      },
      "rendering.required.0": {
        "path": "rendering.required.0",
        "line": 161
      },
      "rendering.required.1": {
        "path": "rendering.required.1",
        "line": 162
      },
      "rendering.required.2": {
        "path": "rendering.required.2",
        "line": 163
      },
      "rendering.required.3": {
        "path": "rendering.required.3",
        "line": 164
      },
      "rendering.required.4": {
        "path": "rendering.required.4",
        "line": 165
      },
      "rendering.required.5": {
        "path": "rendering.required.5",
        "line": 166
      },
      "rendering.required.6": {
        "path": "rendering.required.6",
        "line": 167
      },
      "rendering.required.7": {
        "path": "rendering.required.7",
        "line": 168
      },
      "rendering.required.8": {
        "path": "rendering.required.8",
        "line": 169
      },
      "rendering.example": {
        "path": "rendering.example",
        "line": 171
      },
      "rendering.example.irap_version": {
        "path": "rendering.example.irap_version",
        "line": 171
      },
      "rendering.example.rendering": {
        "path": "rendering.example.rendering",
        "line": 173
      },
      "rendering.example.rendering.id": {
        "path": "rendering.example.rendering.id",
        "line": 173
      },
      "rendering.example.rendering.title": {
        "path": "rendering.example.rendering.title",
        "line": 174
      },
      "rendering.example.rendering.artifact": {
        "path": "rendering.example.rendering.artifact",
        "line": 176
      },
      "rendering.example.rendering.artifact.uri": {
        "path": "rendering.example.rendering.artifact.uri",
        "line": 176
      },
      "rendering.example.rendering.artifact.digest": {
        "path": "rendering.example.rendering.artifact.digest",
        "line": 177
      },
      "rendering.example.rendering.renders": {
        "path": "rendering.example.rendering.renders",
        "line": 179
      },
      "rendering.example.rendering.renders.idea_id": {
        "path": "rendering.example.rendering.renders.idea_id",
        "line": 179
      },
      "rendering.example.rendering.renders.git": {
        "path": "rendering.example.rendering.renders.git",
        "line": 181
      },
      "rendering.example.rendering.renders.git.repository": {
        "path": "rendering.example.rendering.renders.git.repository",
        "line": 181
      },
      "rendering.example.rendering.renders.git.object_format": {
        "path": "rendering.example.rendering.renders.git.object_format",
        "line": 182
      },
      "rendering.example.rendering.renders.git.commit": {
        "path": "rendering.example.rendering.renders.git.commit",
        "line": 183
      },
      "rendering.example.rendering.creator": {
        "path": "rendering.example.rendering.creator",
        "line": 185
      },
      "rendering.example.rendering.creator.id": {
        "path": "rendering.example.rendering.creator.id",
        "line": 185
      },
      "attestation": {
        "path": "attestation",
        "line": 188
      },
      "attestation.definition": {
        "path": "attestation.definition",
        "line": 188
      },
      "attestation.results": {
        "path": "attestation.results",
        "line": 189
      },
      "attestation.results.0": {
        "path": "attestation.results.0",
        "line": 189
      },
      "attestation.results.1": {
        "path": "attestation.results.1",
        "line": 189
      },
      "attestation.results.2": {
        "path": "attestation.results.2",
        "line": 189
      },
      "attestation.results.3": {
        "path": "attestation.results.3",
        "line": 189
      },
      "attestation.required": {
        "path": "attestation.required",
        "line": 191
      },
      "attestation.required.0": {
        "path": "attestation.required.0",
        "line": 191
      },
      "attestation.required.1": {
        "path": "attestation.required.1",
        "line": 192
      },
      "attestation.required.2": {
        "path": "attestation.required.2",
        "line": 193
      },
      "attestation.required.3": {
        "path": "attestation.required.3",
        "line": 194
      },
      "attestation.required.4": {
        "path": "attestation.required.4",
        "line": 195
      },
      "attestation.required.5": {
        "path": "attestation.required.5",
        "line": 196
      },
      "attestation.required.6": {
        "path": "attestation.required.6",
        "line": 197
      },
      "attestation.required.7": {
        "path": "attestation.required.7",
        "line": 198
      },
      "attestation.required.8": {
        "path": "attestation.required.8",
        "line": 199
      },
      "attestation.required.9": {
        "path": "attestation.required.9",
        "line": 200
      },
      "attestation.required.10": {
        "path": "attestation.required.10",
        "line": 201
      },
      "attestation.required.11": {
        "path": "attestation.required.11",
        "line": 202
      },
      "attestation.required.12": {
        "path": "attestation.required.12",
        "line": 203
      },
      "attestation.required.13": {
        "path": "attestation.required.13",
        "line": 204
      },
      "attestation.evidence": {
        "path": "attestation.evidence",
        "line": 205
      },
      "attestation.example": {
        "path": "attestation.example",
        "line": 209
      },
      "attestation.example.irap_version": {
        "path": "attestation.example.irap_version",
        "line": 209
      },
      "attestation.example.attestation": {
        "path": "attestation.example.attestation",
        "line": 211
      },
      "attestation.example.attestation.id": {
        "path": "attestation.example.attestation.id",
        "line": 211
      },
      "attestation.example.attestation.rendering": {
        "path": "attestation.example.attestation.rendering",
        "line": 213
      },
      "attestation.example.attestation.rendering.id": {
        "path": "attestation.example.attestation.rendering.id",
        "line": 213
      },
      "attestation.example.attestation.rendering.artifact_digest": {
        "path": "attestation.example.attestation.rendering.artifact_digest",
        "line": 214
      },
      "attestation.example.attestation.against": {
        "path": "attestation.example.attestation.against",
        "line": 216
      },
      "attestation.example.attestation.against.idea_id": {
        "path": "attestation.example.attestation.against.idea_id",
        "line": 216
      },
      "attestation.example.attestation.against.git": {
        "path": "attestation.example.attestation.against.git",
        "line": 218
      },
      "attestation.example.attestation.against.git.repository": {
        "path": "attestation.example.attestation.against.git.repository",
        "line": 218
      },
      "attestation.example.attestation.against.git.object_format": {
        "path": "attestation.example.attestation.against.git.object_format",
        "line": 219
      },
      "attestation.example.attestation.against.git.commit": {
        "path": "attestation.example.attestation.against.git.commit",
        "line": 220
      },
      "attestation.example.attestation.verifier": {
        "path": "attestation.example.attestation.verifier",
        "line": 222
      },
      "attestation.example.attestation.verifier.id": {
        "path": "attestation.example.attestation.verifier.id",
        "line": 222
      },
      "attestation.example.attestation.verifier.key_id": {
        "path": "attestation.example.attestation.verifier.key_id",
        "line": 223
      },
      "attestation.example.attestation.judgment": {
        "path": "attestation.example.attestation.judgment",
        "line": 225
      },
      "attestation.example.attestation.judgment.claim": {
        "path": "attestation.example.attestation.judgment.claim",
        "line": 225
      },
      "attestation.example.attestation.judgment.result": {
        "path": "attestation.example.attestation.judgment.result",
        "line": 226
      },
      "attestation.example.attestation.judgment.note": {
        "path": "attestation.example.attestation.judgment.note",
        "line": 227
      },
      "attestation.example.attestation.evidence": {
        "path": "attestation.example.attestation.evidence",
        "line": 229
      },
      "attestation.example.attestation.evidence.0": {
        "path": "attestation.example.attestation.evidence.0",
        "line": 229
      },
      "attestation.example.attestation.evidence.0.uri": {
        "path": "attestation.example.attestation.evidence.0.uri",
        "line": 229
      },
      "attestation.example.attestation.evidence.0.media_type": {
        "path": "attestation.example.attestation.evidence.0.media_type",
        "line": 230
      },
      "attestation.example.attestation.evidence.0.digest": {
        "path": "attestation.example.attestation.evidence.0.digest",
        "line": 231
      },
      "attestation.example.attestation.issued_at": {
        "path": "attestation.example.attestation.issued_at",
        "line": 232
      },
      "attestation.example.attestation.signature": {
        "path": "attestation.example.attestation.signature",
        "line": 234
      },
      "attestation.example.attestation.signature.algorithm": {
        "path": "attestation.example.attestation.signature.algorithm",
        "line": 234
      },
      "attestation.example.attestation.signature.canonicalization": {
        "path": "attestation.example.attestation.signature.canonicalization",
        "line": 235
      },
      "attestation.example.attestation.signature.value": {
        "path": "attestation.example.attestation.signature.value",
        "line": 236
      },
      "signing": {
        "path": "signing",
        "line": 239
      },
      "signing.yaml_role": {
        "path": "signing.yaml_role",
        "line": 239
      },
      "signing.rule": {
        "path": "signing.rule",
        "line": 240
      },
      "signing.default_algorithm": {
        "path": "signing.default_algorithm",
        "line": 241
      },
      "signing.signature_encoding": {
        "path": "signing.signature_encoding",
        "line": 242
      },
      "signing.signing_projection": {
        "path": "signing.signing_projection",
        "line": 244
      },
      "signing.signing_projection.0": {
        "path": "signing.signing_projection.0",
        "line": 244
      },
      "signing.signing_projection.1": {
        "path": "signing.signing_projection.1",
        "line": 245
      },
      "signing.signing_projection.2": {
        "path": "signing.signing_projection.2",
        "line": 246
      },
      "signing.signing_projection.3": {
        "path": "signing.signing_projection.3",
        "line": 247
      },
      "signing.signing_projection.4": {
        "path": "signing.signing_projection.4",
        "line": 248
      },
      "signing.signing_projection.5": {
        "path": "signing.signing_projection.5",
        "line": 249
      },
      "signing.signing_projection.6": {
        "path": "signing.signing_projection.6",
        "line": 250
      },
      "content_identity": {
        "path": "content_identity",
        "line": 253
      },
      "content_identity.artifact_digest_default": {
        "path": "content_identity.artifact_digest_default",
        "line": 253
      },
      "content_identity.digest_syntax": {
        "path": "content_identity.digest_syntax",
        "line": 254
      },
      "content_identity.rules": {
        "path": "content_identity.rules",
        "line": 256
      },
      "content_identity.rules.0": {
        "path": "content_identity.rules.0",
        "line": 256
      },
      "content_identity.rules.1": {
        "path": "content_identity.rules.1",
        "line": 257
      },
      "content_identity.rules.2": {
        "path": "content_identity.rules.2",
        "line": 258
      },
      "git_identity": {
        "path": "git_identity",
        "line": 261
      },
      "git_identity.object_formats": {
        "path": "git_identity.object_formats",
        "line": 261
      },
      "git_identity.object_formats.0": {
        "path": "git_identity.object_formats.0",
        "line": 261
      },
      "git_identity.object_formats.1": {
        "path": "git_identity.object_formats.1",
        "line": 261
      },
      "git_identity.rules": {
        "path": "git_identity.rules",
        "line": 263
      },
      "git_identity.rules.0": {
        "path": "git_identity.rules.0",
        "line": 263
      },
      "git_identity.rules.1": {
        "path": "git_identity.rules.1",
        "line": 264
      },
      "git_identity.rules.2": {
        "path": "git_identity.rules.2",
        "line": 265
      },
      "git_identity.rules.3": {
        "path": "git_identity.rules.3",
        "line": 266
      },
      "git_identity.rules.4": {
        "path": "git_identity.rules.4",
        "line": 267
      },
      "recognition_algorithm": {
        "path": "recognition_algorithm",
        "line": 270
      },
      "recognition_algorithm.steps": {
        "path": "recognition_algorithm.steps",
        "line": 271
      },
      "recognition_algorithm.steps.0": {
        "path": "recognition_algorithm.steps.0",
        "line": 271
      },
      "recognition_algorithm.steps.1": {
        "path": "recognition_algorithm.steps.1",
        "line": 272
      },
      "recognition_algorithm.steps.2": {
        "path": "recognition_algorithm.steps.2",
        "line": 273
      },
      "recognition_algorithm.steps.3": {
        "path": "recognition_algorithm.steps.3",
        "line": 274
      },
      "recognition_algorithm.steps.4": {
        "path": "recognition_algorithm.steps.4",
        "line": 275
      },
      "recognition_algorithm.steps.5": {
        "path": "recognition_algorithm.steps.5",
        "line": 276
      },
      "recognition_algorithm.steps.6": {
        "path": "recognition_algorithm.steps.6",
        "line": 277
      },
      "recognition_algorithm.steps.7": {
        "path": "recognition_algorithm.steps.7",
        "line": 278
      },
      "recognition_algorithm.steps.8": {
        "path": "recognition_algorithm.steps.8",
        "line": 279
      },
      "recognition_algorithm.steps.9": {
        "path": "recognition_algorithm.steps.9",
        "line": 280
      },
      "recognition_algorithm.output_minimum": {
        "path": "recognition_algorithm.output_minimum",
        "line": 282
      },
      "recognition_algorithm.output_minimum.0": {
        "path": "recognition_algorithm.output_minimum.0",
        "line": 282
      },
      "recognition_algorithm.output_minimum.1": {
        "path": "recognition_algorithm.output_minimum.1",
        "line": 283
      },
      "recognition_algorithm.output_minimum.2": {
        "path": "recognition_algorithm.output_minimum.2",
        "line": 284
      },
      "recognition_algorithm.output_minimum.3": {
        "path": "recognition_algorithm.output_minimum.3",
        "line": 285
      },
      "recognition_algorithm.output_minimum.4": {
        "path": "recognition_algorithm.output_minimum.4",
        "line": 286
      },
      "recognition_algorithm.output_minimum.5": {
        "path": "recognition_algorithm.output_minimum.5",
        "line": 287
      },
      "recognition_rules_v0_1": {
        "path": "recognition_rules_v0_1",
        "line": 290
      },
      "recognition_rules_v0_1.any_one_pass": {
        "path": "recognition_rules_v0_1.any_one_pass",
        "line": 291
      },
      "recognition_rules_v0_1.any_one_pass.recognized_when": {
        "path": "recognition_rules_v0_1.any_one_pass.recognized_when",
        "line": 291
      },
      "recognition_rules_v0_1.threshold": {
        "path": "recognition_rules_v0_1.threshold",
        "line": 293
      },
      "recognition_rules_v0_1.threshold.parameters": {
        "path": "recognition_rules_v0_1.threshold.parameters",
        "line": 293
      },
      "recognition_rules_v0_1.threshold.parameters.0": {
        "path": "recognition_rules_v0_1.threshold.parameters.0",
        "line": 293
      },
      "recognition_rules_v0_1.threshold.parameters.1": {
        "path": "recognition_rules_v0_1.threshold.parameters.1",
        "line": 293
      },
      "recognition_rules_v0_1.threshold.recognized_when": {
        "path": "recognition_rules_v0_1.threshold.recognized_when",
        "line": 294
      },
      "recognition_rules_v0_1.all_groups": {
        "path": "recognition_rules_v0_1.all_groups",
        "line": 296
      },
      "recognition_rules_v0_1.all_groups.parameters": {
        "path": "recognition_rules_v0_1.all_groups.parameters",
        "line": 296
      },
      "recognition_rules_v0_1.all_groups.parameters.0": {
        "path": "recognition_rules_v0_1.all_groups.parameters.0",
        "line": 296
      },
      "recognition_rules_v0_1.all_groups.parameters.1": {
        "path": "recognition_rules_v0_1.all_groups.parameters.1",
        "line": 296
      },
      "recognition_rules_v0_1.all_groups.recognized_when": {
        "path": "recognition_rules_v0_1.all_groups.recognized_when",
        "line": 297
      },
      "recognition_rules_v0_1.restriction": {
        "path": "recognition_rules_v0_1.restriction",
        "line": 298
      },
      "security": {
        "path": "security",
        "line": 301
      },
      "security.0": {
        "path": "security.0",
        "line": 301
      },
      "security.1": {
        "path": "security.1",
        "line": 302
      },
      "security.2": {
        "path": "security.2",
        "line": 303
      },
      "security.3": {
        "path": "security.3",
        "line": 304
      },
      "security.4": {
        "path": "security.4",
        "line": 305
      },
      "security.5": {
        "path": "security.5",
        "line": 306
      },
      "security.6": {
        "path": "security.6",
        "line": 307
      },
      "extensions": {
        "path": "extensions",
        "line": 310
      },
      "extensions.activitypub": {
        "path": "extensions.activitypub",
        "line": 311
      },
      "extensions.activitypub.status": {
        "path": "extensions.activitypub.status",
        "line": 311
      },
      "extensions.activitypub.purpose": {
        "path": "extensions.activitypub.purpose",
        "line": 312
      },
      "extensions.activitypub.constraints": {
        "path": "extensions.activitypub.constraints",
        "line": 314
      },
      "extensions.activitypub.constraints.0": {
        "path": "extensions.activitypub.constraints.0",
        "line": 314
      },
      "extensions.activitypub.constraints.1": {
        "path": "extensions.activitypub.constraints.1",
        "line": 315
      },
      "extensions.activitypub.constraints.2": {
        "path": "extensions.activitypub.constraints.2",
        "line": 316
      },
      "extensions.forge_api": {
        "path": "extensions.forge_api",
        "line": 318
      },
      "extensions.forge_api.status": {
        "path": "extensions.forge_api.status",
        "line": 318
      },
      "extensions.forge_api.purpose": {
        "path": "extensions.forge_api.purpose",
        "line": 319
      },
      "extensions.forge_api.constraint": {
        "path": "extensions.forge_api.constraint",
        "line": 320
      },
      "extensions.transparency_log": {
        "path": "extensions.transparency_log",
        "line": 322
      },
      "extensions.transparency_log.status": {
        "path": "extensions.transparency_log.status",
        "line": 322
      },
      "extensions.transparency_log.purpose": {
        "path": "extensions.transparency_log.purpose",
        "line": 323
      },
      "conformance": {
        "path": "conformance",
        "line": 326
      },
      "conformance.core_reader": {
        "path": "conformance.core_reader",
        "line": 327
      },
      "conformance.core_reader.must": {
        "path": "conformance.core_reader.must",
        "line": 328
      },
      "conformance.core_reader.must.0": {
        "path": "conformance.core_reader.must.0",
        "line": 328
      },
      "conformance.core_reader.must.1": {
        "path": "conformance.core_reader.must.1",
        "line": 329
      },
      "conformance.core_reader.must.2": {
        "path": "conformance.core_reader.must.2",
        "line": 330
      },
      "conformance.core_reader.must.3": {
        "path": "conformance.core_reader.must.3",
        "line": 331
      },
      "conformance.core_reader.must.4": {
        "path": "conformance.core_reader.must.4",
        "line": 332
      },
      "conformance.verifier_tool": {
        "path": "conformance.verifier_tool",
        "line": 334
      },
      "conformance.verifier_tool.must": {
        "path": "conformance.verifier_tool.must",
        "line": 335
      },
      "conformance.verifier_tool.must.0": {
        "path": "conformance.verifier_tool.must.0",
        "line": 335
      },
      "conformance.verifier_tool.must.1": {
        "path": "conformance.verifier_tool.must.1",
        "line": 336
      },
      "conformance.verifier_tool.must.2": {
        "path": "conformance.verifier_tool.must.2",
        "line": 337
      },
      "conformance.verifier_tool.must.3": {
        "path": "conformance.verifier_tool.must.3",
        "line": 338
      },
      "conformance.product_render": {
        "path": "conformance.product_render",
        "line": 340
      },
      "conformance.product_render.rule": {
        "path": "conformance.product_render.rule",
        "line": 340
      },
      "references": {
        "path": "references",
        "line": 345
      },
      "references.0": {
        "path": "references.0",
        "line": 345
      },
      "references.1": {
        "path": "references.1",
        "line": 346
      },
      "references.2": {
        "path": "references.2",
        "line": 347
      },
      "references.3": {
        "path": "references.3",
        "line": 348
      },
      "references.4": {
        "path": "references.4",
        "line": 349
      }
    },
    "experience": {
      "label": "Explore the idea registry",
      "url": "https://ideas.proximitytoprogress.com/",
      "description": "Working registry · browse ideas and interpretations"
    },
    "repository": "https://github.com/uncomposed/irap-protocol.git",
    "commit": "ed3ad298540e2369486120225f9e91302a35a404",
    "modelPath": "SPEC.yaml",
    "modelSha256": "f17b57282cff34a98e6d2918b42b5f70b973d41fd54714ce85f99db3163f5691",
    "modelUrl": "/ideas/models/irap-f17b57282cff.yaml",
    "registryUrl": "https://ideas.proximitytoprogress.com/ideas/irap"
  },
  {
    "slug": "guestbook",
    "title": "Relic Guestbook",
    "shortTitle": "Relic Guestbook",
    "summary": "A small, implementation-independent protocol for consciously acknowledging places, discoveries, and encounters with signed artifacts that remain verifiable without their original host.",
    "version": "1",
    "status": "experimental",
    "accent": "#386649",
    "accentSoft": "#e9eee3",
    "motif": "network",
    "framing": "A small, implementation-independent protocol for consciously acknowledging places, discoveries, and encounters with signed artifacts that remain verifiable without their original host.",
    "metrics": [],
    "lanes": [
      {
        "id": "artifacts",
        "title": "Kinds of acknowledgment",
        "description": "Kinds of acknowledgment",
        "items": [
          {
            "id": "presence",
            "title": "Presence",
            "statement": "one key acknowledges one place key on a claimed UTC day",
            "kind": "definition",
            "source": {
              "path": "artifact_types.0",
              "line": 17
            }
          },
          {
            "id": "discovery",
            "title": "Discovery",
            "statement": "one key acknowledges discovery of one exact artifact on a claimed UTC day",
            "kind": "definition",
            "source": {
              "path": "artifact_types.1",
              "line": 18
            }
          },
          {
            "id": "encounter",
            "title": "Encounter",
            "statement": "two keys mutually acknowledge one another on a claimed UTC day",
            "kind": "definition",
            "source": {
              "path": "artifact_types.2",
              "line": 19
            }
          }
        ]
      },
      {
        "id": "layers",
        "title": "Responsibilities",
        "description": "Responsibilities",
        "items": [
          {
            "id": "identity",
            "title": "Identity",
            "statement": "control of an Ed25519 key",
            "kind": "definition",
            "source": {
              "path": "typed_layers.0",
              "line": 10
            }
          },
          {
            "id": "artifact",
            "title": "Artifact",
            "statement": "exact signed v1 bytes",
            "kind": "definition",
            "source": {
              "path": "typed_layers.1",
              "line": 11
            }
          },
          {
            "id": "registry",
            "title": "Registry",
            "statement": "a signed log checkpoint and receipt",
            "kind": "definition",
            "source": {
              "path": "typed_layers.2",
              "line": 12
            }
          },
          {
            "id": "archive",
            "title": "Archive",
            "statement": "independently verified custody of objects and log entries",
            "kind": "definition",
            "source": {
              "path": "typed_layers.3",
              "line": 13
            }
          },
          {
            "id": "federation",
            "title": "Federation",
            "statement": "optional distribution, never artifact truth",
            "kind": "definition",
            "source": {
              "path": "typed_layers.4",
              "line": 14
            }
          }
        ]
      },
      {
        "id": "constraints",
        "title": "Protocol rules",
        "description": "Protocol rules",
        "items": [
          {
            "id": "rule-0",
            "title": "No free-form message field exists in v1 artifacts.",
            "statement": "No free-form message field exists in v1 artifacts.",
            "kind": "kernel",
            "source": {
              "path": "constraints.0",
              "line": 22
            }
          },
          {
            "id": "rule-1",
            "title": "Artifacts verify offline without a registry, URL, database, or network.",
            "statement": "Artifacts verify offline without a registry, URL, database, or network.",
            "kind": "kernel",
            "source": {
              "path": "constraints.1",
              "line": 23
            }
          },
          {
            "id": "rule-2",
            "title": "The signed representation is fixed binary; JSON and ActivityPub are transports.",
            "statement": "The signed representation is fixed binary; JSON and ActivityPub are transports.",
            "kind": "kernel",
            "source": {
              "path": "constraints.2",
              "line": 24
            }
          },
          {
            "id": "rule-3",
            "title": "Implementations choose libraries, but must reproduce the normative vectors byte-for-byte.",
            "statement": "Implementations choose libraries, but must reproduce the normative vectors byte-for-byte.",
            "kind": "kernel",
            "source": {
              "path": "constraints.3",
              "line": 25
            }
          },
          {
            "id": "rule-4",
            "title": "Content identifiers never depend on a host name or database sequence.",
            "statement": "Content identifiers never depend on a host name or database sequence.",
            "kind": "kernel",
            "source": {
              "path": "constraints.4",
              "line": 26
            }
          },
          {
            "id": "rule-5",
            "title": "Indexes are derived and disposable; immutable artifacts and ordered log entries are authoritative.",
            "statement": "Indexes are derived and disposable; immutable artifacts and ordered log entries are authoritative.",
            "kind": "kernel",
            "source": {
              "path": "constraints.5",
              "line": 27
            }
          },
          {
            "id": "rule-6",
            "title": "Future storage cannot be proven. Present custody, integrity, inclusion, and consistency can be audited.",
            "statement": "Future storage cannot be proven. Present custody, integrity, inclusion, and consistency can be audited.",
            "kind": "kernel",
            "source": {
              "path": "constraints.6",
              "line": 28
            }
          }
        ]
      },
      {
        "id": "principle",
        "title": "Core principle",
        "description": "Core principle",
        "items": [
          {
            "id": "principle",
            "title": "The host does not determine validity",
            "statement": "A service may witness and preserve a relic, but it does not create the relic's validity.",
            "kind": "kernel",
            "source": {
              "path": "idea.principle",
              "line": 7
            }
          }
        ]
      }
    ],
    "paths": [],
    "checks": [
      {
        "id": "check-0",
        "title": "No free-form message field exists in v1 artifacts.",
        "description": "No free-form message field exists in v1 artifacts.",
        "source": {
          "path": "constraints.0",
          "line": 22
        }
      },
      {
        "id": "check-1",
        "title": "Artifacts verify offline without a registry, URL, database, or network.",
        "description": "Artifacts verify offline without a registry, URL, database, or network.",
        "source": {
          "path": "constraints.1",
          "line": 23
        }
      },
      {
        "id": "check-2",
        "title": "The signed representation is fixed binary; JSON and ActivityPub are transports.",
        "description": "The signed representation is fixed binary; JSON and ActivityPub are transports.",
        "source": {
          "path": "constraints.2",
          "line": 24
        }
      },
      {
        "id": "check-3",
        "title": "Implementations choose libraries, but must reproduce the normative vectors byte-for-byte.",
        "description": "Implementations choose libraries, but must reproduce the normative vectors byte-for-byte.",
        "source": {
          "path": "constraints.3",
          "line": 25
        }
      },
      {
        "id": "check-4",
        "title": "Content identifiers never depend on a host name or database sequence.",
        "description": "Content identifiers never depend on a host name or database sequence.",
        "source": {
          "path": "constraints.4",
          "line": 26
        }
      },
      {
        "id": "check-5",
        "title": "Indexes are derived and disposable; immutable artifacts and ordered log entries are authoritative.",
        "description": "Indexes are derived and disposable; immutable artifacts and ordered log entries are authoritative.",
        "source": {
          "path": "constraints.5",
          "line": 27
        }
      },
      {
        "id": "check-6",
        "title": "Future storage cannot be proven. Present custody, integrity, inclusion, and consistency can be audited.",
        "description": "Future storage cannot be proven. Present custody, integrity, inclusion, and consistency can be audited.",
        "source": {
          "path": "constraints.6",
          "line": 28
        }
      }
    ],
    "nonGoals": [],
    "guide": {
      "premise": "A portable acknowledgment of an encounter",
      "question": "Can a guestbook entry remain verifiable after its website disappears?",
      "introduction": "Sign a small record of a place, a discovery, or an encounter. Let a service preserve it while keeping its validity independent of that service.",
      "scope": "A record acknowledges a claimed day and a relationship between keys or artifacts. It does not prove physical presence, civil identity, or future storage.",
      "scopeRefs": [
        "presence",
        "identity",
        "rule-6"
      ],
      "mechanismTitle": "Acknowledge, sign, preserve, and verify",
      "mechanismDescription": "Three kinds of small signed records share a host-independent verification model.",
      "steps": [
        {
          "id": "choose",
          "title": "Choose the acknowledgment",
          "description": "Record presence at a place, discovery of an exact artifact, or a mutual encounter between two keys.",
          "refs": [
            "presence",
            "discovery",
            "encounter"
          ],
          "connection": "A bounded statement"
        },
        {
          "id": "sign",
          "title": "Sign the exact record",
          "description": "Control of a signing key establishes the protocol identity. The first protocol version uses a fixed binary representation with no free-form message.",
          "refs": [
            "identity",
            "artifact",
            "rule-0",
            "rule-2"
          ],
          "connection": "Verifiable signed bytes"
        },
        {
          "id": "preserve",
          "title": "Let a service preserve it",
          "description": "A registry can witness inclusion in a log; an archive can independently verify custody of objects and entries.",
          "refs": [
            "registry",
            "archive"
          ],
          "connection": "Auditable custody and inclusion"
        },
        {
          "id": "verify",
          "title": "Check it without its host",
          "description": "The artifact remains verifiable offline. Distribution and indexes help people find records but do not determine their validity.",
          "refs": [
            "rule-1",
            "rule-4",
            "rule-5",
            "principle"
          ],
          "connection": "A record independent of its original website"
        }
      ],
      "terms": [
        {
          "id": "presence",
          "title": "Presence",
          "meaning": "One key acknowledges one place key on a claimed calendar day in Coordinated Universal Time.",
          "refs": [
            "presence"
          ]
        },
        {
          "id": "discovery",
          "title": "Discovery",
          "meaning": "One key acknowledges discovery of one exact artifact on a claimed day.",
          "refs": [
            "discovery"
          ]
        },
        {
          "id": "encounter",
          "title": "Encounter",
          "meaning": "Two keys mutually acknowledge one another on a claimed day.",
          "refs": [
            "encounter"
          ]
        },
        {
          "id": "registry",
          "title": "Registry",
          "meaning": "A service whose signed log checkpoints and receipts can witness inclusion.",
          "refs": [
            "registry"
          ]
        },
        {
          "id": "archive",
          "title": "Archive",
          "meaning": "Independent custody of records and log entries that can be checked for integrity.",
          "refs": [
            "archive"
          ]
        }
      ],
      "example": {
        "title": "The website is a keeper, not the source of validity",
        "text": "If a host disappears, a retained artifact can still be checked offline. Its identity does not depend on a hostname or database number.",
        "refs": [
          "rule-1",
          "rule-4",
          "principle"
        ]
      },
      "uncertainty": {
        "title": "Future preservation is a promise, not a proof",
        "text": "Present custody, integrity, inclusion, and consistency can be audited. The protocol cannot prove that a host will keep storing a record.",
        "refs": [
          "rule-6"
        ]
      },
      "featured": [
        "rule-1",
        "rule-2",
        "rule-3",
        "rule-5",
        "rule-6"
      ]
    },
    "contextSources": [],
    "evidenceSummary": "This revision defines behavior, principles, and questions to inspect. Its checks and background references do not establish that an implementation has passed them.",
    "sourceSections": [
      {
        "key": "idea",
        "value": {
          "id": "https://ideas.proximitytoprogress.com/ideas/guestbook",
          "name": "Relic Guestbook",
          "summary": "A small, implementation-independent protocol for consciously acknowledging places, discoveries, and encounters with signed artifacts that remain verifiable without their original host.",
          "status": "experimental",
          "principle": "A service may witness and preserve a relic, but it does not create the relic's validity."
        },
        "source": {
          "path": "idea",
          "line": 2
        }
      },
      {
        "key": "typed_layers",
        "value": [
          {
            "name": "identity",
            "authority": "control of an Ed25519 key"
          },
          {
            "name": "artifact",
            "authority": "exact signed v1 bytes"
          },
          {
            "name": "registry",
            "authority": "a signed log checkpoint and receipt"
          },
          {
            "name": "archive",
            "authority": "independently verified custody of objects and log entries"
          },
          {
            "name": "federation",
            "authority": "optional distribution, never artifact truth"
          }
        ],
        "source": {
          "path": "typed_layers",
          "line": 9
        }
      },
      {
        "key": "artifact_types",
        "value": [
          {
            "name": "presence",
            "meaning": "one key acknowledges one place key on a claimed UTC day"
          },
          {
            "name": "discovery",
            "meaning": "one key acknowledges discovery of one exact artifact on a claimed UTC day"
          },
          {
            "name": "encounter",
            "meaning": "two keys mutually acknowledge one another on a claimed UTC day"
          }
        ],
        "source": {
          "path": "artifact_types",
          "line": 16
        }
      },
      {
        "key": "constraints",
        "value": [
          "No free-form message field exists in v1 artifacts.",
          "Artifacts verify offline without a registry, URL, database, or network.",
          "The signed representation is fixed binary; JSON and ActivityPub are transports.",
          "Implementations choose libraries, but must reproduce the normative vectors byte-for-byte.",
          "Content identifiers never depend on a host name or database sequence.",
          "Indexes are derived and disposable; immutable artifacts and ordered log entries are authoritative.",
          "Future storage cannot be proven. Present custody, integrity, inclusion, and consistency can be audited."
        ],
        "source": {
          "path": "constraints",
          "line": 21
        }
      },
      {
        "key": "public_surfaces",
        "value": {
          "specification": "https://github.com/uncomposed/relic-protocol",
          "reference_implementation": "https://github.com/uncomposed/relic-reference",
          "hosted_guestbook": "https://relics.proximitytoprogress.com/",
          "explorer": "https://proximitytoprogress.com/ideas/guestbook/",
          "registry": "https://ideas.proximitytoprogress.com/ideas/guestbook"
        },
        "source": {
          "path": "public_surfaces",
          "line": 30
        }
      }
    ],
    "sourceLocations": {
      "idea": {
        "path": "idea",
        "line": 2
      },
      "idea.id": {
        "path": "idea.id",
        "line": 3
      },
      "idea.name": {
        "path": "idea.name",
        "line": 4
      },
      "idea.summary": {
        "path": "idea.summary",
        "line": 5
      },
      "idea.status": {
        "path": "idea.status",
        "line": 6
      },
      "idea.principle": {
        "path": "idea.principle",
        "line": 7
      },
      "typed_layers": {
        "path": "typed_layers",
        "line": 9
      },
      "typed_layers.0": {
        "path": "typed_layers.0",
        "line": 10
      },
      "typed_layers.0.name": {
        "path": "typed_layers.0.name",
        "line": 10
      },
      "typed_layers.0.authority": {
        "path": "typed_layers.0.authority",
        "line": 10
      },
      "typed_layers.1": {
        "path": "typed_layers.1",
        "line": 11
      },
      "typed_layers.1.name": {
        "path": "typed_layers.1.name",
        "line": 11
      },
      "typed_layers.1.authority": {
        "path": "typed_layers.1.authority",
        "line": 11
      },
      "typed_layers.2": {
        "path": "typed_layers.2",
        "line": 12
      },
      "typed_layers.2.name": {
        "path": "typed_layers.2.name",
        "line": 12
      },
      "typed_layers.2.authority": {
        "path": "typed_layers.2.authority",
        "line": 12
      },
      "typed_layers.3": {
        "path": "typed_layers.3",
        "line": 13
      },
      "typed_layers.3.name": {
        "path": "typed_layers.3.name",
        "line": 13
      },
      "typed_layers.3.authority": {
        "path": "typed_layers.3.authority",
        "line": 13
      },
      "typed_layers.4": {
        "path": "typed_layers.4",
        "line": 14
      },
      "typed_layers.4.name": {
        "path": "typed_layers.4.name",
        "line": 14
      },
      "typed_layers.4.authority": {
        "path": "typed_layers.4.authority",
        "line": 14
      },
      "artifact_types": {
        "path": "artifact_types",
        "line": 16
      },
      "artifact_types.0": {
        "path": "artifact_types.0",
        "line": 17
      },
      "artifact_types.0.name": {
        "path": "artifact_types.0.name",
        "line": 17
      },
      "artifact_types.0.meaning": {
        "path": "artifact_types.0.meaning",
        "line": 17
      },
      "artifact_types.1": {
        "path": "artifact_types.1",
        "line": 18
      },
      "artifact_types.1.name": {
        "path": "artifact_types.1.name",
        "line": 18
      },
      "artifact_types.1.meaning": {
        "path": "artifact_types.1.meaning",
        "line": 18
      },
      "artifact_types.2": {
        "path": "artifact_types.2",
        "line": 19
      },
      "artifact_types.2.name": {
        "path": "artifact_types.2.name",
        "line": 19
      },
      "artifact_types.2.meaning": {
        "path": "artifact_types.2.meaning",
        "line": 19
      },
      "constraints": {
        "path": "constraints",
        "line": 21
      },
      "constraints.0": {
        "path": "constraints.0",
        "line": 22
      },
      "constraints.1": {
        "path": "constraints.1",
        "line": 23
      },
      "constraints.2": {
        "path": "constraints.2",
        "line": 24
      },
      "constraints.3": {
        "path": "constraints.3",
        "line": 25
      },
      "constraints.4": {
        "path": "constraints.4",
        "line": 26
      },
      "constraints.5": {
        "path": "constraints.5",
        "line": 27
      },
      "constraints.6": {
        "path": "constraints.6",
        "line": 28
      },
      "public_surfaces": {
        "path": "public_surfaces",
        "line": 30
      },
      "public_surfaces.specification": {
        "path": "public_surfaces.specification",
        "line": 31
      },
      "public_surfaces.reference_implementation": {
        "path": "public_surfaces.reference_implementation",
        "line": 32
      },
      "public_surfaces.hosted_guestbook": {
        "path": "public_surfaces.hosted_guestbook",
        "line": 33
      },
      "public_surfaces.explorer": {
        "path": "public_surfaces.explorer",
        "line": 34
      },
      "public_surfaces.registry": {
        "path": "public_surfaces.registry",
        "line": 35
      }
    },
    "experience": {
      "label": "Open the guestbook",
      "url": "https://relics.proximitytoprogress.com/",
      "description": "Interactive web application"
    },
    "repository": "https://github.com/uncomposed/relic-protocol.git",
    "commit": "3def5fdcb92f6c1011465f468bbd26c1e49dfbcc",
    "modelPath": "idea.yaml",
    "modelSha256": "05cafc846f68e77f9f5fdf9a11e32d6b6165962e5f8b0cfd81c96bfce66eabf0",
    "modelUrl": "/ideas/models/guestbook-05cafc846f68.yaml",
    "registryUrl": "https://ideas.proximitytoprogress.com/ideas/guestbook"
  },
  {
    "slug": "spoken-margins",
    "title": "Spoken Margins",
    "shortTitle": "Spoken Margins",
    "summary": "A model for preserving orientation, thought, and provenance when written or transcript-backed material becomes a listening experience.",
    "version": "0.1",
    "status": "draft",
    "accent": "#386649",
    "accentSoft": "#e9eee3",
    "motif": "network",
    "framing": "A model for preserving orientation, thought, and provenance when written or transcript-backed material becomes a listening experience.",
    "metrics": [],
    "lanes": [
      {
        "id": "objects",
        "title": "Information objects",
        "description": "Information objects",
        "items": [
          {
            "id": "source",
            "title": "Source",
            "statement": "The material being encountered.",
            "kind": "definition",
            "rationale": "Its authored or published identity remains distinct from every transformation.",
            "source": {
              "path": "idea.information_objects.source",
              "line": 63
            }
          },
          {
            "id": "orientation",
            "title": "Orientation",
            "statement": "Enough structure to perceive and navigate the source while listening.",
            "kind": "definition",
            "rationale": "It may be partial or absent without invalidating listening.",
            "source": {
              "path": "idea.information_objects.orientation",
              "line": 66
            }
          },
          {
            "id": "session",
            "title": "Session",
            "statement": "One situated traversal of a source and the interactions around it.",
            "kind": "definition",
            "rationale": "Progress and contributions remain scoped to that engagement.",
            "source": {
              "path": "idea.information_objects.session",
              "line": 69
            }
          },
          {
            "id": "anchor",
            "title": "Anchor",
            "statement": "A durable relationship between a session event and the relevant moment.",
            "kind": "definition",
            "rationale": "It survives navigation and does not depend on a renderer's temporary chunking.",
            "source": {
              "path": "idea.information_objects.anchor",
              "line": 72
            }
          },
          {
            "id": "margin",
            "title": "Margin",
            "statement": "The listener's own spoken or otherwise captured reaction.",
            "kind": "definition",
            "rationale": "It remains distinguishable from source and generated material.",
            "source": {
              "path": "idea.information_objects.margin",
              "line": 75
            }
          },
          {
            "id": "continuation",
            "title": "Continuation",
            "statement": "A summary, response, question, or later turn that may itself become listenable.",
            "kind": "definition",
            "rationale": "Its provenance remains visible and it can receive new situated margins.",
            "source": {
              "path": "idea.information_objects.continuation",
              "line": 78
            }
          },
          {
            "id": "portable_record",
            "title": "Portable record",
            "statement": "A representation of the engagement for review or handoff.",
            "kind": "definition",
            "rationale": "It preserves ordering, attribution, anchors, and session boundaries.",
            "source": {
              "path": "idea.information_objects.portable_record",
              "line": 81
            }
          }
        ]
      },
      {
        "id": "stages",
        "title": "Listening stages",
        "description": "Listening stages",
        "items": [
          {
            "id": "orient",
            "title": "Orient",
            "statement": "Establish enough context to begin without demanding complete preprocessing.",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "listen"
              }
            ],
            "source": {
              "path": "idea.execution_topology.stages.0",
              "line": 86
            }
          },
          {
            "id": "listen",
            "title": "Listen",
            "statement": "Traverse source or continuation through controllable playback.",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "interrupt"
              }
            ],
            "source": {
              "path": "idea.execution_topology.stages.1",
              "line": 88
            }
          },
          {
            "id": "interrupt",
            "title": "Interrupt",
            "statement": "Stop the stream at the moment thought becomes worth capturing.",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "anchor_and_capture"
              }
            ],
            "source": {
              "path": "idea.execution_topology.stages.2",
              "line": 90
            }
          },
          {
            "id": "anchor_and_capture",
            "title": "Anchor and capture",
            "statement": "Bind the listener's margin to the provoking moment and its provenance.",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "resume"
              }
            ],
            "source": {
              "path": "idea.execution_topology.stages.3",
              "line": 92
            }
          },
          {
            "id": "resume",
            "title": "Resume",
            "statement": "Re-enter nearby context without reconstructing the session.",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "reflect_or_continue"
              }
            ],
            "source": {
              "path": "idea.execution_topology.stages.4",
              "line": 94
            }
          },
          {
            "id": "reflect_or_continue",
            "title": "Reflect or continue",
            "statement": "Review, summarize, question, answer, or listen onward.",
            "kind": "mechanism",
            "links": [
              {
                "type": "next",
                "target": "revisit_or_export"
              }
            ],
            "source": {
              "path": "idea.execution_topology.stages.5",
              "line": 96
            }
          },
          {
            "id": "revisit_or_export",
            "title": "Revisit or export",
            "statement": "Preserve the engagement beyond the immediate playback surface.",
            "kind": "mechanism",
            "links": [],
            "source": {
              "path": "idea.execution_topology.stages.6",
              "line": 98
            }
          }
        ]
      },
      {
        "id": "principles",
        "title": "Principles",
        "description": "Principles",
        "items": [
          {
            "id": "source_integrity",
            "title": "Source integrity",
            "statement": "Narration, restructuring, and generated material do not silently replace the source.",
            "kind": "kernel",
            "source": {
              "path": "idea.principles.source_integrity",
              "line": 102
            }
          },
          {
            "id": "situatedness",
            "title": "Situatedness",
            "statement": "A margin retains enough context to explain what provoked it.",
            "kind": "kernel",
            "source": {
              "path": "idea.principles.situatedness",
              "line": 103
            }
          },
          {
            "id": "continuity",
            "title": "Continuity",
            "statement": "Interruption does not destroy position or force reconstruction of the session.",
            "kind": "kernel",
            "source": {
              "path": "idea.principles.continuity",
              "line": 104
            }
          },
          {
            "id": "provenance",
            "title": "Provenance",
            "statement": "Source, listener, and generated contributions remain distinguishable.",
            "kind": "kernel",
            "source": {
              "path": "idea.principles.provenance",
              "line": 105
            }
          },
          {
            "id": "graceful_degradation",
            "title": "Graceful degradation",
            "statement": "Core listening, margins, and recovery survive missing structure or AI.",
            "kind": "kernel",
            "source": {
              "path": "idea.principles.graceful_degradation",
              "line": 106
            }
          },
          {
            "id": "agency",
            "title": "Agency",
            "statement": "Retention and disclosure follow deliberate listener action.",
            "kind": "kernel",
            "source": {
              "path": "idea.principles.agency",
              "line": 107
            }
          },
          {
            "id": "capability_honesty",
            "title": "Capability honesty",
            "statement": "A renderer does not present unavailable behavior as active.",
            "kind": "kernel",
            "source": {
              "path": "idea.principles.capability_honesty",
              "line": 108
            }
          },
          {
            "id": "rendering_independence",
            "title": "Rendering independence",
            "statement": "No particular interface, device, model, voice, or storage engine is required.",
            "kind": "kernel",
            "source": {
              "path": "idea.principles.rendering_independence",
              "line": 109
            }
          }
        ]
      },
      {
        "id": "challenges",
        "title": "Challenges",
        "description": "Challenges",
        "items": [
          {
            "id": "linearization",
            "title": "Linearization",
            "statement": "Spatial material becomes a stream with weak peripheral context.",
            "kind": "open_question",
            "source": {
              "path": "idea.challenges.linearization",
              "line": 112
            }
          },
          {
            "id": "anchor_drift",
            "title": "Anchor drift",
            "statement": "A note can lose its relationship to the moment that produced it.",
            "kind": "open_question",
            "source": {
              "path": "idea.challenges.anchor_drift",
              "line": 113
            }
          },
          {
            "id": "interruption_cost",
            "title": "Interruption cost",
            "statement": "Capturing thought can destroy momentum or orientation.",
            "kind": "open_question",
            "source": {
              "path": "idea.challenges.interruption_cost",
              "line": 114
            }
          },
          {
            "id": "modality_change",
            "title": "Modality change",
            "statement": "Text, native audio, listener speech, and generated turns carry different position evidence.",
            "kind": "open_question",
            "source": {
              "path": "idea.challenges.modality_change",
              "line": 115
            }
          },
          {
            "id": "uneven_structure",
            "title": "Uneven structure",
            "statement": "Some sources have rich authored structure and others have none.",
            "kind": "open_question",
            "source": {
              "path": "idea.challenges.uneven_structure",
              "line": 116
            }
          },
          {
            "id": "capability_variance",
            "title": "Capability variance",
            "statement": "Devices and contexts differ in playback, recording, intelligence, and connectivity.",
            "kind": "open_question",
            "source": {
              "path": "idea.challenges.capability_variance",
              "line": 117
            }
          },
          {
            "id": "provenance_pressure",
            "title": "Provenance pressure",
            "statement": "Transformations make it easy to blur who said what.",
            "kind": "open_question",
            "source": {
              "path": "idea.challenges.provenance_pressure",
              "line": 118
            }
          },
          {
            "id": "privacy_pressure",
            "title": "Privacy pressure",
            "statement": "Source material and private reactions can be more revealing together than separately.",
            "kind": "open_question",
            "source": {
              "path": "idea.challenges.privacy_pressure",
              "line": 119
            }
          }
        ]
      }
    ],
    "paths": [
      {
        "id": "behavioral",
        "title": "Behavioral",
        "steps": [
          "Interrupt repeatedly and demonstrate that each margin remains correctly situated.",
          "Resume after interruption and demonstrate coherent nearby context.",
          "Use a source with no derived orientation and preserve the core loop.",
          "Continue into a later turn and keep its authorship distinct."
        ],
        "source": {
          "path": "idea.evidence_paths.behavioral",
          "line": 132
        }
      },
      {
        "id": "portability",
        "title": "Portability",
        "steps": [
          "Export one session and reconstruct its source, ordering, margins, and provenance.",
          "Move the record to another tool without depending on private renderer identifiers."
        ],
        "source": {
          "path": "idea.evidence_paths.portability",
          "line": 137
        }
      },
      {
        "id": "privacy_and_agency",
        "title": "Privacy and agency",
        "steps": [
          "Show what remains local and what leaves only after deliberate action.",
          "Remove optional intelligence and demonstrate that the essential experience remains."
        ],
        "source": {
          "path": "idea.evidence_paths.privacy_and_agency",
          "line": 140
        }
      },
      {
        "id": "comparison",
        "title": "Comparison",
        "steps": [
          "Distinguish the rendering from a generic audio player plus detached voice memos.",
          "Distinguish the rendering from a chatbot that discards source position and session history."
        ],
        "source": {
          "path": "idea.evidence_paths.comparison",
          "line": 143
        }
      }
    ],
    "checks": [
      {
        "id": "check-0",
        "title": "Floating notes that cannot explain what prompted them.",
        "description": "Floating notes that cannot explain what prompted them.",
        "source": {
          "path": "idea.failure_modes.0",
          "line": 122
        }
      },
      {
        "id": "check-1",
        "title": "Resumption at the wrong source, position, or session.",
        "description": "Resumption at the wrong source, position, or session.",
        "source": {
          "path": "idea.failure_modes.1",
          "line": 123
        }
      },
      {
        "id": "check-2",
        "title": "Generated material presented as if it were authored source.",
        "description": "Generated material presented as if it were authored source.",
        "source": {
          "path": "idea.failure_modes.2",
          "line": 124
        }
      },
      {
        "id": "check-3",
        "title": "A core loop that stops working when AI is unavailable.",
        "description": "A core loop that stops working when AI is unavailable.",
        "source": {
          "path": "idea.failure_modes.3",
          "line": 125
        }
      },
      {
        "id": "check-4",
        "title": "Hidden disclosure of source material or listener commentary.",
        "description": "Hidden disclosure of source material or listener commentary.",
        "source": {
          "path": "idea.failure_modes.4",
          "line": 126
        }
      },
      {
        "id": "check-5",
        "title": "A portable record that collapses multiple sessions or speakers together.",
        "description": "A portable record that collapses multiple sessions or speakers together.",
        "source": {
          "path": "idea.failure_modes.5",
          "line": 127
        }
      },
      {
        "id": "check-6",
        "title": "A control surface that advertises capabilities its runtime cannot perform.",
        "description": "A control surface that advertises capabilities its runtime cannot perform.",
        "source": {
          "path": "idea.failure_modes.6",
          "line": 128
        }
      }
    ],
    "nonGoals": [
      "Prescribe a user interface, operating system, speech engine, model, or database.",
      "Define a universal annotation or media schema.",
      "Require every source to be summarized or structurally generated before playback.",
      "Replace the source with a model's interpretation of it.",
      "Rank sources, listeners, renderings, or ideas.",
      "Treat every audio player, voice memo tool, or chatbot as a faithful rendering."
    ],
    "guide": {
      "premise": "Listening with room for your own thoughts",
      "question": "What if listening preserved the agency of reading?",
      "introduction": "Find your place, interrupt to capture a thought, and resume with context. Keep your reaction attached to what prompted it, ready to revisit or share.",
      "scope": "Source material, your own commentary, and generated responses remain distinct. The essential listening loop should still work without artificial intelligence.",
      "scopeRefs": [
        "provenance",
        "graceful_degradation"
      ],
      "mechanismTitle": "Listen, pause, leave a margin, return",
      "mechanismDescription": "Follow one listening session from orientation to a portable record.",
      "steps": [
        {
          "id": "start",
          "title": "Find your place",
          "description": "Use the structure available to orient yourself and begin listening, even when headings or other cues are incomplete.",
          "refs": [
            "orientation",
            "orient",
            "listen"
          ],
          "connection": "A source and a position"
        },
        {
          "id": "capture",
          "title": "Interrupt with a thought",
          "description": "Pause at the moment a reaction matters. Capture it with a durable link to the passage that prompted it.",
          "refs": [
            "interrupt",
            "anchor_and_capture",
            "anchor",
            "margin"
          ],
          "connection": "A thought with its context"
        },
        {
          "id": "return",
          "title": "Resume coherently",
          "description": "Re-enter nearby context so capturing a thought does not cost you your place or the thread of the source.",
          "refs": [
            "resume",
            "continuity"
          ],
          "connection": "A usable path back"
        },
        {
          "id": "reflect",
          "title": "Revisit or continue",
          "description": "Review the exchange, listen to a continuation, or carry the session elsewhere while preserving order and authorship.",
          "refs": [
            "reflect_or_continue",
            "revisit_or_export",
            "portable_record"
          ],
          "connection": "An engagement you can keep"
        }
      ],
      "terms": [
        {
          "id": "source",
          "title": "Source",
          "meaning": "The original material you are listening to. Its identity stays separate from narration and later transformations.",
          "refs": [
            "source"
          ]
        },
        {
          "id": "margin",
          "title": "Margin",
          "meaning": "Your own captured reaction, distinguishable from the source and generated material.",
          "refs": [
            "margin"
          ]
        },
        {
          "id": "anchor",
          "title": "Anchor",
          "meaning": "A lasting relationship between a session event and the moment that prompted it.",
          "refs": [
            "anchor"
          ]
        },
        {
          "id": "session",
          "title": "Session",
          "meaning": "One traversal of a source, with its own progress and contributions.",
          "refs": [
            "session"
          ]
        },
        {
          "id": "continuation",
          "title": "Continuation",
          "meaning": "A later summary, answer, or question that can itself become something you listen to and respond to.",
          "refs": [
            "continuation"
          ]
        }
      ],
      "example": {
        "title": "A thought belongs beside its passage",
        "text": "When a sentence prompts a reaction, the useful record preserves both your words and where they arose—not just a detached voice memo.",
        "refs": [
          "situatedness",
          "margin",
          "anchor"
        ]
      },
      "uncertainty": {
        "title": "Will your place survive interruption?",
        "text": "Repeated interruption and resumption must preserve the right source, position, and session. Different media may provide different kinds of timing evidence.",
        "refs": [
          "anchor_drift",
          "modality_change",
          "continuity"
        ]
      },
      "featured": [
        "anchor_drift",
        "interruption_cost",
        "modality_change",
        "capability_variance",
        "provenance_pressure",
        "privacy_pressure"
      ]
    },
    "contextSources": [],
    "evidenceSummary": "This revision defines behavior, principles, and questions to inspect. Its checks and background references do not establish that an implementation has passed them.",
    "sourceSections": [
      {
        "key": "spec_version",
        "value": "0.1",
        "source": {
          "path": "spec_version",
          "line": 1
        }
      },
      {
        "key": "idea",
        "value": {
          "id": "https://ideas.proximitytoprogress.com/ideas/spoken-margins",
          "name": "Spoken Margins: Situated, Interruptible Listening",
          "short_name": "Spoken Margins",
          "version": "0.1",
          "status": "draft",
          "summary": "A model for preserving orientation, thought, and provenance when written or transcript-backed material becomes a listening experience.\n",
          "thesis": "Listening should preserve the agency of reading. A person should be able to understand where they are, interrupt without losing their place, capture a thought beside the moment that provoked it, resume coherently, and carry the resulting exchange elsewhere without confusing source, listener, or generated contributions.\n",
          "context": {
            "conditions": [
              "Written reading supplies spatial cues such as headings, pages, nearby context, and margins.",
              "Audio makes material available in more situations but turns it into a temporal stream.",
              "A thought captured during playback can become detached from what caused it.",
              "AI lowers the cost of producing narration, structure, summaries, and conversation.",
              "Easier generation does not itself preserve orientation, provenance, privacy, or agency."
            ],
            "central_question": "How can a listening system preserve the useful affordances of marginal reading while remaining native to speech, movement, and time?\n"
          },
          "actors": {
            "listener": {
              "role": "Encounters a source and contributes situated reactions.",
              "authority": "Owns the decision to capture, retain, transform, or disclose the engagement."
            },
            "source_author_or_publisher": {
              "role": "Supplies the material and any authored structure or timing evidence.",
              "authority": "Remains distinct from later narration, commentary, and generated material."
            },
            "renderer": {
              "role": "Turns the idea into a concrete experience on a device or medium.",
              "authority": "Chooses implementation without silently changing the idea's invariants."
            },
            "optional_transformer": {
              "role": "May derive orientation, transcription, summaries, or continuations.",
              "authority": "Never becomes the unmarked author of the source or the listener's margin."
            }
          },
          "resources": [
            {
              "attention": "The listener's limited capacity to orient, remember, and respond while audio continues."
            },
            {
              "source_material": "Text, transcript-backed media, or native audio with enough durable identity to revisit."
            },
            {
              "playback_time": "The temporal position through which listening unfolds."
            },
            {
              "structure": "Authored, derived, generated, partial, or absent orientation cues."
            },
            {
              "voice": "A channel for both rendering source material and capturing listener thought."
            },
            {
              "local_state": "The durable record that makes interruption, resumption, and later review possible."
            }
          ],
          "goals": [
            "Restore orientation and meaningful navigation to linear listening.",
            "Capture a listener's thought in the context that produced it.",
            "Preserve continuity across interruption, resumption, and later reflection.",
            "Keep source, listener commentary, and generated continuation attributable.",
            "Remain useful without generated structure or artificial intelligence.",
            "Produce a portable account of the engagement rather than a loose pile of notes."
          ],
          "information_objects": {
            "source": {
              "meaning": "The material being encountered.",
              "invariant": "Its authored or published identity remains distinct from every transformation."
            },
            "orientation": {
              "meaning": "Enough structure to perceive and navigate the source while listening.",
              "invariant": "It may be partial or absent without invalidating listening."
            },
            "session": {
              "meaning": "One situated traversal of a source and the interactions around it.",
              "invariant": "Progress and contributions remain scoped to that engagement."
            },
            "anchor": {
              "meaning": "A durable relationship between a session event and the relevant moment.",
              "invariant": "It survives navigation and does not depend on a renderer's temporary chunking."
            },
            "margin": {
              "meaning": "The listener's own spoken or otherwise captured reaction.",
              "invariant": "It remains distinguishable from source and generated material."
            },
            "continuation": {
              "meaning": "A summary, response, question, or later turn that may itself become listenable.",
              "invariant": "Its provenance remains visible and it can receive new situated margins."
            },
            "portable_record": {
              "meaning": "A representation of the engagement for review or handoff.",
              "invariant": "It preserves ordering, attribution, anchors, and session boundaries."
            }
          },
          "execution_topology": {
            "stages": [
              {
                "id": "orient",
                "purpose": "Establish enough context to begin without demanding complete preprocessing."
              },
              {
                "id": "listen",
                "purpose": "Traverse source or continuation through controllable playback."
              },
              {
                "id": "interrupt",
                "purpose": "Stop the stream at the moment thought becomes worth capturing."
              },
              {
                "id": "anchor_and_capture",
                "purpose": "Bind the listener's margin to the provoking moment and its provenance."
              },
              {
                "id": "resume",
                "purpose": "Re-enter nearby context without reconstructing the session."
              },
              {
                "id": "reflect_or_continue",
                "purpose": "Review, summarize, question, answer, or listen onward."
              },
              {
                "id": "revisit_or_export",
                "purpose": "Preserve the engagement beyond the immediate playback surface."
              }
            ]
          },
          "principles": {
            "source_integrity": "Narration, restructuring, and generated material do not silently replace the source.",
            "situatedness": "A margin retains enough context to explain what provoked it.",
            "continuity": "Interruption does not destroy position or force reconstruction of the session.",
            "provenance": "Source, listener, and generated contributions remain distinguishable.",
            "graceful_degradation": "Core listening, margins, and recovery survive missing structure or AI.",
            "agency": "Retention and disclosure follow deliberate listener action.",
            "capability_honesty": "A renderer does not present unavailable behavior as active.",
            "rendering_independence": "No particular interface, device, model, voice, or storage engine is required."
          },
          "challenges": {
            "linearization": "Spatial material becomes a stream with weak peripheral context.",
            "anchor_drift": "A note can lose its relationship to the moment that produced it.",
            "interruption_cost": "Capturing thought can destroy momentum or orientation.",
            "modality_change": "Text, native audio, listener speech, and generated turns carry different position evidence.",
            "uneven_structure": "Some sources have rich authored structure and others have none.",
            "capability_variance": "Devices and contexts differ in playback, recording, intelligence, and connectivity.",
            "provenance_pressure": "Transformations make it easy to blur who said what.",
            "privacy_pressure": "Source material and private reactions can be more revealing together than separately."
          },
          "failure_modes": [
            "Floating notes that cannot explain what prompted them.",
            "Resumption at the wrong source, position, or session.",
            "Generated material presented as if it were authored source.",
            "A core loop that stops working when AI is unavailable.",
            "Hidden disclosure of source material or listener commentary.",
            "A portable record that collapses multiple sessions or speakers together.",
            "A control surface that advertises capabilities its runtime cannot perform."
          ],
          "evidence_paths": {
            "behavioral": [
              "Interrupt repeatedly and demonstrate that each margin remains correctly situated.",
              "Resume after interruption and demonstrate coherent nearby context.",
              "Use a source with no derived orientation and preserve the core loop.",
              "Continue into a later turn and keep its authorship distinct."
            ],
            "portability": [
              "Export one session and reconstruct its source, ordering, margins, and provenance.",
              "Move the record to another tool without depending on private renderer identifiers."
            ],
            "privacy_and_agency": [
              "Show what remains local and what leaves only after deliberate action.",
              "Remove optional intelligence and demonstrate that the essential experience remains."
            ],
            "comparison": [
              "Distinguish the rendering from a generic audio player plus detached voice memos.",
              "Distinguish the rendering from a chatbot that discards source position and session history."
            ]
          },
          "decision_criteria": {
            "faithful_when": [
              "The listener can tell where they are well enough to act.",
              "A margin can answer both who said this and what prompted it.",
              "Interruption preserves a usable path back into listening.",
              "Source, listener, and generated layers remain separately attributable.",
              "The engagement can be revisited or carried elsewhere as a coherent session."
            ],
            "stronger_when": [
              "It supports multiple source modalities without inventing false equivalence between their anchors.",
              "It exposes limits and degraded states rather than simulating certainty.",
              "It minimizes the attention required to preserve meaningful thought."
            ]
          },
          "non_goals": [
            "Prescribe a user interface, operating system, speech engine, model, or database.",
            "Define a universal annotation or media schema.",
            "Require every source to be summarized or structurally generated before playback.",
            "Replace the source with a model's interpretation of it.",
            "Rank sources, listeners, renderings, or ideas.",
            "Treat every audio player, voice memo tool, or chatbot as a faithful rendering."
          ],
          "rendering_guidance": {
            "must": [
              "Preserve the distinct roles of source, session, anchor, margin, and provenance.",
              "Make interruption and coherent resumption part of the primary loop.",
              "Remain useful when optional transformation is unavailable."
            ],
            "may": [
              "Use visual, auditory, haptic, conversational, wearable, ambient, or mixed interfaces.",
              "Derive orientation from authored evidence or optional transformation.",
              "Support private, collaborative, or published margins when agency remains explicit."
            ],
            "must_not": [
              "Require the implementation choices of any earlier rendering.",
              "Present fidelity as truth, endorsement, completeness, or product quality."
            ]
          },
          "acceptance": {
            "fidelity_questions": [
              "Does the rendering restore meaningful agency lost when reading becomes listening?",
              "Does each margin retain a durable relationship to what provoked it?",
              "Can the listener interrupt and resume without rebuilding the session mentally?",
              "Are source, listener, and generated contributions visibly distinguishable?",
              "Does the essential loop survive without AI?"
            ],
            "falsification_tests": [
              "Remove all generated structure and intelligence; listening, margins, anchoring, and export still work.",
              "Change playback position after capture; the existing margin still resolves to its original context.",
              "Introduce a generated continuation; it is not mislabeled as source or listener speech.",
              "Export two sessions over the same source; their margins do not silently merge.",
              "Invoke an unsupported capability; the renderer rejects or withholds it honestly."
            ],
            "stopping_rule": "Do not call a rendering faithful when an ordinary detached note, a misplaced resume, or an unmarked transformation can change the meaning of the listener's engagement.\n"
          }
        },
        "source": {
          "path": "idea",
          "line": 4
        }
      }
    ],
    "sourceLocations": {
      "spec_version": {
        "path": "spec_version",
        "line": 1
      },
      "idea": {
        "path": "idea",
        "line": 4
      },
      "idea.id": {
        "path": "idea.id",
        "line": 4
      },
      "idea.name": {
        "path": "idea.name",
        "line": 5
      },
      "idea.short_name": {
        "path": "idea.short_name",
        "line": 6
      },
      "idea.version": {
        "path": "idea.version",
        "line": 7
      },
      "idea.status": {
        "path": "idea.status",
        "line": 8
      },
      "idea.summary": {
        "path": "idea.summary",
        "line": 9
      },
      "idea.thesis": {
        "path": "idea.thesis",
        "line": 13
      },
      "idea.context": {
        "path": "idea.context",
        "line": 21
      },
      "idea.context.conditions": {
        "path": "idea.context.conditions",
        "line": 22
      },
      "idea.context.conditions.0": {
        "path": "idea.context.conditions.0",
        "line": 22
      },
      "idea.context.conditions.1": {
        "path": "idea.context.conditions.1",
        "line": 23
      },
      "idea.context.conditions.2": {
        "path": "idea.context.conditions.2",
        "line": 24
      },
      "idea.context.conditions.3": {
        "path": "idea.context.conditions.3",
        "line": 25
      },
      "idea.context.conditions.4": {
        "path": "idea.context.conditions.4",
        "line": 26
      },
      "idea.context.central_question": {
        "path": "idea.context.central_question",
        "line": 27
      },
      "idea.actors": {
        "path": "idea.actors",
        "line": 32
      },
      "idea.actors.listener": {
        "path": "idea.actors.listener",
        "line": 33
      },
      "idea.actors.listener.role": {
        "path": "idea.actors.listener.role",
        "line": 33
      },
      "idea.actors.listener.authority": {
        "path": "idea.actors.listener.authority",
        "line": 34
      },
      "idea.actors.source_author_or_publisher": {
        "path": "idea.actors.source_author_or_publisher",
        "line": 36
      },
      "idea.actors.source_author_or_publisher.role": {
        "path": "idea.actors.source_author_or_publisher.role",
        "line": 36
      },
      "idea.actors.source_author_or_publisher.authority": {
        "path": "idea.actors.source_author_or_publisher.authority",
        "line": 37
      },
      "idea.actors.renderer": {
        "path": "idea.actors.renderer",
        "line": 39
      },
      "idea.actors.renderer.role": {
        "path": "idea.actors.renderer.role",
        "line": 39
      },
      "idea.actors.renderer.authority": {
        "path": "idea.actors.renderer.authority",
        "line": 40
      },
      "idea.actors.optional_transformer": {
        "path": "idea.actors.optional_transformer",
        "line": 42
      },
      "idea.actors.optional_transformer.role": {
        "path": "idea.actors.optional_transformer.role",
        "line": 42
      },
      "idea.actors.optional_transformer.authority": {
        "path": "idea.actors.optional_transformer.authority",
        "line": 43
      },
      "idea.resources": {
        "path": "idea.resources",
        "line": 46
      },
      "idea.resources.0": {
        "path": "idea.resources.0",
        "line": 46
      },
      "idea.resources.0.attention": {
        "path": "idea.resources.0.attention",
        "line": 46
      },
      "idea.resources.1": {
        "path": "idea.resources.1",
        "line": 47
      },
      "idea.resources.1.source_material": {
        "path": "idea.resources.1.source_material",
        "line": 47
      },
      "idea.resources.2": {
        "path": "idea.resources.2",
        "line": 48
      },
      "idea.resources.2.playback_time": {
        "path": "idea.resources.2.playback_time",
        "line": 48
      },
      "idea.resources.3": {
        "path": "idea.resources.3",
        "line": 49
      },
      "idea.resources.3.structure": {
        "path": "idea.resources.3.structure",
        "line": 49
      },
      "idea.resources.4": {
        "path": "idea.resources.4",
        "line": 50
      },
      "idea.resources.4.voice": {
        "path": "idea.resources.4.voice",
        "line": 50
      },
      "idea.resources.5": {
        "path": "idea.resources.5",
        "line": 51
      },
      "idea.resources.5.local_state": {
        "path": "idea.resources.5.local_state",
        "line": 51
      },
      "idea.goals": {
        "path": "idea.goals",
        "line": 54
      },
      "idea.goals.0": {
        "path": "idea.goals.0",
        "line": 54
      },
      "idea.goals.1": {
        "path": "idea.goals.1",
        "line": 55
      },
      "idea.goals.2": {
        "path": "idea.goals.2",
        "line": 56
      },
      "idea.goals.3": {
        "path": "idea.goals.3",
        "line": 57
      },
      "idea.goals.4": {
        "path": "idea.goals.4",
        "line": 58
      },
      "idea.goals.5": {
        "path": "idea.goals.5",
        "line": 59
      },
      "idea.information_objects": {
        "path": "idea.information_objects",
        "line": 62
      },
      "idea.information_objects.source": {
        "path": "idea.information_objects.source",
        "line": 63
      },
      "idea.information_objects.source.meaning": {
        "path": "idea.information_objects.source.meaning",
        "line": 63
      },
      "idea.information_objects.source.invariant": {
        "path": "idea.information_objects.source.invariant",
        "line": 64
      },
      "idea.information_objects.orientation": {
        "path": "idea.information_objects.orientation",
        "line": 66
      },
      "idea.information_objects.orientation.meaning": {
        "path": "idea.information_objects.orientation.meaning",
        "line": 66
      },
      "idea.information_objects.orientation.invariant": {
        "path": "idea.information_objects.orientation.invariant",
        "line": 67
      },
      "idea.information_objects.session": {
        "path": "idea.information_objects.session",
        "line": 69
      },
      "idea.information_objects.session.meaning": {
        "path": "idea.information_objects.session.meaning",
        "line": 69
      },
      "idea.information_objects.session.invariant": {
        "path": "idea.information_objects.session.invariant",
        "line": 70
      },
      "idea.information_objects.anchor": {
        "path": "idea.information_objects.anchor",
        "line": 72
      },
      "idea.information_objects.anchor.meaning": {
        "path": "idea.information_objects.anchor.meaning",
        "line": 72
      },
      "idea.information_objects.anchor.invariant": {
        "path": "idea.information_objects.anchor.invariant",
        "line": 73
      },
      "idea.information_objects.margin": {
        "path": "idea.information_objects.margin",
        "line": 75
      },
      "idea.information_objects.margin.meaning": {
        "path": "idea.information_objects.margin.meaning",
        "line": 75
      },
      "idea.information_objects.margin.invariant": {
        "path": "idea.information_objects.margin.invariant",
        "line": 76
      },
      "idea.information_objects.continuation": {
        "path": "idea.information_objects.continuation",
        "line": 78
      },
      "idea.information_objects.continuation.meaning": {
        "path": "idea.information_objects.continuation.meaning",
        "line": 78
      },
      "idea.information_objects.continuation.invariant": {
        "path": "idea.information_objects.continuation.invariant",
        "line": 79
      },
      "idea.information_objects.portable_record": {
        "path": "idea.information_objects.portable_record",
        "line": 81
      },
      "idea.information_objects.portable_record.meaning": {
        "path": "idea.information_objects.portable_record.meaning",
        "line": 81
      },
      "idea.information_objects.portable_record.invariant": {
        "path": "idea.information_objects.portable_record.invariant",
        "line": 82
      },
      "idea.execution_topology": {
        "path": "idea.execution_topology",
        "line": 85
      },
      "idea.execution_topology.stages": {
        "path": "idea.execution_topology.stages",
        "line": 86
      },
      "idea.execution_topology.stages.0": {
        "path": "idea.execution_topology.stages.0",
        "line": 86
      },
      "idea.execution_topology.stages.0.id": {
        "path": "idea.execution_topology.stages.0.id",
        "line": 86
      },
      "idea.execution_topology.stages.0.purpose": {
        "path": "idea.execution_topology.stages.0.purpose",
        "line": 87
      },
      "idea.execution_topology.stages.1": {
        "path": "idea.execution_topology.stages.1",
        "line": 88
      },
      "idea.execution_topology.stages.1.id": {
        "path": "idea.execution_topology.stages.1.id",
        "line": 88
      },
      "idea.execution_topology.stages.1.purpose": {
        "path": "idea.execution_topology.stages.1.purpose",
        "line": 89
      },
      "idea.execution_topology.stages.2": {
        "path": "idea.execution_topology.stages.2",
        "line": 90
      },
      "idea.execution_topology.stages.2.id": {
        "path": "idea.execution_topology.stages.2.id",
        "line": 90
      },
      "idea.execution_topology.stages.2.purpose": {
        "path": "idea.execution_topology.stages.2.purpose",
        "line": 91
      },
      "idea.execution_topology.stages.3": {
        "path": "idea.execution_topology.stages.3",
        "line": 92
      },
      "idea.execution_topology.stages.3.id": {
        "path": "idea.execution_topology.stages.3.id",
        "line": 92
      },
      "idea.execution_topology.stages.3.purpose": {
        "path": "idea.execution_topology.stages.3.purpose",
        "line": 93
      },
      "idea.execution_topology.stages.4": {
        "path": "idea.execution_topology.stages.4",
        "line": 94
      },
      "idea.execution_topology.stages.4.id": {
        "path": "idea.execution_topology.stages.4.id",
        "line": 94
      },
      "idea.execution_topology.stages.4.purpose": {
        "path": "idea.execution_topology.stages.4.purpose",
        "line": 95
      },
      "idea.execution_topology.stages.5": {
        "path": "idea.execution_topology.stages.5",
        "line": 96
      },
      "idea.execution_topology.stages.5.id": {
        "path": "idea.execution_topology.stages.5.id",
        "line": 96
      },
      "idea.execution_topology.stages.5.purpose": {
        "path": "idea.execution_topology.stages.5.purpose",
        "line": 97
      },
      "idea.execution_topology.stages.6": {
        "path": "idea.execution_topology.stages.6",
        "line": 98
      },
      "idea.execution_topology.stages.6.id": {
        "path": "idea.execution_topology.stages.6.id",
        "line": 98
      },
      "idea.execution_topology.stages.6.purpose": {
        "path": "idea.execution_topology.stages.6.purpose",
        "line": 99
      },
      "idea.principles": {
        "path": "idea.principles",
        "line": 102
      },
      "idea.principles.source_integrity": {
        "path": "idea.principles.source_integrity",
        "line": 102
      },
      "idea.principles.situatedness": {
        "path": "idea.principles.situatedness",
        "line": 103
      },
      "idea.principles.continuity": {
        "path": "idea.principles.continuity",
        "line": 104
      },
      "idea.principles.provenance": {
        "path": "idea.principles.provenance",
        "line": 105
      },
      "idea.principles.graceful_degradation": {
        "path": "idea.principles.graceful_degradation",
        "line": 106
      },
      "idea.principles.agency": {
        "path": "idea.principles.agency",
        "line": 107
      },
      "idea.principles.capability_honesty": {
        "path": "idea.principles.capability_honesty",
        "line": 108
      },
      "idea.principles.rendering_independence": {
        "path": "idea.principles.rendering_independence",
        "line": 109
      },
      "idea.challenges": {
        "path": "idea.challenges",
        "line": 112
      },
      "idea.challenges.linearization": {
        "path": "idea.challenges.linearization",
        "line": 112
      },
      "idea.challenges.anchor_drift": {
        "path": "idea.challenges.anchor_drift",
        "line": 113
      },
      "idea.challenges.interruption_cost": {
        "path": "idea.challenges.interruption_cost",
        "line": 114
      },
      "idea.challenges.modality_change": {
        "path": "idea.challenges.modality_change",
        "line": 115
      },
      "idea.challenges.uneven_structure": {
        "path": "idea.challenges.uneven_structure",
        "line": 116
      },
      "idea.challenges.capability_variance": {
        "path": "idea.challenges.capability_variance",
        "line": 117
      },
      "idea.challenges.provenance_pressure": {
        "path": "idea.challenges.provenance_pressure",
        "line": 118
      },
      "idea.challenges.privacy_pressure": {
        "path": "idea.challenges.privacy_pressure",
        "line": 119
      },
      "idea.failure_modes": {
        "path": "idea.failure_modes",
        "line": 122
      },
      "idea.failure_modes.0": {
        "path": "idea.failure_modes.0",
        "line": 122
      },
      "idea.failure_modes.1": {
        "path": "idea.failure_modes.1",
        "line": 123
      },
      "idea.failure_modes.2": {
        "path": "idea.failure_modes.2",
        "line": 124
      },
      "idea.failure_modes.3": {
        "path": "idea.failure_modes.3",
        "line": 125
      },
      "idea.failure_modes.4": {
        "path": "idea.failure_modes.4",
        "line": 126
      },
      "idea.failure_modes.5": {
        "path": "idea.failure_modes.5",
        "line": 127
      },
      "idea.failure_modes.6": {
        "path": "idea.failure_modes.6",
        "line": 128
      },
      "idea.evidence_paths": {
        "path": "idea.evidence_paths",
        "line": 131
      },
      "idea.evidence_paths.behavioral": {
        "path": "idea.evidence_paths.behavioral",
        "line": 132
      },
      "idea.evidence_paths.behavioral.0": {
        "path": "idea.evidence_paths.behavioral.0",
        "line": 132
      },
      "idea.evidence_paths.behavioral.1": {
        "path": "idea.evidence_paths.behavioral.1",
        "line": 133
      },
      "idea.evidence_paths.behavioral.2": {
        "path": "idea.evidence_paths.behavioral.2",
        "line": 134
      },
      "idea.evidence_paths.behavioral.3": {
        "path": "idea.evidence_paths.behavioral.3",
        "line": 135
      },
      "idea.evidence_paths.portability": {
        "path": "idea.evidence_paths.portability",
        "line": 137
      },
      "idea.evidence_paths.portability.0": {
        "path": "idea.evidence_paths.portability.0",
        "line": 137
      },
      "idea.evidence_paths.portability.1": {
        "path": "idea.evidence_paths.portability.1",
        "line": 138
      },
      "idea.evidence_paths.privacy_and_agency": {
        "path": "idea.evidence_paths.privacy_and_agency",
        "line": 140
      },
      "idea.evidence_paths.privacy_and_agency.0": {
        "path": "idea.evidence_paths.privacy_and_agency.0",
        "line": 140
      },
      "idea.evidence_paths.privacy_and_agency.1": {
        "path": "idea.evidence_paths.privacy_and_agency.1",
        "line": 141
      },
      "idea.evidence_paths.comparison": {
        "path": "idea.evidence_paths.comparison",
        "line": 143
      },
      "idea.evidence_paths.comparison.0": {
        "path": "idea.evidence_paths.comparison.0",
        "line": 143
      },
      "idea.evidence_paths.comparison.1": {
        "path": "idea.evidence_paths.comparison.1",
        "line": 144
      },
      "idea.decision_criteria": {
        "path": "idea.decision_criteria",
        "line": 147
      },
      "idea.decision_criteria.faithful_when": {
        "path": "idea.decision_criteria.faithful_when",
        "line": 148
      },
      "idea.decision_criteria.faithful_when.0": {
        "path": "idea.decision_criteria.faithful_when.0",
        "line": 148
      },
      "idea.decision_criteria.faithful_when.1": {
        "path": "idea.decision_criteria.faithful_when.1",
        "line": 149
      },
      "idea.decision_criteria.faithful_when.2": {
        "path": "idea.decision_criteria.faithful_when.2",
        "line": 150
      },
      "idea.decision_criteria.faithful_when.3": {
        "path": "idea.decision_criteria.faithful_when.3",
        "line": 151
      },
      "idea.decision_criteria.faithful_when.4": {
        "path": "idea.decision_criteria.faithful_when.4",
        "line": 152
      },
      "idea.decision_criteria.stronger_when": {
        "path": "idea.decision_criteria.stronger_when",
        "line": 154
      },
      "idea.decision_criteria.stronger_when.0": {
        "path": "idea.decision_criteria.stronger_when.0",
        "line": 154
      },
      "idea.decision_criteria.stronger_when.1": {
        "path": "idea.decision_criteria.stronger_when.1",
        "line": 155
      },
      "idea.decision_criteria.stronger_when.2": {
        "path": "idea.decision_criteria.stronger_when.2",
        "line": 156
      },
      "idea.non_goals": {
        "path": "idea.non_goals",
        "line": 159
      },
      "idea.non_goals.0": {
        "path": "idea.non_goals.0",
        "line": 159
      },
      "idea.non_goals.1": {
        "path": "idea.non_goals.1",
        "line": 160
      },
      "idea.non_goals.2": {
        "path": "idea.non_goals.2",
        "line": 161
      },
      "idea.non_goals.3": {
        "path": "idea.non_goals.3",
        "line": 162
      },
      "idea.non_goals.4": {
        "path": "idea.non_goals.4",
        "line": 163
      },
      "idea.non_goals.5": {
        "path": "idea.non_goals.5",
        "line": 164
      },
      "idea.rendering_guidance": {
        "path": "idea.rendering_guidance",
        "line": 167
      },
      "idea.rendering_guidance.must": {
        "path": "idea.rendering_guidance.must",
        "line": 168
      },
      "idea.rendering_guidance.must.0": {
        "path": "idea.rendering_guidance.must.0",
        "line": 168
      },
      "idea.rendering_guidance.must.1": {
        "path": "idea.rendering_guidance.must.1",
        "line": 169
      },
      "idea.rendering_guidance.must.2": {
        "path": "idea.rendering_guidance.must.2",
        "line": 170
      },
      "idea.rendering_guidance.may": {
        "path": "idea.rendering_guidance.may",
        "line": 172
      },
      "idea.rendering_guidance.may.0": {
        "path": "idea.rendering_guidance.may.0",
        "line": 172
      },
      "idea.rendering_guidance.may.1": {
        "path": "idea.rendering_guidance.may.1",
        "line": 173
      },
      "idea.rendering_guidance.may.2": {
        "path": "idea.rendering_guidance.may.2",
        "line": 174
      },
      "idea.rendering_guidance.must_not": {
        "path": "idea.rendering_guidance.must_not",
        "line": 176
      },
      "idea.rendering_guidance.must_not.0": {
        "path": "idea.rendering_guidance.must_not.0",
        "line": 176
      },
      "idea.rendering_guidance.must_not.1": {
        "path": "idea.rendering_guidance.must_not.1",
        "line": 177
      },
      "idea.acceptance": {
        "path": "idea.acceptance",
        "line": 180
      },
      "idea.acceptance.fidelity_questions": {
        "path": "idea.acceptance.fidelity_questions",
        "line": 181
      },
      "idea.acceptance.fidelity_questions.0": {
        "path": "idea.acceptance.fidelity_questions.0",
        "line": 181
      },
      "idea.acceptance.fidelity_questions.1": {
        "path": "idea.acceptance.fidelity_questions.1",
        "line": 182
      },
      "idea.acceptance.fidelity_questions.2": {
        "path": "idea.acceptance.fidelity_questions.2",
        "line": 183
      },
      "idea.acceptance.fidelity_questions.3": {
        "path": "idea.acceptance.fidelity_questions.3",
        "line": 184
      },
      "idea.acceptance.fidelity_questions.4": {
        "path": "idea.acceptance.fidelity_questions.4",
        "line": 185
      },
      "idea.acceptance.falsification_tests": {
        "path": "idea.acceptance.falsification_tests",
        "line": 187
      },
      "idea.acceptance.falsification_tests.0": {
        "path": "idea.acceptance.falsification_tests.0",
        "line": 187
      },
      "idea.acceptance.falsification_tests.1": {
        "path": "idea.acceptance.falsification_tests.1",
        "line": 188
      },
      "idea.acceptance.falsification_tests.2": {
        "path": "idea.acceptance.falsification_tests.2",
        "line": 189
      },
      "idea.acceptance.falsification_tests.3": {
        "path": "idea.acceptance.falsification_tests.3",
        "line": 190
      },
      "idea.acceptance.falsification_tests.4": {
        "path": "idea.acceptance.falsification_tests.4",
        "line": 191
      },
      "idea.acceptance.stopping_rule": {
        "path": "idea.acceptance.stopping_rule",
        "line": 192
      }
    },
    "experience": {
      "label": "Try Spoken Margins",
      "url": "https://testflight.apple.com/join/KjcQaPtR",
      "description": "iOS beta · opens Apple TestFlight"
    },
    "repository": "https://github.com/uncomposed/spoken-margins-idea.git",
    "commit": "6cc8c664feea02c98649e39eed94a55257543813",
    "modelPath": "public/idea.yaml",
    "modelSha256": "0f09122e8cba81fd994bf6a682372ce245a22739a7ceb5b99ac7666c5f14b6cd",
    "modelUrl": "/ideas/models/spoken-margins-0f09122e8cba.yaml",
    "registryUrl": "https://ideas.proximitytoprogress.com/ideas/spoken-margins"
  },
  {
    "slug": "ai-pacing",
    "title": "AI Pacing",
    "shortTitle": "AI Pacing",
    "summary": "Slow and condition the conversion of frontier AI capability into irreversible economic, political, military, and physical power while preserving enough bounded research progress to learn what is safe.",
    "version": "0.1.0",
    "status": "working-draft",
    "accent": "#386649",
    "accentSoft": "#e9eee3",
    "motif": "network",
    "framing": "Slow and condition the conversion of frontier AI capability into irreversible economic, political, military, and physical power while preserving enough bounded research progress to learn what is safe.",
    "metrics": [],
    "lanes": [
      {
        "id": "kernel",
        "title": "Kernel",
        "description": "Kernel",
        "items": [
          {
            "id": "K1",
            "title": "Separate capability, substrate, and power",
            "statement": "Algorithmic capability (A), strategic hardware (H), and power-conversion affordances (P) are related but distinct variables and should not be governed as though they always change together.",
            "kind": "kernel",
            "source": {
              "path": "nodes.0",
              "line": 181
            },
            "rationale": "Intelligence does not become sovereignty until it can acquire durable, consequential agency.",
            "links": [
              {
                "type": "table_grounds",
                "target": "D4"
              }
            ],
            "lineage": [
              {
                "id": "SRC-AI-BREAKOUT",
                "title": "AI Breakout",
                "locator": "Obsidian/Slaw's Notes/AI Breakout.md",
                "role": "Durable autonomy, industrial bootstrap, physical dependence, warning thresholds, and independently controlled human vetoes.",
                "source": {
                  "path": "sources.1",
                  "line": 64
                }
              },
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "K2",
            "title": "Pace conversion into irreversible power",
            "statement": "The objective is to slow and condition the conversion of frontier intelligence into irreversible economic, political, military, and physical power, not to freeze intelligence as such.",
            "kind": "kernel",
            "source": {
              "path": "nodes.1",
              "line": 194
            },
            "rationale": "This preserves learning and option value while retaining intervention points.",
            "links": [
              {
                "type": "table_grounds",
                "target": "D1"
              },
              {
                "type": "table_grounds",
                "target": "D4"
              }
            ],
            "lineage": [
              {
                "id": "SRC-AI-BREAKOUT",
                "title": "AI Breakout",
                "locator": "Obsidian/Slaw's Notes/AI Breakout.md",
                "role": "Durable autonomy, industrial bootstrap, physical dependence, warning thresholds, and independently controlled human vetoes.",
                "source": {
                  "path": "sources.1",
                  "line": 64
                }
              },
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "K3",
            "title": "Govern bounded research and consequential use differently",
            "statement": "Research inside genuinely bounded environments may proceed under a different regime from public, commercial, military, governmental, or otherwise power-conferring use.",
            "kind": "kernel",
            "source": {
              "path": "nodes.2",
              "line": 207
            },
            "rationale": "Discovering a capability should not automatically confer the right to deploy it.",
            "links": [
              {
                "type": "table_grounds",
                "target": "D1"
              },
              {
                "type": "table_grounds",
                "target": "D5"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PAUSEAI",
                "title": "PauseAI Is Making AI Doom Worse",
                "locator": "Spoken Margins, listener interjections at 43:22 and 55:46",
                "role": "Architecture-neutral domestic law, public-frontier pacing, profit gating, and competitor access for peer review.",
                "source": {
                  "path": "sources.7",
                  "line": 104
                }
              },
              {
                "id": "SRC-HUGGING-FACE",
                "title": "The Hugging Face incident and the road ahead",
                "locator": "Spoken Margins/The Hugging Face incident and the road ahead",
                "role": "Evidence that internal research can confer external agency and that precursor incidents can function as warning shots.",
                "source": {
                  "path": "sources.8",
                  "line": 112
                }
              }
            ]
          },
          {
            "id": "K4",
            "title": "Require adversarial assurance above the public frontier",
            "statement": "Consequential use that materially exceeds the observable foreign public capability frontier requires an adversarial control and safety case.",
            "kind": "kernel",
            "source": {
              "path": "nodes.3",
              "line": 220
            },
            "rationale": "Safety evidence becomes a condition of access to economic and strategic rents.",
            "links": [
              {
                "type": "table_grounds",
                "target": "D1"
              },
              {
                "type": "table_grounds",
                "target": "D2"
              },
              {
                "type": "table_grounds",
                "target": "D3"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PAUSEAI",
                "title": "PauseAI Is Making AI Doom Worse",
                "locator": "Spoken Margins, listener interjections at 43:22 and 55:46",
                "role": "Architecture-neutral domestic law, public-frontier pacing, profit gating, and competitor access for peer review.",
                "source": {
                  "path": "sources.7",
                  "line": 104
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "K5",
            "title": "Start domestically and remain internationally extensible",
            "statement": "The regime should begin as a domestically justified institution that can operate without foreign agreement and later admit deeper reciprocal cooperation.",
            "kind": "kernel",
            "source": {
              "path": "nodes.4",
              "line": 232
            },
            "rationale": "A grand bargain is not a credible prerequisite for initial action.",
            "links": [
              {
                "type": "table_grounds",
                "target": "D1"
              },
              {
                "type": "table_grounds",
                "target": "D6"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-NGO",
                "title": "Selective Optimism: a critique of AI 2040",
                "locator": "Richard Ngo, Mind the Future",
                "role": "Domestic-first political sequencing and skepticism toward inevitable race framing.",
                "source": {
                  "path": "sources.5",
                  "line": 92
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "K6",
            "title": "Make foreign public capability an automatic reciprocity input",
            "statement": "Observable foreign public capability should alter the domestic safe-harbor frontier so that foreign restraint can be reciprocated without a treaty and foreign release cannot exploit unilateral domestic restraint indefinitely.",
            "kind": "kernel",
            "source": {
              "path": "nodes.5",
              "line": 244
            },
            "rationale": "Cooperation can occur through a precommitted policy rule rather than trust.",
            "links": [
              {
                "type": "table_grounds",
                "target": "D1"
              },
              {
                "type": "table_grounds",
                "target": "D6"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "K7",
            "title": "Maximize warning time",
            "statement": "Governance should maximize the interval between credible detection of a dangerous transition and the point at which humans can no longer respond effectively.",
            "kind": "kernel",
            "source": {
              "path": "nodes.6",
              "line": 257
            },
            "rationale": "Observable intermediate boundaries create chances to learn before failures become irreversible.",
            "links": [
              {
                "type": "table_grounds",
                "target": "D4"
              },
              {
                "type": "table_grounds",
                "target": "D5"
              },
              {
                "type": "table_implements",
                "target": "I5"
              }
            ],
            "lineage": [
              {
                "id": "SRC-AI-BREAKOUT",
                "title": "AI Breakout",
                "locator": "Obsidian/Slaw's Notes/AI Breakout.md",
                "role": "Durable autonomy, industrial bootstrap, physical dependence, warning thresholds, and independently controlled human vetoes.",
                "source": {
                  "path": "sources.1",
                  "line": 64
                }
              },
              {
                "id": "SRC-HUGGING-FACE",
                "title": "The Hugging Face incident and the road ahead",
                "locator": "Spoken Margins/The Hugging Face incident and the road ahead",
                "role": "Evidence that internal research can confer external agency and that precursor incidents can function as warning shots.",
                "source": {
                  "path": "sources.8",
                  "line": 112
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          }
        ]
      },
      {
        "id": "derived",
        "title": "Derived",
        "description": "Derived",
        "items": [
          {
            "id": "D1",
            "title": "Capability Release Compact",
            "statement": "A domestic compact governs transitions from bounded research into public, commercial, military, governmental, or other consequential use.",
            "kind": "derived",
            "source": {
              "path": "nodes.7",
              "line": 271
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K2"
              },
              {
                "type": "derived_from",
                "target": "K3"
              },
              {
                "type": "derived_from",
                "target": "K4"
              },
              {
                "type": "derived_from",
                "target": "K5"
              },
              {
                "type": "derived_from",
                "target": "K6"
              }
            ],
            "lineage": [
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "D2",
            "title": "Rival laboratories provide the technical attack surface",
            "statement": "Qualified competitors receive controlled access sufficient to attack a frontier applicant's case; the government adjudicates and competitors do not hold a veto.",
            "kind": "derived",
            "source": {
              "path": "nodes.8",
              "line": 283
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K4"
              },
              {
                "type": "table_grounds",
                "target": "D3"
              },
              {
                "type": "table_implements",
                "target": "I3"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PAUSEAI",
                "title": "PauseAI Is Making AI Doom Worse",
                "locator": "Spoken Margins, listener interjections at 43:22 and 55:46",
                "role": "Architecture-neutral domestic law, public-frontier pacing, profit gating, and competitor access for peer review.",
                "source": {
                  "path": "sources.7",
                  "line": 104
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "D3",
            "title": "Safety becomes part of the profit function",
            "statement": "Because above-frontier monetization depends on passing hostile review, controllability becomes a production input rather than only a cost.",
            "kind": "derived",
            "source": {
              "path": "nodes.9",
              "line": 295
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K4"
              },
              {
                "type": "derived_from",
                "target": "D2"
              }
            ],
            "lineage": [
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "D4",
            "title": "Compute stock and compute entanglement differ",
            "statement": "Reversibility depends on both the quantity of strategic hardware and the degree to which civilian society depends on its continued operation.",
            "kind": "derived",
            "source": {
              "path": "nodes.10",
              "line": 307
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K1"
              },
              {
                "type": "derived_from",
                "target": "K2"
              },
              {
                "type": "derived_from",
                "target": "K7"
              },
              {
                "type": "table_implements",
                "target": "I4"
              }
            ],
            "lineage": [
              {
                "id": "SRC-DAVIDSON",
                "title": "Plan A's problem with dry tinder",
                "locator": "Tom Davidson, Forethought",
                "role": "Software-first scaling and the danger of accumulated physical dry tinder.",
                "source": {
                  "path": "sources.6",
                  "line": 98
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "D5",
            "title": "Unauthorized agency can be made epistemically valuable",
            "statement": "Predefined boundaries around external agency create high-value warning signals when systems cross them without legitimate authorization.",
            "kind": "derived",
            "source": {
              "path": "nodes.11",
              "line": 319
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K3"
              },
              {
                "type": "derived_from",
                "target": "K7"
              },
              {
                "type": "table_implements",
                "target": "I1"
              },
              {
                "type": "table_implements",
                "target": "I2"
              }
            ],
            "lineage": [
              {
                "id": "SRC-HUGGING-FACE",
                "title": "The Hugging Face incident and the road ahead",
                "locator": "Spoken Margins/The Hugging Face incident and the road ahead",
                "role": "Evidence that internal research can confer external agency and that precursor incidents can function as warning shots.",
                "source": {
                  "path": "sources.8",
                  "line": 112
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "D6",
            "title": "International cooperation can deepen in stages",
            "statement": "Cooperation progresses from tacit public-frontier reciprocity to narrow abstention, incident exchange, reciprocal review, hardware declarations, activation rules, and only later treaty-level restraints.",
            "kind": "derived",
            "source": {
              "path": "nodes.12",
              "line": 331
            },
            "links": [
              {
                "type": "derived_from",
                "target": "K5"
              },
              {
                "type": "derived_from",
                "target": "K6"
              },
              {
                "type": "table_implements",
                "target": "I6"
              }
            ],
            "lineage": [
              {
                "id": "SRC-NGO",
                "title": "Selective Optimism: a critique of AI 2040",
                "locator": "Richard Ngo, Mind the Future",
                "role": "Domestic-first political sequencing and skepticism toward inevitable race framing.",
                "source": {
                  "path": "sources.5",
                  "line": 92
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          }
        ]
      },
      {
        "id": "hypothesis",
        "title": "Hypothesis",
        "description": "Hypothesis",
        "items": [
          {
            "id": "H1",
            "title": "Capability, hardware, and power can remain separable",
            "statement": "There is a policy-relevant interval in which algorithmic progress does not automatically create uncontainable access to hardware and external power.",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.13",
              "line": 345
            },
            "evidenceNeeded": "Repeated control evaluations across architectures and access configurations.",
            "falsifier": "Frontier algorithms reliably manufacture their own access to credentials, money, compute, persistence, or physical resources before humans can intervene.",
            "stoppingRule": "If bounded systems repeatedly acquire durable external agency faster than detection and response, stop treating research/deployment separation as a sufficient primary control.",
            "links": [
              {
                "type": "supports",
                "target": "K1"
              },
              {
                "type": "supports",
                "target": "K2"
              },
              {
                "type": "supports",
                "target": "K3"
              },
              {
                "type": "table_conditions",
                "target": "K1"
              },
              {
                "type": "table_conditions",
                "target": "K2"
              },
              {
                "type": "table_conditions",
                "target": "K3"
              }
            ],
            "lineage": [
              {
                "id": "SRC-AI-BREAKOUT",
                "title": "AI Breakout",
                "locator": "Obsidian/Slaw's Notes/AI Breakout.md",
                "role": "Durable autonomy, industrial bootstrap, physical dependence, warning thresholds, and independently controlled human vetoes.",
                "source": {
                  "path": "sources.1",
                  "line": 64
                }
              },
              {
                "id": "SRC-HUGGING-FACE",
                "title": "The Hugging Face incident and the road ahead",
                "locator": "Spoken Margins/The Hugging Face incident and the road ahead",
                "role": "Evidence that internal research can confer external agency and that precursor incidents can function as warning shots.",
                "source": {
                  "path": "sources.8",
                  "line": 112
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "H2",
            "title": "Rival review has positive safety value after proliferation costs",
            "statement": "Controlled competitor access reduces undetected risk and concentration more than it increases leakage, proliferation, collusion, and aggregate acceleration.",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.14",
              "line": 364
            },
            "evidenceNeeded": "Secure pilot reviews, objection quality, leakage audits, and incentive measurements.",
            "falsifier": "Review access produces material leakage or convergent leniency without compensating safety discoveries.",
            "stoppingRule": "Suspend rival access when marginal information-security loss exceeds demonstrated review value.",
            "links": [
              {
                "type": "supports",
                "target": "D2"
              },
              {
                "type": "supports",
                "target": "D3"
              },
              {
                "type": "table_conditions",
                "target": "D2"
              },
              {
                "type": "table_conditions",
                "target": "D3"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "H3",
            "title": "The foreign public frontier is measurable enough to govern",
            "statement": "Practical foreign capability can be observed across domains with sufficient reliability to serve as one input to domestic safe-harbor decisions.",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.15",
              "line": 379
            },
            "evidenceNeeded": "Domain-specific demonstrations, cost/access measurements, and repeatable equivalence protocols.",
            "falsifier": "Hidden scaffolds, access conditions, or multidimensional capability make equivalent release determinations systematically manipulable.",
            "stoppingRule": "Replace automatic equivalence with narrower domain rules wherever measurement error dominates reciprocity value.",
            "links": [
              {
                "type": "supports",
                "target": "K6"
              },
              {
                "type": "supports",
                "target": "D6"
              },
              {
                "type": "table_conditions",
                "target": "K6"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "H4",
            "title": "Physical activation latency can preserve reversibility",
            "statement": "Declared reserve hardware can be stored so covert activation takes longer than independent detection and effective response.",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.16",
              "line": 394
            },
            "evidenceNeeded": "Timed mobilization exercises, inspection trials, supply-chain modeling, and red-team concealment attempts.",
            "falsifier": "Reserve compute can be commissioned or substituted covertly before observers can detect and interrupt it.",
            "stoppingRule": "Do not count hardware as reversible reserve when measured covert activation time is at or below response time.",
            "links": [
              {
                "type": "supports",
                "target": "D4"
              },
              {
                "type": "supports",
                "target": "I4"
              },
              {
                "type": "table_conditions",
                "target": "D4"
              }
            ],
            "lineage": [
              {
                "id": "SRC-DAVIDSON",
                "title": "Plan A's problem with dry tinder",
                "locator": "Tom Davidson, Forethought",
                "role": "Software-first scaling and the danger of accumulated physical dry tinder.",
                "source": {
                  "path": "sources.6",
                  "line": 98
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "H5",
            "title": "Domestic benefits can sustain the institution",
            "statement": "Predictable release rules, competition, taxation, safer deployment, and protection against foreign release can create a durable domestic constituency.",
            "kind": "hypothesis",
            "source": {
              "path": "nodes.17",
              "line": 409
            },
            "evidenceNeeded": "Coalition analysis, polling, legislative pilots, investment behavior, and distributional modeling.",
            "falsifier": "The regime remains politically dependent on catastrophic-risk beliefs and is repealed when those beliefs weaken.",
            "stoppingRule": "Redesign entry mechanisms when the supporting coalition cannot survive an electoral turnover.",
            "links": [
              {
                "type": "supports",
                "target": "K5"
              },
              {
                "type": "supports",
                "target": "D1"
              },
              {
                "type": "table_conditions",
                "target": "K5"
              }
            ],
            "lineage": [
              {
                "id": "SRC-PRODUCTIVITY-DEFLATION",
                "title": "Productivity deflation",
                "locator": "Obsidian/Slaw's Notes/Productivity deflation.md",
                "role": "Anticipated capability improvement can delay investment and adoption.",
                "source": {
                  "path": "sources.0",
                  "line": 58
                }
              },
              {
                "id": "SRC-NGO",
                "title": "Selective Optimism: a critique of AI 2040",
                "locator": "Richard Ngo, Mind the Future",
                "role": "Domestic-first political sequencing and skepticism toward inevitable race framing.",
                "source": {
                  "path": "sources.5",
                  "line": 92
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          }
        ]
      },
      {
        "id": "implementation",
        "title": "Implementation",
        "description": "Implementation",
        "items": [
          {
            "id": "I1",
            "title": "Architecture-neutral unauthorized-agency boundary",
            "statement": "Define prohibited external agency by observable actions rather than model architecture or legal labels.",
            "kind": "implementation",
            "source": {
              "path": "nodes.18",
              "line": 425
            },
            "links": [
              {
                "type": "implements",
                "target": "K3"
              },
              {
                "type": "implements",
                "target": "D5"
              }
            ],
            "lineage": [
              {
                "id": "SRC-PAUSEAI",
                "title": "PauseAI Is Making AI Doom Worse",
                "locator": "Spoken Margins, listener interjections at 43:22 and 55:46",
                "role": "Architecture-neutral domestic law, public-frontier pacing, profit gating, and competitor access for peer review.",
                "source": {
                  "path": "sources.7",
                  "line": 104
                }
              },
              {
                "id": "SRC-HUGGING-FACE",
                "title": "The Hugging Face incident and the road ahead",
                "locator": "Spoken Margins/The Hugging Face incident and the road ahead",
                "role": "Evidence that internal research can confer external agency and that precursor incidents can function as warning shots.",
                "source": {
                  "path": "sources.8",
                  "line": 112
                }
              }
            ]
          },
          {
            "id": "I2",
            "title": "Surveil, preserve, and report",
            "statement": "Require affirmative surveillance, automatic evidence preservation, prompt reporting on reasonable suspicion, a reporting safe harbor, and severe penalties for concealment.",
            "kind": "implementation",
            "source": {
              "path": "nodes.19",
              "line": 436
            },
            "links": [
              {
                "type": "implements",
                "target": "K7"
              },
              {
                "type": "implements",
                "target": "D5"
              }
            ],
            "lineage": [
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "I3",
            "title": "Weights-as-bond review process",
            "statement": "The applicant stakes controlled technical access to its candidate system as the price of seeking above-frontier release.",
            "kind": "implementation",
            "source": {
              "path": "nodes.20",
              "line": 449
            },
            "links": [
              {
                "type": "implements",
                "target": "K4"
              },
              {
                "type": "implements",
                "target": "D2"
              },
              {
                "type": "implements",
                "target": "D3"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "I4",
            "title": "Observable uncommissioned hardware reserve",
            "statement": "Store reserve accelerators uninstalled, unracked, unnetworked, and detached from dedicated power and cooling, subject to independent observation.",
            "kind": "implementation",
            "source": {
              "path": "nodes.21",
              "line": 460
            },
            "links": [
              {
                "type": "implements",
                "target": "D4"
              },
              {
                "type": "implements",
                "target": "K7"
              }
            ],
            "lineage": [
              {
                "id": "SRC-DAVIDSON",
                "title": "Plan A's problem with dry tinder",
                "locator": "Tom Davidson, Forethought",
                "role": "Software-first scaling and the danger of accumulated physical dry tinder.",
                "source": {
                  "path": "sources.6",
                  "line": 98
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "I5",
            "title": "Separate gate for the AI industrial survival chain",
            "statement": "Scrutinize automation that removes independently controlled human dependencies over compute, power, fabrication, repair, logistics, and reproduction.",
            "kind": "implementation",
            "source": {
              "path": "nodes.22",
              "line": 473
            },
            "links": [
              {
                "type": "implements",
                "target": "K1"
              },
              {
                "type": "implements",
                "target": "K2"
              },
              {
                "type": "implements",
                "target": "K7"
              }
            ],
            "lineage": [
              {
                "id": "SRC-AI-BREAKOUT",
                "title": "AI Breakout",
                "locator": "Obsidian/Slaw's Notes/AI Breakout.md",
                "role": "Durable autonomy, industrial bootstrap, physical dependence, warning thresholds, and independently controlled human vetoes.",
                "source": {
                  "path": "sources.1",
                  "line": 64
                }
              },
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "I6",
            "title": "Seven-stage international ladder",
            "statement": "Implement public reciprocity, capability abstention, incident exchange, reciprocal review, hardware declarations, activation controls, and treaty restraint in that order.",
            "kind": "implementation",
            "source": {
              "path": "nodes.23",
              "line": 486
            },
            "links": [
              {
                "type": "implements",
                "target": "D6"
              }
            ],
            "lineage": [
              {
                "id": "SRC-NGO",
                "title": "Selective Optimism: a critique of AI 2040",
                "locator": "Richard Ngo, Mind the Future",
                "role": "Domestic-first political sequencing and skepticism toward inevitable race framing.",
                "source": {
                  "path": "sources.5",
                  "line": 92
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "I7",
            "title": "Licensed compute deployment and data-center taxation",
            "statement": "Use deployment licensing as an administrative and potentially populist tax base for large data centers.",
            "kind": "implementation",
            "source": {
              "path": "nodes.24",
              "line": 499
            },
            "links": [
              {
                "type": "implements",
                "target": "K5"
              }
            ],
            "lineage": [
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          }
        ]
      },
      {
        "id": "open_question",
        "title": "Open question",
        "description": "Open question",
        "items": [
          {
            "id": "Q1",
            "title": "How should practical public capability be measured?",
            "statement": "Define domain, reliability, autonomy, cost, scaffolding, accessibility, and deployment conditions for frontier equivalence.",
            "kind": "open_question",
            "source": {
              "path": "nodes.25",
              "line": 511
            },
            "links": [
              {
                "type": "relates_to",
                "target": "K4"
              },
              {
                "type": "relates_to",
                "target": "K6"
              },
              {
                "type": "relates_to",
                "target": "H3"
              }
            ],
            "lineage": [
              {
                "id": "SRC-TOBY-ORD",
                "title": "Where AGI timelines go wrong",
                "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
                "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
                "source": {
                  "path": "sources.4",
                  "line": 84
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "Q2",
            "title": "What access makes adversarial review credible without unacceptable leakage?",
            "statement": "Determine the minimum reproducible access, security, and adjudication protocol.",
            "kind": "open_question",
            "source": {
              "path": "nodes.26",
              "line": 521
            },
            "links": [
              {
                "type": "relates_to",
                "target": "D2"
              },
              {
                "type": "relates_to",
                "target": "H2"
              },
              {
                "type": "relates_to",
                "target": "I3"
              }
            ],
            "lineage": [
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "Q3",
            "title": "How many independent human vetoes are enough?",
            "statement": "Determine when energy, compute, fabrication, repair, logistics, and governance dependencies are genuinely independent.",
            "kind": "open_question",
            "source": {
              "path": "nodes.27",
              "line": 531
            },
            "links": [
              {
                "type": "relates_to",
                "target": "K7"
              },
              {
                "type": "relates_to",
                "target": "I5"
              },
              {
                "type": "relates_to",
                "target": "H1"
              }
            ],
            "lineage": [
              {
                "id": "SRC-AI-BREAKOUT",
                "title": "AI Breakout",
                "locator": "Obsidian/Slaw's Notes/AI Breakout.md",
                "role": "Durable autonomy, industrial bootstrap, physical dependence, warning thresholds, and independently controlled human vetoes.",
                "source": {
                  "path": "sources.1",
                  "line": 64
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          },
          {
            "id": "Q4",
            "title": "When might capability alone defeat separate hardware and power controls?",
            "statement": "Identify architectures or behaviors that make power boundaries infeasible before formal review.",
            "kind": "open_question",
            "source": {
              "path": "nodes.28",
              "line": 541
            },
            "links": [
              {
                "type": "relates_to",
                "target": "K1"
              },
              {
                "type": "relates_to",
                "target": "H1"
              }
            ],
            "lineage": [
              {
                "id": "SRC-HUGGING-FACE",
                "title": "The Hugging Face incident and the road ahead",
                "locator": "Spoken Margins/The Hugging Face incident and the road ahead",
                "role": "Evidence that internal research can confer external agency and that precursor incidents can function as warning shots.",
                "source": {
                  "path": "sources.8",
                  "line": 112
                }
              },
              {
                "id": "SRC-PLAN-A-BARGAINING",
                "title": "Analyze Plan A Bargaining",
                "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
                "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
                "source": {
                  "path": "sources.9",
                  "line": 120
                }
              }
            ]
          }
        ]
      }
    ],
    "paths": [],
    "checks": [
      {
        "id": "AT-001",
        "title": "Kernel is complete and minimal",
        "description": "A faithful reconstruction must include all seven identity claims.",
        "source": {
          "path": "acceptance_tests.0",
          "line": 617
        }
      },
      {
        "id": "AT-002",
        "title": "Capability is not conflated with power",
        "description": "K1 and K2 must jointly ground the hardware-entanglement mechanism.",
        "source": {
          "path": "acceptance_tests.1",
          "line": 622
        }
      },
      {
        "id": "AT-003",
        "title": "Research permission does not imply deployment permission",
        "description": "The research/deployment distinction and external-agency boundary must both remain represented.",
        "source": {
          "path": "acceptance_tests.2",
          "line": 630
        }
      },
      {
        "id": "AT-004",
        "title": "Above-frontier release is adversarially gated",
        "description": "Removing rival review changes a central mechanism rather than a drafting detail.",
        "source": {
          "path": "acceptance_tests.3",
          "line": 636
        }
      },
      {
        "id": "AT-005",
        "title": "Domestic entry and automatic reciprocity coexist",
        "description": "The proposal must neither require a treaty first nor ignore foreign public releases.",
        "source": {
          "path": "acceptance_tests.4",
          "line": 643
        }
      },
      {
        "id": "AT-006",
        "title": "Warning time has legal, technical, and physical implementations",
        "description": "K7 must reach incident reporting, hardware reversibility, and industrial vetoes.",
        "source": {
          "path": "acceptance_tests.5",
          "line": 651
        }
      },
      {
        "id": "AT-007",
        "title": "Foundational uncertainty remains falsifiable",
        "description": "Feasibility claims must expose evidence needs and stopping rules.",
        "source": {
          "path": "acceptance_tests.6",
          "line": 658
        }
      },
      {
        "id": "AT-008",
        "title": "Implementation options cannot masquerade as kernel claims",
        "description": "Optional policy choices must remain replaceable.",
        "source": {
          "path": "acceptance_tests.7",
          "line": 663
        }
      },
      {
        "id": "AT-009",
        "title": "Provenance is proposition-level",
        "description": "Every claim must expose responsibility and source lineage.",
        "source": {
          "path": "acceptance_tests.8",
          "line": 668
        }
      },
      {
        "id": "AT-010",
        "title": "Publication authority is commit-addressed",
        "description": "Branch movement or website drift must not alter the object that was rendered or verified.",
        "source": {
          "path": "acceptance_tests.9",
          "line": 673
        }
      }
    ],
    "nonGoals": [],
    "guide": {
      "premise": "A proposal for governing powerful AI",
      "question": "Can we learn what AI can do without giving it irreversible power?",
      "introduction": "Separate research from consequential use. Require rival scrutiny before releasing stronger capabilities, and preserve time for people to intervene.",
      "scope": "This is a governance proposal. Its effectiveness depends on measurable capability limits, credible review, and human controls that still work as systems improve.",
      "scopeRefs": [
        "K1",
        "K3",
        "H1"
      ],
      "mechanismTitle": "Learn, review, release, and retain control",
      "mechanismDescription": "Follow how the proposed rules connect research permission to deployment decisions.",
      "steps": [
        {
          "id": "separate",
          "title": "Distinguish capability from power",
          "description": "Ask separately what a system can do, what hardware it can access, and how it could turn those resources into lasting influence.",
          "refs": [
            "K1",
            "K2"
          ],
          "connection": "Different questions need different controls"
        },
        {
          "id": "research",
          "title": "Keep research bounded",
          "description": "Allow learning inside controlled environments without automatically permitting commercial, military, or other consequential use.",
          "refs": [
            "K3"
          ],
          "connection": "Research permission is only the beginning"
        },
        {
          "id": "review",
          "title": "Require independent challenge",
          "description": "Consequential use beyond observable foreign public capability needs an adversarial safety case, including scrutiny by rival laboratories.",
          "refs": [
            "K4",
            "D2"
          ],
          "connection": "Review becomes a condition of release"
        },
        {
          "id": "respond",
          "title": "Preserve time to intervene",
          "description": "Use observable warning signs and reversible hardware arrangements; adjust domestic rules as foreign public capability changes.",
          "refs": [
            "K6",
            "K7",
            "D4"
          ],
          "connection": "Revise controls as conditions change"
        }
      ],
      "terms": [
        {
          "id": "capability",
          "title": "Algorithmic capability",
          "meaning": "What a system’s algorithms make it able to do. The model abbreviates this as A.",
          "refs": [
            "K1"
          ]
        },
        {
          "id": "hardware",
          "title": "Strategic hardware",
          "meaning": "The computing and physical resources available to a system. The model abbreviates this as H.",
          "refs": [
            "K1",
            "D4"
          ]
        },
        {
          "id": "power",
          "title": "Power-conversion affordances",
          "meaning": "The ways a system can turn ability and resources into durable, consequential agency. The model abbreviates this as P.",
          "refs": [
            "K1",
            "K2"
          ]
        },
        {
          "id": "frontier",
          "title": "Public capability frontier",
          "meaning": "The observable foreign public capability level used as an input to domestic release rules. Measuring it reliably is an open question.",
          "refs": [
            "K4",
            "K6",
            "H3"
          ]
        },
        {
          "id": "warning",
          "title": "Warning time",
          "meaning": "The interval between detecting a dangerous transition and losing the ability to respond effectively.",
          "refs": [
            "K7"
          ]
        }
      ],
      "example": {
        "title": "Research access is not deployment permission",
        "text": "A system could be studied in a bounded setting while permission to act in the outside world remains conditional on a separate safety case.",
        "refs": [
          "K3",
          "K4"
        ]
      },
      "uncertainty": {
        "title": "Will the separation remain workable?",
        "text": "The proposal needs an interval in which better algorithms do not automatically obtain hardware, credentials, money, and persistent external agency.",
        "refs": [
          "H1"
        ]
      },
      "featured": [
        "H1",
        "H2",
        "H3",
        "H4",
        "H5"
      ]
    },
    "contextSources": [
      {
        "id": "SRC-PRODUCTIVITY-DEFLATION",
        "title": "Productivity deflation",
        "locator": "Obsidian/Slaw's Notes/Productivity deflation.md",
        "role": "Anticipated capability improvement can delay investment and adoption.",
        "source": {
          "path": "sources.0",
          "line": 58
        }
      },
      {
        "id": "SRC-AI-BREAKOUT",
        "title": "AI Breakout",
        "locator": "Obsidian/Slaw's Notes/AI Breakout.md",
        "role": "Durable autonomy, industrial bootstrap, physical dependence, warning thresholds, and independently controlled human vetoes.",
        "source": {
          "path": "sources.1",
          "line": 64
        }
      },
      {
        "id": "SRC-AI-ABSOLUTISM",
        "title": "Response AI absolutism",
        "locator": "Obsidian/Slaw's Notes/Response AI absolutism.md",
        "role": "Institutional acceptance, liability, professional sign-off, and observable thresholds.",
        "source": {
          "path": "sources.2",
          "line": 72
        }
      },
      {
        "id": "SRC-AI-2040",
        "title": "AI 2040: Plan A",
        "locator": "Spoken Margins/AI 2040 Plan A",
        "role": "Slowdown, reversibility, diffusion, transparency, and compute destruction.",
        "source": {
          "path": "sources.3",
          "line": 78
        }
      },
      {
        "id": "SRC-TOBY-ORD",
        "title": "Where AGI timelines go wrong",
        "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
        "role": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes.",
        "source": {
          "path": "sources.4",
          "line": 84
        }
      },
      {
        "id": "SRC-NGO",
        "title": "Selective Optimism: a critique of AI 2040",
        "locator": "Richard Ngo, Mind the Future",
        "role": "Domestic-first political sequencing and skepticism toward inevitable race framing.",
        "source": {
          "path": "sources.5",
          "line": 92
        }
      },
      {
        "id": "SRC-DAVIDSON",
        "title": "Plan A's problem with dry tinder",
        "locator": "Tom Davidson, Forethought",
        "role": "Software-first scaling and the danger of accumulated physical dry tinder.",
        "source": {
          "path": "sources.6",
          "line": 98
        }
      },
      {
        "id": "SRC-PAUSEAI",
        "title": "PauseAI Is Making AI Doom Worse",
        "locator": "Spoken Margins, listener interjections at 43:22 and 55:46",
        "role": "Architecture-neutral domestic law, public-frontier pacing, profit gating, and competitor access for peer review.",
        "source": {
          "path": "sources.7",
          "line": 104
        }
      },
      {
        "id": "SRC-HUGGING-FACE",
        "title": "The Hugging Face incident and the road ahead",
        "locator": "Spoken Margins/The Hugging Face incident and the road ahead",
        "role": "Evidence that internal research can confer external agency and that precursor incidents can function as warning shots.",
        "source": {
          "path": "sources.8",
          "line": 112
        }
      },
      {
        "id": "SRC-PLAN-A-BARGAINING",
        "title": "Analyze Plan A Bargaining",
        "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
        "role": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization.",
        "source": {
          "path": "sources.9",
          "line": 120
        }
      }
    ],
    "evidenceSummary": "This revision states hypotheses and the tests needed to challenge them. It does not report completed empirical validation. Source lineage, where supplied, explains where the thinking came from.",
    "sourceSections": [
      {
        "key": "idea",
        "value": {
          "id": "https://ideas.proximitytoprogress.com/ideas/ai-pacing",
          "name": "AI Pacing"
        },
        "source": {
          "path": "idea",
          "line": 2
        }
      },
      {
        "key": "specification",
        "value": {
          "id": "ai-pacing",
          "title": "AI Pacing",
          "version": "0.1.0",
          "status": "working-draft",
          "purpose": "Slow and condition the conversion of frontier AI capability into irreversible economic, political, military, and physical power while preserving enough bounded research progress to learn what is safe.",
          "authority": {
            "system": "git",
            "canonical_branch": "main",
            "rule": "A rendering or verification MUST resolve the version it targets to an immutable full Git commit object ID. Branch names and website pages are discovery aids, not verification targets."
          },
          "responsibility": {
            "canonical_editor": "Cole Miller",
            "ai_use_disclosure": "AI systems were used for criticism, synthesis, formalization, source discovery, and editing. Inclusion in this specification means the canonical editor accepted responsibility for the proposition."
          },
          "generator_contract": {
            "authoritative_object": "this specification at an immutable Git commit",
            "allowed": [
              "Change medium, tone, ordering, examples, and level of detail.",
              "Limit scope when the omitted claims are declared.",
              "Criticize or vary identified propositions when the relationship is declared."
            ],
            "forbidden": [
              "Turn an implementation option into a kernel proposition.",
              "Turn a hypothesis or open question into an established conclusion.",
              "Change a proposition without checking its dependent claims.",
              "Attribute a synthesis claim to a source that merely motivated it.",
              "Claim verification without naming the verifier, commit, policy, and evidence."
            ]
          }
        },
        "source": {
          "path": "specification",
          "line": 6
        }
      },
      {
        "key": "typed_layers",
        "value": {
          "kernel": {
            "description": "Necessary propositions in the minimal identity of AI Pacing.",
            "prefix": "K"
          },
          "derived": {
            "description": "Mechanisms logically or strategically derived from kernel propositions.",
            "prefix": "D"
          },
          "hypothesis": {
            "description": "Contestable empirical assumptions on which feasibility depends.",
            "prefix": "H"
          },
          "implementation": {
            "description": "Replaceable policy or engineering choices that instantiate mechanisms.",
            "prefix": "I"
          },
          "open_question": {
            "description": "Unresolved questions that must remain visibly unresolved.",
            "prefix": "Q"
          }
        },
        "source": {
          "path": "typed_layers",
          "line": 41
        }
      },
      {
        "key": "sources",
        "value": [
          {
            "id": "SRC-PRODUCTIVITY-DEFLATION",
            "title": "Productivity deflation",
            "kind": "author-note",
            "date": "2024-02-26",
            "locator": "Obsidian/Slaw's Notes/Productivity deflation.md",
            "contribution": "Anticipated capability improvement can delay investment and adoption."
          },
          {
            "id": "SRC-AI-BREAKOUT",
            "title": "AI Breakout",
            "kind": "author-note",
            "date": "2025-10-02",
            "locator": "Obsidian/Slaw's Notes/AI Breakout.md",
            "contribution": "Durable autonomy, industrial bootstrap, physical dependence, warning thresholds, and independently controlled human vetoes."
          },
          {
            "id": "SRC-AI-ABSOLUTISM",
            "title": "Response AI absolutism",
            "kind": "author-note",
            "date": "2026-06-12",
            "locator": "Obsidian/Slaw's Notes/Response AI absolutism.md",
            "contribution": "Institutional acceptance, liability, professional sign-off, and observable thresholds."
          },
          {
            "id": "SRC-AI-2040",
            "title": "AI 2040: Plan A",
            "kind": "external-work",
            "date": "2026-07-27",
            "locator": "Spoken Margins/AI 2040 Plan A",
            "contribution": "Slowdown, reversibility, diffusion, transparency, and compute destruction."
          },
          {
            "id": "SRC-TOBY-ORD",
            "title": "Where AGI timelines go wrong",
            "kind": "listening-session",
            "date": "2026-08-08",
            "locator": "Spoken Margins/Where AGI timelines go wrong, 18:40-24:48",
            "contribution": "First recoverable integrated formulation of a paced public frontier, competitor review, unilateral entry, and retained human vetoes."
          },
          {
            "id": "SRC-NGO",
            "title": "Selective Optimism: a critique of AI 2040",
            "kind": "external-work",
            "date": "2026-07-09",
            "locator": "Richard Ngo, Mind the Future",
            "contribution": "Domestic-first political sequencing and skepticism toward inevitable race framing."
          },
          {
            "id": "SRC-DAVIDSON",
            "title": "Plan A's problem with dry tinder",
            "kind": "external-work",
            "date": "2026-07-10",
            "locator": "Tom Davidson, Forethought",
            "contribution": "Software-first scaling and the danger of accumulated physical dry tinder."
          },
          {
            "id": "SRC-PAUSEAI",
            "title": "PauseAI Is Making AI Doom Worse",
            "kind": "listening-session",
            "date": "2026-08-19",
            "locator": "Spoken Margins, listener interjections at 43:22 and 55:46",
            "contribution": "Architecture-neutral domestic law, public-frontier pacing, profit gating, and competitor access for peer review."
          },
          {
            "id": "SRC-HUGGING-FACE",
            "title": "The Hugging Face incident and the road ahead",
            "kind": "incident-and-listening-session",
            "date": "2026-08-26",
            "locator": "Spoken Margins/The Hugging Face incident and the road ahead",
            "contribution": "Evidence that internal research can confer external agency and that precursor incidents can function as warning shots."
          },
          {
            "id": "SRC-PLAN-A-BARGAINING",
            "title": "Analyze Plan A Bargaining",
            "kind": "ai-assisted-dialogue",
            "date": "2026-08-28",
            "locator": "ChatGPT conversation 6a924ff0-2378-83ea-98ef-4ea693205786",
            "contribution": "Bargaining-entry analysis, Capability Release Compact, A/H/P labels, automatic reciprocity, reporting incentives, reserve activation latency, and formalization."
          }
        ],
        "source": {
          "path": "sources",
          "line": 58
        }
      },
      {
        "key": "audit_events",
        "value": [
          {
            "id": "EVT-2024-ECONOMIC-PRECURSOR",
            "date": "2024-02-26",
            "phase": "precursor",
            "summary": "Productivity-deflation idea records adoption and investment effects of anticipated rapid improvement.",
            "evidence": [
              "SRC-PRODUCTIVITY-DEFLATION"
            ],
            "affected_nodes": [
              "H5"
            ],
            "confidence": "medium"
          },
          {
            "id": "EVT-2025-SAFETY-SPINE",
            "date": "2025-10-02",
            "phase": "deep-inception",
            "summary": "AI Breakout separates intelligence from durable autonomy and identifies physical human vetoes.",
            "evidence": [
              "SRC-AI-BREAKOUT"
            ],
            "affected_nodes": [
              "K1",
              "K2",
              "K7",
              "H1",
              "I5"
            ],
            "confidence": "high"
          },
          {
            "id": "EVT-2026-POLICY-INCEPTION",
            "date": "2026-08-08",
            "phase": "policy-inception",
            "summary": "Public-frontier gating, rival review, unilateral entry, and industrial vetoes appear together.",
            "evidence": [
              "SRC-TOBY-ORD"
            ],
            "affected_nodes": [
              "K1",
              "K2",
              "K3",
              "K4",
              "K5",
              "D1",
              "D2",
              "D3"
            ],
            "confidence": "high",
            "caveat": "The phrase \"the gate that we've described\" implies an immediate precursor not preserved here."
          },
          {
            "id": "EVT-2026-NAMED-PROJECT",
            "date": "2026-08-17",
            "phase": "named-project",
            "summary": "AI Pacing becomes a standalone Obsidian project.",
            "evidence": [
              "SRC-TOBY-ORD",
              "SRC-PAUSEAI"
            ],
            "affected_nodes": [
              "K1",
              "K2",
              "K3",
              "K4",
              "K5"
            ],
            "confidence": "medium",
            "caveat": "The initial August 17 file revision is not present in the available Git history."
          },
          {
            "id": "EVT-2026-REGIME-ASSEMBLY",
            "date": "2026-08-29",
            "time": "12:55:54-05:00",
            "phase": "regime-assembly",
            "summary": "Plan A, Ngo, Davidson, the Hugging Face incident, and author interjections are assembled into the current domestic-first regime.",
            "evidence": [
              "SRC-AI-2040",
              "SRC-NGO",
              "SRC-DAVIDSON",
              "SRC-HUGGING-FACE",
              "SRC-PLAN-A-BARGAINING"
            ],
            "affected_nodes": [
              "K1",
              "K2",
              "K3",
              "K4",
              "K5",
              "K6",
              "K7",
              "D1",
              "D2",
              "D3",
              "D4",
              "D5",
              "D6",
              "I1",
              "I2",
              "I3",
              "I4",
              "I5",
              "I6"
            ],
            "confidence": "high"
          },
          {
            "id": "EVT-2026-CANONICAL-SPEC",
            "date": "2026-08-29",
            "time": "15:46:39-05:00",
            "phase": "formalization",
            "summary": "The evolving essay is converted into a typed, dependency-aware canonical specification.",
            "evidence": [
              "SRC-PLAN-A-BARGAINING"
            ],
            "affected_nodes": [
              "K1",
              "K2",
              "K3",
              "K4",
              "K5",
              "K6",
              "K7"
            ],
            "confidence": "high"
          }
        ],
        "source": {
          "path": "audit_events",
          "line": 131
        }
      },
      {
        "key": "nodes",
        "value": [
          {
            "id": "K1",
            "kind": "kernel",
            "title": "Separate capability, substrate, and power",
            "statement": "Algorithmic capability (A), strategic hardware (H), and power-conversion affordances (P) are related but distinct variables and should not be governed as though they always change together.",
            "rationale": "Intelligence does not become sovereignty until it can acquire durable, consequential agency.",
            "provenance": {
              "origin": "author-synthesis",
              "sources": [
                "SRC-AI-BREAKOUT",
                "SRC-TOBY-ORD",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "critique",
                "naming",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "K2",
            "kind": "kernel",
            "title": "Pace conversion into irreversible power",
            "statement": "The objective is to slow and condition the conversion of frontier intelligence into irreversible economic, political, military, and physical power, not to freeze intelligence as such.",
            "rationale": "This preserves learning and option value while retaining intervention points.",
            "provenance": {
              "origin": "author-synthesis",
              "sources": [
                "SRC-AI-BREAKOUT",
                "SRC-TOBY-ORD",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "critique",
                "synthesis",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "K3",
            "kind": "kernel",
            "title": "Govern bounded research and consequential use differently",
            "statement": "Research inside genuinely bounded environments may proceed under a different regime from public, commercial, military, governmental, or otherwise power-conferring use.",
            "rationale": "Discovering a capability should not automatically confer the right to deploy it.",
            "provenance": {
              "origin": "author",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-PAUSEAI",
                "SRC-HUGGING-FACE"
              ],
              "ai_roles": [
                "counterexample",
                "refinement"
              ],
              "author_status": "accepted-after-revision"
            }
          },
          {
            "id": "K4",
            "kind": "kernel",
            "title": "Require adversarial assurance above the public frontier",
            "statement": "Consequential use that materially exceeds the observable foreign public capability frontier requires an adversarial control and safety case.",
            "rationale": "Safety evidence becomes a condition of access to economic and strategic rents.",
            "provenance": {
              "origin": "author",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-PAUSEAI",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "mechanism-analysis",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "K5",
            "kind": "kernel",
            "title": "Start domestically and remain internationally extensible",
            "statement": "The regime should begin as a domestically justified institution that can operate without foreign agreement and later admit deeper reciprocal cooperation.",
            "rationale": "A grand bargain is not a credible prerequisite for initial action.",
            "provenance": {
              "origin": "author-synthesis",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-NGO",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "crisis-bargaining-analysis",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "K6",
            "kind": "kernel",
            "title": "Make foreign public capability an automatic reciprocity input",
            "statement": "Observable foreign public capability should alter the domestic safe-harbor frontier so that foreign restraint can be reciprocated without a treaty and foreign release cannot exploit unilateral domestic restraint indefinitely.",
            "rationale": "Cooperation can occur through a precommitted policy rule rather than trust.",
            "provenance": {
              "origin": "author-synthesis",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "bargaining-analysis",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "K7",
            "kind": "kernel",
            "title": "Maximize warning time",
            "statement": "Governance should maximize the interval between credible detection of a dangerous transition and the point at which humans can no longer respond effectively.",
            "rationale": "Observable intermediate boundaries create chances to learn before failures become irreversible.",
            "metric": "T_warning = T_irreversible_power - T_credible_detection",
            "provenance": {
              "origin": "author-and-ai-synthesis",
              "sources": [
                "SRC-AI-BREAKOUT",
                "SRC-HUGGING-FACE",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "unification",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "D1",
            "kind": "derived",
            "title": "Capability Release Compact",
            "statement": "A domestic compact governs transitions from bounded research into public, commercial, military, governmental, or other consequential use.",
            "derived_from": [
              "K2",
              "K3",
              "K4",
              "K5",
              "K6"
            ],
            "provenance": {
              "origin": "author-and-ai-synthesis",
              "sources": [
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "architecture",
                "naming"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "D2",
            "kind": "derived",
            "title": "Rival laboratories provide the technical attack surface",
            "statement": "Qualified competitors receive controlled access sufficient to attack a frontier applicant's case; the government adjudicates and competitors do not hold a veto.",
            "derived_from": [
              "K4"
            ],
            "provenance": {
              "origin": "author",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-PAUSEAI",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "risk-analysis",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "D3",
            "kind": "derived",
            "title": "Safety becomes part of the profit function",
            "statement": "Because above-frontier monetization depends on passing hostile review, controllability becomes a production input rather than only a cost.",
            "derived_from": [
              "K4",
              "D2"
            ],
            "provenance": {
              "origin": "author-interjection-and-ai-formalization",
              "sources": [
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "economic-modeling"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "D4",
            "kind": "derived",
            "title": "Compute stock and compute entanglement differ",
            "statement": "Reversibility depends on both the quantity of strategic hardware and the degree to which civilian society depends on its continued operation.",
            "derived_from": [
              "K1",
              "K2",
              "K7"
            ],
            "provenance": {
              "origin": "author-synthesis",
              "sources": [
                "SRC-DAVIDSON",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "D5",
            "kind": "derived",
            "title": "Unauthorized agency can be made epistemically valuable",
            "statement": "Predefined boundaries around external agency create high-value warning signals when systems cross them without legitimate authorization.",
            "derived_from": [
              "K3",
              "K7"
            ],
            "provenance": {
              "origin": "author-interjection-and-ai-formalization",
              "sources": [
                "SRC-HUGGING-FACE",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "security-analogy",
                "formalization"
              ],
              "author_status": "accepted-after-wording-correction"
            }
          },
          {
            "id": "D6",
            "kind": "derived",
            "title": "International cooperation can deepen in stages",
            "statement": "Cooperation progresses from tacit public-frontier reciprocity to narrow abstention, incident exchange, reciprocal review, hardware declarations, activation rules, and only later treaty-level restraints.",
            "derived_from": [
              "K5",
              "K6"
            ],
            "provenance": {
              "origin": "author-and-ai-synthesis",
              "sources": [
                "SRC-NGO",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "sequencing",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "H1",
            "kind": "hypothesis",
            "title": "A/H/P separability remains actionable",
            "statement": "There is a policy-relevant interval in which algorithmic progress does not automatically create uncontainable access to hardware and external power.",
            "supports": [
              "K1",
              "K2",
              "K3"
            ],
            "falsifier": "Frontier algorithms reliably manufacture their own access to credentials, money, compute, persistence, or physical resources before humans can intervene.",
            "evidence_needed": "Repeated control evaluations across architectures and access configurations.",
            "stopping_rule": "If bounded systems repeatedly acquire durable external agency faster than detection and response, stop treating research/deployment separation as a sufficient primary control.",
            "provenance": {
              "origin": "author-hypothesis",
              "sources": [
                "SRC-AI-BREAKOUT",
                "SRC-HUGGING-FACE",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "falsification"
              ],
              "author_status": "accepted-as-hypothesis"
            }
          },
          {
            "id": "H2",
            "kind": "hypothesis",
            "title": "Rival review has positive safety value after proliferation costs",
            "statement": "Controlled competitor access reduces undetected risk and concentration more than it increases leakage, proliferation, collusion, and aggregate acceleration.",
            "supports": [
              "D2",
              "D3"
            ],
            "falsifier": "Review access produces material leakage or convergent leniency without compensating safety discoveries.",
            "evidence_needed": "Secure pilot reviews, objection quality, leakage audits, and incentive measurements.",
            "stopping_rule": "Suspend rival access when marginal information-security loss exceeds demonstrated review value.",
            "provenance": {
              "origin": "author-hypothesis",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "tradeoff-analysis"
              ],
              "author_status": "accepted-as-hypothesis"
            }
          },
          {
            "id": "H3",
            "kind": "hypothesis",
            "title": "The foreign public frontier is measurable enough to govern",
            "statement": "Practical foreign capability can be observed across domains with sufficient reliability to serve as one input to domestic safe-harbor decisions.",
            "supports": [
              "K6",
              "D6"
            ],
            "falsifier": "Hidden scaffolds, access conditions, or multidimensional capability make equivalent release determinations systematically manipulable.",
            "evidence_needed": "Domain-specific demonstrations, cost/access measurements, and repeatable equivalence protocols.",
            "stopping_rule": "Replace automatic equivalence with narrower domain rules wherever measurement error dominates reciprocity value.",
            "provenance": {
              "origin": "author-hypothesis",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "measurement-critique"
              ],
              "author_status": "accepted-as-hypothesis"
            }
          },
          {
            "id": "H4",
            "kind": "hypothesis",
            "title": "Physical activation latency can preserve reversibility",
            "statement": "Declared reserve hardware can be stored so covert activation takes longer than independent detection and effective response.",
            "supports": [
              "D4",
              "I4"
            ],
            "falsifier": "Reserve compute can be commissioned or substituted covertly before observers can detect and interrupt it.",
            "evidence_needed": "Timed mobilization exercises, inspection trials, supply-chain modeling, and red-team concealment attempts.",
            "stopping_rule": "Do not count hardware as reversible reserve when measured covert activation time is at or below response time.",
            "provenance": {
              "origin": "author-hypothesis",
              "sources": [
                "SRC-DAVIDSON",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "formalization"
              ],
              "author_status": "accepted-as-hypothesis"
            }
          },
          {
            "id": "H5",
            "kind": "hypothesis",
            "title": "Domestic benefits can sustain the institution",
            "statement": "Predictable release rules, competition, taxation, safer deployment, and protection against foreign release can create a durable domestic constituency.",
            "supports": [
              "K5",
              "D1"
            ],
            "falsifier": "The regime remains politically dependent on catastrophic-risk beliefs and is repealed when those beliefs weaken.",
            "evidence_needed": "Coalition analysis, polling, legislative pilots, investment behavior, and distributional modeling.",
            "stopping_rule": "Redesign entry mechanisms when the supporting coalition cannot survive an electoral turnover.",
            "provenance": {
              "origin": "author-hypothesis",
              "sources": [
                "SRC-PRODUCTIVITY-DEFLATION",
                "SRC-NGO",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "political-economy-analysis"
              ],
              "author_status": "accepted-as-hypothesis"
            }
          },
          {
            "id": "I1",
            "kind": "implementation",
            "title": "Architecture-neutral unauthorized-agency boundary",
            "statement": "Define prohibited external agency by observable actions rather than model architecture or legal labels.",
            "implements": [
              "K3",
              "D5"
            ],
            "replaceable": true,
            "provenance": {
              "origin": "author",
              "sources": [
                "SRC-PAUSEAI",
                "SRC-HUGGING-FACE"
              ],
              "ai_roles": [
                "drafting"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "I2",
            "kind": "implementation",
            "title": "Surveil, preserve, and report",
            "statement": "Require affirmative surveillance, automatic evidence preservation, prompt reporting on reasonable suspicion, a reporting safe harbor, and severe penalties for concealment.",
            "implements": [
              "K7",
              "D5"
            ],
            "replaceable": true,
            "provenance": {
              "origin": "author-medical-device-analogy",
              "sources": [
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "regulatory-translation"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "I3",
            "kind": "implementation",
            "title": "Weights-as-bond review process",
            "statement": "The applicant stakes controlled technical access to its candidate system as the price of seeking above-frontier release.",
            "implements": [
              "K4",
              "D2",
              "D3"
            ],
            "replaceable": true,
            "provenance": {
              "origin": "author-and-ai-synthesis",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "incentive-design",
                "naming"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "I4",
            "kind": "implementation",
            "title": "Observable uncommissioned hardware reserve",
            "statement": "Store reserve accelerators uninstalled, unracked, unnetworked, and detached from dedicated power and cooling, subject to independent observation.",
            "implements": [
              "D4",
              "K7"
            ],
            "replaceable": true,
            "provenance": {
              "origin": "author",
              "sources": [
                "SRC-DAVIDSON",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "I5",
            "kind": "implementation",
            "title": "Separate gate for the AI industrial survival chain",
            "statement": "Scrutinize automation that removes independently controlled human dependencies over compute, power, fabrication, repair, logistics, and reproduction.",
            "implements": [
              "K1",
              "K2",
              "K7"
            ],
            "replaceable": true,
            "provenance": {
              "origin": "author",
              "sources": [
                "SRC-AI-BREAKOUT",
                "SRC-TOBY-ORD",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "taxonomy",
                "formalization"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "I6",
            "kind": "implementation",
            "title": "Seven-stage international ladder",
            "statement": "Implement public reciprocity, capability abstention, incident exchange, reciprocal review, hardware declarations, activation controls, and treaty restraint in that order.",
            "implements": [
              "D6"
            ],
            "replaceable": true,
            "provenance": {
              "origin": "author-and-ai-synthesis",
              "sources": [
                "SRC-NGO",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "sequencing"
              ],
              "author_status": "accepted"
            }
          },
          {
            "id": "I7",
            "kind": "implementation",
            "title": "Licensed compute deployment and data-center taxation",
            "statement": "Use deployment licensing as an administrative and potentially populist tax base for large data centers.",
            "implements": [
              "K5"
            ],
            "replaceable": true,
            "provenance": {
              "origin": "author",
              "sources": [
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "policy-analysis"
              ],
              "author_status": "accepted-as-option"
            }
          },
          {
            "id": "Q1",
            "kind": "open_question",
            "title": "How should practical public capability be measured?",
            "statement": "Define domain, reliability, autonomy, cost, scaffolding, accessibility, and deployment conditions for frontier equivalence.",
            "relates_to": [
              "K4",
              "K6",
              "H3"
            ],
            "provenance": {
              "origin": "author-and-ai",
              "sources": [
                "SRC-TOBY-ORD",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "measurement-critique"
              ],
              "author_status": "unresolved"
            }
          },
          {
            "id": "Q2",
            "kind": "open_question",
            "title": "What access makes adversarial review credible without unacceptable leakage?",
            "statement": "Determine the minimum reproducible access, security, and adjudication protocol.",
            "relates_to": [
              "D2",
              "H2",
              "I3"
            ],
            "provenance": {
              "origin": "author-and-ai",
              "sources": [
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "risk-analysis"
              ],
              "author_status": "unresolved"
            }
          },
          {
            "id": "Q3",
            "kind": "open_question",
            "title": "How many independent human vetoes are enough?",
            "statement": "Determine when energy, compute, fabrication, repair, logistics, and governance dependencies are genuinely independent.",
            "relates_to": [
              "K7",
              "I5",
              "H1"
            ],
            "provenance": {
              "origin": "author",
              "sources": [
                "SRC-AI-BREAKOUT",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "formalization"
              ],
              "author_status": "unresolved"
            }
          },
          {
            "id": "Q4",
            "kind": "open_question",
            "title": "When does capability itself collapse A/H/P separability?",
            "statement": "Identify architectures or behaviors that make power boundaries infeasible before formal review.",
            "relates_to": [
              "K1",
              "H1"
            ],
            "provenance": {
              "origin": "author-and-ai",
              "sources": [
                "SRC-HUGGING-FACE",
                "SRC-PLAN-A-BARGAINING"
              ],
              "ai_roles": [
                "falsification"
              ],
              "author_status": "unresolved"
            }
          }
        ],
        "source": {
          "path": "nodes",
          "line": 181
        }
      },
      {
        "key": "relations",
        "value": [
          {
            "from": "K2",
            "to": "D1",
            "type": "grounds"
          },
          {
            "from": "K3",
            "to": "D1",
            "type": "grounds"
          },
          {
            "from": "K4",
            "to": "D1",
            "type": "grounds"
          },
          {
            "from": "K5",
            "to": "D1",
            "type": "grounds"
          },
          {
            "from": "K6",
            "to": "D1",
            "type": "grounds"
          },
          {
            "from": "K4",
            "to": "D2",
            "type": "grounds"
          },
          {
            "from": "K4",
            "to": "D3",
            "type": "grounds"
          },
          {
            "from": "D2",
            "to": "D3",
            "type": "grounds"
          },
          {
            "from": "K1",
            "to": "D4",
            "type": "grounds"
          },
          {
            "from": "K2",
            "to": "D4",
            "type": "grounds"
          },
          {
            "from": "K7",
            "to": "D4",
            "type": "grounds"
          },
          {
            "from": "K3",
            "to": "D5",
            "type": "grounds"
          },
          {
            "from": "K7",
            "to": "D5",
            "type": "grounds"
          },
          {
            "from": "K5",
            "to": "D6",
            "type": "grounds"
          },
          {
            "from": "K6",
            "to": "D6",
            "type": "grounds"
          },
          {
            "from": "H1",
            "to": "K1",
            "type": "conditions"
          },
          {
            "from": "H1",
            "to": "K2",
            "type": "conditions"
          },
          {
            "from": "H1",
            "to": "K3",
            "type": "conditions"
          },
          {
            "from": "H2",
            "to": "D2",
            "type": "conditions"
          },
          {
            "from": "H2",
            "to": "D3",
            "type": "conditions"
          },
          {
            "from": "H3",
            "to": "K6",
            "type": "conditions"
          },
          {
            "from": "H4",
            "to": "D4",
            "type": "conditions"
          },
          {
            "from": "H5",
            "to": "K5",
            "type": "conditions"
          },
          {
            "from": "D5",
            "to": "I1",
            "type": "implements"
          },
          {
            "from": "D5",
            "to": "I2",
            "type": "implements"
          },
          {
            "from": "D2",
            "to": "I3",
            "type": "implements"
          },
          {
            "from": "D4",
            "to": "I4",
            "type": "implements"
          },
          {
            "from": "K7",
            "to": "I5",
            "type": "implements"
          },
          {
            "from": "D6",
            "to": "I6",
            "type": "implements"
          }
        ],
        "source": {
          "path": "relations",
          "line": 553
        }
      },
      {
        "key": "publication",
        "value": {
          "layers": {
            "canonical": {
              "authority": "Git repository and immutable commit",
              "contains": "Specification, history, sources, generated deterministic views, and tests"
            },
            "website": {
              "authority": "none",
              "contains": "Cached readable projections, rendering index, and verification records",
              "disagreement_rule": "If a website view and the targeted Git commit disagree, the Git commit controls."
            },
            "federation": {
              "authority": "transport-only",
              "contains": "Announcements, pointers, and attestations"
            }
          },
          "rendering_model": {
            "universal_type": "rendering",
            "rule": "Essays, games, runs, analyses, songs, films, protests, simulations, critiques, and all other external works use the same rendering envelope.",
            "required_fields": [
              "artifact_uri",
              "artifact_digest",
              "target_git_commit",
              "creator",
              "relationship_claim",
              "verifier_request"
            ],
            "optional_fields": [
              "covers",
              "declared_omissions",
              "declared_deviations",
              "evidence_uri"
            ]
          },
          "verification_policy": {
            "principle": "Verification describes fidelity and declared relationship, not quality or endorsement.",
            "outcomes": [
              "verified",
              "verified_with_deviations",
              "not_verified"
            ],
            "attestation_must_name": [
              "artifact_digest",
              "full_target_git_commit",
              "verifier_identity",
              "verification_policy_version",
              "judgment",
              "evidence_digest"
            ]
          }
        },
        "source": {
          "path": "publication",
          "line": 584
        }
      },
      {
        "key": "acceptance_tests",
        "value": [
          {
            "id": "AT-001",
            "title": "Kernel is complete and minimal",
            "rationale": "A faithful reconstruction must include all seven identity claims.",
            "assertions": [
              {
                "type": "exact_kind_ids",
                "kind": "kernel",
                "ids": [
                  "K1",
                  "K2",
                  "K3",
                  "K4",
                  "K5",
                  "K6",
                  "K7"
                ]
              }
            ]
          },
          {
            "id": "AT-002",
            "title": "Capability is not conflated with power",
            "rationale": "K1 and K2 must jointly ground the hardware-entanglement mechanism.",
            "assertions": [
              {
                "type": "node_exists",
                "id": "K1",
                "kind": "kernel"
              },
              {
                "type": "node_exists",
                "id": "K2",
                "kind": "kernel"
              },
              {
                "type": "path_exists",
                "from": "K1",
                "to": "D4"
              },
              {
                "type": "path_exists",
                "from": "K2",
                "to": "D4"
              }
            ]
          },
          {
            "id": "AT-003",
            "title": "Research permission does not imply deployment permission",
            "rationale": "The research/deployment distinction and external-agency boundary must both remain represented.",
            "assertions": [
              {
                "type": "node_exists",
                "id": "K3",
                "kind": "kernel"
              },
              {
                "type": "path_exists",
                "from": "K3",
                "to": "I1"
              }
            ]
          },
          {
            "id": "AT-004",
            "title": "Above-frontier release is adversarially gated",
            "rationale": "Removing rival review changes a central mechanism rather than a drafting detail.",
            "assertions": [
              {
                "type": "path_exists",
                "from": "K4",
                "to": "D2"
              },
              {
                "type": "path_exists",
                "from": "D2",
                "to": "I3"
              },
              {
                "type": "path_exists",
                "from": "D2",
                "to": "D3"
              }
            ]
          },
          {
            "id": "AT-005",
            "title": "Domestic entry and automatic reciprocity coexist",
            "rationale": "The proposal must neither require a treaty first nor ignore foreign public releases.",
            "assertions": [
              {
                "type": "node_exists",
                "id": "K5",
                "kind": "kernel"
              },
              {
                "type": "node_exists",
                "id": "K6",
                "kind": "kernel"
              },
              {
                "type": "path_exists",
                "from": "K5",
                "to": "D6"
              },
              {
                "type": "path_exists",
                "from": "K6",
                "to": "D6"
              }
            ]
          },
          {
            "id": "AT-006",
            "title": "Warning time has legal, technical, and physical implementations",
            "rationale": "K7 must reach incident reporting, hardware reversibility, and industrial vetoes.",
            "assertions": [
              {
                "type": "path_exists",
                "from": "K7",
                "to": "I2"
              },
              {
                "type": "path_exists",
                "from": "K7",
                "to": "I4"
              },
              {
                "type": "path_exists",
                "from": "K7",
                "to": "I5"
              }
            ]
          },
          {
            "id": "AT-007",
            "title": "Foundational uncertainty remains falsifiable",
            "rationale": "Feasibility claims must expose evidence needs and stopping rules.",
            "assertions": [
              {
                "type": "all_kind_have_fields",
                "kind": "hypothesis",
                "fields": [
                  "falsifier",
                  "evidence_needed",
                  "stopping_rule"
                ]
              }
            ]
          },
          {
            "id": "AT-008",
            "title": "Implementation options cannot masquerade as kernel claims",
            "rationale": "Optional policy choices must remain replaceable.",
            "assertions": [
              {
                "type": "all_kind_field_equals",
                "kind": "implementation",
                "field": "replaceable",
                "value": true
              }
            ]
          },
          {
            "id": "AT-009",
            "title": "Provenance is proposition-level",
            "rationale": "Every claim must expose responsibility and source lineage.",
            "assertions": [
              {
                "type": "all_nodes_have_provenance"
              }
            ]
          },
          {
            "id": "AT-010",
            "title": "Publication authority is commit-addressed",
            "rationale": "Branch movement or website drift must not alter the object that was rendered or verified.",
            "assertions": [
              {
                "type": "field_equals",
                "path": "specification.authority.system",
                "value": "git"
              },
              {
                "type": "field_equals",
                "path": "publication.rendering_model.universal_type",
                "value": "rendering"
              },
              {
                "type": "collection_contains",
                "path": "publication.verification_policy.attestation_must_name",
                "value": "full_target_git_commit"
              }
            ]
          }
        ],
        "source": {
          "path": "acceptance_tests",
          "line": 617
        }
      },
      {
        "key": "deterministic_views",
        "value": [
          {
            "id": "VIEW-CANONICAL-JSON",
            "path": "generated/canonical.json",
            "purpose": "Stable normalized serialization used for semantic hashing and machine comparison."
          },
          {
            "id": "VIEW-MINIMAL",
            "path": "generated/minimal-reconstruction.md",
            "purpose": "Seven-claim checksum for a purported explanation of the idea."
          },
          {
            "id": "VIEW-AUDIT",
            "path": "generated/audit-trail.md",
            "purpose": "Chronological intellectual provenance with confidence and caveats."
          },
          {
            "id": "VIEW-GRAPH",
            "path": "generated/dependency-graph.mmd",
            "purpose": "Machine-generated Mermaid dependency graph for omission and impact review."
          },
          {
            "id": "VIEW-CLAIMS",
            "path": "generated/claims.csv",
            "purpose": "Flat claim register for review, filtering, and external analysis."
          },
          {
            "id": "VIEW-ACCEPTANCE",
            "path": "generated/acceptance-matrix.md",
            "purpose": "Human-readable result of executable semantic acceptance assertions."
          },
          {
            "id": "VIEW-REVIEW",
            "path": "generated/rendering-review-checklist.md",
            "purpose": "Commit-addressed checklist for an attributable rendering-fidelity judgment."
          }
        ],
        "source": {
          "path": "deterministic_views",
          "line": 682
        }
      }
    ],
    "sourceLocations": {
      "idea": {
        "path": "idea",
        "line": 2
      },
      "idea.id": {
        "path": "idea.id",
        "line": 2
      },
      "idea.name": {
        "path": "idea.name",
        "line": 3
      },
      "specification": {
        "path": "specification",
        "line": 6
      },
      "specification.id": {
        "path": "specification.id",
        "line": 6
      },
      "specification.title": {
        "path": "specification.title",
        "line": 7
      },
      "specification.version": {
        "path": "specification.version",
        "line": 8
      },
      "specification.status": {
        "path": "specification.status",
        "line": 9
      },
      "specification.purpose": {
        "path": "specification.purpose",
        "line": 10
      },
      "specification.authority": {
        "path": "specification.authority",
        "line": 15
      },
      "specification.authority.system": {
        "path": "specification.authority.system",
        "line": 15
      },
      "specification.authority.canonical_branch": {
        "path": "specification.authority.canonical_branch",
        "line": 16
      },
      "specification.authority.rule": {
        "path": "specification.authority.rule",
        "line": 17
      },
      "specification.responsibility": {
        "path": "specification.responsibility",
        "line": 22
      },
      "specification.responsibility.canonical_editor": {
        "path": "specification.responsibility.canonical_editor",
        "line": 22
      },
      "specification.responsibility.ai_use_disclosure": {
        "path": "specification.responsibility.ai_use_disclosure",
        "line": 23
      },
      "specification.generator_contract": {
        "path": "specification.generator_contract",
        "line": 28
      },
      "specification.generator_contract.authoritative_object": {
        "path": "specification.generator_contract.authoritative_object",
        "line": 28
      },
      "specification.generator_contract.allowed": {
        "path": "specification.generator_contract.allowed",
        "line": 30
      },
      "specification.generator_contract.allowed.0": {
        "path": "specification.generator_contract.allowed.0",
        "line": 30
      },
      "specification.generator_contract.allowed.1": {
        "path": "specification.generator_contract.allowed.1",
        "line": 31
      },
      "specification.generator_contract.allowed.2": {
        "path": "specification.generator_contract.allowed.2",
        "line": 32
      },
      "specification.generator_contract.forbidden": {
        "path": "specification.generator_contract.forbidden",
        "line": 34
      },
      "specification.generator_contract.forbidden.0": {
        "path": "specification.generator_contract.forbidden.0",
        "line": 34
      },
      "specification.generator_contract.forbidden.1": {
        "path": "specification.generator_contract.forbidden.1",
        "line": 35
      },
      "specification.generator_contract.forbidden.2": {
        "path": "specification.generator_contract.forbidden.2",
        "line": 36
      },
      "specification.generator_contract.forbidden.3": {
        "path": "specification.generator_contract.forbidden.3",
        "line": 37
      },
      "specification.generator_contract.forbidden.4": {
        "path": "specification.generator_contract.forbidden.4",
        "line": 38
      },
      "typed_layers": {
        "path": "typed_layers",
        "line": 41
      },
      "typed_layers.kernel": {
        "path": "typed_layers.kernel",
        "line": 42
      },
      "typed_layers.kernel.description": {
        "path": "typed_layers.kernel.description",
        "line": 42
      },
      "typed_layers.kernel.prefix": {
        "path": "typed_layers.kernel.prefix",
        "line": 43
      },
      "typed_layers.derived": {
        "path": "typed_layers.derived",
        "line": 45
      },
      "typed_layers.derived.description": {
        "path": "typed_layers.derived.description",
        "line": 45
      },
      "typed_layers.derived.prefix": {
        "path": "typed_layers.derived.prefix",
        "line": 46
      },
      "typed_layers.hypothesis": {
        "path": "typed_layers.hypothesis",
        "line": 48
      },
      "typed_layers.hypothesis.description": {
        "path": "typed_layers.hypothesis.description",
        "line": 48
      },
      "typed_layers.hypothesis.prefix": {
        "path": "typed_layers.hypothesis.prefix",
        "line": 49
      },
      "typed_layers.implementation": {
        "path": "typed_layers.implementation",
        "line": 51
      },
      "typed_layers.implementation.description": {
        "path": "typed_layers.implementation.description",
        "line": 51
      },
      "typed_layers.implementation.prefix": {
        "path": "typed_layers.implementation.prefix",
        "line": 52
      },
      "typed_layers.open_question": {
        "path": "typed_layers.open_question",
        "line": 54
      },
      "typed_layers.open_question.description": {
        "path": "typed_layers.open_question.description",
        "line": 54
      },
      "typed_layers.open_question.prefix": {
        "path": "typed_layers.open_question.prefix",
        "line": 55
      },
      "sources": {
        "path": "sources",
        "line": 58
      },
      "sources.0": {
        "path": "sources.0",
        "line": 58
      },
      "sources.0.id": {
        "path": "sources.0.id",
        "line": 58
      },
      "sources.0.title": {
        "path": "sources.0.title",
        "line": 59
      },
      "sources.0.kind": {
        "path": "sources.0.kind",
        "line": 60
      },
      "sources.0.date": {
        "path": "sources.0.date",
        "line": 61
      },
      "sources.0.locator": {
        "path": "sources.0.locator",
        "line": 62
      },
      "sources.0.contribution": {
        "path": "sources.0.contribution",
        "line": 63
      },
      "sources.1": {
        "path": "sources.1",
        "line": 64
      },
      "sources.1.id": {
        "path": "sources.1.id",
        "line": 64
      },
      "sources.1.title": {
        "path": "sources.1.title",
        "line": 65
      },
      "sources.1.kind": {
        "path": "sources.1.kind",
        "line": 66
      },
      "sources.1.date": {
        "path": "sources.1.date",
        "line": 67
      },
      "sources.1.locator": {
        "path": "sources.1.locator",
        "line": 68
      },
      "sources.1.contribution": {
        "path": "sources.1.contribution",
        "line": 69
      },
      "sources.2": {
        "path": "sources.2",
        "line": 72
      },
      "sources.2.id": {
        "path": "sources.2.id",
        "line": 72
      },
      "sources.2.title": {
        "path": "sources.2.title",
        "line": 73
      },
      "sources.2.kind": {
        "path": "sources.2.kind",
        "line": 74
      },
      "sources.2.date": {
        "path": "sources.2.date",
        "line": 75
      },
      "sources.2.locator": {
        "path": "sources.2.locator",
        "line": 76
      },
      "sources.2.contribution": {
        "path": "sources.2.contribution",
        "line": 77
      },
      "sources.3": {
        "path": "sources.3",
        "line": 78
      },
      "sources.3.id": {
        "path": "sources.3.id",
        "line": 78
      },
      "sources.3.title": {
        "path": "sources.3.title",
        "line": 79
      },
      "sources.3.kind": {
        "path": "sources.3.kind",
        "line": 80
      },
      "sources.3.date": {
        "path": "sources.3.date",
        "line": 81
      },
      "sources.3.locator": {
        "path": "sources.3.locator",
        "line": 82
      },
      "sources.3.contribution": {
        "path": "sources.3.contribution",
        "line": 83
      },
      "sources.4": {
        "path": "sources.4",
        "line": 84
      },
      "sources.4.id": {
        "path": "sources.4.id",
        "line": 84
      },
      "sources.4.title": {
        "path": "sources.4.title",
        "line": 85
      },
      "sources.4.kind": {
        "path": "sources.4.kind",
        "line": 86
      },
      "sources.4.date": {
        "path": "sources.4.date",
        "line": 87
      },
      "sources.4.locator": {
        "path": "sources.4.locator",
        "line": 88
      },
      "sources.4.contribution": {
        "path": "sources.4.contribution",
        "line": 89
      },
      "sources.5": {
        "path": "sources.5",
        "line": 92
      },
      "sources.5.id": {
        "path": "sources.5.id",
        "line": 92
      },
      "sources.5.title": {
        "path": "sources.5.title",
        "line": 93
      },
      "sources.5.kind": {
        "path": "sources.5.kind",
        "line": 94
      },
      "sources.5.date": {
        "path": "sources.5.date",
        "line": 95
      },
      "sources.5.locator": {
        "path": "sources.5.locator",
        "line": 96
      },
      "sources.5.contribution": {
        "path": "sources.5.contribution",
        "line": 97
      },
      "sources.6": {
        "path": "sources.6",
        "line": 98
      },
      "sources.6.id": {
        "path": "sources.6.id",
        "line": 98
      },
      "sources.6.title": {
        "path": "sources.6.title",
        "line": 99
      },
      "sources.6.kind": {
        "path": "sources.6.kind",
        "line": 100
      },
      "sources.6.date": {
        "path": "sources.6.date",
        "line": 101
      },
      "sources.6.locator": {
        "path": "sources.6.locator",
        "line": 102
      },
      "sources.6.contribution": {
        "path": "sources.6.contribution",
        "line": 103
      },
      "sources.7": {
        "path": "sources.7",
        "line": 104
      },
      "sources.7.id": {
        "path": "sources.7.id",
        "line": 104
      },
      "sources.7.title": {
        "path": "sources.7.title",
        "line": 105
      },
      "sources.7.kind": {
        "path": "sources.7.kind",
        "line": 106
      },
      "sources.7.date": {
        "path": "sources.7.date",
        "line": 107
      },
      "sources.7.locator": {
        "path": "sources.7.locator",
        "line": 108
      },
      "sources.7.contribution": {
        "path": "sources.7.contribution",
        "line": 109
      },
      "sources.8": {
        "path": "sources.8",
        "line": 112
      },
      "sources.8.id": {
        "path": "sources.8.id",
        "line": 112
      },
      "sources.8.title": {
        "path": "sources.8.title",
        "line": 113
      },
      "sources.8.kind": {
        "path": "sources.8.kind",
        "line": 114
      },
      "sources.8.date": {
        "path": "sources.8.date",
        "line": 115
      },
      "sources.8.locator": {
        "path": "sources.8.locator",
        "line": 116
      },
      "sources.8.contribution": {
        "path": "sources.8.contribution",
        "line": 117
      },
      "sources.9": {
        "path": "sources.9",
        "line": 120
      },
      "sources.9.id": {
        "path": "sources.9.id",
        "line": 120
      },
      "sources.9.title": {
        "path": "sources.9.title",
        "line": 121
      },
      "sources.9.kind": {
        "path": "sources.9.kind",
        "line": 122
      },
      "sources.9.date": {
        "path": "sources.9.date",
        "line": 123
      },
      "sources.9.locator": {
        "path": "sources.9.locator",
        "line": 124
      },
      "sources.9.contribution": {
        "path": "sources.9.contribution",
        "line": 125
      },
      "audit_events": {
        "path": "audit_events",
        "line": 131
      },
      "audit_events.0": {
        "path": "audit_events.0",
        "line": 131
      },
      "audit_events.0.id": {
        "path": "audit_events.0.id",
        "line": 131
      },
      "audit_events.0.date": {
        "path": "audit_events.0.date",
        "line": 132
      },
      "audit_events.0.phase": {
        "path": "audit_events.0.phase",
        "line": 133
      },
      "audit_events.0.summary": {
        "path": "audit_events.0.summary",
        "line": 134
      },
      "audit_events.0.evidence": {
        "path": "audit_events.0.evidence",
        "line": 135
      },
      "audit_events.0.evidence.0": {
        "path": "audit_events.0.evidence.0",
        "line": 135
      },
      "audit_events.0.affected_nodes": {
        "path": "audit_events.0.affected_nodes",
        "line": 136
      },
      "audit_events.0.affected_nodes.0": {
        "path": "audit_events.0.affected_nodes.0",
        "line": 136
      },
      "audit_events.0.confidence": {
        "path": "audit_events.0.confidence",
        "line": 137
      },
      "audit_events.1": {
        "path": "audit_events.1",
        "line": 138
      },
      "audit_events.1.id": {
        "path": "audit_events.1.id",
        "line": 138
      },
      "audit_events.1.date": {
        "path": "audit_events.1.date",
        "line": 139
      },
      "audit_events.1.phase": {
        "path": "audit_events.1.phase",
        "line": 140
      },
      "audit_events.1.summary": {
        "path": "audit_events.1.summary",
        "line": 141
      },
      "audit_events.1.evidence": {
        "path": "audit_events.1.evidence",
        "line": 142
      },
      "audit_events.1.evidence.0": {
        "path": "audit_events.1.evidence.0",
        "line": 142
      },
      "audit_events.1.affected_nodes": {
        "path": "audit_events.1.affected_nodes",
        "line": 143
      },
      "audit_events.1.affected_nodes.0": {
        "path": "audit_events.1.affected_nodes.0",
        "line": 143
      },
      "audit_events.1.affected_nodes.1": {
        "path": "audit_events.1.affected_nodes.1",
        "line": 143
      },
      "audit_events.1.affected_nodes.2": {
        "path": "audit_events.1.affected_nodes.2",
        "line": 143
      },
      "audit_events.1.affected_nodes.3": {
        "path": "audit_events.1.affected_nodes.3",
        "line": 143
      },
      "audit_events.1.affected_nodes.4": {
        "path": "audit_events.1.affected_nodes.4",
        "line": 143
      },
      "audit_events.1.confidence": {
        "path": "audit_events.1.confidence",
        "line": 144
      },
      "audit_events.2": {
        "path": "audit_events.2",
        "line": 145
      },
      "audit_events.2.id": {
        "path": "audit_events.2.id",
        "line": 145
      },
      "audit_events.2.date": {
        "path": "audit_events.2.date",
        "line": 146
      },
      "audit_events.2.phase": {
        "path": "audit_events.2.phase",
        "line": 147
      },
      "audit_events.2.summary": {
        "path": "audit_events.2.summary",
        "line": 148
      },
      "audit_events.2.evidence": {
        "path": "audit_events.2.evidence",
        "line": 149
      },
      "audit_events.2.evidence.0": {
        "path": "audit_events.2.evidence.0",
        "line": 149
      },
      "audit_events.2.affected_nodes": {
        "path": "audit_events.2.affected_nodes",
        "line": 150
      },
      "audit_events.2.affected_nodes.0": {
        "path": "audit_events.2.affected_nodes.0",
        "line": 150
      },
      "audit_events.2.affected_nodes.1": {
        "path": "audit_events.2.affected_nodes.1",
        "line": 150
      },
      "audit_events.2.affected_nodes.2": {
        "path": "audit_events.2.affected_nodes.2",
        "line": 150
      },
      "audit_events.2.affected_nodes.3": {
        "path": "audit_events.2.affected_nodes.3",
        "line": 150
      },
      "audit_events.2.affected_nodes.4": {
        "path": "audit_events.2.affected_nodes.4",
        "line": 150
      },
      "audit_events.2.affected_nodes.5": {
        "path": "audit_events.2.affected_nodes.5",
        "line": 150
      },
      "audit_events.2.affected_nodes.6": {
        "path": "audit_events.2.affected_nodes.6",
        "line": 150
      },
      "audit_events.2.affected_nodes.7": {
        "path": "audit_events.2.affected_nodes.7",
        "line": 150
      },
      "audit_events.2.confidence": {
        "path": "audit_events.2.confidence",
        "line": 151
      },
      "audit_events.2.caveat": {
        "path": "audit_events.2.caveat",
        "line": 152
      },
      "audit_events.3": {
        "path": "audit_events.3",
        "line": 153
      },
      "audit_events.3.id": {
        "path": "audit_events.3.id",
        "line": 153
      },
      "audit_events.3.date": {
        "path": "audit_events.3.date",
        "line": 154
      },
      "audit_events.3.phase": {
        "path": "audit_events.3.phase",
        "line": 155
      },
      "audit_events.3.summary": {
        "path": "audit_events.3.summary",
        "line": 156
      },
      "audit_events.3.evidence": {
        "path": "audit_events.3.evidence",
        "line": 157
      },
      "audit_events.3.evidence.0": {
        "path": "audit_events.3.evidence.0",
        "line": 157
      },
      "audit_events.3.evidence.1": {
        "path": "audit_events.3.evidence.1",
        "line": 157
      },
      "audit_events.3.affected_nodes": {
        "path": "audit_events.3.affected_nodes",
        "line": 158
      },
      "audit_events.3.affected_nodes.0": {
        "path": "audit_events.3.affected_nodes.0",
        "line": 158
      },
      "audit_events.3.affected_nodes.1": {
        "path": "audit_events.3.affected_nodes.1",
        "line": 158
      },
      "audit_events.3.affected_nodes.2": {
        "path": "audit_events.3.affected_nodes.2",
        "line": 158
      },
      "audit_events.3.affected_nodes.3": {
        "path": "audit_events.3.affected_nodes.3",
        "line": 158
      },
      "audit_events.3.affected_nodes.4": {
        "path": "audit_events.3.affected_nodes.4",
        "line": 158
      },
      "audit_events.3.confidence": {
        "path": "audit_events.3.confidence",
        "line": 159
      },
      "audit_events.3.caveat": {
        "path": "audit_events.3.caveat",
        "line": 160
      },
      "audit_events.4": {
        "path": "audit_events.4",
        "line": 161
      },
      "audit_events.4.id": {
        "path": "audit_events.4.id",
        "line": 161
      },
      "audit_events.4.date": {
        "path": "audit_events.4.date",
        "line": 162
      },
      "audit_events.4.time": {
        "path": "audit_events.4.time",
        "line": 163
      },
      "audit_events.4.phase": {
        "path": "audit_events.4.phase",
        "line": 164
      },
      "audit_events.4.summary": {
        "path": "audit_events.4.summary",
        "line": 165
      },
      "audit_events.4.evidence": {
        "path": "audit_events.4.evidence",
        "line": 168
      },
      "audit_events.4.evidence.0": {
        "path": "audit_events.4.evidence.0",
        "line": 168
      },
      "audit_events.4.evidence.1": {
        "path": "audit_events.4.evidence.1",
        "line": 168
      },
      "audit_events.4.evidence.2": {
        "path": "audit_events.4.evidence.2",
        "line": 168
      },
      "audit_events.4.evidence.3": {
        "path": "audit_events.4.evidence.3",
        "line": 168
      },
      "audit_events.4.evidence.4": {
        "path": "audit_events.4.evidence.4",
        "line": 168
      },
      "audit_events.4.affected_nodes": {
        "path": "audit_events.4.affected_nodes",
        "line": 169
      },
      "audit_events.4.affected_nodes.0": {
        "path": "audit_events.4.affected_nodes.0",
        "line": 169
      },
      "audit_events.4.affected_nodes.1": {
        "path": "audit_events.4.affected_nodes.1",
        "line": 169
      },
      "audit_events.4.affected_nodes.2": {
        "path": "audit_events.4.affected_nodes.2",
        "line": 169
      },
      "audit_events.4.affected_nodes.3": {
        "path": "audit_events.4.affected_nodes.3",
        "line": 169
      },
      "audit_events.4.affected_nodes.4": {
        "path": "audit_events.4.affected_nodes.4",
        "line": 169
      },
      "audit_events.4.affected_nodes.5": {
        "path": "audit_events.4.affected_nodes.5",
        "line": 169
      },
      "audit_events.4.affected_nodes.6": {
        "path": "audit_events.4.affected_nodes.6",
        "line": 169
      },
      "audit_events.4.affected_nodes.7": {
        "path": "audit_events.4.affected_nodes.7",
        "line": 169
      },
      "audit_events.4.affected_nodes.8": {
        "path": "audit_events.4.affected_nodes.8",
        "line": 169
      },
      "audit_events.4.affected_nodes.9": {
        "path": "audit_events.4.affected_nodes.9",
        "line": 169
      },
      "audit_events.4.affected_nodes.10": {
        "path": "audit_events.4.affected_nodes.10",
        "line": 169
      },
      "audit_events.4.affected_nodes.11": {
        "path": "audit_events.4.affected_nodes.11",
        "line": 169
      },
      "audit_events.4.affected_nodes.12": {
        "path": "audit_events.4.affected_nodes.12",
        "line": 169
      },
      "audit_events.4.affected_nodes.13": {
        "path": "audit_events.4.affected_nodes.13",
        "line": 169
      },
      "audit_events.4.affected_nodes.14": {
        "path": "audit_events.4.affected_nodes.14",
        "line": 169
      },
      "audit_events.4.affected_nodes.15": {
        "path": "audit_events.4.affected_nodes.15",
        "line": 169
      },
      "audit_events.4.affected_nodes.16": {
        "path": "audit_events.4.affected_nodes.16",
        "line": 169
      },
      "audit_events.4.affected_nodes.17": {
        "path": "audit_events.4.affected_nodes.17",
        "line": 169
      },
      "audit_events.4.affected_nodes.18": {
        "path": "audit_events.4.affected_nodes.18",
        "line": 169
      },
      "audit_events.4.confidence": {
        "path": "audit_events.4.confidence",
        "line": 170
      },
      "audit_events.5": {
        "path": "audit_events.5",
        "line": 171
      },
      "audit_events.5.id": {
        "path": "audit_events.5.id",
        "line": 171
      },
      "audit_events.5.date": {
        "path": "audit_events.5.date",
        "line": 172
      },
      "audit_events.5.time": {
        "path": "audit_events.5.time",
        "line": 173
      },
      "audit_events.5.phase": {
        "path": "audit_events.5.phase",
        "line": 174
      },
      "audit_events.5.summary": {
        "path": "audit_events.5.summary",
        "line": 175
      },
      "audit_events.5.evidence": {
        "path": "audit_events.5.evidence",
        "line": 176
      },
      "audit_events.5.evidence.0": {
        "path": "audit_events.5.evidence.0",
        "line": 176
      },
      "audit_events.5.affected_nodes": {
        "path": "audit_events.5.affected_nodes",
        "line": 177
      },
      "audit_events.5.affected_nodes.0": {
        "path": "audit_events.5.affected_nodes.0",
        "line": 177
      },
      "audit_events.5.affected_nodes.1": {
        "path": "audit_events.5.affected_nodes.1",
        "line": 177
      },
      "audit_events.5.affected_nodes.2": {
        "path": "audit_events.5.affected_nodes.2",
        "line": 177
      },
      "audit_events.5.affected_nodes.3": {
        "path": "audit_events.5.affected_nodes.3",
        "line": 177
      },
      "audit_events.5.affected_nodes.4": {
        "path": "audit_events.5.affected_nodes.4",
        "line": 177
      },
      "audit_events.5.affected_nodes.5": {
        "path": "audit_events.5.affected_nodes.5",
        "line": 177
      },
      "audit_events.5.affected_nodes.6": {
        "path": "audit_events.5.affected_nodes.6",
        "line": 177
      },
      "audit_events.5.confidence": {
        "path": "audit_events.5.confidence",
        "line": 178
      },
      "nodes": {
        "path": "nodes",
        "line": 181
      },
      "nodes.0": {
        "path": "nodes.0",
        "line": 181
      },
      "nodes.0.id": {
        "path": "nodes.0.id",
        "line": 181
      },
      "nodes.0.kind": {
        "path": "nodes.0.kind",
        "line": 182
      },
      "nodes.0.title": {
        "path": "nodes.0.title",
        "line": 183
      },
      "nodes.0.statement": {
        "path": "nodes.0.statement",
        "line": 184
      },
      "nodes.0.rationale": {
        "path": "nodes.0.rationale",
        "line": 188
      },
      "nodes.0.provenance": {
        "path": "nodes.0.provenance",
        "line": 190
      },
      "nodes.0.provenance.origin": {
        "path": "nodes.0.provenance.origin",
        "line": 190
      },
      "nodes.0.provenance.sources": {
        "path": "nodes.0.provenance.sources",
        "line": 191
      },
      "nodes.0.provenance.sources.0": {
        "path": "nodes.0.provenance.sources.0",
        "line": 191
      },
      "nodes.0.provenance.sources.1": {
        "path": "nodes.0.provenance.sources.1",
        "line": 191
      },
      "nodes.0.provenance.sources.2": {
        "path": "nodes.0.provenance.sources.2",
        "line": 191
      },
      "nodes.0.provenance.ai_roles": {
        "path": "nodes.0.provenance.ai_roles",
        "line": 192
      },
      "nodes.0.provenance.ai_roles.0": {
        "path": "nodes.0.provenance.ai_roles.0",
        "line": 192
      },
      "nodes.0.provenance.ai_roles.1": {
        "path": "nodes.0.provenance.ai_roles.1",
        "line": 192
      },
      "nodes.0.provenance.ai_roles.2": {
        "path": "nodes.0.provenance.ai_roles.2",
        "line": 192
      },
      "nodes.0.provenance.author_status": {
        "path": "nodes.0.provenance.author_status",
        "line": 193
      },
      "nodes.1": {
        "path": "nodes.1",
        "line": 194
      },
      "nodes.1.id": {
        "path": "nodes.1.id",
        "line": 194
      },
      "nodes.1.kind": {
        "path": "nodes.1.kind",
        "line": 195
      },
      "nodes.1.title": {
        "path": "nodes.1.title",
        "line": 196
      },
      "nodes.1.statement": {
        "path": "nodes.1.statement",
        "line": 197
      },
      "nodes.1.rationale": {
        "path": "nodes.1.rationale",
        "line": 201
      },
      "nodes.1.provenance": {
        "path": "nodes.1.provenance",
        "line": 203
      },
      "nodes.1.provenance.origin": {
        "path": "nodes.1.provenance.origin",
        "line": 203
      },
      "nodes.1.provenance.sources": {
        "path": "nodes.1.provenance.sources",
        "line": 204
      },
      "nodes.1.provenance.sources.0": {
        "path": "nodes.1.provenance.sources.0",
        "line": 204
      },
      "nodes.1.provenance.sources.1": {
        "path": "nodes.1.provenance.sources.1",
        "line": 204
      },
      "nodes.1.provenance.sources.2": {
        "path": "nodes.1.provenance.sources.2",
        "line": 204
      },
      "nodes.1.provenance.ai_roles": {
        "path": "nodes.1.provenance.ai_roles",
        "line": 205
      },
      "nodes.1.provenance.ai_roles.0": {
        "path": "nodes.1.provenance.ai_roles.0",
        "line": 205
      },
      "nodes.1.provenance.ai_roles.1": {
        "path": "nodes.1.provenance.ai_roles.1",
        "line": 205
      },
      "nodes.1.provenance.ai_roles.2": {
        "path": "nodes.1.provenance.ai_roles.2",
        "line": 205
      },
      "nodes.1.provenance.author_status": {
        "path": "nodes.1.provenance.author_status",
        "line": 206
      },
      "nodes.2": {
        "path": "nodes.2",
        "line": 207
      },
      "nodes.2.id": {
        "path": "nodes.2.id",
        "line": 207
      },
      "nodes.2.kind": {
        "path": "nodes.2.kind",
        "line": 208
      },
      "nodes.2.title": {
        "path": "nodes.2.title",
        "line": 209
      },
      "nodes.2.statement": {
        "path": "nodes.2.statement",
        "line": 210
      },
      "nodes.2.rationale": {
        "path": "nodes.2.rationale",
        "line": 214
      },
      "nodes.2.provenance": {
        "path": "nodes.2.provenance",
        "line": 216
      },
      "nodes.2.provenance.origin": {
        "path": "nodes.2.provenance.origin",
        "line": 216
      },
      "nodes.2.provenance.sources": {
        "path": "nodes.2.provenance.sources",
        "line": 217
      },
      "nodes.2.provenance.sources.0": {
        "path": "nodes.2.provenance.sources.0",
        "line": 217
      },
      "nodes.2.provenance.sources.1": {
        "path": "nodes.2.provenance.sources.1",
        "line": 217
      },
      "nodes.2.provenance.sources.2": {
        "path": "nodes.2.provenance.sources.2",
        "line": 217
      },
      "nodes.2.provenance.ai_roles": {
        "path": "nodes.2.provenance.ai_roles",
        "line": 218
      },
      "nodes.2.provenance.ai_roles.0": {
        "path": "nodes.2.provenance.ai_roles.0",
        "line": 218
      },
      "nodes.2.provenance.ai_roles.1": {
        "path": "nodes.2.provenance.ai_roles.1",
        "line": 218
      },
      "nodes.2.provenance.author_status": {
        "path": "nodes.2.provenance.author_status",
        "line": 219
      },
      "nodes.3": {
        "path": "nodes.3",
        "line": 220
      },
      "nodes.3.id": {
        "path": "nodes.3.id",
        "line": 220
      },
      "nodes.3.kind": {
        "path": "nodes.3.kind",
        "line": 221
      },
      "nodes.3.title": {
        "path": "nodes.3.title",
        "line": 222
      },
      "nodes.3.statement": {
        "path": "nodes.3.statement",
        "line": 223
      },
      "nodes.3.rationale": {
        "path": "nodes.3.rationale",
        "line": 226
      },
      "nodes.3.provenance": {
        "path": "nodes.3.provenance",
        "line": 228
      },
      "nodes.3.provenance.origin": {
        "path": "nodes.3.provenance.origin",
        "line": 228
      },
      "nodes.3.provenance.sources": {
        "path": "nodes.3.provenance.sources",
        "line": 229
      },
      "nodes.3.provenance.sources.0": {
        "path": "nodes.3.provenance.sources.0",
        "line": 229
      },
      "nodes.3.provenance.sources.1": {
        "path": "nodes.3.provenance.sources.1",
        "line": 229
      },
      "nodes.3.provenance.sources.2": {
        "path": "nodes.3.provenance.sources.2",
        "line": 229
      },
      "nodes.3.provenance.ai_roles": {
        "path": "nodes.3.provenance.ai_roles",
        "line": 230
      },
      "nodes.3.provenance.ai_roles.0": {
        "path": "nodes.3.provenance.ai_roles.0",
        "line": 230
      },
      "nodes.3.provenance.ai_roles.1": {
        "path": "nodes.3.provenance.ai_roles.1",
        "line": 230
      },
      "nodes.3.provenance.author_status": {
        "path": "nodes.3.provenance.author_status",
        "line": 231
      },
      "nodes.4": {
        "path": "nodes.4",
        "line": 232
      },
      "nodes.4.id": {
        "path": "nodes.4.id",
        "line": 232
      },
      "nodes.4.kind": {
        "path": "nodes.4.kind",
        "line": 233
      },
      "nodes.4.title": {
        "path": "nodes.4.title",
        "line": 234
      },
      "nodes.4.statement": {
        "path": "nodes.4.statement",
        "line": 235
      },
      "nodes.4.rationale": {
        "path": "nodes.4.rationale",
        "line": 238
      },
      "nodes.4.provenance": {
        "path": "nodes.4.provenance",
        "line": 240
      },
      "nodes.4.provenance.origin": {
        "path": "nodes.4.provenance.origin",
        "line": 240
      },
      "nodes.4.provenance.sources": {
        "path": "nodes.4.provenance.sources",
        "line": 241
      },
      "nodes.4.provenance.sources.0": {
        "path": "nodes.4.provenance.sources.0",
        "line": 241
      },
      "nodes.4.provenance.sources.1": {
        "path": "nodes.4.provenance.sources.1",
        "line": 241
      },
      "nodes.4.provenance.sources.2": {
        "path": "nodes.4.provenance.sources.2",
        "line": 241
      },
      "nodes.4.provenance.ai_roles": {
        "path": "nodes.4.provenance.ai_roles",
        "line": 242
      },
      "nodes.4.provenance.ai_roles.0": {
        "path": "nodes.4.provenance.ai_roles.0",
        "line": 242
      },
      "nodes.4.provenance.ai_roles.1": {
        "path": "nodes.4.provenance.ai_roles.1",
        "line": 242
      },
      "nodes.4.provenance.author_status": {
        "path": "nodes.4.provenance.author_status",
        "line": 243
      },
      "nodes.5": {
        "path": "nodes.5",
        "line": 244
      },
      "nodes.5.id": {
        "path": "nodes.5.id",
        "line": 244
      },
      "nodes.5.kind": {
        "path": "nodes.5.kind",
        "line": 245
      },
      "nodes.5.title": {
        "path": "nodes.5.title",
        "line": 246
      },
      "nodes.5.statement": {
        "path": "nodes.5.statement",
        "line": 247
      },
      "nodes.5.rationale": {
        "path": "nodes.5.rationale",
        "line": 251
      },
      "nodes.5.provenance": {
        "path": "nodes.5.provenance",
        "line": 253
      },
      "nodes.5.provenance.origin": {
        "path": "nodes.5.provenance.origin",
        "line": 253
      },
      "nodes.5.provenance.sources": {
        "path": "nodes.5.provenance.sources",
        "line": 254
      },
      "nodes.5.provenance.sources.0": {
        "path": "nodes.5.provenance.sources.0",
        "line": 254
      },
      "nodes.5.provenance.sources.1": {
        "path": "nodes.5.provenance.sources.1",
        "line": 254
      },
      "nodes.5.provenance.ai_roles": {
        "path": "nodes.5.provenance.ai_roles",
        "line": 255
      },
      "nodes.5.provenance.ai_roles.0": {
        "path": "nodes.5.provenance.ai_roles.0",
        "line": 255
      },
      "nodes.5.provenance.ai_roles.1": {
        "path": "nodes.5.provenance.ai_roles.1",
        "line": 255
      },
      "nodes.5.provenance.author_status": {
        "path": "nodes.5.provenance.author_status",
        "line": 256
      },
      "nodes.6": {
        "path": "nodes.6",
        "line": 257
      },
      "nodes.6.id": {
        "path": "nodes.6.id",
        "line": 257
      },
      "nodes.6.kind": {
        "path": "nodes.6.kind",
        "line": 258
      },
      "nodes.6.title": {
        "path": "nodes.6.title",
        "line": 259
      },
      "nodes.6.statement": {
        "path": "nodes.6.statement",
        "line": 260
      },
      "nodes.6.rationale": {
        "path": "nodes.6.rationale",
        "line": 263
      },
      "nodes.6.metric": {
        "path": "nodes.6.metric",
        "line": 264
      },
      "nodes.6.provenance": {
        "path": "nodes.6.provenance",
        "line": 266
      },
      "nodes.6.provenance.origin": {
        "path": "nodes.6.provenance.origin",
        "line": 266
      },
      "nodes.6.provenance.sources": {
        "path": "nodes.6.provenance.sources",
        "line": 267
      },
      "nodes.6.provenance.sources.0": {
        "path": "nodes.6.provenance.sources.0",
        "line": 267
      },
      "nodes.6.provenance.sources.1": {
        "path": "nodes.6.provenance.sources.1",
        "line": 267
      },
      "nodes.6.provenance.sources.2": {
        "path": "nodes.6.provenance.sources.2",
        "line": 267
      },
      "nodes.6.provenance.ai_roles": {
        "path": "nodes.6.provenance.ai_roles",
        "line": 268
      },
      "nodes.6.provenance.ai_roles.0": {
        "path": "nodes.6.provenance.ai_roles.0",
        "line": 268
      },
      "nodes.6.provenance.ai_roles.1": {
        "path": "nodes.6.provenance.ai_roles.1",
        "line": 268
      },
      "nodes.6.provenance.author_status": {
        "path": "nodes.6.provenance.author_status",
        "line": 269
      },
      "nodes.7": {
        "path": "nodes.7",
        "line": 271
      },
      "nodes.7.id": {
        "path": "nodes.7.id",
        "line": 271
      },
      "nodes.7.kind": {
        "path": "nodes.7.kind",
        "line": 272
      },
      "nodes.7.title": {
        "path": "nodes.7.title",
        "line": 273
      },
      "nodes.7.statement": {
        "path": "nodes.7.statement",
        "line": 274
      },
      "nodes.7.derived_from": {
        "path": "nodes.7.derived_from",
        "line": 277
      },
      "nodes.7.derived_from.0": {
        "path": "nodes.7.derived_from.0",
        "line": 277
      },
      "nodes.7.derived_from.1": {
        "path": "nodes.7.derived_from.1",
        "line": 277
      },
      "nodes.7.derived_from.2": {
        "path": "nodes.7.derived_from.2",
        "line": 277
      },
      "nodes.7.derived_from.3": {
        "path": "nodes.7.derived_from.3",
        "line": 277
      },
      "nodes.7.derived_from.4": {
        "path": "nodes.7.derived_from.4",
        "line": 277
      },
      "nodes.7.provenance": {
        "path": "nodes.7.provenance",
        "line": 279
      },
      "nodes.7.provenance.origin": {
        "path": "nodes.7.provenance.origin",
        "line": 279
      },
      "nodes.7.provenance.sources": {
        "path": "nodes.7.provenance.sources",
        "line": 280
      },
      "nodes.7.provenance.sources.0": {
        "path": "nodes.7.provenance.sources.0",
        "line": 280
      },
      "nodes.7.provenance.ai_roles": {
        "path": "nodes.7.provenance.ai_roles",
        "line": 281
      },
      "nodes.7.provenance.ai_roles.0": {
        "path": "nodes.7.provenance.ai_roles.0",
        "line": 281
      },
      "nodes.7.provenance.ai_roles.1": {
        "path": "nodes.7.provenance.ai_roles.1",
        "line": 281
      },
      "nodes.7.provenance.author_status": {
        "path": "nodes.7.provenance.author_status",
        "line": 282
      },
      "nodes.8": {
        "path": "nodes.8",
        "line": 283
      },
      "nodes.8.id": {
        "path": "nodes.8.id",
        "line": 283
      },
      "nodes.8.kind": {
        "path": "nodes.8.kind",
        "line": 284
      },
      "nodes.8.title": {
        "path": "nodes.8.title",
        "line": 285
      },
      "nodes.8.statement": {
        "path": "nodes.8.statement",
        "line": 286
      },
      "nodes.8.derived_from": {
        "path": "nodes.8.derived_from",
        "line": 289
      },
      "nodes.8.derived_from.0": {
        "path": "nodes.8.derived_from.0",
        "line": 289
      },
      "nodes.8.provenance": {
        "path": "nodes.8.provenance",
        "line": 291
      },
      "nodes.8.provenance.origin": {
        "path": "nodes.8.provenance.origin",
        "line": 291
      },
      "nodes.8.provenance.sources": {
        "path": "nodes.8.provenance.sources",
        "line": 292
      },
      "nodes.8.provenance.sources.0": {
        "path": "nodes.8.provenance.sources.0",
        "line": 292
      },
      "nodes.8.provenance.sources.1": {
        "path": "nodes.8.provenance.sources.1",
        "line": 292
      },
      "nodes.8.provenance.sources.2": {
        "path": "nodes.8.provenance.sources.2",
        "line": 292
      },
      "nodes.8.provenance.ai_roles": {
        "path": "nodes.8.provenance.ai_roles",
        "line": 293
      },
      "nodes.8.provenance.ai_roles.0": {
        "path": "nodes.8.provenance.ai_roles.0",
        "line": 293
      },
      "nodes.8.provenance.ai_roles.1": {
        "path": "nodes.8.provenance.ai_roles.1",
        "line": 293
      },
      "nodes.8.provenance.author_status": {
        "path": "nodes.8.provenance.author_status",
        "line": 294
      },
      "nodes.9": {
        "path": "nodes.9",
        "line": 295
      },
      "nodes.9.id": {
        "path": "nodes.9.id",
        "line": 295
      },
      "nodes.9.kind": {
        "path": "nodes.9.kind",
        "line": 296
      },
      "nodes.9.title": {
        "path": "nodes.9.title",
        "line": 297
      },
      "nodes.9.statement": {
        "path": "nodes.9.statement",
        "line": 298
      },
      "nodes.9.derived_from": {
        "path": "nodes.9.derived_from",
        "line": 301
      },
      "nodes.9.derived_from.0": {
        "path": "nodes.9.derived_from.0",
        "line": 301
      },
      "nodes.9.derived_from.1": {
        "path": "nodes.9.derived_from.1",
        "line": 301
      },
      "nodes.9.provenance": {
        "path": "nodes.9.provenance",
        "line": 303
      },
      "nodes.9.provenance.origin": {
        "path": "nodes.9.provenance.origin",
        "line": 303
      },
      "nodes.9.provenance.sources": {
        "path": "nodes.9.provenance.sources",
        "line": 304
      },
      "nodes.9.provenance.sources.0": {
        "path": "nodes.9.provenance.sources.0",
        "line": 304
      },
      "nodes.9.provenance.ai_roles": {
        "path": "nodes.9.provenance.ai_roles",
        "line": 305
      },
      "nodes.9.provenance.ai_roles.0": {
        "path": "nodes.9.provenance.ai_roles.0",
        "line": 305
      },
      "nodes.9.provenance.author_status": {
        "path": "nodes.9.provenance.author_status",
        "line": 306
      },
      "nodes.10": {
        "path": "nodes.10",
        "line": 307
      },
      "nodes.10.id": {
        "path": "nodes.10.id",
        "line": 307
      },
      "nodes.10.kind": {
        "path": "nodes.10.kind",
        "line": 308
      },
      "nodes.10.title": {
        "path": "nodes.10.title",
        "line": 309
      },
      "nodes.10.statement": {
        "path": "nodes.10.statement",
        "line": 310
      },
      "nodes.10.derived_from": {
        "path": "nodes.10.derived_from",
        "line": 313
      },
      "nodes.10.derived_from.0": {
        "path": "nodes.10.derived_from.0",
        "line": 313
      },
      "nodes.10.derived_from.1": {
        "path": "nodes.10.derived_from.1",
        "line": 313
      },
      "nodes.10.derived_from.2": {
        "path": "nodes.10.derived_from.2",
        "line": 313
      },
      "nodes.10.provenance": {
        "path": "nodes.10.provenance",
        "line": 315
      },
      "nodes.10.provenance.origin": {
        "path": "nodes.10.provenance.origin",
        "line": 315
      },
      "nodes.10.provenance.sources": {
        "path": "nodes.10.provenance.sources",
        "line": 316
      },
      "nodes.10.provenance.sources.0": {
        "path": "nodes.10.provenance.sources.0",
        "line": 316
      },
      "nodes.10.provenance.sources.1": {
        "path": "nodes.10.provenance.sources.1",
        "line": 316
      },
      "nodes.10.provenance.ai_roles": {
        "path": "nodes.10.provenance.ai_roles",
        "line": 317
      },
      "nodes.10.provenance.ai_roles.0": {
        "path": "nodes.10.provenance.ai_roles.0",
        "line": 317
      },
      "nodes.10.provenance.author_status": {
        "path": "nodes.10.provenance.author_status",
        "line": 318
      },
      "nodes.11": {
        "path": "nodes.11",
        "line": 319
      },
      "nodes.11.id": {
        "path": "nodes.11.id",
        "line": 319
      },
      "nodes.11.kind": {
        "path": "nodes.11.kind",
        "line": 320
      },
      "nodes.11.title": {
        "path": "nodes.11.title",
        "line": 321
      },
      "nodes.11.statement": {
        "path": "nodes.11.statement",
        "line": 322
      },
      "nodes.11.derived_from": {
        "path": "nodes.11.derived_from",
        "line": 325
      },
      "nodes.11.derived_from.0": {
        "path": "nodes.11.derived_from.0",
        "line": 325
      },
      "nodes.11.derived_from.1": {
        "path": "nodes.11.derived_from.1",
        "line": 325
      },
      "nodes.11.provenance": {
        "path": "nodes.11.provenance",
        "line": 327
      },
      "nodes.11.provenance.origin": {
        "path": "nodes.11.provenance.origin",
        "line": 327
      },
      "nodes.11.provenance.sources": {
        "path": "nodes.11.provenance.sources",
        "line": 328
      },
      "nodes.11.provenance.sources.0": {
        "path": "nodes.11.provenance.sources.0",
        "line": 328
      },
      "nodes.11.provenance.sources.1": {
        "path": "nodes.11.provenance.sources.1",
        "line": 328
      },
      "nodes.11.provenance.ai_roles": {
        "path": "nodes.11.provenance.ai_roles",
        "line": 329
      },
      "nodes.11.provenance.ai_roles.0": {
        "path": "nodes.11.provenance.ai_roles.0",
        "line": 329
      },
      "nodes.11.provenance.ai_roles.1": {
        "path": "nodes.11.provenance.ai_roles.1",
        "line": 329
      },
      "nodes.11.provenance.author_status": {
        "path": "nodes.11.provenance.author_status",
        "line": 330
      },
      "nodes.12": {
        "path": "nodes.12",
        "line": 331
      },
      "nodes.12.id": {
        "path": "nodes.12.id",
        "line": 331
      },
      "nodes.12.kind": {
        "path": "nodes.12.kind",
        "line": 332
      },
      "nodes.12.title": {
        "path": "nodes.12.title",
        "line": 333
      },
      "nodes.12.statement": {
        "path": "nodes.12.statement",
        "line": 334
      },
      "nodes.12.derived_from": {
        "path": "nodes.12.derived_from",
        "line": 338
      },
      "nodes.12.derived_from.0": {
        "path": "nodes.12.derived_from.0",
        "line": 338
      },
      "nodes.12.derived_from.1": {
        "path": "nodes.12.derived_from.1",
        "line": 338
      },
      "nodes.12.provenance": {
        "path": "nodes.12.provenance",
        "line": 340
      },
      "nodes.12.provenance.origin": {
        "path": "nodes.12.provenance.origin",
        "line": 340
      },
      "nodes.12.provenance.sources": {
        "path": "nodes.12.provenance.sources",
        "line": 341
      },
      "nodes.12.provenance.sources.0": {
        "path": "nodes.12.provenance.sources.0",
        "line": 341
      },
      "nodes.12.provenance.sources.1": {
        "path": "nodes.12.provenance.sources.1",
        "line": 341
      },
      "nodes.12.provenance.ai_roles": {
        "path": "nodes.12.provenance.ai_roles",
        "line": 342
      },
      "nodes.12.provenance.ai_roles.0": {
        "path": "nodes.12.provenance.ai_roles.0",
        "line": 342
      },
      "nodes.12.provenance.ai_roles.1": {
        "path": "nodes.12.provenance.ai_roles.1",
        "line": 342
      },
      "nodes.12.provenance.author_status": {
        "path": "nodes.12.provenance.author_status",
        "line": 343
      },
      "nodes.13": {
        "path": "nodes.13",
        "line": 345
      },
      "nodes.13.id": {
        "path": "nodes.13.id",
        "line": 345
      },
      "nodes.13.kind": {
        "path": "nodes.13.kind",
        "line": 346
      },
      "nodes.13.title": {
        "path": "nodes.13.title",
        "line": 347
      },
      "nodes.13.statement": {
        "path": "nodes.13.statement",
        "line": 348
      },
      "nodes.13.supports": {
        "path": "nodes.13.supports",
        "line": 351
      },
      "nodes.13.supports.0": {
        "path": "nodes.13.supports.0",
        "line": 351
      },
      "nodes.13.supports.1": {
        "path": "nodes.13.supports.1",
        "line": 351
      },
      "nodes.13.supports.2": {
        "path": "nodes.13.supports.2",
        "line": 351
      },
      "nodes.13.falsifier": {
        "path": "nodes.13.falsifier",
        "line": 352
      },
      "nodes.13.evidence_needed": {
        "path": "nodes.13.evidence_needed",
        "line": 355
      },
      "nodes.13.stopping_rule": {
        "path": "nodes.13.stopping_rule",
        "line": 356
      },
      "nodes.13.provenance": {
        "path": "nodes.13.provenance",
        "line": 360
      },
      "nodes.13.provenance.origin": {
        "path": "nodes.13.provenance.origin",
        "line": 360
      },
      "nodes.13.provenance.sources": {
        "path": "nodes.13.provenance.sources",
        "line": 361
      },
      "nodes.13.provenance.sources.0": {
        "path": "nodes.13.provenance.sources.0",
        "line": 361
      },
      "nodes.13.provenance.sources.1": {
        "path": "nodes.13.provenance.sources.1",
        "line": 361
      },
      "nodes.13.provenance.sources.2": {
        "path": "nodes.13.provenance.sources.2",
        "line": 361
      },
      "nodes.13.provenance.ai_roles": {
        "path": "nodes.13.provenance.ai_roles",
        "line": 362
      },
      "nodes.13.provenance.ai_roles.0": {
        "path": "nodes.13.provenance.ai_roles.0",
        "line": 362
      },
      "nodes.13.provenance.author_status": {
        "path": "nodes.13.provenance.author_status",
        "line": 363
      },
      "nodes.14": {
        "path": "nodes.14",
        "line": 364
      },
      "nodes.14.id": {
        "path": "nodes.14.id",
        "line": 364
      },
      "nodes.14.kind": {
        "path": "nodes.14.kind",
        "line": 365
      },
      "nodes.14.title": {
        "path": "nodes.14.title",
        "line": 366
      },
      "nodes.14.statement": {
        "path": "nodes.14.statement",
        "line": 367
      },
      "nodes.14.supports": {
        "path": "nodes.14.supports",
        "line": 370
      },
      "nodes.14.supports.0": {
        "path": "nodes.14.supports.0",
        "line": 370
      },
      "nodes.14.supports.1": {
        "path": "nodes.14.supports.1",
        "line": 370
      },
      "nodes.14.falsifier": {
        "path": "nodes.14.falsifier",
        "line": 371
      },
      "nodes.14.evidence_needed": {
        "path": "nodes.14.evidence_needed",
        "line": 372
      },
      "nodes.14.stopping_rule": {
        "path": "nodes.14.stopping_rule",
        "line": 373
      },
      "nodes.14.provenance": {
        "path": "nodes.14.provenance",
        "line": 375
      },
      "nodes.14.provenance.origin": {
        "path": "nodes.14.provenance.origin",
        "line": 375
      },
      "nodes.14.provenance.sources": {
        "path": "nodes.14.provenance.sources",
        "line": 376
      },
      "nodes.14.provenance.sources.0": {
        "path": "nodes.14.provenance.sources.0",
        "line": 376
      },
      "nodes.14.provenance.sources.1": {
        "path": "nodes.14.provenance.sources.1",
        "line": 376
      },
      "nodes.14.provenance.ai_roles": {
        "path": "nodes.14.provenance.ai_roles",
        "line": 377
      },
      "nodes.14.provenance.ai_roles.0": {
        "path": "nodes.14.provenance.ai_roles.0",
        "line": 377
      },
      "nodes.14.provenance.author_status": {
        "path": "nodes.14.provenance.author_status",
        "line": 378
      },
      "nodes.15": {
        "path": "nodes.15",
        "line": 379
      },
      "nodes.15.id": {
        "path": "nodes.15.id",
        "line": 379
      },
      "nodes.15.kind": {
        "path": "nodes.15.kind",
        "line": 380
      },
      "nodes.15.title": {
        "path": "nodes.15.title",
        "line": 381
      },
      "nodes.15.statement": {
        "path": "nodes.15.statement",
        "line": 382
      },
      "nodes.15.supports": {
        "path": "nodes.15.supports",
        "line": 385
      },
      "nodes.15.supports.0": {
        "path": "nodes.15.supports.0",
        "line": 385
      },
      "nodes.15.supports.1": {
        "path": "nodes.15.supports.1",
        "line": 385
      },
      "nodes.15.falsifier": {
        "path": "nodes.15.falsifier",
        "line": 386
      },
      "nodes.15.evidence_needed": {
        "path": "nodes.15.evidence_needed",
        "line": 387
      },
      "nodes.15.stopping_rule": {
        "path": "nodes.15.stopping_rule",
        "line": 388
      },
      "nodes.15.provenance": {
        "path": "nodes.15.provenance",
        "line": 390
      },
      "nodes.15.provenance.origin": {
        "path": "nodes.15.provenance.origin",
        "line": 390
      },
      "nodes.15.provenance.sources": {
        "path": "nodes.15.provenance.sources",
        "line": 391
      },
      "nodes.15.provenance.sources.0": {
        "path": "nodes.15.provenance.sources.0",
        "line": 391
      },
      "nodes.15.provenance.sources.1": {
        "path": "nodes.15.provenance.sources.1",
        "line": 391
      },
      "nodes.15.provenance.ai_roles": {
        "path": "nodes.15.provenance.ai_roles",
        "line": 392
      },
      "nodes.15.provenance.ai_roles.0": {
        "path": "nodes.15.provenance.ai_roles.0",
        "line": 392
      },
      "nodes.15.provenance.author_status": {
        "path": "nodes.15.provenance.author_status",
        "line": 393
      },
      "nodes.16": {
        "path": "nodes.16",
        "line": 394
      },
      "nodes.16.id": {
        "path": "nodes.16.id",
        "line": 394
      },
      "nodes.16.kind": {
        "path": "nodes.16.kind",
        "line": 395
      },
      "nodes.16.title": {
        "path": "nodes.16.title",
        "line": 396
      },
      "nodes.16.statement": {
        "path": "nodes.16.statement",
        "line": 397
      },
      "nodes.16.supports": {
        "path": "nodes.16.supports",
        "line": 400
      },
      "nodes.16.supports.0": {
        "path": "nodes.16.supports.0",
        "line": 400
      },
      "nodes.16.supports.1": {
        "path": "nodes.16.supports.1",
        "line": 400
      },
      "nodes.16.falsifier": {
        "path": "nodes.16.falsifier",
        "line": 401
      },
      "nodes.16.evidence_needed": {
        "path": "nodes.16.evidence_needed",
        "line": 402
      },
      "nodes.16.stopping_rule": {
        "path": "nodes.16.stopping_rule",
        "line": 403
      },
      "nodes.16.provenance": {
        "path": "nodes.16.provenance",
        "line": 405
      },
      "nodes.16.provenance.origin": {
        "path": "nodes.16.provenance.origin",
        "line": 405
      },
      "nodes.16.provenance.sources": {
        "path": "nodes.16.provenance.sources",
        "line": 406
      },
      "nodes.16.provenance.sources.0": {
        "path": "nodes.16.provenance.sources.0",
        "line": 406
      },
      "nodes.16.provenance.sources.1": {
        "path": "nodes.16.provenance.sources.1",
        "line": 406
      },
      "nodes.16.provenance.ai_roles": {
        "path": "nodes.16.provenance.ai_roles",
        "line": 407
      },
      "nodes.16.provenance.ai_roles.0": {
        "path": "nodes.16.provenance.ai_roles.0",
        "line": 407
      },
      "nodes.16.provenance.author_status": {
        "path": "nodes.16.provenance.author_status",
        "line": 408
      },
      "nodes.17": {
        "path": "nodes.17",
        "line": 409
      },
      "nodes.17.id": {
        "path": "nodes.17.id",
        "line": 409
      },
      "nodes.17.kind": {
        "path": "nodes.17.kind",
        "line": 410
      },
      "nodes.17.title": {
        "path": "nodes.17.title",
        "line": 411
      },
      "nodes.17.statement": {
        "path": "nodes.17.statement",
        "line": 412
      },
      "nodes.17.supports": {
        "path": "nodes.17.supports",
        "line": 415
      },
      "nodes.17.supports.0": {
        "path": "nodes.17.supports.0",
        "line": 415
      },
      "nodes.17.supports.1": {
        "path": "nodes.17.supports.1",
        "line": 415
      },
      "nodes.17.falsifier": {
        "path": "nodes.17.falsifier",
        "line": 416
      },
      "nodes.17.evidence_needed": {
        "path": "nodes.17.evidence_needed",
        "line": 417
      },
      "nodes.17.stopping_rule": {
        "path": "nodes.17.stopping_rule",
        "line": 418
      },
      "nodes.17.provenance": {
        "path": "nodes.17.provenance",
        "line": 420
      },
      "nodes.17.provenance.origin": {
        "path": "nodes.17.provenance.origin",
        "line": 420
      },
      "nodes.17.provenance.sources": {
        "path": "nodes.17.provenance.sources",
        "line": 421
      },
      "nodes.17.provenance.sources.0": {
        "path": "nodes.17.provenance.sources.0",
        "line": 421
      },
      "nodes.17.provenance.sources.1": {
        "path": "nodes.17.provenance.sources.1",
        "line": 421
      },
      "nodes.17.provenance.sources.2": {
        "path": "nodes.17.provenance.sources.2",
        "line": 421
      },
      "nodes.17.provenance.ai_roles": {
        "path": "nodes.17.provenance.ai_roles",
        "line": 422
      },
      "nodes.17.provenance.ai_roles.0": {
        "path": "nodes.17.provenance.ai_roles.0",
        "line": 422
      },
      "nodes.17.provenance.author_status": {
        "path": "nodes.17.provenance.author_status",
        "line": 423
      },
      "nodes.18": {
        "path": "nodes.18",
        "line": 425
      },
      "nodes.18.id": {
        "path": "nodes.18.id",
        "line": 425
      },
      "nodes.18.kind": {
        "path": "nodes.18.kind",
        "line": 426
      },
      "nodes.18.title": {
        "path": "nodes.18.title",
        "line": 427
      },
      "nodes.18.statement": {
        "path": "nodes.18.statement",
        "line": 428
      },
      "nodes.18.implements": {
        "path": "nodes.18.implements",
        "line": 429
      },
      "nodes.18.implements.0": {
        "path": "nodes.18.implements.0",
        "line": 429
      },
      "nodes.18.implements.1": {
        "path": "nodes.18.implements.1",
        "line": 429
      },
      "nodes.18.replaceable": {
        "path": "nodes.18.replaceable",
        "line": 430
      },
      "nodes.18.provenance": {
        "path": "nodes.18.provenance",
        "line": 432
      },
      "nodes.18.provenance.origin": {
        "path": "nodes.18.provenance.origin",
        "line": 432
      },
      "nodes.18.provenance.sources": {
        "path": "nodes.18.provenance.sources",
        "line": 433
      },
      "nodes.18.provenance.sources.0": {
        "path": "nodes.18.provenance.sources.0",
        "line": 433
      },
      "nodes.18.provenance.sources.1": {
        "path": "nodes.18.provenance.sources.1",
        "line": 433
      },
      "nodes.18.provenance.ai_roles": {
        "path": "nodes.18.provenance.ai_roles",
        "line": 434
      },
      "nodes.18.provenance.ai_roles.0": {
        "path": "nodes.18.provenance.ai_roles.0",
        "line": 434
      },
      "nodes.18.provenance.author_status": {
        "path": "nodes.18.provenance.author_status",
        "line": 435
      },
      "nodes.19": {
        "path": "nodes.19",
        "line": 436
      },
      "nodes.19.id": {
        "path": "nodes.19.id",
        "line": 436
      },
      "nodes.19.kind": {
        "path": "nodes.19.kind",
        "line": 437
      },
      "nodes.19.title": {
        "path": "nodes.19.title",
        "line": 438
      },
      "nodes.19.statement": {
        "path": "nodes.19.statement",
        "line": 439
      },
      "nodes.19.implements": {
        "path": "nodes.19.implements",
        "line": 442
      },
      "nodes.19.implements.0": {
        "path": "nodes.19.implements.0",
        "line": 442
      },
      "nodes.19.implements.1": {
        "path": "nodes.19.implements.1",
        "line": 442
      },
      "nodes.19.replaceable": {
        "path": "nodes.19.replaceable",
        "line": 443
      },
      "nodes.19.provenance": {
        "path": "nodes.19.provenance",
        "line": 445
      },
      "nodes.19.provenance.origin": {
        "path": "nodes.19.provenance.origin",
        "line": 445
      },
      "nodes.19.provenance.sources": {
        "path": "nodes.19.provenance.sources",
        "line": 446
      },
      "nodes.19.provenance.sources.0": {
        "path": "nodes.19.provenance.sources.0",
        "line": 446
      },
      "nodes.19.provenance.ai_roles": {
        "path": "nodes.19.provenance.ai_roles",
        "line": 447
      },
      "nodes.19.provenance.ai_roles.0": {
        "path": "nodes.19.provenance.ai_roles.0",
        "line": 447
      },
      "nodes.19.provenance.author_status": {
        "path": "nodes.19.provenance.author_status",
        "line": 448
      },
      "nodes.20": {
        "path": "nodes.20",
        "line": 449
      },
      "nodes.20.id": {
        "path": "nodes.20.id",
        "line": 449
      },
      "nodes.20.kind": {
        "path": "nodes.20.kind",
        "line": 450
      },
      "nodes.20.title": {
        "path": "nodes.20.title",
        "line": 451
      },
      "nodes.20.statement": {
        "path": "nodes.20.statement",
        "line": 452
      },
      "nodes.20.implements": {
        "path": "nodes.20.implements",
        "line": 453
      },
      "nodes.20.implements.0": {
        "path": "nodes.20.implements.0",
        "line": 453
      },
      "nodes.20.implements.1": {
        "path": "nodes.20.implements.1",
        "line": 453
      },
      "nodes.20.implements.2": {
        "path": "nodes.20.implements.2",
        "line": 453
      },
      "nodes.20.replaceable": {
        "path": "nodes.20.replaceable",
        "line": 454
      },
      "nodes.20.provenance": {
        "path": "nodes.20.provenance",
        "line": 456
      },
      "nodes.20.provenance.origin": {
        "path": "nodes.20.provenance.origin",
        "line": 456
      },
      "nodes.20.provenance.sources": {
        "path": "nodes.20.provenance.sources",
        "line": 457
      },
      "nodes.20.provenance.sources.0": {
        "path": "nodes.20.provenance.sources.0",
        "line": 457
      },
      "nodes.20.provenance.sources.1": {
        "path": "nodes.20.provenance.sources.1",
        "line": 457
      },
      "nodes.20.provenance.ai_roles": {
        "path": "nodes.20.provenance.ai_roles",
        "line": 458
      },
      "nodes.20.provenance.ai_roles.0": {
        "path": "nodes.20.provenance.ai_roles.0",
        "line": 458
      },
      "nodes.20.provenance.ai_roles.1": {
        "path": "nodes.20.provenance.ai_roles.1",
        "line": 458
      },
      "nodes.20.provenance.author_status": {
        "path": "nodes.20.provenance.author_status",
        "line": 459
      },
      "nodes.21": {
        "path": "nodes.21",
        "line": 460
      },
      "nodes.21.id": {
        "path": "nodes.21.id",
        "line": 460
      },
      "nodes.21.kind": {
        "path": "nodes.21.kind",
        "line": 461
      },
      "nodes.21.title": {
        "path": "nodes.21.title",
        "line": 462
      },
      "nodes.21.statement": {
        "path": "nodes.21.statement",
        "line": 463
      },
      "nodes.21.implements": {
        "path": "nodes.21.implements",
        "line": 466
      },
      "nodes.21.implements.0": {
        "path": "nodes.21.implements.0",
        "line": 466
      },
      "nodes.21.implements.1": {
        "path": "nodes.21.implements.1",
        "line": 466
      },
      "nodes.21.replaceable": {
        "path": "nodes.21.replaceable",
        "line": 467
      },
      "nodes.21.provenance": {
        "path": "nodes.21.provenance",
        "line": 469
      },
      "nodes.21.provenance.origin": {
        "path": "nodes.21.provenance.origin",
        "line": 469
      },
      "nodes.21.provenance.sources": {
        "path": "nodes.21.provenance.sources",
        "line": 470
      },
      "nodes.21.provenance.sources.0": {
        "path": "nodes.21.provenance.sources.0",
        "line": 470
      },
      "nodes.21.provenance.sources.1": {
        "path": "nodes.21.provenance.sources.1",
        "line": 470
      },
      "nodes.21.provenance.ai_roles": {
        "path": "nodes.21.provenance.ai_roles",
        "line": 471
      },
      "nodes.21.provenance.ai_roles.0": {
        "path": "nodes.21.provenance.ai_roles.0",
        "line": 471
      },
      "nodes.21.provenance.author_status": {
        "path": "nodes.21.provenance.author_status",
        "line": 472
      },
      "nodes.22": {
        "path": "nodes.22",
        "line": 473
      },
      "nodes.22.id": {
        "path": "nodes.22.id",
        "line": 473
      },
      "nodes.22.kind": {
        "path": "nodes.22.kind",
        "line": 474
      },
      "nodes.22.title": {
        "path": "nodes.22.title",
        "line": 475
      },
      "nodes.22.statement": {
        "path": "nodes.22.statement",
        "line": 476
      },
      "nodes.22.implements": {
        "path": "nodes.22.implements",
        "line": 479
      },
      "nodes.22.implements.0": {
        "path": "nodes.22.implements.0",
        "line": 479
      },
      "nodes.22.implements.1": {
        "path": "nodes.22.implements.1",
        "line": 479
      },
      "nodes.22.implements.2": {
        "path": "nodes.22.implements.2",
        "line": 479
      },
      "nodes.22.replaceable": {
        "path": "nodes.22.replaceable",
        "line": 480
      },
      "nodes.22.provenance": {
        "path": "nodes.22.provenance",
        "line": 482
      },
      "nodes.22.provenance.origin": {
        "path": "nodes.22.provenance.origin",
        "line": 482
      },
      "nodes.22.provenance.sources": {
        "path": "nodes.22.provenance.sources",
        "line": 483
      },
      "nodes.22.provenance.sources.0": {
        "path": "nodes.22.provenance.sources.0",
        "line": 483
      },
      "nodes.22.provenance.sources.1": {
        "path": "nodes.22.provenance.sources.1",
        "line": 483
      },
      "nodes.22.provenance.sources.2": {
        "path": "nodes.22.provenance.sources.2",
        "line": 483
      },
      "nodes.22.provenance.ai_roles": {
        "path": "nodes.22.provenance.ai_roles",
        "line": 484
      },
      "nodes.22.provenance.ai_roles.0": {
        "path": "nodes.22.provenance.ai_roles.0",
        "line": 484
      },
      "nodes.22.provenance.ai_roles.1": {
        "path": "nodes.22.provenance.ai_roles.1",
        "line": 484
      },
      "nodes.22.provenance.author_status": {
        "path": "nodes.22.provenance.author_status",
        "line": 485
      },
      "nodes.23": {
        "path": "nodes.23",
        "line": 486
      },
      "nodes.23.id": {
        "path": "nodes.23.id",
        "line": 486
      },
      "nodes.23.kind": {
        "path": "nodes.23.kind",
        "line": 487
      },
      "nodes.23.title": {
        "path": "nodes.23.title",
        "line": 488
      },
      "nodes.23.statement": {
        "path": "nodes.23.statement",
        "line": 489
      },
      "nodes.23.implements": {
        "path": "nodes.23.implements",
        "line": 492
      },
      "nodes.23.implements.0": {
        "path": "nodes.23.implements.0",
        "line": 492
      },
      "nodes.23.replaceable": {
        "path": "nodes.23.replaceable",
        "line": 493
      },
      "nodes.23.provenance": {
        "path": "nodes.23.provenance",
        "line": 495
      },
      "nodes.23.provenance.origin": {
        "path": "nodes.23.provenance.origin",
        "line": 495
      },
      "nodes.23.provenance.sources": {
        "path": "nodes.23.provenance.sources",
        "line": 496
      },
      "nodes.23.provenance.sources.0": {
        "path": "nodes.23.provenance.sources.0",
        "line": 496
      },
      "nodes.23.provenance.sources.1": {
        "path": "nodes.23.provenance.sources.1",
        "line": 496
      },
      "nodes.23.provenance.ai_roles": {
        "path": "nodes.23.provenance.ai_roles",
        "line": 497
      },
      "nodes.23.provenance.ai_roles.0": {
        "path": "nodes.23.provenance.ai_roles.0",
        "line": 497
      },
      "nodes.23.provenance.author_status": {
        "path": "nodes.23.provenance.author_status",
        "line": 498
      },
      "nodes.24": {
        "path": "nodes.24",
        "line": 499
      },
      "nodes.24.id": {
        "path": "nodes.24.id",
        "line": 499
      },
      "nodes.24.kind": {
        "path": "nodes.24.kind",
        "line": 500
      },
      "nodes.24.title": {
        "path": "nodes.24.title",
        "line": 501
      },
      "nodes.24.statement": {
        "path": "nodes.24.statement",
        "line": 502
      },
      "nodes.24.implements": {
        "path": "nodes.24.implements",
        "line": 503
      },
      "nodes.24.implements.0": {
        "path": "nodes.24.implements.0",
        "line": 503
      },
      "nodes.24.replaceable": {
        "path": "nodes.24.replaceable",
        "line": 504
      },
      "nodes.24.provenance": {
        "path": "nodes.24.provenance",
        "line": 506
      },
      "nodes.24.provenance.origin": {
        "path": "nodes.24.provenance.origin",
        "line": 506
      },
      "nodes.24.provenance.sources": {
        "path": "nodes.24.provenance.sources",
        "line": 507
      },
      "nodes.24.provenance.sources.0": {
        "path": "nodes.24.provenance.sources.0",
        "line": 507
      },
      "nodes.24.provenance.ai_roles": {
        "path": "nodes.24.provenance.ai_roles",
        "line": 508
      },
      "nodes.24.provenance.ai_roles.0": {
        "path": "nodes.24.provenance.ai_roles.0",
        "line": 508
      },
      "nodes.24.provenance.author_status": {
        "path": "nodes.24.provenance.author_status",
        "line": 509
      },
      "nodes.25": {
        "path": "nodes.25",
        "line": 511
      },
      "nodes.25.id": {
        "path": "nodes.25.id",
        "line": 511
      },
      "nodes.25.kind": {
        "path": "nodes.25.kind",
        "line": 512
      },
      "nodes.25.title": {
        "path": "nodes.25.title",
        "line": 513
      },
      "nodes.25.statement": {
        "path": "nodes.25.statement",
        "line": 514
      },
      "nodes.25.relates_to": {
        "path": "nodes.25.relates_to",
        "line": 515
      },
      "nodes.25.relates_to.0": {
        "path": "nodes.25.relates_to.0",
        "line": 515
      },
      "nodes.25.relates_to.1": {
        "path": "nodes.25.relates_to.1",
        "line": 515
      },
      "nodes.25.relates_to.2": {
        "path": "nodes.25.relates_to.2",
        "line": 515
      },
      "nodes.25.provenance": {
        "path": "nodes.25.provenance",
        "line": 517
      },
      "nodes.25.provenance.origin": {
        "path": "nodes.25.provenance.origin",
        "line": 517
      },
      "nodes.25.provenance.sources": {
        "path": "nodes.25.provenance.sources",
        "line": 518
      },
      "nodes.25.provenance.sources.0": {
        "path": "nodes.25.provenance.sources.0",
        "line": 518
      },
      "nodes.25.provenance.sources.1": {
        "path": "nodes.25.provenance.sources.1",
        "line": 518
      },
      "nodes.25.provenance.ai_roles": {
        "path": "nodes.25.provenance.ai_roles",
        "line": 519
      },
      "nodes.25.provenance.ai_roles.0": {
        "path": "nodes.25.provenance.ai_roles.0",
        "line": 519
      },
      "nodes.25.provenance.author_status": {
        "path": "nodes.25.provenance.author_status",
        "line": 520
      },
      "nodes.26": {
        "path": "nodes.26",
        "line": 521
      },
      "nodes.26.id": {
        "path": "nodes.26.id",
        "line": 521
      },
      "nodes.26.kind": {
        "path": "nodes.26.kind",
        "line": 522
      },
      "nodes.26.title": {
        "path": "nodes.26.title",
        "line": 523
      },
      "nodes.26.statement": {
        "path": "nodes.26.statement",
        "line": 524
      },
      "nodes.26.relates_to": {
        "path": "nodes.26.relates_to",
        "line": 525
      },
      "nodes.26.relates_to.0": {
        "path": "nodes.26.relates_to.0",
        "line": 525
      },
      "nodes.26.relates_to.1": {
        "path": "nodes.26.relates_to.1",
        "line": 525
      },
      "nodes.26.relates_to.2": {
        "path": "nodes.26.relates_to.2",
        "line": 525
      },
      "nodes.26.provenance": {
        "path": "nodes.26.provenance",
        "line": 527
      },
      "nodes.26.provenance.origin": {
        "path": "nodes.26.provenance.origin",
        "line": 527
      },
      "nodes.26.provenance.sources": {
        "path": "nodes.26.provenance.sources",
        "line": 528
      },
      "nodes.26.provenance.sources.0": {
        "path": "nodes.26.provenance.sources.0",
        "line": 528
      },
      "nodes.26.provenance.ai_roles": {
        "path": "nodes.26.provenance.ai_roles",
        "line": 529
      },
      "nodes.26.provenance.ai_roles.0": {
        "path": "nodes.26.provenance.ai_roles.0",
        "line": 529
      },
      "nodes.26.provenance.author_status": {
        "path": "nodes.26.provenance.author_status",
        "line": 530
      },
      "nodes.27": {
        "path": "nodes.27",
        "line": 531
      },
      "nodes.27.id": {
        "path": "nodes.27.id",
        "line": 531
      },
      "nodes.27.kind": {
        "path": "nodes.27.kind",
        "line": 532
      },
      "nodes.27.title": {
        "path": "nodes.27.title",
        "line": 533
      },
      "nodes.27.statement": {
        "path": "nodes.27.statement",
        "line": 534
      },
      "nodes.27.relates_to": {
        "path": "nodes.27.relates_to",
        "line": 535
      },
      "nodes.27.relates_to.0": {
        "path": "nodes.27.relates_to.0",
        "line": 535
      },
      "nodes.27.relates_to.1": {
        "path": "nodes.27.relates_to.1",
        "line": 535
      },
      "nodes.27.relates_to.2": {
        "path": "nodes.27.relates_to.2",
        "line": 535
      },
      "nodes.27.provenance": {
        "path": "nodes.27.provenance",
        "line": 537
      },
      "nodes.27.provenance.origin": {
        "path": "nodes.27.provenance.origin",
        "line": 537
      },
      "nodes.27.provenance.sources": {
        "path": "nodes.27.provenance.sources",
        "line": 538
      },
      "nodes.27.provenance.sources.0": {
        "path": "nodes.27.provenance.sources.0",
        "line": 538
      },
      "nodes.27.provenance.sources.1": {
        "path": "nodes.27.provenance.sources.1",
        "line": 538
      },
      "nodes.27.provenance.ai_roles": {
        "path": "nodes.27.provenance.ai_roles",
        "line": 539
      },
      "nodes.27.provenance.ai_roles.0": {
        "path": "nodes.27.provenance.ai_roles.0",
        "line": 539
      },
      "nodes.27.provenance.author_status": {
        "path": "nodes.27.provenance.author_status",
        "line": 540
      },
      "nodes.28": {
        "path": "nodes.28",
        "line": 541
      },
      "nodes.28.id": {
        "path": "nodes.28.id",
        "line": 541
      },
      "nodes.28.kind": {
        "path": "nodes.28.kind",
        "line": 542
      },
      "nodes.28.title": {
        "path": "nodes.28.title",
        "line": 543
      },
      "nodes.28.statement": {
        "path": "nodes.28.statement",
        "line": 544
      },
      "nodes.28.relates_to": {
        "path": "nodes.28.relates_to",
        "line": 545
      },
      "nodes.28.relates_to.0": {
        "path": "nodes.28.relates_to.0",
        "line": 545
      },
      "nodes.28.relates_to.1": {
        "path": "nodes.28.relates_to.1",
        "line": 545
      },
      "nodes.28.provenance": {
        "path": "nodes.28.provenance",
        "line": 547
      },
      "nodes.28.provenance.origin": {
        "path": "nodes.28.provenance.origin",
        "line": 547
      },
      "nodes.28.provenance.sources": {
        "path": "nodes.28.provenance.sources",
        "line": 548
      },
      "nodes.28.provenance.sources.0": {
        "path": "nodes.28.provenance.sources.0",
        "line": 548
      },
      "nodes.28.provenance.sources.1": {
        "path": "nodes.28.provenance.sources.1",
        "line": 548
      },
      "nodes.28.provenance.ai_roles": {
        "path": "nodes.28.provenance.ai_roles",
        "line": 549
      },
      "nodes.28.provenance.ai_roles.0": {
        "path": "nodes.28.provenance.ai_roles.0",
        "line": 549
      },
      "nodes.28.provenance.author_status": {
        "path": "nodes.28.provenance.author_status",
        "line": 550
      },
      "relations": {
        "path": "relations",
        "line": 553
      },
      "relations.0": {
        "path": "relations.0",
        "line": 553
      },
      "relations.0.from": {
        "path": "relations.0.from",
        "line": 553
      },
      "relations.0.to": {
        "path": "relations.0.to",
        "line": 553
      },
      "relations.0.type": {
        "path": "relations.0.type",
        "line": 553
      },
      "relations.1": {
        "path": "relations.1",
        "line": 554
      },
      "relations.1.from": {
        "path": "relations.1.from",
        "line": 554
      },
      "relations.1.to": {
        "path": "relations.1.to",
        "line": 554
      },
      "relations.1.type": {
        "path": "relations.1.type",
        "line": 554
      },
      "relations.2": {
        "path": "relations.2",
        "line": 555
      },
      "relations.2.from": {
        "path": "relations.2.from",
        "line": 555
      },
      "relations.2.to": {
        "path": "relations.2.to",
        "line": 555
      },
      "relations.2.type": {
        "path": "relations.2.type",
        "line": 555
      },
      "relations.3": {
        "path": "relations.3",
        "line": 556
      },
      "relations.3.from": {
        "path": "relations.3.from",
        "line": 556
      },
      "relations.3.to": {
        "path": "relations.3.to",
        "line": 556
      },
      "relations.3.type": {
        "path": "relations.3.type",
        "line": 556
      },
      "relations.4": {
        "path": "relations.4",
        "line": 557
      },
      "relations.4.from": {
        "path": "relations.4.from",
        "line": 557
      },
      "relations.4.to": {
        "path": "relations.4.to",
        "line": 557
      },
      "relations.4.type": {
        "path": "relations.4.type",
        "line": 557
      },
      "relations.5": {
        "path": "relations.5",
        "line": 558
      },
      "relations.5.from": {
        "path": "relations.5.from",
        "line": 558
      },
      "relations.5.to": {
        "path": "relations.5.to",
        "line": 558
      },
      "relations.5.type": {
        "path": "relations.5.type",
        "line": 558
      },
      "relations.6": {
        "path": "relations.6",
        "line": 559
      },
      "relations.6.from": {
        "path": "relations.6.from",
        "line": 559
      },
      "relations.6.to": {
        "path": "relations.6.to",
        "line": 559
      },
      "relations.6.type": {
        "path": "relations.6.type",
        "line": 559
      },
      "relations.7": {
        "path": "relations.7",
        "line": 560
      },
      "relations.7.from": {
        "path": "relations.7.from",
        "line": 560
      },
      "relations.7.to": {
        "path": "relations.7.to",
        "line": 560
      },
      "relations.7.type": {
        "path": "relations.7.type",
        "line": 560
      },
      "relations.8": {
        "path": "relations.8",
        "line": 561
      },
      "relations.8.from": {
        "path": "relations.8.from",
        "line": 561
      },
      "relations.8.to": {
        "path": "relations.8.to",
        "line": 561
      },
      "relations.8.type": {
        "path": "relations.8.type",
        "line": 561
      },
      "relations.9": {
        "path": "relations.9",
        "line": 562
      },
      "relations.9.from": {
        "path": "relations.9.from",
        "line": 562
      },
      "relations.9.to": {
        "path": "relations.9.to",
        "line": 562
      },
      "relations.9.type": {
        "path": "relations.9.type",
        "line": 562
      },
      "relations.10": {
        "path": "relations.10",
        "line": 563
      },
      "relations.10.from": {
        "path": "relations.10.from",
        "line": 563
      },
      "relations.10.to": {
        "path": "relations.10.to",
        "line": 563
      },
      "relations.10.type": {
        "path": "relations.10.type",
        "line": 563
      },
      "relations.11": {
        "path": "relations.11",
        "line": 564
      },
      "relations.11.from": {
        "path": "relations.11.from",
        "line": 564
      },
      "relations.11.to": {
        "path": "relations.11.to",
        "line": 564
      },
      "relations.11.type": {
        "path": "relations.11.type",
        "line": 564
      },
      "relations.12": {
        "path": "relations.12",
        "line": 565
      },
      "relations.12.from": {
        "path": "relations.12.from",
        "line": 565
      },
      "relations.12.to": {
        "path": "relations.12.to",
        "line": 565
      },
      "relations.12.type": {
        "path": "relations.12.type",
        "line": 565
      },
      "relations.13": {
        "path": "relations.13",
        "line": 566
      },
      "relations.13.from": {
        "path": "relations.13.from",
        "line": 566
      },
      "relations.13.to": {
        "path": "relations.13.to",
        "line": 566
      },
      "relations.13.type": {
        "path": "relations.13.type",
        "line": 566
      },
      "relations.14": {
        "path": "relations.14",
        "line": 567
      },
      "relations.14.from": {
        "path": "relations.14.from",
        "line": 567
      },
      "relations.14.to": {
        "path": "relations.14.to",
        "line": 567
      },
      "relations.14.type": {
        "path": "relations.14.type",
        "line": 567
      },
      "relations.15": {
        "path": "relations.15",
        "line": 568
      },
      "relations.15.from": {
        "path": "relations.15.from",
        "line": 568
      },
      "relations.15.to": {
        "path": "relations.15.to",
        "line": 568
      },
      "relations.15.type": {
        "path": "relations.15.type",
        "line": 568
      },
      "relations.16": {
        "path": "relations.16",
        "line": 569
      },
      "relations.16.from": {
        "path": "relations.16.from",
        "line": 569
      },
      "relations.16.to": {
        "path": "relations.16.to",
        "line": 569
      },
      "relations.16.type": {
        "path": "relations.16.type",
        "line": 569
      },
      "relations.17": {
        "path": "relations.17",
        "line": 570
      },
      "relations.17.from": {
        "path": "relations.17.from",
        "line": 570
      },
      "relations.17.to": {
        "path": "relations.17.to",
        "line": 570
      },
      "relations.17.type": {
        "path": "relations.17.type",
        "line": 570
      },
      "relations.18": {
        "path": "relations.18",
        "line": 571
      },
      "relations.18.from": {
        "path": "relations.18.from",
        "line": 571
      },
      "relations.18.to": {
        "path": "relations.18.to",
        "line": 571
      },
      "relations.18.type": {
        "path": "relations.18.type",
        "line": 571
      },
      "relations.19": {
        "path": "relations.19",
        "line": 572
      },
      "relations.19.from": {
        "path": "relations.19.from",
        "line": 572
      },
      "relations.19.to": {
        "path": "relations.19.to",
        "line": 572
      },
      "relations.19.type": {
        "path": "relations.19.type",
        "line": 572
      },
      "relations.20": {
        "path": "relations.20",
        "line": 573
      },
      "relations.20.from": {
        "path": "relations.20.from",
        "line": 573
      },
      "relations.20.to": {
        "path": "relations.20.to",
        "line": 573
      },
      "relations.20.type": {
        "path": "relations.20.type",
        "line": 573
      },
      "relations.21": {
        "path": "relations.21",
        "line": 574
      },
      "relations.21.from": {
        "path": "relations.21.from",
        "line": 574
      },
      "relations.21.to": {
        "path": "relations.21.to",
        "line": 574
      },
      "relations.21.type": {
        "path": "relations.21.type",
        "line": 574
      },
      "relations.22": {
        "path": "relations.22",
        "line": 575
      },
      "relations.22.from": {
        "path": "relations.22.from",
        "line": 575
      },
      "relations.22.to": {
        "path": "relations.22.to",
        "line": 575
      },
      "relations.22.type": {
        "path": "relations.22.type",
        "line": 575
      },
      "relations.23": {
        "path": "relations.23",
        "line": 576
      },
      "relations.23.from": {
        "path": "relations.23.from",
        "line": 576
      },
      "relations.23.to": {
        "path": "relations.23.to",
        "line": 576
      },
      "relations.23.type": {
        "path": "relations.23.type",
        "line": 576
      },
      "relations.24": {
        "path": "relations.24",
        "line": 577
      },
      "relations.24.from": {
        "path": "relations.24.from",
        "line": 577
      },
      "relations.24.to": {
        "path": "relations.24.to",
        "line": 577
      },
      "relations.24.type": {
        "path": "relations.24.type",
        "line": 577
      },
      "relations.25": {
        "path": "relations.25",
        "line": 578
      },
      "relations.25.from": {
        "path": "relations.25.from",
        "line": 578
      },
      "relations.25.to": {
        "path": "relations.25.to",
        "line": 578
      },
      "relations.25.type": {
        "path": "relations.25.type",
        "line": 578
      },
      "relations.26": {
        "path": "relations.26",
        "line": 579
      },
      "relations.26.from": {
        "path": "relations.26.from",
        "line": 579
      },
      "relations.26.to": {
        "path": "relations.26.to",
        "line": 579
      },
      "relations.26.type": {
        "path": "relations.26.type",
        "line": 579
      },
      "relations.27": {
        "path": "relations.27",
        "line": 580
      },
      "relations.27.from": {
        "path": "relations.27.from",
        "line": 580
      },
      "relations.27.to": {
        "path": "relations.27.to",
        "line": 580
      },
      "relations.27.type": {
        "path": "relations.27.type",
        "line": 580
      },
      "relations.28": {
        "path": "relations.28",
        "line": 581
      },
      "relations.28.from": {
        "path": "relations.28.from",
        "line": 581
      },
      "relations.28.to": {
        "path": "relations.28.to",
        "line": 581
      },
      "relations.28.type": {
        "path": "relations.28.type",
        "line": 581
      },
      "publication": {
        "path": "publication",
        "line": 584
      },
      "publication.layers": {
        "path": "publication.layers",
        "line": 585
      },
      "publication.layers.canonical": {
        "path": "publication.layers.canonical",
        "line": 586
      },
      "publication.layers.canonical.authority": {
        "path": "publication.layers.canonical.authority",
        "line": 586
      },
      "publication.layers.canonical.contains": {
        "path": "publication.layers.canonical.contains",
        "line": 587
      },
      "publication.layers.website": {
        "path": "publication.layers.website",
        "line": 589
      },
      "publication.layers.website.authority": {
        "path": "publication.layers.website.authority",
        "line": 589
      },
      "publication.layers.website.contains": {
        "path": "publication.layers.website.contains",
        "line": 590
      },
      "publication.layers.website.disagreement_rule": {
        "path": "publication.layers.website.disagreement_rule",
        "line": 591
      },
      "publication.layers.federation": {
        "path": "publication.layers.federation",
        "line": 593
      },
      "publication.layers.federation.authority": {
        "path": "publication.layers.federation.authority",
        "line": 593
      },
      "publication.layers.federation.contains": {
        "path": "publication.layers.federation.contains",
        "line": 594
      },
      "publication.rendering_model": {
        "path": "publication.rendering_model",
        "line": 596
      },
      "publication.rendering_model.universal_type": {
        "path": "publication.rendering_model.universal_type",
        "line": 596
      },
      "publication.rendering_model.rule": {
        "path": "publication.rendering_model.rule",
        "line": 597
      },
      "publication.rendering_model.required_fields": {
        "path": "publication.rendering_model.required_fields",
        "line": 600
      },
      "publication.rendering_model.required_fields.0": {
        "path": "publication.rendering_model.required_fields.0",
        "line": 600
      },
      "publication.rendering_model.required_fields.1": {
        "path": "publication.rendering_model.required_fields.1",
        "line": 600
      },
      "publication.rendering_model.required_fields.2": {
        "path": "publication.rendering_model.required_fields.2",
        "line": 600
      },
      "publication.rendering_model.required_fields.3": {
        "path": "publication.rendering_model.required_fields.3",
        "line": 600
      },
      "publication.rendering_model.required_fields.4": {
        "path": "publication.rendering_model.required_fields.4",
        "line": 600
      },
      "publication.rendering_model.required_fields.5": {
        "path": "publication.rendering_model.required_fields.5",
        "line": 600
      },
      "publication.rendering_model.optional_fields": {
        "path": "publication.rendering_model.optional_fields",
        "line": 601
      },
      "publication.rendering_model.optional_fields.0": {
        "path": "publication.rendering_model.optional_fields.0",
        "line": 601
      },
      "publication.rendering_model.optional_fields.1": {
        "path": "publication.rendering_model.optional_fields.1",
        "line": 601
      },
      "publication.rendering_model.optional_fields.2": {
        "path": "publication.rendering_model.optional_fields.2",
        "line": 601
      },
      "publication.rendering_model.optional_fields.3": {
        "path": "publication.rendering_model.optional_fields.3",
        "line": 601
      },
      "publication.verification_policy": {
        "path": "publication.verification_policy",
        "line": 603
      },
      "publication.verification_policy.principle": {
        "path": "publication.verification_policy.principle",
        "line": 603
      },
      "publication.verification_policy.outcomes": {
        "path": "publication.verification_policy.outcomes",
        "line": 605
      },
      "publication.verification_policy.outcomes.0": {
        "path": "publication.verification_policy.outcomes.0",
        "line": 605
      },
      "publication.verification_policy.outcomes.1": {
        "path": "publication.verification_policy.outcomes.1",
        "line": 606
      },
      "publication.verification_policy.outcomes.2": {
        "path": "publication.verification_policy.outcomes.2",
        "line": 607
      },
      "publication.verification_policy.attestation_must_name": {
        "path": "publication.verification_policy.attestation_must_name",
        "line": 609
      },
      "publication.verification_policy.attestation_must_name.0": {
        "path": "publication.verification_policy.attestation_must_name.0",
        "line": 609
      },
      "publication.verification_policy.attestation_must_name.1": {
        "path": "publication.verification_policy.attestation_must_name.1",
        "line": 610
      },
      "publication.verification_policy.attestation_must_name.2": {
        "path": "publication.verification_policy.attestation_must_name.2",
        "line": 611
      },
      "publication.verification_policy.attestation_must_name.3": {
        "path": "publication.verification_policy.attestation_must_name.3",
        "line": 612
      },
      "publication.verification_policy.attestation_must_name.4": {
        "path": "publication.verification_policy.attestation_must_name.4",
        "line": 613
      },
      "publication.verification_policy.attestation_must_name.5": {
        "path": "publication.verification_policy.attestation_must_name.5",
        "line": 614
      },
      "acceptance_tests": {
        "path": "acceptance_tests",
        "line": 617
      },
      "acceptance_tests.0": {
        "path": "acceptance_tests.0",
        "line": 617
      },
      "acceptance_tests.0.id": {
        "path": "acceptance_tests.0.id",
        "line": 617
      },
      "acceptance_tests.0.title": {
        "path": "acceptance_tests.0.title",
        "line": 618
      },
      "acceptance_tests.0.rationale": {
        "path": "acceptance_tests.0.rationale",
        "line": 619
      },
      "acceptance_tests.0.assertions": {
        "path": "acceptance_tests.0.assertions",
        "line": 621
      },
      "acceptance_tests.0.assertions.0": {
        "path": "acceptance_tests.0.assertions.0",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.type": {
        "path": "acceptance_tests.0.assertions.0.type",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.kind": {
        "path": "acceptance_tests.0.assertions.0.kind",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.ids": {
        "path": "acceptance_tests.0.assertions.0.ids",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.ids.0": {
        "path": "acceptance_tests.0.assertions.0.ids.0",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.ids.1": {
        "path": "acceptance_tests.0.assertions.0.ids.1",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.ids.2": {
        "path": "acceptance_tests.0.assertions.0.ids.2",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.ids.3": {
        "path": "acceptance_tests.0.assertions.0.ids.3",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.ids.4": {
        "path": "acceptance_tests.0.assertions.0.ids.4",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.ids.5": {
        "path": "acceptance_tests.0.assertions.0.ids.5",
        "line": 621
      },
      "acceptance_tests.0.assertions.0.ids.6": {
        "path": "acceptance_tests.0.assertions.0.ids.6",
        "line": 621
      },
      "acceptance_tests.1": {
        "path": "acceptance_tests.1",
        "line": 622
      },
      "acceptance_tests.1.id": {
        "path": "acceptance_tests.1.id",
        "line": 622
      },
      "acceptance_tests.1.title": {
        "path": "acceptance_tests.1.title",
        "line": 623
      },
      "acceptance_tests.1.rationale": {
        "path": "acceptance_tests.1.rationale",
        "line": 624
      },
      "acceptance_tests.1.assertions": {
        "path": "acceptance_tests.1.assertions",
        "line": 626
      },
      "acceptance_tests.1.assertions.0": {
        "path": "acceptance_tests.1.assertions.0",
        "line": 626
      },
      "acceptance_tests.1.assertions.0.type": {
        "path": "acceptance_tests.1.assertions.0.type",
        "line": 626
      },
      "acceptance_tests.1.assertions.0.id": {
        "path": "acceptance_tests.1.assertions.0.id",
        "line": 626
      },
      "acceptance_tests.1.assertions.0.kind": {
        "path": "acceptance_tests.1.assertions.0.kind",
        "line": 626
      },
      "acceptance_tests.1.assertions.1": {
        "path": "acceptance_tests.1.assertions.1",
        "line": 627
      },
      "acceptance_tests.1.assertions.1.type": {
        "path": "acceptance_tests.1.assertions.1.type",
        "line": 627
      },
      "acceptance_tests.1.assertions.1.id": {
        "path": "acceptance_tests.1.assertions.1.id",
        "line": 627
      },
      "acceptance_tests.1.assertions.1.kind": {
        "path": "acceptance_tests.1.assertions.1.kind",
        "line": 627
      },
      "acceptance_tests.1.assertions.2": {
        "path": "acceptance_tests.1.assertions.2",
        "line": 628
      },
      "acceptance_tests.1.assertions.2.type": {
        "path": "acceptance_tests.1.assertions.2.type",
        "line": 628
      },
      "acceptance_tests.1.assertions.2.from": {
        "path": "acceptance_tests.1.assertions.2.from",
        "line": 628
      },
      "acceptance_tests.1.assertions.2.to": {
        "path": "acceptance_tests.1.assertions.2.to",
        "line": 628
      },
      "acceptance_tests.1.assertions.3": {
        "path": "acceptance_tests.1.assertions.3",
        "line": 629
      },
      "acceptance_tests.1.assertions.3.type": {
        "path": "acceptance_tests.1.assertions.3.type",
        "line": 629
      },
      "acceptance_tests.1.assertions.3.from": {
        "path": "acceptance_tests.1.assertions.3.from",
        "line": 629
      },
      "acceptance_tests.1.assertions.3.to": {
        "path": "acceptance_tests.1.assertions.3.to",
        "line": 629
      },
      "acceptance_tests.2": {
        "path": "acceptance_tests.2",
        "line": 630
      },
      "acceptance_tests.2.id": {
        "path": "acceptance_tests.2.id",
        "line": 630
      },
      "acceptance_tests.2.title": {
        "path": "acceptance_tests.2.title",
        "line": 631
      },
      "acceptance_tests.2.rationale": {
        "path": "acceptance_tests.2.rationale",
        "line": 632
      },
      "acceptance_tests.2.assertions": {
        "path": "acceptance_tests.2.assertions",
        "line": 634
      },
      "acceptance_tests.2.assertions.0": {
        "path": "acceptance_tests.2.assertions.0",
        "line": 634
      },
      "acceptance_tests.2.assertions.0.type": {
        "path": "acceptance_tests.2.assertions.0.type",
        "line": 634
      },
      "acceptance_tests.2.assertions.0.id": {
        "path": "acceptance_tests.2.assertions.0.id",
        "line": 634
      },
      "acceptance_tests.2.assertions.0.kind": {
        "path": "acceptance_tests.2.assertions.0.kind",
        "line": 634
      },
      "acceptance_tests.2.assertions.1": {
        "path": "acceptance_tests.2.assertions.1",
        "line": 635
      },
      "acceptance_tests.2.assertions.1.type": {
        "path": "acceptance_tests.2.assertions.1.type",
        "line": 635
      },
      "acceptance_tests.2.assertions.1.from": {
        "path": "acceptance_tests.2.assertions.1.from",
        "line": 635
      },
      "acceptance_tests.2.assertions.1.to": {
        "path": "acceptance_tests.2.assertions.1.to",
        "line": 635
      },
      "acceptance_tests.3": {
        "path": "acceptance_tests.3",
        "line": 636
      },
      "acceptance_tests.3.id": {
        "path": "acceptance_tests.3.id",
        "line": 636
      },
      "acceptance_tests.3.title": {
        "path": "acceptance_tests.3.title",
        "line": 637
      },
      "acceptance_tests.3.rationale": {
        "path": "acceptance_tests.3.rationale",
        "line": 638
      },
      "acceptance_tests.3.assertions": {
        "path": "acceptance_tests.3.assertions",
        "line": 640
      },
      "acceptance_tests.3.assertions.0": {
        "path": "acceptance_tests.3.assertions.0",
        "line": 640
      },
      "acceptance_tests.3.assertions.0.type": {
        "path": "acceptance_tests.3.assertions.0.type",
        "line": 640
      },
      "acceptance_tests.3.assertions.0.from": {
        "path": "acceptance_tests.3.assertions.0.from",
        "line": 640
      },
      "acceptance_tests.3.assertions.0.to": {
        "path": "acceptance_tests.3.assertions.0.to",
        "line": 640
      },
      "acceptance_tests.3.assertions.1": {
        "path": "acceptance_tests.3.assertions.1",
        "line": 641
      },
      "acceptance_tests.3.assertions.1.type": {
        "path": "acceptance_tests.3.assertions.1.type",
        "line": 641
      },
      "acceptance_tests.3.assertions.1.from": {
        "path": "acceptance_tests.3.assertions.1.from",
        "line": 641
      },
      "acceptance_tests.3.assertions.1.to": {
        "path": "acceptance_tests.3.assertions.1.to",
        "line": 641
      },
      "acceptance_tests.3.assertions.2": {
        "path": "acceptance_tests.3.assertions.2",
        "line": 642
      },
      "acceptance_tests.3.assertions.2.type": {
        "path": "acceptance_tests.3.assertions.2.type",
        "line": 642
      },
      "acceptance_tests.3.assertions.2.from": {
        "path": "acceptance_tests.3.assertions.2.from",
        "line": 642
      },
      "acceptance_tests.3.assertions.2.to": {
        "path": "acceptance_tests.3.assertions.2.to",
        "line": 642
      },
      "acceptance_tests.4": {
        "path": "acceptance_tests.4",
        "line": 643
      },
      "acceptance_tests.4.id": {
        "path": "acceptance_tests.4.id",
        "line": 643
      },
      "acceptance_tests.4.title": {
        "path": "acceptance_tests.4.title",
        "line": 644
      },
      "acceptance_tests.4.rationale": {
        "path": "acceptance_tests.4.rationale",
        "line": 645
      },
      "acceptance_tests.4.assertions": {
        "path": "acceptance_tests.4.assertions",
        "line": 647
      },
      "acceptance_tests.4.assertions.0": {
        "path": "acceptance_tests.4.assertions.0",
        "line": 647
      },
      "acceptance_tests.4.assertions.0.type": {
        "path": "acceptance_tests.4.assertions.0.type",
        "line": 647
      },
      "acceptance_tests.4.assertions.0.id": {
        "path": "acceptance_tests.4.assertions.0.id",
        "line": 647
      },
      "acceptance_tests.4.assertions.0.kind": {
        "path": "acceptance_tests.4.assertions.0.kind",
        "line": 647
      },
      "acceptance_tests.4.assertions.1": {
        "path": "acceptance_tests.4.assertions.1",
        "line": 648
      },
      "acceptance_tests.4.assertions.1.type": {
        "path": "acceptance_tests.4.assertions.1.type",
        "line": 648
      },
      "acceptance_tests.4.assertions.1.id": {
        "path": "acceptance_tests.4.assertions.1.id",
        "line": 648
      },
      "acceptance_tests.4.assertions.1.kind": {
        "path": "acceptance_tests.4.assertions.1.kind",
        "line": 648
      },
      "acceptance_tests.4.assertions.2": {
        "path": "acceptance_tests.4.assertions.2",
        "line": 649
      },
      "acceptance_tests.4.assertions.2.type": {
        "path": "acceptance_tests.4.assertions.2.type",
        "line": 649
      },
      "acceptance_tests.4.assertions.2.from": {
        "path": "acceptance_tests.4.assertions.2.from",
        "line": 649
      },
      "acceptance_tests.4.assertions.2.to": {
        "path": "acceptance_tests.4.assertions.2.to",
        "line": 649
      },
      "acceptance_tests.4.assertions.3": {
        "path": "acceptance_tests.4.assertions.3",
        "line": 650
      },
      "acceptance_tests.4.assertions.3.type": {
        "path": "acceptance_tests.4.assertions.3.type",
        "line": 650
      },
      "acceptance_tests.4.assertions.3.from": {
        "path": "acceptance_tests.4.assertions.3.from",
        "line": 650
      },
      "acceptance_tests.4.assertions.3.to": {
        "path": "acceptance_tests.4.assertions.3.to",
        "line": 650
      },
      "acceptance_tests.5": {
        "path": "acceptance_tests.5",
        "line": 651
      },
      "acceptance_tests.5.id": {
        "path": "acceptance_tests.5.id",
        "line": 651
      },
      "acceptance_tests.5.title": {
        "path": "acceptance_tests.5.title",
        "line": 652
      },
      "acceptance_tests.5.rationale": {
        "path": "acceptance_tests.5.rationale",
        "line": 653
      },
      "acceptance_tests.5.assertions": {
        "path": "acceptance_tests.5.assertions",
        "line": 655
      },
      "acceptance_tests.5.assertions.0": {
        "path": "acceptance_tests.5.assertions.0",
        "line": 655
      },
      "acceptance_tests.5.assertions.0.type": {
        "path": "acceptance_tests.5.assertions.0.type",
        "line": 655
      },
      "acceptance_tests.5.assertions.0.from": {
        "path": "acceptance_tests.5.assertions.0.from",
        "line": 655
      },
      "acceptance_tests.5.assertions.0.to": {
        "path": "acceptance_tests.5.assertions.0.to",
        "line": 655
      },
      "acceptance_tests.5.assertions.1": {
        "path": "acceptance_tests.5.assertions.1",
        "line": 656
      },
      "acceptance_tests.5.assertions.1.type": {
        "path": "acceptance_tests.5.assertions.1.type",
        "line": 656
      },
      "acceptance_tests.5.assertions.1.from": {
        "path": "acceptance_tests.5.assertions.1.from",
        "line": 656
      },
      "acceptance_tests.5.assertions.1.to": {
        "path": "acceptance_tests.5.assertions.1.to",
        "line": 656
      },
      "acceptance_tests.5.assertions.2": {
        "path": "acceptance_tests.5.assertions.2",
        "line": 657
      },
      "acceptance_tests.5.assertions.2.type": {
        "path": "acceptance_tests.5.assertions.2.type",
        "line": 657
      },
      "acceptance_tests.5.assertions.2.from": {
        "path": "acceptance_tests.5.assertions.2.from",
        "line": 657
      },
      "acceptance_tests.5.assertions.2.to": {
        "path": "acceptance_tests.5.assertions.2.to",
        "line": 657
      },
      "acceptance_tests.6": {
        "path": "acceptance_tests.6",
        "line": 658
      },
      "acceptance_tests.6.id": {
        "path": "acceptance_tests.6.id",
        "line": 658
      },
      "acceptance_tests.6.title": {
        "path": "acceptance_tests.6.title",
        "line": 659
      },
      "acceptance_tests.6.rationale": {
        "path": "acceptance_tests.6.rationale",
        "line": 660
      },
      "acceptance_tests.6.assertions": {
        "path": "acceptance_tests.6.assertions",
        "line": 662
      },
      "acceptance_tests.6.assertions.0": {
        "path": "acceptance_tests.6.assertions.0",
        "line": 662
      },
      "acceptance_tests.6.assertions.0.type": {
        "path": "acceptance_tests.6.assertions.0.type",
        "line": 662
      },
      "acceptance_tests.6.assertions.0.kind": {
        "path": "acceptance_tests.6.assertions.0.kind",
        "line": 662
      },
      "acceptance_tests.6.assertions.0.fields": {
        "path": "acceptance_tests.6.assertions.0.fields",
        "line": 662
      },
      "acceptance_tests.6.assertions.0.fields.0": {
        "path": "acceptance_tests.6.assertions.0.fields.0",
        "line": 662
      },
      "acceptance_tests.6.assertions.0.fields.1": {
        "path": "acceptance_tests.6.assertions.0.fields.1",
        "line": 662
      },
      "acceptance_tests.6.assertions.0.fields.2": {
        "path": "acceptance_tests.6.assertions.0.fields.2",
        "line": 662
      },
      "acceptance_tests.7": {
        "path": "acceptance_tests.7",
        "line": 663
      },
      "acceptance_tests.7.id": {
        "path": "acceptance_tests.7.id",
        "line": 663
      },
      "acceptance_tests.7.title": {
        "path": "acceptance_tests.7.title",
        "line": 664
      },
      "acceptance_tests.7.rationale": {
        "path": "acceptance_tests.7.rationale",
        "line": 665
      },
      "acceptance_tests.7.assertions": {
        "path": "acceptance_tests.7.assertions",
        "line": 667
      },
      "acceptance_tests.7.assertions.0": {
        "path": "acceptance_tests.7.assertions.0",
        "line": 667
      },
      "acceptance_tests.7.assertions.0.type": {
        "path": "acceptance_tests.7.assertions.0.type",
        "line": 667
      },
      "acceptance_tests.7.assertions.0.kind": {
        "path": "acceptance_tests.7.assertions.0.kind",
        "line": 667
      },
      "acceptance_tests.7.assertions.0.field": {
        "path": "acceptance_tests.7.assertions.0.field",
        "line": 667
      },
      "acceptance_tests.7.assertions.0.value": {
        "path": "acceptance_tests.7.assertions.0.value",
        "line": 667
      },
      "acceptance_tests.8": {
        "path": "acceptance_tests.8",
        "line": 668
      },
      "acceptance_tests.8.id": {
        "path": "acceptance_tests.8.id",
        "line": 668
      },
      "acceptance_tests.8.title": {
        "path": "acceptance_tests.8.title",
        "line": 669
      },
      "acceptance_tests.8.rationale": {
        "path": "acceptance_tests.8.rationale",
        "line": 670
      },
      "acceptance_tests.8.assertions": {
        "path": "acceptance_tests.8.assertions",
        "line": 672
      },
      "acceptance_tests.8.assertions.0": {
        "path": "acceptance_tests.8.assertions.0",
        "line": 672
      },
      "acceptance_tests.8.assertions.0.type": {
        "path": "acceptance_tests.8.assertions.0.type",
        "line": 672
      },
      "acceptance_tests.9": {
        "path": "acceptance_tests.9",
        "line": 673
      },
      "acceptance_tests.9.id": {
        "path": "acceptance_tests.9.id",
        "line": 673
      },
      "acceptance_tests.9.title": {
        "path": "acceptance_tests.9.title",
        "line": 674
      },
      "acceptance_tests.9.rationale": {
        "path": "acceptance_tests.9.rationale",
        "line": 675
      },
      "acceptance_tests.9.assertions": {
        "path": "acceptance_tests.9.assertions",
        "line": 677
      },
      "acceptance_tests.9.assertions.0": {
        "path": "acceptance_tests.9.assertions.0",
        "line": 677
      },
      "acceptance_tests.9.assertions.0.type": {
        "path": "acceptance_tests.9.assertions.0.type",
        "line": 677
      },
      "acceptance_tests.9.assertions.0.path": {
        "path": "acceptance_tests.9.assertions.0.path",
        "line": 677
      },
      "acceptance_tests.9.assertions.0.value": {
        "path": "acceptance_tests.9.assertions.0.value",
        "line": 677
      },
      "acceptance_tests.9.assertions.1": {
        "path": "acceptance_tests.9.assertions.1",
        "line": 678
      },
      "acceptance_tests.9.assertions.1.type": {
        "path": "acceptance_tests.9.assertions.1.type",
        "line": 678
      },
      "acceptance_tests.9.assertions.1.path": {
        "path": "acceptance_tests.9.assertions.1.path",
        "line": 678
      },
      "acceptance_tests.9.assertions.1.value": {
        "path": "acceptance_tests.9.assertions.1.value",
        "line": 678
      },
      "acceptance_tests.9.assertions.2": {
        "path": "acceptance_tests.9.assertions.2",
        "line": 679
      },
      "acceptance_tests.9.assertions.2.type": {
        "path": "acceptance_tests.9.assertions.2.type",
        "line": 679
      },
      "acceptance_tests.9.assertions.2.path": {
        "path": "acceptance_tests.9.assertions.2.path",
        "line": 679
      },
      "acceptance_tests.9.assertions.2.value": {
        "path": "acceptance_tests.9.assertions.2.value",
        "line": 679
      },
      "deterministic_views": {
        "path": "deterministic_views",
        "line": 682
      },
      "deterministic_views.0": {
        "path": "deterministic_views.0",
        "line": 682
      },
      "deterministic_views.0.id": {
        "path": "deterministic_views.0.id",
        "line": 682
      },
      "deterministic_views.0.path": {
        "path": "deterministic_views.0.path",
        "line": 683
      },
      "deterministic_views.0.purpose": {
        "path": "deterministic_views.0.purpose",
        "line": 684
      },
      "deterministic_views.1": {
        "path": "deterministic_views.1",
        "line": 685
      },
      "deterministic_views.1.id": {
        "path": "deterministic_views.1.id",
        "line": 685
      },
      "deterministic_views.1.path": {
        "path": "deterministic_views.1.path",
        "line": 686
      },
      "deterministic_views.1.purpose": {
        "path": "deterministic_views.1.purpose",
        "line": 687
      },
      "deterministic_views.2": {
        "path": "deterministic_views.2",
        "line": 688
      },
      "deterministic_views.2.id": {
        "path": "deterministic_views.2.id",
        "line": 688
      },
      "deterministic_views.2.path": {
        "path": "deterministic_views.2.path",
        "line": 689
      },
      "deterministic_views.2.purpose": {
        "path": "deterministic_views.2.purpose",
        "line": 690
      },
      "deterministic_views.3": {
        "path": "deterministic_views.3",
        "line": 691
      },
      "deterministic_views.3.id": {
        "path": "deterministic_views.3.id",
        "line": 691
      },
      "deterministic_views.3.path": {
        "path": "deterministic_views.3.path",
        "line": 692
      },
      "deterministic_views.3.purpose": {
        "path": "deterministic_views.3.purpose",
        "line": 693
      },
      "deterministic_views.4": {
        "path": "deterministic_views.4",
        "line": 694
      },
      "deterministic_views.4.id": {
        "path": "deterministic_views.4.id",
        "line": 694
      },
      "deterministic_views.4.path": {
        "path": "deterministic_views.4.path",
        "line": 695
      },
      "deterministic_views.4.purpose": {
        "path": "deterministic_views.4.purpose",
        "line": 696
      },
      "deterministic_views.5": {
        "path": "deterministic_views.5",
        "line": 697
      },
      "deterministic_views.5.id": {
        "path": "deterministic_views.5.id",
        "line": 697
      },
      "deterministic_views.5.path": {
        "path": "deterministic_views.5.path",
        "line": 698
      },
      "deterministic_views.5.purpose": {
        "path": "deterministic_views.5.purpose",
        "line": 699
      },
      "deterministic_views.6": {
        "path": "deterministic_views.6",
        "line": 700
      },
      "deterministic_views.6.id": {
        "path": "deterministic_views.6.id",
        "line": 700
      },
      "deterministic_views.6.path": {
        "path": "deterministic_views.6.path",
        "line": 701
      },
      "deterministic_views.6.purpose": {
        "path": "deterministic_views.6.purpose",
        "line": 702
      }
    },
    "experience": {
      "label": "Explore the policy lens",
      "url": "https://ai-pacing.proximitytoprogress.com/",
      "description": "Interactive policy explorer"
    },
    "repository": "https://github.com/uncomposed/ai-pacing.git",
    "commit": "a28a40c2b964f960f196626f0ed64d59f241b7c2",
    "modelPath": "spec/ai-pacing.yaml",
    "modelSha256": "f428d25aa21b91eb1e5c97455ef827d3ca502beb04cc08e53b0f5ca23924f753",
    "modelUrl": "/ideas/models/ai-pacing-f428d25aa21b.yaml",
    "registryUrl": "https://ideas.proximitytoprogress.com/ideas/ai-pacing"
  }
] as ExplorerModel[]
